import { IndirectStorageBufferAttribute, StorageBufferAttribute } from 'three/webgpu';
import {
  storage,
  uniform,
  workgroupArray,
  workgroupId,
  localId,
  wgsl,
  wgslFn,
} from './tslCompat';
import { qrot, qconj, qmul } from './quatUtils';
import {
  AVBD_COLLISION_MARGIN,
  AVBD_FRICTION_DYNAMIC,
  AVBD_FRICTION_STATIC,
  AVBD_REFERENCE_ANGULAR_DUAL_UPDATE_BETA,
  AVBD_REFERENCE_DUAL_UPDATE_BETA,
  AVBD_REFERENCE_PENALTY_DECAY_GAMMA,
  AVBD_REFERENCE_REGULARIZATION_ALPHA,
} from '../avbdParams';
import type { AvbdBodySolveMode } from '../types';
import { assertStorageBufferBudget } from './bindingBudget';
import {
  CONTACT_INFO_SPECULATIVE_BIT,
  CONTACT_RECORD_ARM_A_OFFSET,
  CONTACT_RECORD_ARM_B_OFFSET,
  CONTACT_RECORD_CONSTRAINT_C0_OFFSET,
  CONTACT_RECORD_CACHE_OFFSET,
  CONTACT_RECORD_DUAL_OFFSET,
  CONTACT_RECORD_FLOATS,
  CONTACT_RECORD_META_OFFSET,
  CONTACT_RECORD_NORMAL_PEN_OFFSET,
  CONTACT_RECORD_PENALTY_OFFSET,
  CONTACT_RECORD_VEC4S,
  contactRecordHelpers,
  contactRecordVec4FloatIndex,
} from './contactRecord';
import {
  JOINT_RECORD_VEC4S,
  jointRecordHelpers,
} from './jointRecord';
import {
  SPRING_RECORD_VEC4S,
  springRecordHelpers,
} from './springRecord';
import {
  AVBD_RELATIVE_LDL_PIVOT,
  makePackedLdl6RankOneStampWGSL,
  makePackedModifiedLdl6SolveWGSL,
} from './modifiedLdl6';
import {
  decodeSolverContactListHeader,
  SOLVER_CONTACT_CANONICAL_ALIAS_BIT,
  SOLVER_CONTACT_COUNT_MASK,
} from './solverContactList';

const WORKGROUP_SIZE = 64;
// WebGPU guarantees at least 65,535 workgroups per dispatch dimension. Active
// contact streams can exceed that on the 510K path, so contact-only indirect
// dispatches tile X into Y while retaining the identical linear row order.
const CONTACT_DISPATCH_WORKGROUP_WIDTH = 65_535;

export function resolveAvbdContactDispatchForTesting(
  activeContactCount: number,
): readonly [number, number, number] {
  const contacts = Math.max(0, Math.floor(activeContactCount));
  const workgroups = Math.ceil(contacts / WORKGROUP_SIZE);
  return Object.freeze([
    Math.min(workgroups, CONTACT_DISPATCH_WORKGROUP_WIDTH),
    Math.max(1, Math.ceil(workgroups / CONTACT_DISPATCH_WORKGROUP_WIDTH)),
    1,
  ] as const);
}
// VBD/AVBD's paper implementation assigns one 16-lane workgroup to one
// body. Keep the general-purpose kernels at 64 threads and use this mapping
// only for the explicitly enabled fixed contact-only reference solve.
const COOPERATIVE_BODY_SOLVE_LANES = 16;
const COOPERATIVE_PACKED_SYSTEM_VEC4S = 7;
const COOPERATIVE_PACKED_SYSTEM_SHARED_VEC4S =
  COOPERATIVE_BODY_SOLVE_LANES * COOPERATIVE_PACKED_SYSTEM_VEC4S;
const AVBD_GAMMA = AVBD_REFERENCE_PENALTY_DECAY_GAMMA;
const AVBD_BETA = AVBD_REFERENCE_DUAL_UPDATE_BETA;
const AVBD_K_START = 1.0;
const AVBD_JOINT_PENALTY_MAX = 10000000000.0;
const AVBD_STICK_THRESHOLD = 1e-5;
const AVBD_REGULARIZATION_ALPHA_DEFAULT = AVBD_REFERENCE_REGULARIZATION_ALPHA;
// The body solve is evaluated in f32. Keep the inertial seed and LDL safety
// pivot relative to each coordinate's own scale instead of mixing metres and
// radians through one global maximum. Eight/32 ulps cover f32 accumulation.
const AVBD_RELATIVE_INERTIAL_SHIFT = 8.0 * 1.1920928955078125e-7;
const INERTIAL_POSE_VEC4S_PER_BODY = 5;
const CONTACT_SOLVER_GEOMETRY_VEC4S = 5;
const BODY_COLOR_FALLBACK_FLAG = 0x01000000;
const BODY_COLOR_REPAIR_FLAG = 0x02000000;
// Written by SleepingIslandsStage after narrowphase. It lives outside the
// 16-bit degree and 8-bit color fields, and is preserved by coloring so later
// stages can distinguish an intentionally frozen zero-constraint body.
export const BODY_SLEEP_FLAG = 0x04000000;
const CONSTRAINT_REF_TAG_MASK = 0xc0000000;
const CONSTRAINT_REF_INDEX_MASK = 0x3fffffff;
const CONSTRAINT_REF_TAG_JOINT = 0x80000000;
const CONSTRAINT_REF_TAG_SPRING = 0xc0000000;
const BODY_COLOR_HARD_REPAIR_ROUNDS = 2;
const BODY_COLOR_DISPATCH_ARGS_BATCH_SIZE = 9;
// During an AVBD solve, active contact arms temporarily hold their world-space
// values at x^t.  The persistent local/world representation is kept in fields
// that are dead after prepare has consumed the previous warm-start shadow:
//   shadow.xyz = local/world arm A, shadow.w + C0.w + armB.w = arm B.
// meta.w carries the original sphere/world-arm flags and an explicit scratch
// marker until the unconditional finalize pass restores the persistent record.
const CONTACT_SOLVER_SCRATCH_ARM_FLAGS_SHIFT = 24;
const CONTACT_SOLVER_SCRATCH_ACTIVE_BIT = 0x04000000;
const CONTACT_SOLVER_SCRATCH_MASK = 0x07000000;
// Solver iterations touch dual/penalty state far more often than the complete
// 9-vec4 persistent contact record. Keep those two vec4s in a dense sidecar so
// the fixed reference path does not pull a 144-byte-stride record for every
// body/contact visit. Values remain f32 and are copied without arithmetic.
const CONTACT_SOLVER_HOT_VEC4S = 2;
const contactSolverHotHelpers = wgsl(/* wgsl */`
      fn contactSolverHotBase(p: u32) -> u32 {
        return p * ${CONTACT_SOLVER_HOT_VEC4S}u;
      }

      fn loadSolverContactDual(
        contactSolverHot: ptr<storage, array<vec4f>, read_write>,
        p: u32,
      ) -> vec4f {
        return contactSolverHot[contactSolverHotBase(p)];
      }

      fn loadSolverContactPenalty(
        contactSolverHot: ptr<storage, array<vec4f>, read_write>,
        p: u32,
      ) -> vec4f {
        return contactSolverHot[contactSolverHotBase(p) + 1u];
      }

      fn storeSolverContactState(
        contactSolverHot: ptr<storage, array<vec4f>, read_write>,
        p: u32,
        dual: vec4f,
        penalty: vec4f,
      ) {
        let base = contactSolverHotBase(p);
        contactSolverHot[base] = dual;
        contactSolverHot[base + 1u] = penalty;
      }
`);
// GPU-only adaptive iteration state. Slots 0..4 and 7..9 describe the current
// substep, slots 5..6 and 10..13 are monotonically accumulated diagnostics,
// slots 14..15 retain the current min/max bounds, and slot 16 persists the
// previous substep's fallback count for topology-transition fail-open. Slot 17
// isolates the current fallback scratch lane's body residual violation. Slot
// 18 is the queue-ordered per-substep residual certificate. Sleeping combines
// it with the four-step polish counter before admitting an adaptive island.
// Slot 19 enables residual sampling only for rounds that can contribute to the
// two-round convergence certificate. Slot 20 preserves any non-finite result
// observed during the pre-minimum warm-up rounds until the first decision.
// Slots 21..24 implement the queue-ordered adaptive sleep-polish handshake:
// sleeping publishes a request for the next substep, initialize consumes it,
// and only four consecutive converged full-max substeps open the sleep gate.
// The CPU can therefore sample convergence/fail-open history without
// synchronizing every round.
export const AVBD_ADAPTIVE_STATE_WORDS = 25;
export const AVBD_ADAPTIVE_SLEEP_CONVERGED = 18;
export const AVBD_ADAPTIVE_SLEEP_POLISH_REQUESTED = 21;
export const AVBD_ADAPTIVE_SLEEP_POLISH_COMPLETED = 23;
export const AVBD_ADAPTIVE_SLEEP_POLISH_CONFIGURED = 24;
export const AVBD_ADAPTIVE_SLEEP_POLISH_STEPS = 4;
const ADAPTIVE_STATE_WORDS = AVBD_ADAPTIVE_STATE_WORDS;
const ADAPTIVE_RESIDUAL_SHARDS = 1024;
const ADAPTIVE_RESIDUAL_WORDS_PER_SHARD = 2;
const ADAPTIVE_RESIDUAL_WORDS = ADAPTIVE_RESIDUAL_SHARDS * ADAPTIVE_RESIDUAL_WORDS_PER_SHARD;
const ADAPTIVE_ENABLED = 0;
const ADAPTIVE_BELOW_STREAK = 1;
const ADAPTIVE_EXECUTED_LAST = 2;
const ADAPTIVE_BODY_VIOLATION = 3;
const ADAPTIVE_CONTACT_VIOLATION = 4;
const ADAPTIVE_TOTAL_FALLBACK_FAIL_OPEN = 5;
const ADAPTIVE_TOTAL_INVALID_FAIL_OPEN = 6;
const ADAPTIVE_INVALID_LAST = 7;
const ADAPTIVE_EARLY_LAST = 8;
const ADAPTIVE_FALLBACK_LAST = 9;
const ADAPTIVE_TOTAL_STEPS = 10;
const ADAPTIVE_TOTAL_ITERATIONS = 11;
const ADAPTIVE_TOTAL_EARLY_EXITS = 12;
const ADAPTIVE_TOTAL_FAIL_OPEN = 13;
const ADAPTIVE_MIN_LAST = 14;
const ADAPTIVE_MAX_LAST = 15;
const ADAPTIVE_PREVIOUS_FALLBACK_COUNT = 16;
const ADAPTIVE_FALLBACK_BODY_VIOLATION = 17;
const ADAPTIVE_TRACK_RESIDUALS = 19;
const ADAPTIVE_PREMIN_INVALID = 20;
const ADAPTIVE_SLEEP_POLISH_ACTIVE = 22;
const ADAPTIVE_SLEEP_POLISH_COMPLETED = AVBD_ADAPTIVE_SLEEP_POLISH_COMPLETED;
// This is surfaced with the selected adaptive specialization so regressions
// certify that production shaders use sticky conditional stores instead of
// unconditional writes/atomicMax for binary residual flags.
const ADAPTIVE_RESIDUAL_BOOLEAN_STORES_DEDUPLICATED = true;
const adaptiveResidualHelpers = wgsl(/* wgsl */`
      fn adaptiveFiniteF32(value: f32) -> bool {
        return (bitcast<u32>(value) & 0x7f800000u) != 0x7f800000u;
      }

      fn adaptiveFinite4(value: vec4f) -> bool {
        let exponent = bitcast<vec4u>(value) & vec4u(0x7f800000u);
        return all(exponent != vec4u(0x7f800000u));
      }

      fn adaptiveMaxAbs3(value: vec3f) -> f32 {
        return max(abs(value.x), max(abs(value.y), abs(value.z)));
      }

      fn markAdaptiveInvalid(
        adaptiveState: ptr<storage, array<atomic<u32>>, read_write>,
      ) {
        let tracking = atomicLoad(
          &(*adaptiveState)[${ADAPTIVE_TRACK_RESIDUALS}u]
        ) != 0u;
        let destination = select(
          ${ADAPTIVE_PREMIN_INVALID}u,
          ${ADAPTIVE_INVALID_LAST}u,
          tracking,
        );
        atomicStore(&(*adaptiveState)[destination], 1u);
      }

      fn markAdaptiveResidualViolation(
        adaptiveState: ptr<storage, array<atomic<u32>>, read_write>,
        adaptiveResiduals: ptr<storage, array<atomic<u32>>, read_write>,
        shard: u32,
        component: u32,
        normalizedResidual: f32,
      ) {
        if (!adaptiveFiniteF32(normalizedResidual) || normalizedResidual < 0.0) {
          markAdaptiveInvalid(adaptiveState);
          return;
        }
        if (normalizedResidual <= 1.0) { return; }
        let residualIndex = (shard & ${ADAPTIVE_RESIDUAL_SHARDS - 1}u)
          * ${ADAPTIVE_RESIDUAL_WORDS_PER_SHARD}u + component;
        // The flag is binary and sticky for the round. Once one body/contact
        // has published the violation, later contributors must not keep
        // issuing redundant stores to the same shard cache line.
        if (atomicLoad(&(*adaptiveResiduals)[residualIndex]) == 0u) {
          atomicStore(&(*adaptiveResiduals)[residualIndex], 1u);
        }
      }
`);
const tangentBasisHelpers = wgsl(/* wgsl */`
      const TANGENT_BASIS_TWO_PI: f32 = 6.283185307179586;

      struct TangentBasis {
        t1: vec3f,
        t2: vec3f,
      };

      fn canonicalTangentBasis(n: vec3f) -> TangentBasis {
        let refAxis = select(vec3f(0.0, 1.0, 0.0), vec3f(1.0, 0.0, 0.0), abs(n.y) > 0.999);
        let t1Raw = cross(refAxis, n);
        let t1Len2 = dot(t1Raw, t1Raw);
        var t1 = vec3f(0.0, 0.0, 1.0);
        if (t1Len2 > 1e-12) {
          t1 = t1Raw * inverseSqrt(t1Len2);
        }
        let t2Raw = cross(n, t1);
        let t2Len2 = dot(t2Raw, t2Raw);
        var t2 = vec3f(1.0, 0.0, 0.0);
        if (t2Len2 > 1e-12) {
          t2 = t2Raw * inverseSqrt(t2Len2);
        }
        return TangentBasis(t1, t2);
      }

      fn tangentBasisFromPreferredT1(n: vec3f, preferredT1: vec3f) -> TangentBasis {
        let projected = preferredT1 - n * dot(preferredT1, n);
        let projectedLen2 = dot(projected, projected);
        if (projectedLen2 <= 1e-12) {
          return canonicalTangentBasis(n);
        }
        let t1 = projected * inverseSqrt(projectedLen2);
        let t2Raw = cross(n, t1);
        let t2Len2 = dot(t2Raw, t2Raw);
        if (t2Len2 <= 1e-12) {
          return canonicalTangentBasis(n);
        }
        let t2 = t2Raw * inverseSqrt(t2Len2);
        return TangentBasis(t1, t2);
      }

      fn tangentBasisFromAngle(n: vec3f, theta: f32) -> TangentBasis {
        let canonical = canonicalTangentBasis(n);
        let c = cos(theta);
        let s = sin(theta);
        let t1 = canonical.t1 * c + canonical.t2 * s;
        return tangentBasisFromPreferredT1(n, t1);
      }

      fn tangentBasisFromCachedT1(n: vec3f, cachedT1: vec3f, theta: f32) -> TangentBasis {
        // Contact generation stores the normalized/projected t1 produced by
        // tangentBasisFromAngle for this exact normal.  Rebuild only t2 here;
        // its normalization matches tangentBasisFromPreferredT1 while avoiding
        // repeated canonical-basis and trigonometric work in solver hot loops.
        if (dot(cachedT1, cachedT1) <= 1e-12) {
          // Keep manually-seeded/debug contacts that predate the cache valid.
          return tangentBasisFromAngle(n, theta);
        }
        let t2Raw = cross(n, cachedT1);
        let t2Len2 = dot(t2Raw, t2Raw);
        if (t2Len2 <= 1e-12) {
          return tangentBasisFromAngle(n, theta);
        }
        return TangentBasis(cachedT1, t2Raw * inverseSqrt(t2Len2));
      }

      fn tangentBasisFromCachedT1AndInvLength(
        n: vec3f,
        cachedT1: vec3f,
        theta: f32,
        cachedInvT2Length: f32,
      ) -> TangentBasis {
        // prepare computes the same inverseSqrt from the same f32 inputs once
        // per active physical row. Zero is an explicit fallback sentinel for
        // manually seeded/degenerate cached tangents; NaN is deliberately
        // preserved so adaptive INVALID handling remains bit-faithful.
        if (dot(cachedT1, cachedT1) <= 1e-12 || cachedInvT2Length == 0.0) {
          return tangentBasisFromAngle(n, theta);
        }
        let t2Raw = cross(n, cachedT1);
        return TangentBasis(cachedT1, t2Raw * cachedInvT2Length);
      }
`);

const contactStateHelpers = wgsl(/* wgsl */`
      const CONTACT_SOLVER_SCRATCH_ARM_FLAGS_SHIFT: u32 = ${CONTACT_SOLVER_SCRATCH_ARM_FLAGS_SHIFT}u;
      const CONTACT_SOLVER_SCRATCH_ACTIVE_BIT: u32 = ${CONTACT_SOLVER_SCRATCH_ACTIVE_BIT}u;
      const CONTACT_SOLVER_SCRATCH_MASK: u32 = ${CONTACT_SOLVER_SCRATCH_MASK}u;

      struct AvbdContactShadow {
        packed: vec4f,
        lambdaN: f32,
        lambdaTB: vec2f,
        frictionScale: f32,
      };

      struct AvbdContactState {
        dual: vec4f,
        penalty: vec4f,
        lambdaN: f32,
        penaltyN: f32,
        frictionScale: f32,
      };

      struct AvbdContactRecord {
        state: AvbdContactState,
        shadow: AvbdContactShadow,
      };

      fn makeContactState(
        dual: vec4f,
        penalty: vec4f,
        fallbackFrictionScale: f32,
        kStart: f32,
      ) -> AvbdContactState {
        let dualN = min(dual.x, 0.0);
        let penaltyN = max(penalty.x, kStart);
        let penaltyFrictionScale = max(penalty.w, 0.0);
        let frictionScale = max(
          select(fallbackFrictionScale, penaltyFrictionScale, penaltyFrictionScale > 0.0),
          0.0,
        );
        return AvbdContactState(
          dual,
          vec4f(penaltyN, penalty.y, penalty.z, frictionScale),
          max(-dualN, 0.0),
          penaltyN,
          frictionScale,
        );
      }

      fn makeContactShadow(shadow: vec4f) -> AvbdContactShadow {
        let frictionScale = max(shadow.w, 0.0);
        let lambdaN = max(shadow.x, 0.0);
        return AvbdContactShadow(
          vec4f(lambdaN, shadow.y, shadow.z, frictionScale),
          lambdaN,
          shadow.yz,
          frictionScale,
        );
      }

      fn makeContactRecord(
        dual: vec4f,
        penalty: vec4f,
        shadow: vec4f,
        kStart: f32,
      ) -> AvbdContactRecord {
        let contactShadow = makeContactShadow(shadow);
        let contactState = makeContactState(
          dual,
          penalty,
          contactShadow.frictionScale,
          kStart,
        );
        return AvbdContactRecord(contactState, contactShadow);
      }

      // Hot solver passes only need live dual/penalty state. The friction scale is
      // mirrored into penalty.w during prepare/capture so they can avoid pulling
      // shadow/cache data from global memory on every contact touch.
      fn loadContactStateHot(
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        p: u32,
        kStart: f32,
      ) -> AvbdContactState {
        let penalty = loadContactPenalty(pairContacts, p);
        return makeContactState(
          loadContactDual(pairContacts, p),
          penalty,
          max(penalty.w, 0.0),
          kStart,
        );
      }

      fn loadContactRecord(
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        p: u32,
        kStart: f32,
      ) -> AvbdContactRecord {
        var record = makeContactRecord(
          loadContactDual(pairContacts, p),
          loadContactPenalty(pairContacts, p),
          loadContactShadow(pairContacts, p),
          kStart,
        );
        return record;
      }

      fn packContactPenalty(penaltyN: f32, penaltyTB: vec2f, frictionScale: f32, kStart: f32) -> vec4f {
        return vec4f(
          max(penaltyN, kStart),
          max(penaltyTB.x, kStart),
          max(penaltyTB.y, kStart),
          max(frictionScale, 0.0),
        );
      }

      fn packContactShadow(lambdaN: f32, lambdaTB: vec2f, frictionScale: f32) -> vec4f {
        return vec4f(max(lambdaN, 0.0), lambdaTB, max(frictionScale, 0.0));
      }

      fn storeContactRecord(
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        p: u32,
        dual: vec4f,
        penaltyN: f32,
        penaltyTB: vec2f,
        lambdaN: f32,
        lambdaTB: vec2f,
        frictionScale: f32,
        kStart: f32,
        preserveSolverScratch: u32,
      ) {
        storeContactDual(pairContacts, p, dual);
        storeContactPenalty(pairContacts, p, packContactPenalty(
          penaltyN,
          penaltyTB,
          frictionScale,
          kStart,
        ));
        // Once prepare has transitioned the arms, shadow is exact arm scratch
        // until an unconditional post-iteration finalize pass.  Rows that did
        // not enter that lifecycle retain the legacy direct shadow update.
        if (preserveSolverScratch == 0u) {
          storeContactShadow(pairContacts, p, packContactShadow(
            lambdaN,
            lambdaTB,
            frictionScale,
          ));
        }
      }

      fn packSeedDualFromShadow(
        shadow: AvbdContactShadow,
        n: vec3f,
        t1: vec3f,
        t2: vec3f,
        useReferenceTangentialUpdate: u32,
      ) -> vec4f {
        let seedDualN = -shadow.lambdaN;
        let seedDualTWorld = t1 * shadow.lambdaTB.x + t2 * shadow.lambdaTB.y;
        return select(
          vec4f(seedDualN, seedDualTWorld),
          vec4f(seedDualN, shadow.lambdaTB, 0.0),
          useReferenceTangentialUpdate > 0u,
        );
      }

      fn contactStateDualTB(
        state: AvbdContactState,
        n: vec3f,
        t1: vec3f,
        t2: vec3f,
        useReferenceTangentialUpdate: u32,
      ) -> vec2f {
        let dualTWorldRaw = state.dual.yzw;
        let dualTWorld = dualTWorldRaw - n * dot(dualTWorldRaw, n);
        return select(
          vec2f(dot(dualTWorld, t1), dot(dualTWorld, t2)),
          state.dual.yz,
          useReferenceTangentialUpdate > 0u,
        );
      }
`);

// Kept out of the general contact-state helper bundle so only the two kernels
// that actually commit fixed-reference state pay its WGSL parse/compile cost.
const referenceContactCommitHelpers = wgsl(/* wgsl */`
      fn commitReferenceContactPersistent(
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        p: u32,
        solverDual: vec4f,
        solverPenalty: vec4f,
        solverStateCode: u32,
        kStart: f32,
      ) {
        let solverStateValid = (solverStateCode & 1u) != 0u;
        let state = makeContactState(
          vec4f(solverDual.xyz, 0.0),
          solverPenalty,
          max(solverPenalty.w, 0.0),
          kStart,
        );
        storeContactDual(pairContacts, p, state.dual);
        storeContactPenalty(pairContacts, p, state.penalty);

        let contactMeta = loadContactMeta(pairContacts, p);
        let encodedMeta = bitcast<u32>(contactMeta.w);
        let solverScratchActive =
          (encodedMeta & CONTACT_SOLVER_SCRATCH_ACTIVE_BIT) != 0u;
        var committedMeta = encodedMeta;
        if (solverStateValid) {
          committedMeta = (committedMeta & ~(
            (1u << 17u) | (1u << 19u) | (1u << 20u)
          ))
            | (((solverStateCode >> 1u) & 1u) << 17u)
            | (((solverStateCode >> 2u) & 1u) << 19u)
            | (((solverStateCode >> 3u) & 1u) << 20u);
        }
        if (solverScratchActive) {
          let scratchArms = loadContactShadow(pairContacts, p);
          let armAWorld = loadContactArmA(pairContacts, p);
          let armBWorld = loadContactArmB(pairContacts, p);
          let constraintC0 = loadContactConstraintC0(pairContacts, p);
          let sphereArmFlags =
            (encodedMeta >> CONTACT_SOLVER_SCRATCH_ARM_FLAGS_SHIFT) & 0x3u;
          storeContactArmA(
            pairContacts,
            p,
            vec4f(scratchArms.xyz, armAWorld.w),
          );
          storeContactArmB(pairContacts, p, vec4f(
            scratchArms.w,
            constraintC0.w,
            armBWorld.w,
            f32(sphereArmFlags),
          ));
          storeContactConstraintC0(
            pairContacts,
            p,
            vec4f(constraintC0.xyz, 0.0),
          );
        }
        if (solverStateValid || solverScratchActive) {
          storeContactMeta(pairContacts, p, vec4f(
            contactMeta.xyz,
            bitcast<f32>(select(
              committedMeta,
              committedMeta & ~(
                CONTACT_SOLVER_SCRATCH_MASK | CONTACT_INFO_RESERVED_BIT_30
              ),
              solverScratchActive,
            )),
          ));
        }
        storeContactShadow(pairContacts, p, packContactShadow(
          state.lambdaN,
          state.dual.yz,
          state.frictionScale,
        ));
      }
`);


const jointConstraintHelpers = wgsl(/* wgsl */`
      const WORLD_BODY_INDEX: u32 = 0xffffffffu;
      const JOINT_TYPE_SPHERICAL: u32 = 0u;
      const JOINT_TYPE_FIXED: u32 = 1u;

      struct JointPoseState {
        position: vec3f,
        rotation: vec4f,
      };

      fn isFiniteF32(value: f32) -> bool {
        return value == value && abs(value) <= 0x1.fffffep+127f;
      }

      fn integrateQuaternionBackByAngularVelocity(
        currentQ: vec4f,
        angularVelocity: vec3f,
        dt: f32,
      ) -> vec4f {
        let speed = length(angularVelocity);
        if (speed <= 1e-8 || dt <= 0.0) {
          return normalize(currentQ);
        }
        let axis = angularVelocity / speed;
        let halfAngle = 0.5 * speed * dt;
        let delta = vec4f(axis * sin(halfAngle), cos(halfAngle));
        return normalize(qmul(qconj(delta), normalize(currentQ)));
      }

      fn jointRegularizationPose(
        bodyIndex: u32,
        initialPose: ptr<storage, array<vec4f>, read>,
        positions: ptr<storage, array<vec4f>, read>,
        quaternions: ptr<storage, array<vec4f>, read>,
        velocities: ptr<storage, array<vec4f>, read>,
        angularVelocities: ptr<storage, array<vec4f>, read>,
        dt: f32,
      ) -> JointPoseState {
        if (bodyIndex == WORLD_BODY_INDEX) {
          return JointPoseState(vec3f(0.0), vec4f(0.0, 0.0, 0.0, 1.0));
        }

        let invMass = positions[bodyIndex].w;
        if (invMass == 0.0) {
          let currentPos = positions[bodyIndex].xyz;
          let currentQ = normalize(quaternions[bodyIndex]);
          let previousPos = currentPos - velocities[bodyIndex].xyz * dt;
          let previousQ = integrateQuaternionBackByAngularVelocity(
            currentQ,
            angularVelocities[bodyIndex].xyz,
            dt,
          );
          return JointPoseState(previousPos, previousQ);
        }

        let poseBase = bodyIndex * 2u;
        return JointPoseState(
          initialPose[poseBase].xyz,
          normalize(initialPose[poseBase + 1u]),
        );
      }

      fn jointWorldAnchor(
        bodyIndex: u32,
        anchorLocal: vec3f,
        position: vec3f,
        rotation: vec4f,
      ) -> vec3f {
        return select(position + qrot(rotation, anchorLocal), anchorLocal, bodyIndex == WORLD_BODY_INDEX);
      }

      fn jointFixedAngularConstraint(
        bodyA: u32,
        qA: vec4f,
        qB: vec4f,
        restRelative: vec4f,
        torqueArm: f32,
      ) -> vec3f {
        let worldQA = normalize(select(qA, vec4f(0.0, 0.0, 0.0, 1.0), bodyA == WORLD_BODY_INDEX));
        let targetQB = normalize(qmul(worldQA, normalize(restRelative)));
        let deltaRaw = qmul(targetQB, qconj(normalize(qB)));
        let delta = select(deltaRaw, -deltaRaw, deltaRaw.w < 0.0);
        return 2.0 * delta.xyz * torqueArm;
      }

      fn jointBallSocketGeometricDiagonal(r: vec3f, force: vec3f) -> vec3f {
        let col0 = vec3f(
          -force.y * r.y - force.z * r.z,
          force.x * r.y,
          force.x * r.z,
        );
        let col1 = vec3f(
          force.y * r.x,
          -force.x * r.x - force.z * r.z,
          force.y * r.z,
        );
        let col2 = vec3f(
          force.z * r.x,
          force.z * r.y,
          -force.x * r.x - force.y * r.y,
        );
        return vec3f(length(col0), length(col1), length(col2));
      }

      struct DistanceConstraintGeometricDiagonal {
        linear: vec3f,
        angular: vec3f,
      };

      fn distanceConstraintLinearHessianColumnNorm(
        normal: vec3f,
        arm: vec3f,
        inverseLength: f32,
        axis: vec3f,
      ) -> f32 {
        // For C = |d| - L, P = I - n*n^T and H_xx = P / |d|.
        // The lower half of this 6-DoF column is H_theta,x = [r]x H_xx.
        let linearColumn = (axis - normal * dot(normal, axis)) * inverseLength;
        let angularColumn = cross(arm, linearColumn);
        return sqrt(max(
          dot(linearColumn, linearColumn) + dot(angularColumn, angularColumn),
          0.0,
        ));
      }

      fn distanceConstraintAngularHessianColumnNorm(
        normal: vec3f,
        arm: vec3f,
        inverseLength: f32,
        bodySign: f32,
        axis: vec3f,
      ) -> f32 {
        // A unit world-space angular increment moves the anchor by axis x r.
        // B^T P B / |d| supplies both the linear/angular cross block and the
        // first angular block term. The final term is the exact second
        // derivative of exp([theta]x) r contracted with the distance normal.
        let pointMotion = cross(axis, arm);
        let linearColumn = (
          pointMotion - normal * dot(normal, pointMotion)
        ) * inverseLength;
        let rotationCurvature = 0.5 * (
          normal * dot(arm, axis) + arm * dot(normal, axis)
        ) - axis * dot(normal, arm);
        let angularColumn = cross(arm, linearColumn) + bodySign * rotationCurvature;
        return sqrt(max(
          dot(linearColumn, linearColumn) + dot(angularColumn, angularColumn),
          0.0,
        ));
      }

      fn distanceConstraintGeometricDiagonal(
        normal: vec3f,
        arm: vec3f,
        distance: f32,
        bodySign: f32,
        force: f32,
      ) -> DistanceConstraintGeometricDiagonal {
        let inverseLength = 1.0 / max(distance, 1e-6);
        let axisX = vec3f(1.0, 0.0, 0.0);
        let axisY = vec3f(0.0, 1.0, 0.0);
        let axisZ = vec3f(0.0, 0.0, 1.0);
        let forceMagnitude = abs(force);
        return DistanceConstraintGeometricDiagonal(
          forceMagnitude * vec3f(
            distanceConstraintLinearHessianColumnNorm(normal, arm, inverseLength, axisX),
            distanceConstraintLinearHessianColumnNorm(normal, arm, inverseLength, axisY),
            distanceConstraintLinearHessianColumnNorm(normal, arm, inverseLength, axisZ),
          ),
          forceMagnitude * vec3f(
            distanceConstraintAngularHessianColumnNorm(normal, arm, inverseLength, bodySign, axisX),
            distanceConstraintAngularHessianColumnNorm(normal, arm, inverseLength, bodySign, axisY),
            distanceConstraintAngularHessianColumnNorm(normal, arm, inverseLength, bodySign, axisZ),
          ),
        );
      }

      fn addDiagonalVectorConstraint(
        lhs: ptr<function, array<array<f32, 6>, 6>>,
        rhs: ptr<function, array<f32, 6>>,
        j0: array<f32, 6>,
        j1: array<f32, 6>,
        j2: array<f32, 6>,
        stiffness: vec3f,
        force: vec3f,
      ) {
        let rows = array<array<f32, 6>, 3>(j0, j1, j2);
        for (var axis = 0u; axis < 3u; axis++) {
          let k = max(stiffness[axis], 0.0);
          if (k <= 0.0) { continue; }
          let f = force[axis];
          let row = rows[axis];
          for (var r = 0u; r < 6u; r++) {
            (*rhs)[r] += row[r] * f;
            for (var c = 0u; c < 6u; c++) {
              (*lhs)[r][c] += k * row[r] * row[c];
            }
          }
        }
      }

      fn addScalarConstraint(
        lhs: ptr<function, array<array<f32, 6>, 6>>,
        rhs: ptr<function, array<f32, 6>>,
        row: array<f32, 6>,
        stiffness: f32,
        force: f32,
      ) {
        let k = max(stiffness, 0.0);
        if (k <= 0.0) { return; }
        for (var r = 0u; r < 6u; r++) {
          (*rhs)[r] += row[r] * force;
          for (var c = 0u; c < 6u; c++) {
            (*lhs)[r][c] += k * row[r] * row[c];
          }
        }
      }
`);

type PrimalSolveTuning = {
  relaxation?: number;
  frictionRelaxation?: number;
  inertialDiagWeight?: number;
  maxLinearCorrection?: number;
  maxAngularCorrection?: number;
};

export type AvbdAdaptiveIterationPolicy = {
  enabled: boolean;
  minIterations: number;
  linearTolerance?: number;
  angularTolerance?: number;
  dualTolerance?: number;
  penaltyTolerance?: number;
  /** Deterministic regression hook; never enabled by production presets. */
  forceInvalidForTesting?: boolean;
  /** Production true; false retains the pre-optimization graph for A/B proof. */
  deferPreMinResiduals?: boolean;
};

export type AvbdAdaptiveIterationStats = {
  enabledLast: boolean;
  sleepConvergedLast: boolean;
  sleepPolishConfiguredLast: boolean;
  sleepPolishRequestedNext: boolean;
  sleepPolishActiveLast: boolean;
  sleepPolishCompletedLast: number;
  sleepPolishRequiredSteps: number;
  executedIterationsLast: number;
  minIterationsLast: number;
  maxIterationsLast: number;
  earlyExitLast: boolean;
  fallbackBodiesLast: number;
  invalidLast: boolean;
  bodyResidualViolationLast: boolean;
  fallbackBodyResidualViolationLast: boolean;
  contactResidualViolationLast: boolean;
  adaptiveSteps: number;
  totalIterations: number;
  averageIterations: number;
  earlyExits: number;
  failOpenSteps: number;
  fallbackFailOpenSteps: number;
  invalidFailOpenSteps: number;
};

export type AvbdAdaptiveColorSpecializationTelemetry = Readonly<{
  colorCapacity: number;
  strictPrimalKernelCount: number;
  strictDispatchCount: number;
  earlyOutDispatchTargetCount: number;
  earlyOutDispatchClearBatchCount: number;
  residualReductionClosure: boolean;
  fallbackResidualClosure: boolean;
  contactResidualClosure: boolean;
  residualBooleanStoresDeduplicated: boolean;
  completeForConfiguredColorCapacity: boolean;
}>;

/**
 * CPU-side, read-only evidence of the shader path that was actually selected
 * by the most recent combined AVBD primal/capture solve.  These values are
 * latched at the branch that chooses the concrete kernels; callers therefore
 * do not have to infer the live path from configuration defaults.
 *
 * The fallback body count is intentionally not mirrored here because it is
 * GPU-owned. PhysicsEngine combines this snapshot with the body-color GPU
 * census when a regression asks for full solve-path telemetry.
 */
export type AvbdSolvePathTelemetry = Readonly<{
  hasHardConstraints: boolean;
  contactOnly: boolean;
  contactTangentInvLengthCache: boolean;
  denseContactSolverState: boolean;
  referenceFinalizerSpecialization: boolean;
  cooperativeBodySolveConfigured: boolean;
  cooperativeBodySolveLanes: number;
  manifoldCaptureConfigured: boolean;
  captureBodyDeltaCacheConfigured: boolean;
  packedContactSolverGeometryConfigured: boolean;
  finalCaptureCommitFusionConfigured: boolean;
  configuredColorCapacity: number;
  solveInvocations: number;
  referencePrimalInvocations: number;
  cooperativePrimalInvocations: number;
  manifoldCaptureInvocations: number;
  captureBodyDeltaCacheInvocations: number;
  packedContactSolverGeometryInvocations: number;
  finalCaptureCommitFusionInvocations: number;
  referenceCaptureInvocations: number;
  adaptiveInvocations: number;
  genericPrimalInvocations: number;
  genericCaptureInvocations: number;
  bodySolveModeLast: AvbdBodySolveMode;
  iterationsLast: number;
  sweepsLast: number;
  activeColorCountLast: number;
  pairDispatchCountLast: number;
  jointCountLast: number;
  springCountLast: number;
  referencePrimalSelectedLast: boolean;
  cooperativePrimalSelectedLast: boolean;
  manifoldCaptureSelectedLast: boolean;
  captureBodyDeltaCacheSelectedLast: boolean;
  packedContactSolverGeometrySelectedLast: boolean;
  finalCaptureCommitFusionSelectedLast: boolean;
  referenceCaptureSelectedLast: boolean;
  referenceCaptureEncodedLast: boolean;
  adaptiveSelectedLast: boolean;
  fallbackLaneEncodedLast: boolean;
  adaptiveColorSpecialization: AvbdAdaptiveColorSpecializationTelemetry;
}>;

export class AvbdStateStage {
  private readonly buildContactDispatchArgsKernel: any;
  private readonly clearPhaseDebugCountersKernel: any;
  private readonly accumulatePhaseDebugCountersKernel: any;
  private readonly clearDebugCountersKernel: any;
  private readonly accumulateDebugCountersKernel: any;
  private readonly accumulateBodyColorDebugCountersKernel: any;
  private readonly prepareStateKernel: any;
  private readonly prepareJointStateKernel: any;
  private readonly buildSolverConstraintListsKernel: any;
  private readonly prepareContactOnlyAdjacencyKernel: any;
  private readonly clearBodyConstraintOverflowKernel: any;
  private readonly appendJointConstraintRefsKernel: any;
  private readonly appendSpringConstraintRefsKernel: any;
  private readonly clearConstrainedBodyCompactKernel: any;
  private readonly buildConstrainedBodyDispatchArgsKernel: any;
  private readonly greedyBodyColorsKernel: any;
  private readonly greedyBodyColorsReverseKernel: any;
  private readonly markBodyColorFallbackConflictsKernel: any;
  private readonly markHardColorConflictsKernel: any;
  private readonly repairHardBodyColorsKernel: any;
  private readonly clearBodyColorBucketsKernel: any;
  private readonly countBodyColorBucketsKernel: any;
  private readonly prefixBodyColorBucketsKernel: any;
  private readonly buildBodyColorDispatchArgsKernels: any[];
  private readonly buildCooperativeBodyColorDispatchArgsKernels: any[];
  private readonly scatterBodyColorBucketsKernel: any;
  private readonly primalBodySolveKernels: any[];
  private readonly referencePrimalBodySolveKernels: any[];
  private readonly adaptiveReferencePrimalBodySolveKernels: any[];
  private readonly adaptiveFallbackPrimalBodySolveKernel: any;
  private readonly commitBodySolveKernels: any[];
  private readonly adaptiveFallbackCommitBodySolveKernel: any;
  private readonly capturePairDualStateKernel: any;
  private readonly captureBodyDeltaKernel: any | null;
  private readonly referenceCapturePairDualStateKernel: any | null;
  private readonly referenceFinalCapturePairDualStateKernel: any | null;
  private readonly referenceManifoldCapturePairDualStateKernel: any | null;
  private readonly adaptiveReferenceCapturePairDualStateKernel: any | null;
  private readonly finalizeContactSolverScratchKernel: any;
  private readonly referenceFinalizeContactSolverScratchKernel: any | null;
  private readonly captureJointDualStateKernel: any;
  private readonly finalizeVelocitiesKernel: any;
  private readonly initializeAdaptiveStateKernel: any;
  private readonly beginAdaptiveResidualTrackingKernel: any;
  private readonly clearAdaptiveRoundResidualKernel: any;
  private readonly reduceAdaptiveRoundResidualKernel: any;
  private readonly decideAdaptiveEarlyOutKernel: any;
  private readonly clearAdaptiveDispatchArgsKernels: any[];
  private readonly adaptiveDispatchTargetCount: number;

  private readonly contactDispatchIndirectAttr: IndirectStorageBufferAttribute;
  private readonly captureContactDispatchIndirectAttr: IndirectStorageBufferAttribute;
  private readonly finalizeContactDispatchIndirectAttr: IndirectStorageBufferAttribute;
  private readonly bodyColorDispatchIndirectAttr: IndirectStorageBufferAttribute;
  private readonly bodyColorDispatchIndirectAttrs: IndirectStorageBufferAttribute[];
  private readonly cooperativeBodyColorDispatchIndirectAttrs: IndirectStorageBufferAttribute[];
  private readonly bodyColorFallbackDispatchIndirectAttr: IndirectStorageBufferAttribute;
  private readonly constrainedBodyDispatchIndirectAttr: IndirectStorageBufferAttribute;
  private readonly constrainedBodyCompactAttr: StorageBufferAttribute;
  private readonly bodyColorCompactAttr: StorageBufferAttribute;
  private readonly bodyColorScratchNextAttr: StorageBufferAttribute;
  private readonly phaseDebugCountersAttr: StorageBufferAttribute;
  private readonly debugCountersAttr: StorageBufferAttribute;
  private readonly pairContactsAttr: StorageBufferAttribute;
  private readonly contactSolverHotAttr: StorageBufferAttribute;
  private readonly contactSolverGeometryAttr: StorageBufferAttribute;
  private readonly jointRecordsAttr: StorageBufferAttribute;
  private readonly springRecordsAttr: StorageBufferAttribute;
  private readonly positionsAttr: StorageBufferAttribute;
  private readonly initialPoseAttr: StorageBufferAttribute;
  private readonly captureBodyDeltaAttr: StorageBufferAttribute;
  private readonly quaternionsAttr: StorageBufferAttribute;
  private readonly pairActivityAttr: StorageBufferAttribute;
  private readonly bodySolveOutputPoseAttr: StorageBufferAttribute;
  private readonly contactTangentInvLengthAttr: StorageBufferAttribute;
  private readonly adaptiveStateAttr: StorageBufferAttribute;
  private readonly adaptiveResidualsAttr: StorageBufferAttribute;
  private readonly maxPairContacts: number;
  private readonly maxJoints: number;
  private readonly maxSprings: number;
  private readonly maxActivePairContacts: number;
  private readonly pairCanonicalActiveContactsOffset: number;
  private readonly pairSolverActiveContactsOffset: number;
  private readonly contactKeySlotBitCount: number;
  private readonly maxBodyColorCount: number;
  private readonly hasHardConstraints: boolean;
  private readonly contactOnlyAdjacencyAlias: boolean;
  private readonly cacheContactTangentInvLength: boolean;
  private readonly denseContactSolverState: boolean;
  private readonly referenceFinalizerSpecialization: boolean;
  private readonly cooperativeReferenceBodySolve: boolean;
  private readonly captureBodyDeltaCache: boolean;
  private readonly packedContactSolverGeometry: boolean;
  private currentFrictionStatic = AVBD_FRICTION_STATIC;
  private currentFrictionDynamic = AVBD_FRICTION_DYNAMIC;
  private currentRegularizationAlpha = AVBD_REGULARIZATION_ALPHA_DEFAULT;
  private currentCollisionMargin = AVBD_COLLISION_MARGIN;
  private currentPenaltyFloor = AVBD_K_START;
  private currentPreventPenetratingNormalDropout = false;
  private currentFreezeTangentialPenaltyUpdates = false;
  private debugEnabled = false;
  private debugReadbackInFlight = false;
  private separatingTraceReadbackInFlight = false;
  private separatingTraceArmed = true;
  private lastDebugLogFrame = -1;
  private debugEveryNFrames = 30;
  private adaptiveIterationPolicy: Required<AvbdAdaptiveIterationPolicy> = {
    enabled: false,
    minIterations: 1,
    linearTolerance: 2.5e-5,
    angularTolerance: 2.5e-5,
    dualTolerance: 2.5e-4,
    penaltyTolerance: 2.5e-4,
    forceInvalidForTesting: false,
    deferPreMinResiduals: true,
  };
  private adaptiveStatsReadbackInFlight = false;
  private adaptiveStatsReadbackPromise: Promise<AvbdAdaptiveIterationStats | null> | null = null;
  private adaptiveStatsGeneration = 0;
  private adaptiveGpuStateMayBeEnabled = false;
  private solvePathInvocations = 0;
  private referencePrimalInvocations = 0;
  private cooperativePrimalInvocations = 0;
  private manifoldCaptureInvocations = 0;
  private captureBodyDeltaCacheInvocations = 0;
  private packedContactSolverGeometryInvocations = 0;
  private finalCaptureCommitFusionInvocations = 0;
  private referenceCaptureInvocations = 0;
  private adaptiveInvocations = 0;
  private genericPrimalInvocations = 0;
  private genericCaptureInvocations = 0;
  private solvePathBodySolveModeLast: AvbdBodySolveMode = 'colored';
  private solvePathIterationsLast = 0;
  private solvePathSweepsLast = 0;
  private solvePathActiveColorCountLast = 0;
  private solvePathPairDispatchCountLast = 0;
  private solvePathJointCountLast = 0;
  private solvePathSpringCountLast = 0;
  private solvePathReferencePrimalSelectedLast = false;
  private solvePathCooperativePrimalSelectedLast = false;
  private solvePathManifoldCaptureSelectedLast = false;
  private solvePathCaptureBodyDeltaCacheSelectedLast = false;
  private solvePathPackedContactSolverGeometrySelectedLast = false;
  private solvePathFinalCaptureCommitFusionSelectedLast = false;
  private solvePathReferenceCaptureSelectedLast = false;
  private solvePathReferenceCaptureEncodedLast = false;
  private solvePathAdaptiveSelectedLast = false;
  private solvePathFallbackLaneEncodedLast = false;
  private lastAdaptiveIterationStats: AvbdAdaptiveIterationStats = {
    enabledLast: false,
    sleepConvergedLast: true,
    sleepPolishConfiguredLast: false,
    sleepPolishRequestedNext: false,
    sleepPolishActiveLast: false,
    sleepPolishCompletedLast: 0,
    sleepPolishRequiredSteps: AVBD_ADAPTIVE_SLEEP_POLISH_STEPS,
    executedIterationsLast: 0,
    minIterationsLast: 0,
    maxIterationsLast: 0,
    earlyExitLast: false,
    fallbackBodiesLast: 0,
    invalidLast: false,
    bodyResidualViolationLast: false,
    fallbackBodyResidualViolationLast: false,
    contactResidualViolationLast: false,
    adaptiveSteps: 0,
    totalIterations: 0,
    averageIterations: 0,
    earlyExits: 0,
    failOpenSteps: 0,
    fallbackFailOpenSteps: 0,
    invalidFailOpenSteps: 0,
  };

  constructor(
    pairContacts: StorageBufferAttribute,
    jointRecords: StorageBufferAttribute,
    springRecords: StorageBufferAttribute,
    positions: StorageBufferAttribute,
    initialPose: StorageBufferAttribute,
    inertialPose: StorageBufferAttribute,
    quaternions: StorageBufferAttribute,
    velocities: StorageBufferAttribute,
    prevLinearVelocities: StorageBufferAttribute,
    angularVelocities: StorageBufferAttribute,
    inverseInertia: StorageBufferAttribute,
    bodyConstraintCounts: StorageBufferAttribute,
    bodyConstraintRefs: StorageBufferAttribute,
    pairBodyContactCounts: StorageBufferAttribute,
    pairBodyContactIndices: StorageBufferAttribute,
    bodyColorScratch: StorageBufferAttribute,
    pairActivity: StorageBufferAttribute,
    activeManifolds: StorageBufferAttribute,
    persistentManifoldMap: StorageBufferAttribute,
    maxBodyColorCount: number,
    maxBodies: number,
    maxPairContacts: number,
    maxJoints: number,
    maxSprings: number,
    maxActivePairContacts: number,
    maxConstraintsPerBody: number,
    maxPairContactsPerBody: number,
    pairManifoldSlots: number,
    pairCanonicalActiveContactsOffset: number,
    pairSolverActiveContactsOffset: number,
    pairActivityWordCount: number,
    maxPersistentManifolds: number,
    persistentSlotRowOffset: number,
    persistentOutputMaskOffset: number,
    hasHardConstraints: boolean,
    cacheContactTangentInvLength: boolean,
    denseContactSolverState: boolean,
    referenceFinalizerSpecialization: boolean,
    cooperativeReferenceBodySolve: boolean,
    manifoldReferenceCapture: boolean,
    captureBodyDeltaCache: boolean,
    packedContactSolverGeometry: boolean,
    finalCaptureCommitFusion: boolean,
  ) {
    this.pairContactsAttr = pairContacts;
    this.packedContactSolverGeometry = packedContactSolverGeometry
      && denseContactSolverState
      && !hasHardConstraints
      && maxBodies <= 0x10000;
    this.contactSolverHotAttr = new StorageBufferAttribute(
      new Float32Array(maxPairContacts * CONTACT_SOLVER_HOT_VEC4S * 4),
      4,
    );
    this.contactSolverHotAttr.name = 'AVBD Dense Contact Solver State';
    this.contactSolverGeometryAttr = new StorageBufferAttribute(
      new Float32Array(
        this.packedContactSolverGeometry
          ? maxPairContacts * CONTACT_SOLVER_GEOMETRY_VEC4S * 4
          : 4,
      ),
      4,
    );
    this.contactSolverGeometryAttr.name = 'AVBD Packed Contact Solver Geometry';
    this.jointRecordsAttr = jointRecords;
    this.springRecordsAttr = springRecords;
    this.positionsAttr = positions;
    this.initialPoseAttr = initialPose;
    this.quaternionsAttr = quaternions;
    this.pairActivityAttr = pairActivity;
    this.hasHardConstraints = hasHardConstraints;
    this.cacheContactTangentInvLength = cacheContactTangentInvLength
      && !hasHardConstraints;
    this.denseContactSolverState = denseContactSolverState
      && !hasHardConstraints;
    this.referenceFinalizerSpecialization = referenceFinalizerSpecialization
      && this.denseContactSolverState
      && !hasHardConstraints;
    this.cooperativeReferenceBodySolve = cooperativeReferenceBodySolve
      && this.denseContactSolverState
      && !hasHardConstraints;
    this.captureBodyDeltaCache = captureBodyDeltaCache
      && this.denseContactSolverState
      && !hasHardConstraints;
    const bodyColorKernelVariants = Math.max(
      1,
      Math.min(32, Math.floor(maxBodyColorCount)),
    );
    // One extra compact bucket is a race-free Jacobi fallback lane for bodies
    // whose incident neighbors occupy every configured GS color. This is
    // essential for rare high-degree stars such as a large projectile touching
    // hundreds of bricks; forcing such a body into an occupied color races.
    const bodyColorBucketVariants = bodyColorKernelVariants + 1;
    this.maxBodyColorCount = bodyColorKernelVariants;
    this.bodyColorScratchNextAttr = new StorageBufferAttribute(new Uint32Array(maxBodies), 1);
    this.bodyColorScratchNextAttr.name = 'AVBD Body Color Ping-Pong Scratch';
    // The compact constrained-body stream is built while adjacency is being
    // discovered. Word 0 is the live count and words [1, maxBodies] are body
    // ids. Keeping this stream separate from the later color-partitioned list
    // lets scatter write the latter without racing readers of the former.
    this.constrainedBodyCompactAttr = new StorageBufferAttribute(
      new Uint32Array(maxBodies + 1),
      1,
    );
    this.constrainedBodyCompactAttr.name = 'AVBD Compact Constrained Body List';
    this.constrainedBodyDispatchIndirectAttr = new IndirectStorageBufferAttribute(
      new Uint32Array([0, 1, 1]),
      1,
    );
    this.constrainedBodyDispatchIndirectAttr.name = 'AVBD Constrained Body Dispatch Indirect';
    const bodyColorCountOffset = maxBodies;
    const bodyColorOffsetOffset = bodyColorCountOffset + bodyColorBucketVariants;
    const bodyColorCursorOffset = bodyColorOffsetOffset + bodyColorBucketVariants;
    const bodyColorCompactWordCount = maxBodies + bodyColorBucketVariants * 3;
    this.bodyColorCompactAttr = new StorageBufferAttribute(
      new Uint32Array(bodyColorCompactWordCount),
      1,
    );
    this.bodyColorCompactAttr.name = 'AVBD Compact Per-Color Body List';
    this.bodyColorDispatchIndirectAttrs = Array.from(
      { length: bodyColorKernelVariants },
      (_, color) => {
        const attribute = new IndirectStorageBufferAttribute(
          new Uint32Array([0, 1, 1]),
          1,
        );
        attribute.name = `AVBD Color ${color} Bucket Dispatch Indirect`;
        return attribute;
      },
    );
    this.cooperativeBodyColorDispatchIndirectAttrs = this.cooperativeReferenceBodySolve
      ? Array.from(
        { length: bodyColorKernelVariants },
        (_, color) => {
          const attribute = new IndirectStorageBufferAttribute(
            new Uint32Array([0, 1, 1]),
            1,
          );
          attribute.name = `AVBD Cooperative Color ${color} Body Dispatch Indirect`;
          return attribute;
        },
      )
      : [];
    // Retain the singular alias for legacy/diagnostic call sites. The normal
    // production path uses the exact per-color array below.
    this.bodyColorDispatchIndirectAttr = this.bodyColorDispatchIndirectAttrs[0]!;
    this.bodyColorFallbackDispatchIndirectAttr = new IndirectStorageBufferAttribute(
      new Uint32Array([0, 1, 1]),
      1,
    );
    this.bodyColorFallbackDispatchIndirectAttr.name = 'AVBD Fallback Bucket Dispatch Indirect';
    const initialAdaptiveState = new Uint32Array(ADAPTIVE_STATE_WORDS);
    // Fixed-iteration scenes use their established kinematic sleep proof.
    // Adaptive scenes overwrite this to zero in their first ordered solve.
    initialAdaptiveState[AVBD_ADAPTIVE_SLEEP_CONVERGED] = 1;
    this.adaptiveStateAttr = new StorageBufferAttribute(initialAdaptiveState, 1);
    this.adaptiveStateAttr.name = 'AVBD GPU Adaptive Iteration State';
    this.adaptiveResidualsAttr = new StorageBufferAttribute(
      new Uint32Array(ADAPTIVE_RESIDUAL_WORDS),
      1,
    );
    this.adaptiveResidualsAttr.name = 'AVBD Sharded Adaptive Residuals';
    // The third vec4 carries current-vs-initial dTheta through the Jacobi
    // fallback without adding another storage binding to its commit kernel.
    this.bodySolveOutputPoseAttr = new StorageBufferAttribute(new Float32Array(maxBodies * 3 * 4), 4);
    this.bodySolveOutputPoseAttr.name = 'AVBD Body Solve Output Pose';
    this.captureBodyDeltaAttr = new StorageBufferAttribute(
      new Float32Array(this.captureBodyDeltaCache ? maxBodies * 2 * 4 : 4),
      4,
    );
    this.captureBodyDeltaAttr.name = 'AVBD Capture Body Delta Cache';
    this.contactTangentInvLengthAttr = new StorageBufferAttribute(
      new Float32Array(this.cacheContactTangentInvLength ? maxPairContacts : 1),
      1,
    );
    this.contactTangentInvLengthAttr.name = 'AVBD Contact Tangent T2 Inverse Length Cache';
    this.maxPairContacts = maxPairContacts;
    this.maxJoints = maxJoints;
    this.maxSprings = maxSprings;
    this.maxActivePairContacts = maxActivePairContacts;
    this.pairCanonicalActiveContactsOffset = pairCanonicalActiveContactsOffset;
    this.pairSolverActiveContactsOffset = pairSolverActiveContactsOffset;
    if (maxActivePairContacts >= SOLVER_CONTACT_CANONICAL_ALIAS_BIT) {
      throw new Error('AVBD active-contact capacity collides with solver alias bit.');
    }
    // Keep the generated WGSL expressions compact. Every AVBD contact phase
    // masks the post-sleep header and selects canonical vs compact entries on
    // the GPU; the canonical generation graph itself is never rewritten.
    const pairActiveContactsOffset = pairSolverActiveContactsOffset;
    const pairBodyFixedWordCount = maxBodies * maxPairContactsPerBody;
    const pairBodyOverflowCounterOffset = pairBodyFixedWordCount;
    const pairBodyOverflowHeadOffset = pairBodyOverflowCounterOffset + 1;
    const pairBodyOverflowNodeOffset = pairBodyOverflowHeadOffset + maxBodies;
    const pairBodyOverflowCapacity = Math.floor(
      (pairBodyContactIndices.count - pairBodyOverflowNodeOffset) / 2,
    );
    const bodyConstraintFixedWordCount = maxBodies * maxConstraintsPerBody;
    const bodyConstraintOverflowCounterOffset = bodyConstraintFixedWordCount;
    const bodyConstraintOverflowHeadOffset = bodyConstraintOverflowCounterOffset + 1;
    const bodyConstraintOverflowNodeOffset = bodyConstraintOverflowHeadOffset + maxBodies;
    const bodyConstraintOverflowCapacity = Math.floor(
      (bodyConstraintRefs.count - bodyConstraintOverflowNodeOffset) / 2,
    );
    if (pairBodyOverflowCapacity < 1 || bodyConstraintOverflowCapacity < 1) {
      throw new Error('AVBD sparse body-constraint spill pools are missing.');
    }
    this.contactOnlyAdjacencyAlias = bodyConstraintRefs === pairBodyContactIndices;
    if (
      this.contactOnlyAdjacencyAlias
      && (
        hasHardConstraints
        || maxConstraintsPerBody !== maxPairContactsPerBody
        || bodyConstraintOverflowCounterOffset !== pairBodyOverflowCounterOffset
        || bodyConstraintOverflowHeadOffset !== pairBodyOverflowHeadOffset
        || bodyConstraintOverflowNodeOffset !== pairBodyOverflowNodeOffset
        || bodyConstraintOverflowCapacity !== pairBodyOverflowCapacity
      )
    ) {
      throw new Error('AVBD contact-only adjacency alias has incompatible solver-list layout.');
    }
    const bodyConstraintRefHelpers = wgsl(/* wgsl */`
      fn loadBodyConstraintRef(
        bodyConstraintRefs: ptr<storage, array<u32>, read>,
        body: u32,
        logicalIndex: u32,
        overflowNode: ptr<function, u32>,
      ) -> u32 {
        if (logicalIndex < ${maxConstraintsPerBody}u) {
          return bodyConstraintRefs[body * ${maxConstraintsPerBody}u + logicalIndex];
        }
        let node = *overflowNode;
        if (node == 0xffffffffu || node >= ${bodyConstraintOverflowCapacity}u) {
          return 0x3fffffffu;
        }
        let nodeBase = ${bodyConstraintOverflowNodeOffset}u + node * 2u;
        *overflowNode = bodyConstraintRefs[nodeBase + 1u];
        return bodyConstraintRefs[nodeBase];
      }
    `);
    const bodyConstraintAppendHelperSource = /* wgsl */`
      fn appendConstrainedBody(
        constrainedBodies: ptr<storage, array<atomic<u32>>, read_write>,
        body: u32,
      ) {
        let slot = atomicAdd(&constrainedBodies[0], 1u);
        if (slot < ${maxBodies}u) {
          atomicStore(&constrainedBodies[slot + 1u], body);
        }
      }

      fn storeBodyConstraintRef(
        bodyConstraintRefs: ptr<storage, array<atomic<u32>>, read_write>,
        body: u32,
        logicalIndex: u32,
        constraintRef: u32,
      ) -> bool {
        if (logicalIndex < ${maxConstraintsPerBody}u) {
          atomicStore(
            &bodyConstraintRefs[body * ${maxConstraintsPerBody}u + logicalIndex],
            constraintRef,
          );
          return true;
        }
        let node = atomicAdd(
          &bodyConstraintRefs[${bodyConstraintOverflowCounterOffset}u],
          1u,
        );
        if (node >= ${bodyConstraintOverflowCapacity}u) { return false; }
        let nodeBase = ${bodyConstraintOverflowNodeOffset}u + node * 2u;
        let previousHead = atomicExchange(
          &bodyConstraintRefs[${bodyConstraintOverflowHeadOffset}u + body],
          node,
        );
        atomicStore(&bodyConstraintRefs[nodeBase], constraintRef);
        atomicStore(&bodyConstraintRefs[nodeBase + 1u], previousHead);
        return true;
      }

      fn appendBodyConstraintRef(
        bodyConstraintCounts: ptr<storage, array<atomic<u32>>, read_write>,
        bodyConstraintRefs: ptr<storage, array<atomic<u32>>, read_write>,
        constrainedBodies: ptr<storage, array<atomic<u32>>, read_write>,
        body: u32,
        constraintRef: u32,
      ) -> bool {
        // The low 16 bits are the logical degree; upper bits carry coloring
        // state. Reserve with a masked CAS so degree saturation can never
        // carry into (or erase) the color/fallback flags.
        loop {
          let oldWord = atomicLoad(&bodyConstraintCounts[body]);
          let logicalIndex = oldWord & 0xFFFFu;
          if (logicalIndex >= 0xFFFFu) { return false; }
          let nextWord = (oldWord & 0xFFFF0000u) | (logicalIndex + 1u);
          let reserved = atomicCompareExchangeWeak(
            &bodyConstraintCounts[body],
            oldWord,
            nextWord,
          );
          if (reserved.exchanged) {
            let stored = storeBodyConstraintRef(
              bodyConstraintRefs, body, logicalIndex, constraintRef,
            );
            if (stored && logicalIndex == 0u) {
              // The zero-to-one transition is unique even when joint and
              // spring append kernels race on the same body, so the compact
              // list contains each constrained body exactly once.
              appendConstrainedBody(constrainedBodies, body);
            }
            return stored;
          }
        }
      }
    `;
    const clampedPairManifoldSlots = Math.max(1, Math.min(8, Math.floor(pairManifoldSlots)));
    const contactKeySlotBitCount = Math.ceil(Math.log2(clampedPairManifoldSlots));
    this.contactKeySlotBitCount = contactKeySlotBitCount;
    const contactKeyFeatureBitCount = 9;
    const contactKeyBaseBitCount = Math.max(1, 31 - contactKeySlotBitCount);
    const contactKeyPairHashBitCount = Math.max(1, contactKeyBaseBitCount - contactKeyFeatureBitCount);
    const contactKeyPairHashMax = Math.max(1, (2 ** contactKeyPairHashBitCount) - 1);

    this.contactDispatchIndirectAttr = new IndirectStorageBufferAttribute(new Uint32Array([0, 1, 1]), 1);
    this.contactDispatchIndirectAttr.name = 'AVBD Contact Dispatch Indirect';
    this.captureContactDispatchIndirectAttr = new IndirectStorageBufferAttribute(
      new Uint32Array([0, 1, 1]),
      1,
    );
    this.captureContactDispatchIndirectAttr.name = 'AVBD Contact Capture Dispatch Indirect';
    // Keep finalization independent from the iterative dispatch lane. A future
    // residual early-out may zero contactDispatchIndirectAttr, but this exact
    // active-count snapshot must still restore persistent contact records.
    this.finalizeContactDispatchIndirectAttr = new IndirectStorageBufferAttribute(
      new Uint32Array([0, 1, 1]),
      1,
    );
    this.finalizeContactDispatchIndirectAttr.name = 'AVBD Contact Finalize Dispatch Indirect';
    // debugCounters layout:
    // 0 = scannedContacts
    // 1 = validNormals
    // 2 = frictionBoundedContacts
    // 3 = staticRegimeContacts
    // 4 = nearConeLimitContacts
    // 5 = tinyNormalForceContacts
    // 6 = normalYPositive
    // 7 = normalYNegative
    // 8 = coneBoundViolations
    // 9 = nearVerticalNormals
    // 10 = nearHorizontalNormals
    // 11 = constrainedBodies
    // 12 = fallbackColoredBodies
    // 13 = totalBodyConstraintRefs
    // 14 = saturatedBodyConstraintLists
    // 15 = maxBodyConstraintRefs
    // 16 = coloredConstraintEdges
    // 17 = sameColorConstraintEdges
    this.debugCountersAttr = new StorageBufferAttribute(new Uint32Array(18), 1);
    // phaseDebugCounters layout (9 counters per phase):
    // main phase [0..8], post phase [9..17]
    // 0 scanned
    // 1 frictionBounded
    // 2 nearCone
    // 3 tinyNormalApplied
    // 4 normalYPositive
    // 5 normalYNegative
    // 6 separatingCRegN
    // 7 coneClampApplied
    // 8 frictionSuppressedSeparated
    this.phaseDebugCountersAttr = new StorageBufferAttribute(new Uint32Array(18), 1);

    const buildContactDispatchArgsShader = wgslFn(/* wgsl */`
      fn compute(
        pairActivity: ptr<storage, array<u32>, read_write>,
        activeManifolds: ptr<storage, array<u32>, read>,
        dispatchIndirect: ptr<storage, array<u32>, read_write>,
        captureDispatchIndirect: ptr<storage, array<u32>, read_write>,
        finalizeDispatchIndirect: ptr<storage, array<u32>, read_write>,
        pairDispatchCount: u32,
      ) -> void {
        let solverHeader = pairActivity[${pairActiveContactsOffset}u];
        let activeCount = min(min(
          solverHeader & ${SOLVER_CONTACT_COUNT_MASK}u,
          pairDispatchCount,
        ), ${maxActivePairContacts}u);
        let workgroups = (activeCount + ${WORKGROUP_SIZE}u - 1u) / ${WORKGROUP_SIZE}u;
        let canonicalAlias =
          (solverHeader & ${SOLVER_CONTACT_CANONICAL_ALIAS_BIT}u) != 0u;
        let activeManifoldCount = min(activeManifolds[0], ${maxPersistentManifolds}u);
        let captureCount = select(
          activeCount,
          activeManifoldCount,
          canonicalAlias && ${manifoldReferenceCapture ? 'true' : 'false'},
        );
        let captureWorkgroups =
          (captureCount + ${WORKGROUP_SIZE}u - 1u) / ${WORKGROUP_SIZE}u;
        dispatchIndirect[0] = min(workgroups, ${CONTACT_DISPATCH_WORKGROUP_WIDTH}u);
        dispatchIndirect[1] = max(
          1u,
          (workgroups + ${CONTACT_DISPATCH_WORKGROUP_WIDTH - 1}u)
            / ${CONTACT_DISPATCH_WORKGROUP_WIDTH}u,
        );
        dispatchIndirect[2] = 1u;
        captureDispatchIndirect[0] = min(
          captureWorkgroups,
          ${CONTACT_DISPATCH_WORKGROUP_WIDTH}u,
        );
        captureDispatchIndirect[1] = max(
          1u,
          (captureWorkgroups + ${CONTACT_DISPATCH_WORKGROUP_WIDTH - 1}u)
            / ${CONTACT_DISPATCH_WORKGROUP_WIDTH}u,
        );
        captureDispatchIndirect[2] = 1u;
        finalizeDispatchIndirect[0] = min(
          workgroups,
          ${CONTACT_DISPATCH_WORKGROUP_WIDTH}u,
        );
        finalizeDispatchIndirect[1] = max(
          1u,
          (workgroups + ${CONTACT_DISPATCH_WORKGROUP_WIDTH - 1}u)
            / ${CONTACT_DISPATCH_WORKGROUP_WIDTH}u,
        );
        finalizeDispatchIndirect[2] = 1u;
      }
    `);

    this.buildContactDispatchArgsKernel = buildContactDispatchArgsShader({
      pairActivity: storage(pairActivity, 'uint', pairActivityWordCount),
      activeManifolds: storage(
        activeManifolds,
        'uint',
        activeManifolds.count,
      ).toReadOnly(),
      dispatchIndirect: storage(this.contactDispatchIndirectAttr, 'uint', 3),
      captureDispatchIndirect: storage(
        this.captureContactDispatchIndirectAttr,
        'uint',
        3,
      ),
      finalizeDispatchIndirect: storage(this.finalizeContactDispatchIndirectAttr, 'uint', 3),
      pairDispatchCount: uniform(0),
    }).computeKernel([1, 1, 1]).setName('AVBD Build Contact Dispatch Args');

    const initializeAdaptiveStateShader = wgslFn(/* wgsl */`
      fn compute(
        adaptiveState: ptr<storage, array<atomic<u32>>, read_write>,
        bodyColorCompact: ptr<storage, array<atomic<u32>>, read_write>,
        requested: u32,
        minIterations: u32,
        maxIterations: u32,
        trackResidualsImmediately: u32,
      ) -> void {
        let fallbackBodies = atomicLoad(
          &bodyColorCompact[${bodyColorCountOffset + bodyColorKernelVariants}u],
        );
        let previousFallbackBodies = atomicLoad(
          &adaptiveState[${ADAPTIVE_PREVIOUS_FALLBACK_COUNT}u],
        );
        // Sleeping writes REQUESTED after the previous solve. Consume it at
        // the head of this queue-ordered substep; no map/readback participates
        // in the control loop.
        let polishRequested = atomicExchange(
          &adaptiveState[${AVBD_ADAPTIVE_SLEEP_POLISH_REQUESTED}u],
          0u,
        );
        let polishActive = requested != 0u && polishRequested != 0u;
        atomicStore(
          &adaptiveState[${AVBD_ADAPTIVE_SLEEP_POLISH_CONFIGURED}u],
          requested,
        );
        atomicStore(
          &adaptiveState[${ADAPTIVE_SLEEP_POLISH_ACTIVE}u],
          select(0u, 1u, polishActive),
        );
        if (!polishActive) {
          atomicStore(&adaptiveState[${ADAPTIVE_SLEEP_POLISH_COMPLETED}u], 0u);
        }
        atomicStore(&adaptiveState[${ADAPTIVE_ENABLED}u], 0u);
        atomicStore(&adaptiveState[${ADAPTIVE_BELOW_STREAK}u], 0u);
        atomicStore(&adaptiveState[${ADAPTIVE_EXECUTED_LAST}u], 0u);
        atomicStore(&adaptiveState[${ADAPTIVE_BODY_VIOLATION}u], 0u);
        atomicStore(&adaptiveState[${ADAPTIVE_CONTACT_VIOLATION}u], 0u);
        atomicStore(&adaptiveState[${ADAPTIVE_FALLBACK_BODY_VIOLATION}u], 0u);
        atomicStore(&adaptiveState[${ADAPTIVE_TRACK_RESIDUALS}u], 0u);
        atomicStore(&adaptiveState[${ADAPTIVE_PREMIN_INVALID}u], 0u);
        atomicStore(&adaptiveState[${ADAPTIVE_INVALID_LAST}u], 0u);
        atomicStore(&adaptiveState[${ADAPTIVE_EARLY_LAST}u], 0u);
        atomicStore(&adaptiveState[${ADAPTIVE_FALLBACK_LAST}u], fallbackBodies);
        // Fixed-iteration callers retain their established motion/correction
        // sleep gate. Adaptive callers remain fail-closed until sleep polish
        // has produced four consecutive converged full-max substeps.
        atomicStore(
          &adaptiveState[${AVBD_ADAPTIVE_SLEEP_CONVERGED}u],
          select(0u, 1u, requested == 0u),
        );
        atomicStore(
          &adaptiveState[${ADAPTIVE_PREVIOUS_FALLBACK_COUNT}u],
          fallbackBodies,
        );
        atomicStore(&adaptiveState[${ADAPTIVE_MIN_LAST}u], minIterations);
        atomicStore(&adaptiveState[${ADAPTIVE_MAX_LAST}u], maxIterations);

        if (requested == 0u) { return; }
        atomicAdd(&adaptiveState[${ADAPTIVE_TOTAL_STEPS}u], 1u);
        if (fallbackBodies != 0u && previousFallbackBodies == 0u) {
          // Entering the high-degree lane gets one conservative full-max
          // substep. While that lane remains non-empty, count/identity changes
          // are covered by the scratch solve's real normalized pose correction
          // and the full active-contact dual/penalty residuals.
          atomicStore(&adaptiveState[${ADAPTIVE_EXECUTED_LAST}u], maxIterations);
          atomicAdd(&adaptiveState[${ADAPTIVE_TOTAL_ITERATIONS}u], maxIterations);
          atomicAdd(&adaptiveState[${ADAPTIVE_TOTAL_FAIL_OPEN}u], 1u);
          atomicAdd(&adaptiveState[${ADAPTIVE_TOTAL_FALLBACK_FAIL_OPEN}u], 1u);
          atomicStore(&adaptiveState[${ADAPTIVE_SLEEP_POLISH_ACTIVE}u], 0u);
          atomicStore(&adaptiveState[${ADAPTIVE_SLEEP_POLISH_COMPLETED}u], 0u);
          return;
        }
        atomicStore(&adaptiveState[${ADAPTIVE_ENABLED}u], 1u);
        atomicStore(
          &adaptiveState[${ADAPTIVE_TRACK_RESIDUALS}u],
          select(0u, 1u, minIterations <= 1u || trackResidualsImmediately != 0u),
        );
      }
    `);
    this.initializeAdaptiveStateKernel = initializeAdaptiveStateShader({
      adaptiveState: storage(this.adaptiveStateAttr, 'uint', ADAPTIVE_STATE_WORDS).toAtomic(),
      bodyColorCompact: storage(this.bodyColorCompactAttr, 'uint', bodyColorCompactWordCount).toAtomic(),
      requested: uniform(0),
      minIterations: uniform(1),
      maxIterations: uniform(1),
      trackResidualsImmediately: uniform(0),
    }).computeKernel([1, 1, 1]).setName('AVBD Initialize GPU Adaptive Iterations');
    assertStorageBufferBudget('AVBD Initialize GPU Adaptive Iterations', 2);

    const beginAdaptiveResidualTrackingShader = wgslFn(/* wgsl */`
      fn compute(
        adaptiveState: ptr<storage, array<atomic<u32>>, read_write>,
        completedWarmupIterations: u32,
        forcePreminInvalidForTesting: u32,
      ) -> void {
        if (atomicLoad(&adaptiveState[${ADAPTIVE_ENABLED}u]) == 0u) { return; }
        atomicStore(
          &adaptiveState[${ADAPTIVE_EXECUTED_LAST}u],
          completedWarmupIterations,
        );
        atomicAdd(
          &adaptiveState[${ADAPTIVE_TOTAL_ITERATIONS}u],
          completedWarmupIterations,
        );
        if (forcePreminInvalidForTesting != 0u) {
          // Regression-only injection into the sticky warmup INVALID lane.
          // Production presets never enable this hook.
          atomicStore(&adaptiveState[${ADAPTIVE_PREMIN_INVALID}u], 1u);
        }
        atomicStore(&adaptiveState[${ADAPTIVE_TRACK_RESIDUALS}u], 1u);
      }
    `);
    this.beginAdaptiveResidualTrackingKernel = beginAdaptiveResidualTrackingShader({
      adaptiveState: storage(this.adaptiveStateAttr, 'uint', ADAPTIVE_STATE_WORDS).toAtomic(),
      completedWarmupIterations: uniform(0),
      forcePreminInvalidForTesting: uniform(0),
    }).computeKernel([1, 1, 1]).setName('AVBD Begin Adaptive Residual Tracking');
    assertStorageBufferBudget('AVBD Begin Adaptive Residual Tracking', 1);

    const clearAdaptiveRoundResidualShader = wgslFn(/* wgsl */`
      fn compute(
        adaptiveState: ptr<storage, array<atomic<u32>>, read_write>,
        adaptiveResiduals: ptr<storage, array<atomic<u32>>, read_write>,
        workgroupId: vec3u,
        localId: vec3u,
        forceInvalidForTesting: u32,
      ) -> void {
        if (atomicLoad(&adaptiveState[${ADAPTIVE_ENABLED}u]) == 0u
          || atomicLoad(&adaptiveState[${ADAPTIVE_EARLY_LAST}u]) != 0u) {
          return;
        }
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid < ${ADAPTIVE_RESIDUAL_SHARDS}u) {
          let base = gid * ${ADAPTIVE_RESIDUAL_WORDS_PER_SHARD}u;
          atomicStore(&adaptiveResiduals[base], 0u);
          atomicStore(&adaptiveResiduals[base + 1u], 0u);
        }
        if (gid == 0u) {
          atomicStore(&adaptiveState[${ADAPTIVE_BODY_VIOLATION}u], 0u);
          atomicStore(&adaptiveState[${ADAPTIVE_CONTACT_VIOLATION}u], 0u);
          atomicStore(&adaptiveState[${ADAPTIVE_FALLBACK_BODY_VIOLATION}u], 0u);
          atomicStore(&adaptiveState[${ADAPTIVE_INVALID_LAST}u], 0u);
          if (forceInvalidForTesting != 0u) {
            atomicStore(&adaptiveState[${ADAPTIVE_INVALID_LAST}u], 1u);
          }
        }
      }
    `);
    this.clearAdaptiveRoundResidualKernel = clearAdaptiveRoundResidualShader({
      adaptiveState: storage(this.adaptiveStateAttr, 'uint', ADAPTIVE_STATE_WORDS).toAtomic(),
      adaptiveResiduals: storage(
        this.adaptiveResidualsAttr,
        'uint',
        ADAPTIVE_RESIDUAL_WORDS,
      ).toAtomic(),
      workgroupId,
      localId,
      forceInvalidForTesting: uniform(0),
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Clear Sharded Adaptive Residuals');

    const reduceAdaptiveRoundResidualShader = wgslFn(/* wgsl */`
      fn compute(
        adaptiveState: ptr<storage, array<atomic<u32>>, read_write>,
        adaptiveResiduals: ptr<storage, array<atomic<u32>>, read_write>,
        localId: vec3u,
      ) -> void {
        if (atomicLoad(&adaptiveState[${ADAPTIVE_ENABLED}u]) == 0u
          || atomicLoad(&adaptiveState[${ADAPTIVE_EARLY_LAST}u]) != 0u) {
          return;
        }
        var localBodyViolation = 0u;
        var localContactViolation = 0u;
        for (var shard = localId.x; shard < ${ADAPTIVE_RESIDUAL_SHARDS}u; shard += ${WORKGROUP_SIZE}u) {
          let base = shard * ${ADAPTIVE_RESIDUAL_WORDS_PER_SHARD}u;
          localBodyViolation = max(
            localBodyViolation,
            atomicLoad(&adaptiveResiduals[base]),
          );
          localContactViolation = max(
            localContactViolation,
            atomicLoad(&adaptiveResiduals[base + 1u]),
          );
        }
        // These are sticky binary flags, not counters. Skip zero lanes and
        // avoid repeatedly publishing 1 after another reduction lane has
        // already established the result.
        if (localBodyViolation != 0u
          && atomicLoad(&adaptiveState[${ADAPTIVE_BODY_VIOLATION}u]) == 0u) {
          atomicStore(&adaptiveState[${ADAPTIVE_BODY_VIOLATION}u], 1u);
        }
        if (localContactViolation != 0u
          && atomicLoad(&adaptiveState[${ADAPTIVE_CONTACT_VIOLATION}u]) == 0u) {
          atomicStore(&adaptiveState[${ADAPTIVE_CONTACT_VIOLATION}u], 1u);
        }
      }
    `);
    this.reduceAdaptiveRoundResidualKernel = reduceAdaptiveRoundResidualShader({
      adaptiveState: storage(this.adaptiveStateAttr, 'uint', ADAPTIVE_STATE_WORDS).toAtomic(),
      adaptiveResiduals: storage(
        this.adaptiveResidualsAttr,
        'uint',
        ADAPTIVE_RESIDUAL_WORDS,
      ).toAtomic(),
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Reduce Sharded Adaptive Residuals');

    const decideAdaptiveEarlyOutShader = wgslFn(/* wgsl */`
      fn compute(
        adaptiveState: ptr<storage, array<atomic<u32>>, read_write>,
        minIterations: u32,
        maxIterations: u32,
      ) -> void {
        if (atomicLoad(&adaptiveState[${ADAPTIVE_ENABLED}u]) == 0u
          || atomicLoad(&adaptiveState[${ADAPTIVE_EARLY_LAST}u]) != 0u) {
          return;
        }
        let previousExecuted = atomicAdd(
          &adaptiveState[${ADAPTIVE_EXECUTED_LAST}u],
          1u,
        );
        let executed = previousExecuted + 1u;
        let invalid = atomicLoad(&adaptiveState[${ADAPTIVE_INVALID_LAST}u]) != 0u;
        let preminInvalid = atomicLoad(&adaptiveState[${ADAPTIVE_PREMIN_INVALID}u]) != 0u;
        if (invalid || preminInvalid) {
          // Any non-finite contribution disables adaptation for the remainder
          // of this substep. Existing indirect args remain intact, so every
          // remaining AVBD round still executes.
          atomicStore(&adaptiveState[${ADAPTIVE_ENABLED}u], 0u);
          // Publish the sticky pre-min lane through the public per-step
          // diagnostic bit as well as the cumulative fail-open counters.
          atomicStore(&adaptiveState[${ADAPTIVE_INVALID_LAST}u], 1u);
          atomicStore(&adaptiveState[${ADAPTIVE_BELOW_STREAK}u], 0u);
          atomicStore(&adaptiveState[${ADAPTIVE_EXECUTED_LAST}u], maxIterations);
          atomicStore(&adaptiveState[${AVBD_ADAPTIVE_SLEEP_CONVERGED}u], 0u);
          atomicStore(&adaptiveState[${ADAPTIVE_SLEEP_POLISH_ACTIVE}u], 0u);
          atomicStore(&adaptiveState[${ADAPTIVE_SLEEP_POLISH_COMPLETED}u], 0u);
          atomicAdd(
            &adaptiveState[${ADAPTIVE_TOTAL_ITERATIONS}u],
            maxIterations - previousExecuted,
          );
          atomicAdd(&adaptiveState[${ADAPTIVE_TOTAL_FAIL_OPEN}u], 1u);
          atomicAdd(&adaptiveState[${ADAPTIVE_TOTAL_INVALID_FAIL_OPEN}u], 1u);
          return;
        }

        atomicAdd(&adaptiveState[${ADAPTIVE_TOTAL_ITERATIONS}u], 1u);
        let below = atomicLoad(&adaptiveState[${ADAPTIVE_BODY_VIOLATION}u]) == 0u
          && atomicLoad(&adaptiveState[${ADAPTIVE_CONTACT_VIOLATION}u]) == 0u;
        var streak = 0u;
        if (below && executed >= minIterations) {
          streak = atomicLoad(&adaptiveState[${ADAPTIVE_BELOW_STREAK}u]) + 1u;
        }
        atomicStore(&adaptiveState[${ADAPTIVE_BELOW_STREAK}u], streak);
        let polishing = atomicLoad(
          &adaptiveState[${ADAPTIVE_SLEEP_POLISH_ACTIVE}u]
        ) != 0u;
        if (polishing) {
          // A polish substep is deliberately ineligible for adaptive early
          // out. Its final two sampled rounds must converge at the configured
          // max iteration; any miss restarts the consecutive certificate.
          if (executed == maxIterations) {
            if (streak >= 2u) {
              // Slot 18 remains the per-substep residual certificate used by
              // diagnostics. Sleeping combines it with COMPLETED below.
              atomicStore(&adaptiveState[${AVBD_ADAPTIVE_SLEEP_CONVERGED}u], 1u);
              let completed = min(
                atomicLoad(&adaptiveState[${ADAPTIVE_SLEEP_POLISH_COMPLETED}u]) + 1u,
                ${AVBD_ADAPTIVE_SLEEP_POLISH_STEPS}u,
              );
              atomicStore(
                &adaptiveState[${ADAPTIVE_SLEEP_POLISH_COMPLETED}u],
                completed,
              );
            } else {
              atomicStore(&adaptiveState[${ADAPTIVE_SLEEP_POLISH_COMPLETED}u], 0u);
            }
          }
        } else if (streak >= 2u) {
          // Preserve the established per-step convergence diagnostic. The
          // sleeping stage additionally requires four full-max polish steps.
          atomicStore(&adaptiveState[${AVBD_ADAPTIVE_SLEEP_CONVERGED}u], 1u);
          if (executed < maxIterations) {
            atomicStore(&adaptiveState[${ADAPTIVE_EARLY_LAST}u], 1u);
            atomicAdd(&adaptiveState[${ADAPTIVE_TOTAL_EARLY_EXITS}u], 1u);
          }
        }
      }
    `);
    this.decideAdaptiveEarlyOutKernel = decideAdaptiveEarlyOutShader({
      adaptiveState: storage(this.adaptiveStateAttr, 'uint', ADAPTIVE_STATE_WORDS).toAtomic(),
      minIterations: uniform(1),
      maxIterations: uniform(1),
    }).computeKernel([1, 1, 1]).setName('AVBD Decide Adaptive Early Out');

    // One WebGPU bind group may expose at most ten storage buffers. Clear the
    // independent indirect-argument buffers in batches of nine plus state.
    // The separately latched finalizer argument is intentionally absent.
    const adaptiveDispatchTargets = [
      ...this.bodyColorDispatchIndirectAttrs,
      this.bodyColorFallbackDispatchIndirectAttr,
      this.contactDispatchIndirectAttr,
    ];
    this.adaptiveDispatchTargetCount = adaptiveDispatchTargets.length;
    if (this.adaptiveDispatchTargetCount !== bodyColorKernelVariants + 2) {
      throw new Error('Adaptive early-out dispatch closure does not cover every color and residual lane.');
    }
    this.clearAdaptiveDispatchArgsKernels = [];
    for (let batchStart = 0; batchStart < adaptiveDispatchTargets.length; batchStart += 9) {
      const batch = adaptiveDispatchTargets.slice(batchStart, batchStart + 9);
      const params = batch.map((_, index) =>
        `        dispatchIndirect${index}: ptr<storage, array<u32>, read_write>,`,
      ).join('\n');
      const clears = batch.map((_, index) =>
        `        dispatchIndirect${index}[0] = 0u;`,
      ).join('\n');
      const clearAdaptiveDispatchArgsShader = wgslFn(/* wgsl */`
        fn compute(
          adaptiveState: ptr<storage, array<atomic<u32>>, read_write>,
${params}
        ) -> void {
          if (atomicLoad(&adaptiveState[${ADAPTIVE_EARLY_LAST}u]) == 0u) { return; }
${clears}
        }
      `);
      const bindings: Record<string, any> = {
        adaptiveState: storage(this.adaptiveStateAttr, 'uint', ADAPTIVE_STATE_WORDS).toAtomic(),
      };
      batch.forEach((attribute, index) => {
        bindings[`dispatchIndirect${index}`] = storage(attribute, 'uint', 3);
      });
      this.clearAdaptiveDispatchArgsKernels.push(
        clearAdaptiveDispatchArgsShader(bindings)
          .computeKernel([1, 1, 1])
          .setName(`AVBD Clear Adaptive Dispatch Args Batch ${batchStart / 9}`),
      );
      assertStorageBufferBudget(
        `AVBD Clear Adaptive Dispatch Args Batch ${batchStart / 9}`,
        batch.length + 1,
      );
    }

    const clearPhaseDebugCountersShader = wgslFn(/* wgsl */`
      fn compute(
        phaseDebugCounters: ptr<storage, array<atomic<u32>>, read_write>,
      ) -> void {
        for (var i = 0u; i < 18u; i++) {
          atomicStore(&phaseDebugCounters[i], 0u);
        }
      }
    `);

    this.clearPhaseDebugCountersKernel = clearPhaseDebugCountersShader({
      phaseDebugCounters: storage(this.phaseDebugCountersAttr, 'uint', 18).toAtomic(),
    }).computeKernel([1, 1, 1]).setName('AVBD Clear Phase Debug Counters');

    const accumulatePhaseDebugCountersShader = wgslFn(/* wgsl */`
      fn compute(
        positions: ptr<storage, array<vec4f>, read>,
        quaternions: ptr<storage, array<vec4f>, read>,
        initialPose: ptr<storage, array<vec4f>, read>,
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        pairActivity: ptr<storage, array<u32>, read_write>,
        phaseDebugCounters: ptr<storage, array<atomic<u32>>, read_write>,
        pairDispatchCount: u32,
        regularizationAlpha: f32,
        tangentialRegularizationAlpha: f32,
        phaseOffset: u32,
        frictionSolveScale: f32,
        useReferenceTangentialUpdate: u32,
        frictionStatic: f32,
        frictionDynamic: f32,
        kStart: f32,
        dualForceMax: f32,
        enableNormalReleaseHeuristic: u32,
        useNormalContactMargin: u32,
        useLocalContactArms: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = (
          workgroupId.y * ${CONTACT_DISPATCH_WORKGROUP_WIDTH}u + workgroupId.x
        ) * ${WORKGROUP_SIZE}u + localId.x;
        let solverHeader = pairActivity[${pairActiveContactsOffset}u];
        let activeCount = min(min(
          solverHeader & ${SOLVER_CONTACT_COUNT_MASK}u,
          pairDispatchCount,
        ), ${maxActivePairContacts}u);
        if (gid >= activeCount) { return; }

        let activeEntriesOffset = select(
          ${pairActiveContactsOffset + 1}u,
          ${pairCanonicalActiveContactsOffset + 1}u,
          (solverHeader & ${SOLVER_CONTACT_CANONICAL_ALIAS_BIT}u) != 0u,
        );
        let p = pairActivity[activeEntriesOffset + gid];
        if (p >= ${maxPairContacts}u) { return; }
        let base = min(phaseOffset, 9u);
        atomicAdd(&phaseDebugCounters[base + 0u], 1u);

        let n = loadContactNormalPen(pairContacts, p).xyz;
        if (dot(n, n) <= 1e-10) { return; }

        if (n.y >= 0.0) {
          atomicAdd(&phaseDebugCounters[base + 4u], 1u);
        } else {
          atomicAdd(&phaseDebugCounters[base + 5u], 1u);
        }

        let armA = loadContactArmA(pairContacts, p);
        let armB = loadContactArmB(pairContacts, p);
        let contactMeta = loadContactMeta(pairContacts, p);
        let encodedContactInfo = bitcast<u32>(contactMeta.w);
        let i = u32(contactMeta.x + 0.5);
        let j = u32(contactMeta.y + 0.5);
        if (i >= ${maxBodies}u || j >= ${maxBodies}u || i == j) { return; }

        let raStored = armA.xyz;
        let rbStored = armB.xyz;
        let posA = positions[i].xyz;
        let posB = positions[j].xyz;
        let qA = normalize(quaternions[i]);
        let qB = normalize(quaternions[j]);
        let poseBaseA = i * 2u;
        let poseBaseB = j * 2u;
        let prevPosA = initialPose[poseBaseA].xyz;
        let prevPosB = initialPose[poseBaseB].xyz;
        let prevQA = normalize(initialPose[poseBaseA + 1u]);
        let prevQB = normalize(initialPose[poseBaseB + 1u]);
        let localContactArms = useLocalContactArms > 0u;
        let sphereArmFlags = u32(armB.w + 0.5);
        let ra = select(
          raStored,
          qrot(qA, raStored),
          localContactArms && (sphereArmFlags & 1u) == 0u,
        );
        let rb = select(
          rbStored,
          qrot(qB, rbStored),
          localContactArms && (sphereArmFlags & 2u) == 0u,
        );
        let dqAraw = qmul(qA, qconj(prevQA));
        let dqBraw = qmul(qB, qconj(prevQB));
        let dqA = select(dqAraw, -dqAraw, dqAraw.w < 0.0);
        let dqB = select(dqBraw, -dqBraw, dqBraw.w < 0.0);
        let dThetaA = 2.0 * dqA.xyz;
        let dThetaB = 2.0 * dqB.xyz;
        let dPosA = posA - prevPosA;
        let dPosB = posB - prevPosB;

        let jAL = -n;
        let jBL = n;
        let jAA = -cross(ra, n);
        let jBA = cross(rb, n);

        let tangentBasis = tangentBasisFromCachedT1(n, loadContactTangentT1(pairContacts, p), armA.w);
        let t1 = tangentBasis.t1;
        let t2 = tangentBasis.t2;

        let jt1AL = -t1;
        let jt1BL = t1;
        let jt1AA = -cross(ra, t1);
        let jt1BA = cross(rb, t1);
        let jt2AL = -t2;
        let jt2BL = t2;
        let jt2AA = -cross(ra, t2);
        let jt2BA = cross(rb, t2);

        let penetration0 = max(loadContactNormalPen(pairContacts, p).w, 0.0);
        let cachedConstraintC0 = loadContactConstraintC0(pairContacts, p);
        let c0 = cachedConstraintC0.x;
        let cRegN = contactConstraintC0Scale(encodedContactInfo, regularizationAlpha) * c0
          + dot(jAL, dPosA)
          + dot(jAA, dThetaA)
          + dot(jBL, dPosB)
          + dot(jBA, dThetaB);
        if (cRegN > 0.0) {
          atomicAdd(&phaseDebugCounters[base + 6u], 1u);
        }

        let c0T1 = cachedConstraintC0.y;
        let c0T2 = cachedConstraintC0.z;
        let cRegT1 = contactConstraintC0Scale(encodedContactInfo, tangentialRegularizationAlpha) * c0T1
          + dot(jt1AL, dPosA)
          + dot(jt1AA, dThetaA)
          + dot(jt1BL, dPosB)
          + dot(jt1BA, dThetaB);
        let cRegT2 = contactConstraintC0Scale(encodedContactInfo, tangentialRegularizationAlpha) * c0T2
          + dot(jt2AL, dPosA)
          + dot(jt2AA, dThetaA)
          + dot(jt2BL, dPosB)
          + dot(jt2BA, dThetaB);

        let contactState = loadContactStateHot(pairContacts, p, kStart);
        let dualState = contactState.dual;
        let dualN = clamp(dualState.x, -dualForceMax, 0.0);
        let penaltyN = max(contactState.penaltyN, kStart);
        let penaltyTB = max(contactState.penalty.yz, vec2f(kStart));
        let lambdaPlusN = dualN + penaltyN * cRegN;
        var lambdaAppliedN = clamp(lambdaPlusN, -dualForceMax, 0.0);
        let normalSupportThreshold = 1e-6;
        let normalReleaseTolerance = max(2e-5, 1.25 * penetration0);
        let normalReleaseDecayNear = 0.85;
        let normalReleaseDecayFar = 0.6;
        let normalReleaseMinSupport = -5e-4;
        if (
          enableNormalReleaseHeuristic > 0u &&
          penetration0 > 1e-6
          && dualN < -normalSupportThreshold
          && cRegN > 0.0
          && -lambdaAppliedN <= normalSupportThreshold
        ) {
          let releaseDecay = select(normalReleaseDecayFar, normalReleaseDecayNear, cRegN <= normalReleaseTolerance);
          lambdaAppliedN = min(dualN * releaseDecay, normalReleaseMinSupport);
        }
        if (-lambdaAppliedN <= normalSupportThreshold) {
          atomicAdd(&phaseDebugCounters[base + 3u], 1u);
        }
        let frictionSeparationTolerance = normalReleaseTolerance;
        let frictionOffSeparation = -lambdaAppliedN <= normalSupportThreshold
          || (enableNormalReleaseHeuristic > 0u && cRegN > frictionSeparationTolerance);

        let frictionScale = contactState.frictionScale;
        let muStatic = frictionStatic * frictionScale * frictionSolveScale;
        let muDynamic = frictionDynamic * frictionScale * frictionSolveScale;
        let dualTB = contactStateDualTB(contactState, n, t1, t2, useReferenceTangentialUpdate);
        let prevTMag = length(dualTB);
        let staticBoundPrev = muStatic * abs(dualN);
        let useStatic = prevTMag <= staticBoundPrev + 1e-6;
        let mu = select(muDynamic, muStatic, useStatic);
        let frictionBound = max(mu * (-dualN), 0.0);
        if (frictionOffSeparation && frictionBound > 1e-6) {
          atomicAdd(&phaseDebugCounters[base + 8u], 1u);
        }
        if (frictionBound > 1e-6) {
          atomicAdd(&phaseDebugCounters[base + 1u], 1u);
          let lambdaPlusTB = vec2f(
            penaltyTB.x * cRegT1 + dualTB.x,
            penaltyTB.y * cRegT2 + dualTB.y,
          );
          let lambdaPlusTBLen2 = dot(lambdaPlusTB, lambdaPlusTB);
          var lambdaAppliedTB = lambdaPlusTB;
          if (lambdaPlusTBLen2 > frictionBound * frictionBound && lambdaPlusTBLen2 > 1e-12) {
            lambdaAppliedTB *= frictionBound * inverseSqrt(lambdaPlusTBLen2);
            atomicAdd(&phaseDebugCounters[base + 7u], 1u);
          }
          let lambdaAppliedTBLen2 = dot(lambdaAppliedTB, lambdaAppliedTB);
          if (lambdaAppliedTBLen2 >= (0.95 * 0.95) * frictionBound * frictionBound) {
            atomicAdd(&phaseDebugCounters[base + 2u], 1u);
          }
        }
      }
    `, [qrot, qmul, qconj, tangentBasisHelpers, contactRecordHelpers, contactStateHelpers]);

    this.accumulatePhaseDebugCountersKernel = accumulatePhaseDebugCountersShader({
      positions: storage(positions, 'vec4f', maxBodies).toReadOnly(),
      quaternions: storage(quaternions, 'vec4f', maxBodies).toReadOnly(),
      initialPose: storage(initialPose, 'vec4f', maxBodies * 2).toReadOnly(),
      pairContacts: storage(pairContacts, 'vec4f', maxPairContacts * CONTACT_RECORD_VEC4S),
      pairActivity: storage(pairActivity, 'uint', pairActivityWordCount),
      phaseDebugCounters: storage(this.phaseDebugCountersAttr, 'uint', 18).toAtomic(),
      pairDispatchCount: uniform(0),
      regularizationAlpha: uniform(1.0),
      tangentialRegularizationAlpha: uniform(1.0),
      phaseOffset: uniform(0),
      frictionSolveScale: uniform(1.0),
      useReferenceTangentialUpdate: uniform(1),
      frictionStatic: uniform(AVBD_FRICTION_STATIC),
      frictionDynamic: uniform(AVBD_FRICTION_DYNAMIC),
      kStart: uniform(AVBD_K_START),
      dualForceMax: uniform(500000.0),
      enableNormalReleaseHeuristic: uniform(0),
      useNormalContactMargin: uniform(1),
      useLocalContactArms: uniform(1),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Accumulate Phase Debug Counters');

    const clearDebugCountersShader = wgslFn(/* wgsl */`
      fn compute(
        debugCounters: ptr<storage, array<atomic<u32>>, read_write>,
      ) -> void {
        for (var i = 0u; i < 18u; i++) {
          atomicStore(&debugCounters[i], 0u);
        }
      }
    `);

    this.clearDebugCountersKernel = clearDebugCountersShader({
      debugCounters: storage(this.debugCountersAttr, 'uint', 18).toAtomic(),
    }).computeKernel([1, 1, 1]).setName('AVBD Clear Debug Counters');

    const accumulateDebugCountersShader = wgslFn(/* wgsl */`
      fn compute(
        pairActivity: ptr<storage, array<u32>, read_write>,
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        debugCounters: ptr<storage, array<atomic<u32>>, read_write>,
        pairDispatchCount: u32,
        useReferenceTangentialUpdate: u32,
        frictionStatic: f32,
        frictionDynamic: f32,
        kStart: f32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = (
          workgroupId.y * ${CONTACT_DISPATCH_WORKGROUP_WIDTH}u + workgroupId.x
        ) * ${WORKGROUP_SIZE}u + localId.x;
        let solverHeader = pairActivity[${pairActiveContactsOffset}u];
        let activeCount = min(min(
          solverHeader & ${SOLVER_CONTACT_COUNT_MASK}u,
          pairDispatchCount,
        ), ${maxActivePairContacts}u);
        if (gid >= activeCount) { return; }

        let activeEntriesOffset = select(
          ${pairActiveContactsOffset + 1}u,
          ${pairCanonicalActiveContactsOffset + 1}u,
          (solverHeader & ${SOLVER_CONTACT_CANONICAL_ALIAS_BIT}u) != 0u,
        );
        let p = pairActivity[activeEntriesOffset + gid];
        if (p >= ${maxPairContacts}u) { return; }
        atomicAdd(&debugCounters[0], 1u);

        let n = loadContactNormalPen(pairContacts, p).xyz;
        let nLen2 = dot(n, n);
        if (nLen2 <= 1e-10) { return; }
        atomicAdd(&debugCounters[1], 1u);

        if (n.y >= 0.0) {
          atomicAdd(&debugCounters[6], 1u);
        } else {
          atomicAdd(&debugCounters[7], 1u);
        }
        let absNy = abs(n.y);
        if (absNy > 0.9) {
          atomicAdd(&debugCounters[9], 1u);
        } else if (absNy < 0.2) {
          atomicAdd(&debugCounters[10], 1u);
        }

        let contactState = loadContactStateHot(pairContacts, p, kStart);
        let dualState = contactState.dual;
        let normalMag = contactState.lambdaN;
        if (normalMag <= 1e-6) {
          atomicAdd(&debugCounters[5], 1u);
        }

        let frictionScale = contactState.frictionScale;
        let muStatic = frictionStatic * frictionScale;
        let muDynamic = frictionDynamic * frictionScale;
        let dualTB = contactStateDualTB(contactState, n, vec3f(1.0, 0.0, 0.0), vec3f(0.0, 1.0, 0.0), 1u);
        let dualTWorldRaw = dualState.yzw;
        let dualTWorld = dualTWorldRaw - n * dot(dualTWorldRaw, n);
        let dualTMag = select(length(dualTWorld), length(dualTB), useReferenceTangentialUpdate > 0u);
        let staticBound = muStatic * normalMag;
        let useStatic = dualTMag <= staticBound + 1e-6;

        let mu = select(muDynamic, muStatic, useStatic);
        let frictionBound = max(mu * normalMag, 0.0);
        if (frictionBound > 1e-6) {
          atomicAdd(&debugCounters[2], 1u);
          if (useStatic) {
            atomicAdd(&debugCounters[3], 1u);
          }
          if (dualTMag >= 0.95 * frictionBound) {
            atomicAdd(&debugCounters[4], 1u);
          }
          // Strict violation against static-cone bound.
          if (dualTMag > staticBound + 1e-3) {
            atomicAdd(&debugCounters[8], 1u);
          }
        }
      }
    `, [tangentBasisHelpers, contactRecordHelpers, contactStateHelpers]);

    this.accumulateDebugCountersKernel = accumulateDebugCountersShader({
      pairActivity: storage(pairActivity, 'uint', pairActivityWordCount),
      pairContacts: storage(pairContacts, 'vec4f', maxPairContacts * CONTACT_RECORD_VEC4S),
      debugCounters: storage(this.debugCountersAttr, 'uint', 18).toAtomic(),
      pairDispatchCount: uniform(0),
      useReferenceTangentialUpdate: uniform(0),
      frictionStatic: uniform(AVBD_FRICTION_STATIC),
      frictionDynamic: uniform(AVBD_FRICTION_DYNAMIC),
      kStart: uniform(AVBD_K_START),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Accumulate Debug Counters');

    const accumulateBodyColorDebugCountersShader = wgslFn(/* wgsl */`
      fn compute(
        bodyConstraintCounts: ptr<storage, array<u32>, read>,
        bodyConstraintRefs: ptr<storage, array<u32>, read>,
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        jointRecords: ptr<storage, array<vec4f>, read_write>,
        springRecords: ptr<storage, array<vec4f>, read_write>,
        debugCounters: ptr<storage, array<atomic<u32>>, read_write>,
        constrainedBodies: ptr<storage, array<u32>, read>,
        bodyCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let compactIndex = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        let constrainedCount = min(constrainedBodies[0], ${maxBodies}u);
        if (compactIndex >= constrainedCount) { return; }
        let gid = constrainedBodies[compactIndex + 1u];
        if (gid >= bodyCount) { return; }

        let word = bodyConstraintCounts[gid];
        let constraintCount = word & 0xFFFFu;
        if (constraintCount == 0u) { return; }

        atomicAdd(&debugCounters[11], 1u);
        atomicAdd(&debugCounters[13], constraintCount);
        atomicMax(&debugCounters[15], constraintCount);
        if (constraintCount >= 0xffffu) {
          atomicAdd(&debugCounters[14], 1u);
        }
        if ((word & ${BODY_COLOR_FALLBACK_FLAG}u) != 0u) {
          atomicAdd(&debugCounters[12], 1u);
        }

        let bodyColor = (word >> 16u) & 0xFFu;
        var constraintOverflowNode = bodyConstraintRefs[
          ${bodyConstraintOverflowHeadOffset}u + gid
        ];
        for (var k = 0u; k < constraintCount; k++) {
          let constraintRef = loadBodyConstraintRef(
            bodyConstraintRefs, gid, k, &constraintOverflowNode,
          );
          let constraintTag = constraintRef & ${CONSTRAINT_REF_TAG_MASK}u;
          let constraintIndex = constraintRef & ${CONSTRAINT_REF_INDEX_MASK}u;
          var other = debugConstraintOtherBodyForContact(gid, constraintRef, pairContacts);
          if (constraintTag == ${CONSTRAINT_REF_TAG_JOINT}u) {
            other = debugConstraintOtherBodyForJoint(gid, constraintIndex, jointRecords);
          } else if (constraintTag == ${CONSTRAINT_REF_TAG_SPRING}u) {
            other = debugConstraintOtherBodyForSpring(gid, constraintIndex, springRecords);
          }
          if (other >= bodyCount || other <= gid) { continue; }

          let otherWord = bodyConstraintCounts[other];
          let otherConstraintCount = otherWord & 0xFFFFu;
          if (otherConstraintCount == 0u) { continue; }

          // Fallback bodies are solved in a separate Jacobi bucket after all
          // strict GS colors. Their graph edges are therefore not same-color
          // races and must not be counted as colored-edge conflicts.
          if (
            (word & ${BODY_COLOR_FALLBACK_FLAG}u) != 0u
            || (otherWord & ${BODY_COLOR_FALLBACK_FLAG}u) != 0u
          ) { continue; }

          atomicAdd(&debugCounters[16], 1u);
          let otherColor = (otherWord >> 16u) & 0xFFu;
          if (otherColor == bodyColor) {
            atomicAdd(&debugCounters[17], 1u);
          }
        }
      }

      fn debugConstraintOtherBodyForContact(
        body: u32,
        contactIndex: u32,
        pairContacts: ptr<storage, array<vec4f>, read_write>,
      ) -> u32 {
        if (contactIndex >= ${maxPairContacts}u) { return ${maxBodies}u; }
        let contactMeta = loadContactMeta(pairContacts, contactIndex);
        let i = u32(contactMeta.x + 0.5);
        let j = u32(contactMeta.y + 0.5);
        if (i == body) { return j; }
        if (j == body) { return i; }
        return ${maxBodies}u;
      }

      fn debugConstraintOtherBodyForJoint(
        body: u32,
        jointIndex: u32,
        jointRecords: ptr<storage, array<vec4f>, read_write>,
      ) -> u32 {
        if (jointIndex >= ${maxJoints}u) { return ${maxBodies}u; }
        let jointMeta = loadJointMetaWords(jointRecords, jointIndex);
        if (jointMeta.w == 0u) { return ${maxBodies}u; }
        if (jointMeta.x == body) { return jointMeta.y; }
        if (jointMeta.y == body) { return jointMeta.x; }
        return ${maxBodies}u;
      }

      fn debugConstraintOtherBodyForSpring(
        body: u32,
        springIndex: u32,
        springRecords: ptr<storage, array<vec4f>, read_write>,
      ) -> u32 {
        if (springIndex >= ${maxSprings}u) { return ${maxBodies}u; }
        let springMeta = loadSpringMetaWords(springRecords, springIndex);
        if (springMeta.z == 0u) { return ${maxBodies}u; }
        if (springMeta.x == body) { return springMeta.y; }
        if (springMeta.y == body) { return springMeta.x; }
        return ${maxBodies}u;
      }
    `, [contactRecordHelpers, jointRecordHelpers, springRecordHelpers, bodyConstraintRefHelpers]);

    this.accumulateBodyColorDebugCountersKernel = accumulateBodyColorDebugCountersShader({
      bodyConstraintCounts: storage(bodyConstraintCounts, 'uint', maxBodies).toReadOnly(),
      bodyConstraintRefs: storage(bodyConstraintRefs, 'uint', bodyConstraintRefs.count).toReadOnly(),
      pairContacts: storage(pairContacts, 'vec4f', maxPairContacts * CONTACT_RECORD_VEC4S),
      jointRecords: storage(jointRecords, 'vec4f', maxJoints * JOINT_RECORD_VEC4S),
      springRecords: storage(springRecords, 'vec4f', maxSprings * SPRING_RECORD_VEC4S),
      debugCounters: storage(this.debugCountersAttr, 'uint', 18).toAtomic(),
      constrainedBodies: storage(
        this.constrainedBodyCompactAttr,
        'uint',
        maxBodies + 1,
      ).toReadOnly(),
      bodyCount: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Accumulate Body Color Debug Counters');

    const tangentInvLengthPrepareParam = this.cacheContactTangentInvLength
      ? '        contactTangentInvLength: ptr<storage, array<f32>, read_write>,\n'
      : '';
    const solverGeometryPrepareParam = this.packedContactSolverGeometry
      ? '        contactSolverGeometry: ptr<storage, array<vec4f>, read_write>,\n'
      : '';
    const tangentInvLengthPrepareReset = this.cacheContactTangentInvLength
      ? '        contactTangentInvLength[p] = 0.0;\n'
      : '';
    const tangentInvLengthPrepareWrite = this.cacheContactTangentInvLength
      ? /* wgsl */`
        let cachedTangentT1 = loadContactTangentT1(pairContacts, p);
        let cachedTangentT1Len2 = dot(cachedTangentT1, cachedTangentT1);
        if (!(cachedTangentT1Len2 <= 1e-12)) {
          let cachedT2Raw = cross(loadContactNormalPen(pairContacts, p).xyz, cachedTangentT1);
          let cachedT2Len2 = dot(cachedT2Raw, cachedT2Raw);
          if (!(cachedT2Len2 <= 1e-12)) {
            contactTangentInvLength[p] = inverseSqrt(cachedT2Len2);
          }
        }
`
      : '';
    const solverGeometryPrepareWrite = this.packedContactSolverGeometry
      ? /* wgsl */`
        // Compact the immutable per-round solver geometry into five contiguous
        // vec4s. Body ids remain exact numeric f32 values (the path is enabled
        // only for maxBodies <= 65536), avoiding a NaN-valued bitcast payload.
        // Invalid rows receive maxBodies as an endpoint sentinel, so the hot
        // body solve and capture paths retain the authoritative active-row
        // guard without spending another component. Every geometric f32 is
        // copied bit-for-bit after the world-arm transition above.
        let solverMeta = loadContactMeta(pairContacts, p);
        let solverNormal = loadContactNormalPen(pairContacts, p).xyz;
        let solverArmA = loadContactArmA(pairContacts, p);
        let solverArmB = loadContactArmB(pairContacts, p).xyz;
        let solverC0 = loadContactConstraintC0(pairContacts, p).xyz;
        let solverTangentT1 = loadContactTangentT1(pairContacts, p);
        let solverEndpointsValid = solverMeta.z >= 0.5
          && bodyA < ${maxBodies}u
          && bodyB < ${maxBodies}u
          && bodyA != bodyB;
        let solverBodyA = select(${maxBodies}u, bodyA, solverEndpointsValid);
        let solverBodyB = select(${maxBodies}u, bodyB, solverEndpointsValid);
        let solverGeometryBase = p * ${CONTACT_SOLVER_GEOMETRY_VEC4S}u;
        contactSolverGeometry[solverGeometryBase] = vec4f(
          f32(solverBodyA), f32(solverBodyB), solverMeta.w, solverNormal.x,
        );
        contactSolverGeometry[solverGeometryBase + 1u] = vec4f(
          solverNormal.yz, solverArmA.xy,
        );
        contactSolverGeometry[solverGeometryBase + 2u] = vec4f(
          solverArmA.zw, solverArmB.xy,
        );
        contactSolverGeometry[solverGeometryBase + 3u] = vec4f(
          solverArmB.z, solverC0,
        );
        contactSolverGeometry[solverGeometryBase + 4u] = vec4f(
          solverTangentT1,
          ${this.cacheContactTangentInvLength ? 'contactTangentInvLength[p]' : '0.0'},
        );
`
      : '';
    const prepareStateShader = wgslFn(/* wgsl */`
      fn compute(
        initialPose: ptr<storage, array<vec4f>, read>,
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        contactSolverHot: ptr<storage, array<vec4f>, read_write>,
${solverGeometryPrepareParam}
        pairActivity: ptr<storage, array<u32>, read_write>,
${tangentInvLengthPrepareParam}
        pairDispatchCount: u32,
        lambdaWarmstartScale: f32,
        cacheWorldArms: u32,
        softenWarmstartOnSlotChange: u32,
        useReferenceTangentialUpdate: u32,
        preserveTangentialPenaltyOnStick: u32,
        useIsotropicTangentialPenaltyOnStick: u32,
        tangentialPenaltyCapScale: f32,
        frictionStatic: f32,
        gamma: f32,
        kStart: f32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = (
          workgroupId.y * ${CONTACT_DISPATCH_WORKGROUP_WIDTH}u + workgroupId.x
        ) * ${WORKGROUP_SIZE}u + localId.x;
        let solverHeader = pairActivity[${pairActiveContactsOffset}u];
        let activeCount = min(min(
          solverHeader & ${SOLVER_CONTACT_COUNT_MASK}u,
          pairDispatchCount,
        ), ${maxActivePairContacts}u);
        if (gid >= activeCount) { return; }

        let activeEntriesOffset = select(
          ${pairActiveContactsOffset + 1}u,
          ${pairCanonicalActiveContactsOffset + 1}u,
          (solverHeader & ${SOLVER_CONTACT_CANONICAL_ALIAS_BIT}u) != 0u,
        );
        let p = pairActivity[activeEntriesOffset + gid];
        if (p >= ${maxPairContacts}u) { return; }
${tangentInvLengthPrepareReset}
        let prevContactRecord = loadContactRecord(pairContacts, p, kStart);
        let contactShadow = prevContactRecord.shadow.packed;
        let prevContactState = prevContactRecord.state;
        let frictionScale = prevContactState.frictionScale;

        let info = loadContactMeta(pairContacts, p);
        let bodyA = u32(info.x + 0.5);
        let bodyB = u32(info.y + 0.5);
        if (info.z < 0.5) {
${solverGeometryPrepareWrite}
          storeContactCacheWord(pairContacts, p, 0u);
          storeContactDual(pairContacts, p, vec4f(0.0));
          storeContactPenalty(pairContacts, p, packContactPenalty(kStart, vec2f(kStart), frictionScale, kStart));
          storeSolverContactState(
            contactSolverHot,
            p,
            loadContactDual(pairContacts, p),
            loadContactPenalty(pairContacts, p),
          );
          return;
        }
${tangentInvLengthPrepareWrite}

        var i = bodyA;
        var j = bodyB;
        if (i > j) {
          let t = i;
          i = j;
          j = t;
        }
        let encodedInfo = bitcast<u32>(info.w);
        let feature = encodedInfo & 0x1FFu;
        let preserveWarmstart = ((encodedInfo >> 16u) & 0x1u) != 0u;
        let stick = ((encodedInfo >> 17u) & 0x1u) != 0u;
        let warmstartReason = (encodedInfo >> 21u) & 0x7u;
        let exactFeatureWarmstart = warmstartReason == 1u;
        // Logical manifold identity belongs to the row metadata, not to its
        // current physical pool address. Fixed-slot storage encodes p%slots;
        // the compact active-contact pool may relocate the same logical row.
        let localSlot = contactInfoLogicalSlot(encodedInfo);
        let packedBodies = (i & 0xFFFFu) | ((j & 0xFFFFu) << 16u);
        // Build a structured key:
        // high identity bits = body-pair/contact identity, low slot bits = slot id.
        // This allows continuity across manifold slot remaps without letting
        // unrelated clipped contact points inherit the same dual state.
        // Preserve the exact geometric feature bits inside the continuity key.
        // The previous hash-only base could alias adjacent face ordinals
        // (for example 0xa9 and 0xaa), which made slot swaps look like valid
        // continuity holds. Match avbd-demo3d more closely by keeping the
        // feature exact and hashing only the body-pair portion.
        let pairHash = 1u + (((packedBodies * 2246822519u) ^ (packedBodies >> 16u) ^ (packedBodies << 7u)) % ${contactKeyPairHashMax}u);
        let keyBase = (pairHash << ${contactKeyFeatureBitCount}u) | feature;
        let key = (keyBase << ${contactKeySlotBitCount}u) | localSlot;

        let prevKeyPacked = loadContactCacheWord(pairContacts, p);
        let prevKey = prevKeyPacked & 0x7fffffffu;
        let prevBase = prevKey >> ${contactKeySlotBitCount}u;
        let continuityHeld = preserveWarmstart && prevKey != 0u && prevBase == keyBase;
        let slotChanged = prevKey != key;
        var prevDual = prevContactState.dual;
        var prevPenalty = prevContactState.penalty;
        let prevDualN = min(prevDual.x, 0.0);
        let prevLambdaN = prevContactState.lambdaN;
        let prevPenaltyN = prevContactState.penaltyN;
        // pairLambdas is a derived positive-magnitude shadow used for
        // heuristics/debug and one-step contact-generation seed transport.
        // Once prepare runs, avbdDualState becomes the authoritative record.
        let seedShadow = prevContactRecord.shadow;
        let matchedCarryN = seedShadow.lambdaN;
        if (!continuityHeld) {
          // Reference parity: unmatched rows start from zero lambda and
          // minimum penalty. A one-step speculative CCD row is the exception:
          // it has no earlier persistent key by construction, yet it must keep
          // the conservative normal stiffness chosen by generation so a heavy
          // fast body cannot spend one rendered frame inside the target.
          // Tangential penalties and all ordinary DCD rows still start at the
          // canonical floor, and no shadow multiplier is promoted.
          // Generation writes kStart for every ordinary cold row. A value
          // above that floor is therefore an explicit CCD transition seed,
          // either on the swept row itself or on its first DCD successor.
          let seededNormalPenalty = max(prevPenalty.x, kStart);
          storeContactDual(pairContacts, p, vec4f(0.0));
          storeContactPenalty(
            pairContacts,
            p,
            packContactPenalty(
              seededNormalPenalty,
              vec2f(kStart),
              seedShadow.frictionScale,
              kStart,
            ),
          );
        } else {
          let slotWarmstartScale = 1.0;
          var warmDual = prevDual * lambdaWarmstartScale;
          var warmDualN = min(warmDual.x, 0.0);
          var warmStoredDual = vec4f(0.0);
          if (useReferenceTangentialUpdate > 0u) {
            // Reference parity: warmstart is a plain Eq.19-style decay.
            // Do not reclamp tangential dual against the friction cone during
            // prepare; the references leave cone enforcement to the dual
            // update/solve path.
            warmStoredDual = vec4f(warmDualN, warmDual.yz, 0.0);
          } else {
            var warmDualT = warmDual.yzw;
            let nRaw = loadContactNormalPen(pairContacts, p).xyz;
            let nLen2 = dot(nRaw, nRaw);
            if (nLen2 > 1e-10) {
              let nUnit = nRaw * inverseSqrt(nLen2);
              warmDualT -= nUnit * dot(warmDualT, nUnit);
            } else {
              warmDualT = vec3f(0.0);
            }
            warmStoredDual = vec4f(warmDualN, warmDualT);
          }
          var warmPenaltyN = max(prevPenalty.x * gamma, kStart);
          var warmPenaltyT1 = max(prevPenalty.y * gamma, kStart);
          var warmPenaltyT2 = max(prevPenalty.z * gamma, kStart);
          let holdTangentialPenalty = preserveTangentialPenaltyOnStick > 0u
            && useReferenceTangentialUpdate == 0u
            && stick
            && prevKey == key;
          if (holdTangentialPenalty) {
            warmPenaltyT1 = max(prevPenalty.y, kStart);
            warmPenaltyT2 = max(prevPenalty.z, kStart);
          }
          if (tangentialPenaltyCapScale > 0.0 && useReferenceTangentialUpdate == 0u) {
            let tangentialPenaltyCap = max(warmPenaltyN * tangentialPenaltyCapScale, kStart);
            warmPenaltyT1 = min(warmPenaltyT1, tangentialPenaltyCap);
            warmPenaltyT2 = min(warmPenaltyT2, tangentialPenaltyCap);
          }
          if (
            useIsotropicTangentialPenaltyOnStick > 0u
            && useReferenceTangentialUpdate == 0u
            && stick
            && prevKey == key
          ) {
            let sharedPenaltyT = max(0.5 * (warmPenaltyT1 + warmPenaltyT2), kStart);
            warmPenaltyT1 = sharedPenaltyT;
            warmPenaltyT2 = sharedPenaltyT;
          }
          storeContactDual(pairContacts, p, warmStoredDual);
          storeContactPenalty(pairContacts, p, packContactPenalty(
            warmPenaltyN,
            vec2f(warmPenaltyT1, warmPenaltyT2),
            frictionScale,
            kStart,
          ));
        }

        storeContactCacheWord(pairContacts, p, key | select(0u, 0x80000000u, continuityHeld));

        if (
          cacheWorldArms > 0u
          && bodyA < ${maxBodies}u
          && bodyB < ${maxBodies}u
          && bodyA != bodyB
        ) {
          let armA = loadContactArmA(pairContacts, p);
          let armB = loadContactArmB(pairContacts, p);
          let sphereArmFlags = u32(armB.w + 0.5) & 0x3u;
          // Match the legacy hot path exactly: initialPose already stores the
          // normalized x^t orientation, so do not introduce an extra
          // normalization (and its f32 rounding) while moving this work to
          // prepare.
          let qA = initialPose[bodyA * 2u + 1u];
          let qB = initialPose[bodyB * 2u + 1u];
          let worldArmA = select(
            qrot(qA, armA.xyz),
            armA.xyz,
            (sphereArmFlags & 1u) != 0u,
          );
          let worldArmB = select(
            qrot(qB, armB.xyz),
            armB.xyz,
            (sphereArmFlags & 2u) != 0u,
          );
          let constraintC0 = loadContactConstraintC0(pairContacts, p);

          // prepare has consumed the previous persistent shadow. Reuse it and
          // two otherwise-dead components to preserve the six arm components
          // bit-for-bit while ARM_A/B.xyz serve as the x^t world-arm cache.
          storeContactShadow(pairContacts, p, vec4f(armA.xyz, armB.x));
          storeContactConstraintC0(pairContacts, p, vec4f(constraintC0.xyz, armB.y));
          storeContactArmA(pairContacts, p, vec4f(worldArmA, armA.w));
          storeContactArmB(pairContacts, p, vec4f(worldArmB, armB.z));
          storeContactMeta(pairContacts, p, vec4f(
            info.xyz,
            bitcast<f32>(
              (encodedInfo & ~(CONTACT_SOLVER_SCRATCH_MASK | CONTACT_INFO_RESERVED_BIT_30))
              | (sphereArmFlags << CONTACT_SOLVER_SCRATCH_ARM_FLAGS_SHIFT)
              | CONTACT_SOLVER_SCRATCH_ACTIVE_BIT
            ),
          ));
        }
        // Seed the dense sidecar after warmstart and optional world-arm
        // transition are complete. This is a bit-for-bit f32 copy.
        storeSolverContactState(
          contactSolverHot,
          p,
          loadContactDual(pairContacts, p),
          loadContactPenalty(pairContacts, p),
        );
${solverGeometryPrepareWrite}
      }
    `, [
      qrot,
      tangentBasisHelpers,
      contactRecordHelpers,
      contactStateHelpers,
      contactSolverHotHelpers,
    ]);

    const prepareStateBindings: Record<string, any> = {
      initialPose: storage(initialPose, 'vec4f', maxBodies * 2).toReadOnly(),
      pairContacts: storage(pairContacts, 'vec4f', maxPairContacts * CONTACT_RECORD_VEC4S),
      contactSolverHot: storage(
        this.contactSolverHotAttr,
        'vec4f',
        maxPairContacts * CONTACT_SOLVER_HOT_VEC4S,
      ),
      pairActivity: storage(pairActivity, 'uint', pairActivityWordCount),
      pairDispatchCount: uniform(0),
      lambdaWarmstartScale: uniform(AVBD_REGULARIZATION_ALPHA_DEFAULT * AVBD_GAMMA),
      cacheWorldArms: uniform(1),
      softenWarmstartOnSlotChange: uniform(0),
      useReferenceTangentialUpdate: uniform(1),
      preserveTangentialPenaltyOnStick: uniform(0),
      useIsotropicTangentialPenaltyOnStick: uniform(0),
      tangentialPenaltyCapScale: uniform(0.0),
      frictionStatic: uniform(AVBD_FRICTION_STATIC),
      gamma: uniform(AVBD_GAMMA),
      kStart: uniform(AVBD_K_START),
      workgroupId,
      localId,
    };
    if (this.packedContactSolverGeometry) {
      prepareStateBindings.contactSolverGeometry = storage(
        this.contactSolverGeometryAttr,
        'vec4f',
        maxPairContacts * CONTACT_SOLVER_GEOMETRY_VEC4S,
      );
    }
    if (this.cacheContactTangentInvLength) {
      prepareStateBindings.contactTangentInvLength = storage(
        this.contactTangentInvLengthAttr,
        'float',
        maxPairContacts,
      );
    }
    this.prepareStateKernel = prepareStateShader(prepareStateBindings)
      .computeKernel([WORKGROUP_SIZE, 1, 1])
      .setName('AVBD Prepare Contact State');
    assertStorageBufferBudget('AVBD Prepare Contact State', 10);

    const prepareJointStateShader = wgslFn(/* wgsl */`
      fn compute(
        positions: ptr<storage, array<vec4f>, read>,
        quaternions: ptr<storage, array<vec4f>, read>,
        velocities: ptr<storage, array<vec4f>, read>,
        angularVelocities: ptr<storage, array<vec4f>, read>,
        initialPose: ptr<storage, array<vec4f>, read>,
        jointRecords: ptr<storage, array<vec4f>, read_write>,
        jointCount: u32,
        lambdaWarmstartScale: f32,
        gamma: f32,
        kStart: f32,
        dt: f32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid >= jointCount) { return; }

        let jointMetaWords = loadJointMetaWords(jointRecords, gid);
        if (jointMetaWords.w == 0u) { return; }

        let bodyA = jointMetaWords.x;
        let bodyB = jointMetaWords.y;
        let jointType = jointMetaWords.z;
        let anchorA = loadJointAnchorA(jointRecords, gid);
        let anchorB = loadJointAnchorB(jointRecords, gid);
        let restRelative = loadJointRestRelativeRotation(jointRecords, gid);
        let torqueArm = max(anchorA.w, 1.0);

        var qA = vec4f(0.0, 0.0, 0.0, 1.0);
        var posA = anchorA.xyz;
        if (bodyA != WORLD_BODY_INDEX) {
          qA = normalize(initialPose[bodyA * 2u + 1u]);
          posA = initialPose[bodyA * 2u].xyz;
        }
        let qB = normalize(initialPose[bodyB * 2u + 1u]);
        let posB = initialPose[bodyB * 2u].xyz;
        let worldAnchorA = jointWorldAnchor(bodyA, anchorA.xyz, posA, qA);
        let worldAnchorB = posB + qrot(qB, anchorB.xyz);
        let regularizationPoseA = jointRegularizationPose(
          bodyA,
          initialPose,
          positions,
          quaternions,
          velocities,
          angularVelocities,
          dt,
        );
        let regularizationPoseB = jointRegularizationPose(
          bodyB,
          initialPose,
          positions,
          quaternions,
          velocities,
          angularVelocities,
          dt,
        );
        let regularizationAnchorA = jointWorldAnchor(
          bodyA,
          anchorA.xyz,
          regularizationPoseA.position,
          regularizationPoseA.rotation,
        );
        let regularizationAnchorB = jointWorldAnchor(
          bodyB,
          anchorB.xyz,
          regularizationPoseB.position,
          regularizationPoseB.rotation,
        );
        let c0Lin = regularizationAnchorA - regularizationAnchorB;
        storeJointC0Lin(jointRecords, gid, vec4f(c0Lin, 0.0));

        let c0Ang = select(
          vec3f(0.0),
          jointFixedAngularConstraint(
            bodyA,
            regularizationPoseA.rotation,
            regularizationPoseB.rotation,
            restRelative,
            torqueArm,
          ),
          jointType == JOINT_TYPE_FIXED,
        );
        storeJointC0Ang(jointRecords, gid, vec4f(c0Ang, 0.0));

        let prevLambdaLin = loadJointLambdaLin(jointRecords, gid).xyz;
        let prevLambdaAng = loadJointLambdaAng(jointRecords, gid).xyz;
        let prevPenaltyLin = max(loadJointPenaltyLin(jointRecords, gid).xyz, vec3f(kStart));
        let stiffness = loadJointStiffness(jointRecords, gid);
        let rigidLinear = !isFiniteF32(stiffness.x);
        let angularEnabled = jointType == JOINT_TYPE_FIXED && stiffness.y > 0.0;
        let rigidAngular = angularEnabled && !isFiniteF32(stiffness.y);
        let minPenaltyAng = select(vec3f(0.0), vec3f(kStart), angularEnabled);
        let prevPenaltyAng = max(loadJointPenaltyAng(jointRecords, gid).xyz, minPenaltyAng);
        let maxPenaltyLin = vec3f(min(max(stiffness.x, kStart), ${AVBD_JOINT_PENALTY_MAX}));
        let maxPenaltyAng = vec3f(min(max(stiffness.y, 0.0), ${AVBD_JOINT_PENALTY_MAX}));

        storeJointLambdaLin(
          jointRecords,
          gid,
          vec4f(prevLambdaLin * lambdaWarmstartScale, 0.0),
        );
        storeJointLambdaAng(
          jointRecords,
          gid,
          vec4f(select(vec3f(0.0), prevLambdaAng * lambdaWarmstartScale, angularEnabled), 0.0),
        );
        storeJointPenaltyLin(
          jointRecords,
          gid,
          vec4f(min(max(prevPenaltyLin * gamma, vec3f(kStart)), maxPenaltyLin), 0.0),
        );
        storeJointPenaltyAng(
          jointRecords,
          gid,
          vec4f(min(max(prevPenaltyAng * gamma, minPenaltyAng), maxPenaltyAng), 0.0),
        );
      }
    `, [qrot, qmul, qconj, jointRecordHelpers, jointConstraintHelpers]);

    this.prepareJointStateKernel = prepareJointStateShader({
      positions: storage(positions, 'vec4f', maxBodies).toReadOnly(),
      quaternions: storage(quaternions, 'vec4f', maxBodies).toReadOnly(),
      velocities: storage(velocities, 'vec4f', maxBodies).toReadOnly(),
      angularVelocities: storage(angularVelocities, 'vec4f', maxBodies).toReadOnly(),
      initialPose: storage(initialPose, 'vec4f', maxBodies * 2).toReadOnly(),
      jointRecords: storage(jointRecords, 'vec4f', maxJoints * JOINT_RECORD_VEC4S),
      jointCount: uniform(0),
      lambdaWarmstartScale: uniform(AVBD_REGULARIZATION_ALPHA_DEFAULT * AVBD_GAMMA),
      gamma: uniform(AVBD_GAMMA),
      kStart: uniform(AVBD_K_START),
      dt: uniform(1 / 60 / 4),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Prepare Joint State');
    assertStorageBufferBudget('AVBD Prepare Joint State', 6);

    const clearBodyConstraintOverflowShader = wgslFn(/* wgsl */`
      fn compute(
        bodyConstraintRefs: ptr<storage, array<atomic<u32>>, read_write>,
      ) -> void {
        atomicStore(&bodyConstraintRefs[${bodyConstraintOverflowCounterOffset}u], 0u);
      }
    `);
    this.clearBodyConstraintOverflowKernel = clearBodyConstraintOverflowShader({
      bodyConstraintRefs: storage(bodyConstraintRefs, 'uint', bodyConstraintRefs.count).toAtomic(),
    }).computeKernel([1, 1, 1]).setName('AVBD Clear Body Constraint Spill Counter');

    const clearConstrainedBodyCompactShader = wgslFn(/* wgsl */`
      fn compute(
        constrainedBodies: ptr<storage, array<atomic<u32>>, read_write>,
        dispatchIndirect: ptr<storage, array<u32>, read_write>,
      ) -> void {
        atomicStore(&constrainedBodies[0], 0u);
        // Reset x eagerly as well as in the post-discovery builder. This makes
        // an empty graph a true zero-dispatch path even if preparation exits
        // early in a diagnostic mode.
        dispatchIndirect[0] = 0u;
        dispatchIndirect[1] = 1u;
        dispatchIndirect[2] = 1u;
      }
    `);
    this.clearConstrainedBodyCompactKernel = clearConstrainedBodyCompactShader({
      constrainedBodies: storage(
        this.constrainedBodyCompactAttr,
        'uint',
        maxBodies + 1,
      ).toAtomic(),
      dispatchIndirect: storage(this.constrainedBodyDispatchIndirectAttr, 'uint', 3),
    }).computeKernel([1, 1, 1]).setName('AVBD Clear Compact Constrained Bodies');
    assertStorageBufferBudget('AVBD Clear Compact Constrained Bodies', 2);

    const buildConstrainedBodyDispatchArgsShader = wgslFn(/* wgsl */`
      fn compute(
        constrainedBodies: ptr<storage, array<u32>, read>,
        dispatchIndirect: ptr<storage, array<u32>, read_write>,
      ) -> void {
        let constrainedCount = min(constrainedBodies[0], ${maxBodies}u);
        dispatchIndirect[0] =
          (constrainedCount + ${WORKGROUP_SIZE}u - 1u) / ${WORKGROUP_SIZE}u;
        dispatchIndirect[1] = 1u;
        dispatchIndirect[2] = 1u;
      }
    `);
    this.buildConstrainedBodyDispatchArgsKernel = buildConstrainedBodyDispatchArgsShader({
      constrainedBodies: storage(
        this.constrainedBodyCompactAttr,
        'uint',
        maxBodies + 1,
      ).toReadOnly(),
      dispatchIndirect: storage(this.constrainedBodyDispatchIndirectAttr, 'uint', 3),
    }).computeKernel([1, 1, 1]).setName('AVBD Build Constrained Body Dispatch Args');
    assertStorageBufferBudget('AVBD Build Constrained Body Dispatch Args', 2);

    const buildSolverConstraintListsShader = wgslFn(/* wgsl */`
      fn compute(
        pairBodyContactCounts: ptr<storage, array<u32>, read>,
        pairBodyContactIndices: ptr<storage, array<u32>, read>,
        bodyConstraintCounts: ptr<storage, array<u32>, read_write>,
        bodyConstraintRefs: ptr<storage, array<atomic<u32>>, read_write>,
        bodyColorScratch: ptr<storage, array<u32>, read_write>,
        constrainedBodies: ptr<storage, array<atomic<u32>>, read_write>,
        bodyCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid >= bodyCount) { return; }

        let previousWord = bodyConstraintCounts[gid];
        let sleepFlag = previousWord & ${BODY_SLEEP_FLAG}u;
        // Color is persistent cross-timestep state in the paper's one-sweep
        // incremental coloring. Sleeping may mask the degree, but it must not
        // erase this seed or a later wake starts from an all-zero graph and
        // spills most of a deep stack into the Jacobi fallback lane.
        let previousColorWord = previousWord & 0x00FF0000u;
        bodyColorScratch[gid] = previousColorWord;
        atomicStore(
          &bodyConstraintRefs[${bodyConstraintOverflowHeadOffset}u + gid],
          0xffffffffu,
        );

        if (sleepFlag != 0u) {
          bodyConstraintCounts[gid] = sleepFlag | previousColorWord;
          return;
        }

        let requestedContactCount = min(pairBodyContactCounts[gid], 0xffffu);
        let directCount = min(requestedContactCount, ${maxPairContactsPerBody}u);
        let srcBase = gid * ${maxPairContactsPerBody}u;
        var solverCount = 0u;
        for (var k = 0u; k < directCount; k++) {
          let contactRef = pairBodyContactIndices[srcBase + k];
          if (storeBodyConstraintRef(
            bodyConstraintRefs, gid, solverCount, contactRef,
          )) {
            solverCount += 1u;
          }
        }

        var node = pairBodyContactIndices[${pairBodyOverflowHeadOffset}u + gid];
        var visited = 0u;
        loop {
          if (
            solverCount >= requestedContactCount
            || node == 0xffffffffu
            || node >= ${pairBodyOverflowCapacity}u
            || visited >= ${pairBodyOverflowCapacity}u
          ) { break; }
          let nodeBase = ${pairBodyOverflowNodeOffset}u + node * 2u;
          let contactRef = pairBodyContactIndices[nodeBase];
          if (storeBodyConstraintRef(
            bodyConstraintRefs, gid, solverCount, contactRef,
          )) {
            solverCount += 1u;
          }
          node = pairBodyContactIndices[nodeBase + 1u];
          visited += 1u;
        }
        bodyConstraintCounts[gid] = solverCount | sleepFlag | previousColorWord;
        if (solverCount != 0u) {
          appendConstrainedBody(constrainedBodies, gid);
        }
      }

      ${bodyConstraintAppendHelperSource}
    `);

    this.buildSolverConstraintListsKernel = buildSolverConstraintListsShader({
      pairBodyContactCounts: storage(pairBodyContactCounts, 'uint', maxBodies).toReadOnly(),
      pairBodyContactIndices: storage(pairBodyContactIndices, 'uint', pairBodyContactIndices.count).toReadOnly(),
      bodyConstraintCounts: storage(bodyConstraintCounts, 'uint', maxBodies),
      bodyConstraintRefs: storage(bodyConstraintRefs, 'uint', bodyConstraintRefs.count).toAtomic(),
      bodyColorScratch: storage(bodyColorScratch, 'uint', maxBodies),
      constrainedBodies: storage(
        this.constrainedBodyCompactAttr,
        'uint',
        maxBodies + 1,
      ).toAtomic(),
      bodyCount: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Build Solver Constraint Lists');
    assertStorageBufferBudget('AVBD Build Solver Constraint Lists', 6);

    // In a contact-only world, pairBodyContactIndices already has exactly the
    // layout consumed by loadBodyConstraintRef. Reusing it avoids both the
    // full second adjacency allocation and a per-step copy. This kernel only
    // prepares degree/color/sleep state; it must never clear or rewrite the
    // aliased contact list in place.
    const prepareContactOnlyAdjacencyShader = wgslFn(/* wgsl */`
      fn compute(
        pairBodyContactCounts: ptr<storage, array<u32>, read>,
        bodyConstraintCounts: ptr<storage, array<u32>, read_write>,
        bodyColorScratch: ptr<storage, array<u32>, read_write>,
        constrainedBodies: ptr<storage, array<atomic<u32>>, read_write>,
        bodyCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid >= bodyCount) { return; }

        let previousWord = bodyConstraintCounts[gid];
        let sleepFlag = previousWord & ${BODY_SLEEP_FLAG}u;
        let previousColorWord = previousWord & 0x00FF0000u;
        bodyColorScratch[gid] = previousColorWord;
        if (sleepFlag != 0u) {
          bodyConstraintCounts[gid] = sleepFlag | previousColorWord;
          return;
        }

        let requestedContactCount = min(pairBodyContactCounts[gid], 0xffffu);
        bodyConstraintCounts[gid] = requestedContactCount | previousColorWord;
        if (requestedContactCount != 0u) {
          let slot = atomicAdd(&constrainedBodies[0], 1u);
          if (slot < ${maxBodies}u) {
            atomicStore(&constrainedBodies[slot + 1u], gid);
          }
        }
      }
    `);
    this.prepareContactOnlyAdjacencyKernel = prepareContactOnlyAdjacencyShader({
      pairBodyContactCounts: storage(pairBodyContactCounts, 'uint', maxBodies).toReadOnly(),
      bodyConstraintCounts: storage(bodyConstraintCounts, 'uint', maxBodies),
      bodyColorScratch: storage(bodyColorScratch, 'uint', maxBodies),
      constrainedBodies: storage(
        this.constrainedBodyCompactAttr,
        'uint',
        maxBodies + 1,
      ).toAtomic(),
      bodyCount: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Prepare Aliased Contact Adjacency');
    assertStorageBufferBudget('AVBD Prepare Aliased Contact Adjacency', 4);

    const appendJointConstraintRefsShader = wgslFn(/* wgsl */`
      fn compute(
        positions: ptr<storage, array<vec4f>, read>,
        jointRecords: ptr<storage, array<vec4f>, read_write>,
        bodyConstraintCounts: ptr<storage, array<atomic<u32>>, read_write>,
        bodyConstraintRefs: ptr<storage, array<atomic<u32>>, read_write>,
        constrainedBodies: ptr<storage, array<atomic<u32>>, read_write>,
        jointCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid >= jointCount) { return; }

        let jointMetaWords = loadJointMetaWords(jointRecords, gid);
        if (jointMetaWords.w == 0u) { return; }

        let taggedRef = ${CONSTRAINT_REF_TAG_JOINT}u | gid;
        let bodyA = jointMetaWords.x;
        let bodyB = jointMetaWords.y;

        if (bodyA != 0xffffffffu && positions[bodyA].w > 0.0) {
          appendBodyConstraintRef(
            bodyConstraintCounts, bodyConstraintRefs, constrainedBodies, bodyA, taggedRef,
          );
        }
        if (positions[bodyB].w > 0.0) {
          appendBodyConstraintRef(
            bodyConstraintCounts, bodyConstraintRefs, constrainedBodies, bodyB, taggedRef,
          );
        }
      }

      ${bodyConstraintAppendHelperSource}
    `, [qrot, qmul, qconj, jointRecordHelpers, jointConstraintHelpers]);

    this.appendJointConstraintRefsKernel = appendJointConstraintRefsShader({
      positions: storage(positions, 'vec4f', maxBodies).toReadOnly(),
      jointRecords: storage(jointRecords, 'vec4f', maxJoints * JOINT_RECORD_VEC4S),
      bodyConstraintCounts: storage(bodyConstraintCounts, 'uint', maxBodies).toAtomic(),
      bodyConstraintRefs: storage(bodyConstraintRefs, 'uint', bodyConstraintRefs.count).toAtomic(),
      constrainedBodies: storage(
        this.constrainedBodyCompactAttr,
        'uint',
        maxBodies + 1,
      ).toAtomic(),
      jointCount: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Append Joint Constraint Refs');
    assertStorageBufferBudget('AVBD Append Joint Constraint Refs', 5);

    const appendSpringConstraintRefsShader = wgslFn(/* wgsl */`
      fn compute(
        positions: ptr<storage, array<vec4f>, read>,
        quaternions: ptr<storage, array<vec4f>, read>,
        springRecords: ptr<storage, array<vec4f>, read_write>,
        bodyConstraintCounts: ptr<storage, array<atomic<u32>>, read_write>,
        bodyConstraintRefs: ptr<storage, array<atomic<u32>>, read_write>,
        constrainedBodies: ptr<storage, array<atomic<u32>>, read_write>,
        springCount: u32,
        gamma: f32,
        kStart: f32,
        lambdaWarmstartScale: f32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid >= springCount) { return; }

        let springMetaWords = loadSpringMetaWords(springRecords, gid);
        if (springMetaWords.z == 0u) { return; }

        let materialStiffness = max(loadSpringAnchorBStiffness(springRecords, gid).w, 0.0);
        let previousState = loadSpringState(springRecords, gid);
        let rigidDistance = springMetaWords.w != 0u;
        var warmPenalty = 0.0;
        if (rigidDistance) {
          warmPenalty = max(kStart, max(previousState.x, 0.0) * gamma);
        } else if (materialStiffness > 0.0) {
          warmPenalty = min(materialStiffness, max(kStart, max(previousState.x, 0.0) * gamma));
        }

        let bodyA = springMetaWords.x;
        let bodyB = springMetaWords.y;
        let anchorARest = loadSpringAnchorARest(springRecords, gid);
        let anchorBStiffness = loadSpringAnchorBStiffness(springRecords, gid);
        var worldAnchorA = anchorARest.xyz;
        if (bodyA != 0xffffffffu) {
          worldAnchorA = positions[bodyA].xyz + qrot(normalize(quaternions[bodyA]), anchorARest.xyz);
        }
        let worldAnchorB = positions[bodyB].xyz + qrot(normalize(quaternions[bodyB]), anchorBStiffness.xyz);
        let currentLength = length(worldAnchorA - worldAnchorB);
        let c0 = currentLength - max(anchorARest.w, 0.0);
        let warmLambda = select(0.0, previousState.w * lambdaWarmstartScale, rigidDistance);

        // Finite springs use paper Eq. 16. Infinite-stiffness springs are hard
        // scalar distance constraints and carry the same Eq. 19 lambda/k warm
        // start as the other AVBD hard constraints. y stores C0 for Eq. 18 and
        // w stores lambda only for the hard-distance path.
        storeSpringState(
          springRecords,
          gid,
          vec4f(warmPenalty, select(previousState.y, c0, rigidDistance), currentLength, warmLambda),
        );

        let taggedRef = ${CONSTRAINT_REF_TAG_SPRING}u | gid;

        if (bodyA != 0xffffffffu && positions[bodyA].w > 0.0) {
          appendBodyConstraintRef(
            bodyConstraintCounts, bodyConstraintRefs, constrainedBodies, bodyA, taggedRef,
          );
        }
        if (positions[bodyB].w > 0.0) {
          appendBodyConstraintRef(
            bodyConstraintCounts, bodyConstraintRefs, constrainedBodies, bodyB, taggedRef,
          );
        }
      }

      ${bodyConstraintAppendHelperSource}
    `, [qrot, springRecordHelpers]);

    this.appendSpringConstraintRefsKernel = appendSpringConstraintRefsShader({
      positions: storage(positions, 'vec4f', maxBodies).toReadOnly(),
      quaternions: storage(quaternions, 'vec4f', maxBodies).toReadOnly(),
      springRecords: storage(springRecords, 'vec4f', maxSprings * SPRING_RECORD_VEC4S),
      bodyConstraintCounts: storage(bodyConstraintCounts, 'uint', maxBodies).toAtomic(),
      bodyConstraintRefs: storage(bodyConstraintRefs, 'uint', bodyConstraintRefs.count).toAtomic(),
      constrainedBodies: storage(
        this.constrainedBodyCompactAttr,
        'uint',
        maxBodies + 1,
      ).toAtomic(),
      springCount: uniform(0),
      gamma: uniform(AVBD_GAMMA),
      kStart: uniform(AVBD_K_START),
      lambdaWarmstartScale: uniform(1.0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Append Spring Constraint Refs');
    assertStorageBufferBudget('AVBD Append Spring Constraint Refs', 6);

    const greedyBodyColorsShader = wgslFn(/* wgsl */`
      fn compute(
        positions: ptr<storage, array<vec4f>, read>,
        bodyConstraintCounts: ptr<storage, array<u32>, read_write>,
        bodyConstraintRefs: ptr<storage, array<u32>, read>,
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        jointRecords: ptr<storage, array<vec4f>, read_write>,
        springRecords: ptr<storage, array<vec4f>, read_write>,
        bodyColorScratch: ptr<storage, array<u32>, read>,
        bodyColorOutput: ptr<storage, array<u32>, read_write>,
        constrainedBodies: ptr<storage, array<u32>, read>,
        bodyCount: u32,
        colorCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let compactIndex = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        let constrainedCount = min(constrainedBodies[0], ${maxBodies}u);
        if (compactIndex >= constrainedCount) { return; }
        let gid = constrainedBodies[compactIndex + 1u];
        if (gid >= bodyCount) { return; }

        let word = bodyConstraintCounts[gid];
        let sleepFlag = word & ${BODY_SLEEP_FLAG}u;
        let persistentColorWord = word & 0x00FF0000u;
        let contactCount = word & 0xFFFFu;
        if (contactCount == 0u) {
          bodyConstraintCounts[gid] = sleepFlag | persistentColorWord;
          bodyColorOutput[gid] = persistentColorWord;
          return;
        }

        let colors = max(1u, min(colorCount, 32u));
        var usedMask = 0u;
        var constraintOverflowNode = bodyConstraintRefs[
          ${bodyConstraintOverflowHeadOffset}u + gid
        ];
        for (var k = 0u; k < contactCount; k++) {
          let constraintRef = loadBodyConstraintRef(
            bodyConstraintRefs, gid, k, &constraintOverflowNode,
          );
          let constraintTag = constraintRef & ${CONSTRAINT_REF_TAG_MASK}u;
          let constraintIndex = constraintRef & ${CONSTRAINT_REF_INDEX_MASK}u;
          var other = constraintOtherBodyForContact(gid, constraintRef, pairContacts);
          if (constraintTag == ${CONSTRAINT_REF_TAG_JOINT}u) {
            other = constraintOtherBodyForJoint(gid, constraintIndex, jointRecords);
          } else if (constraintTag == ${CONSTRAINT_REF_TAG_SPRING}u) {
            other = constraintOtherBodyForSpring(gid, constraintIndex, springRecords);
          }
          if (other >= bodyCount || other == gid) { continue; }
          // Static endpoints never participate in the body-color solve.
          if (positions[other].w <= 0.0) { continue; }
          // Incremental greedy coloring from the AVBD paper: a mass avoids
          // colors used by connected masses with smaller indices.
          if (other >= gid) { continue; }

          let otherPrevColor = (bodyColorScratch[other] >> 16u) & 0xFFu;
          if (otherPrevColor < colors) {
            usedMask |= 1u << otherPrevColor;
          }
        }

        var chosenColor = (bodyColorScratch[gid] >> 16u) & 0xFFu;
        var fallback = false;
        var needsNewColor = chosenColor >= colors;
        if (!needsNewColor) {
          needsNewColor = (usedMask & (1u << chosenColor)) != 0u;
        }
        if (needsNewColor) {
          fallback = true;
          var found = false;
          for (var color = 0u; color < 32u; color++) {
            if (color >= colors) { break; }
            if ((usedMask & (1u << color)) == 0u) {
              chosenColor = color;
              found = true;
              fallback = false;
              break;
            }
          }
          if (!found) {
            chosenColor = gid % colors;
          }
        }

        let packedColor = chosenColor << 16u;
        bodyConstraintCounts[gid] = sleepFlag | packedColor | contactCount | select(0u, ${BODY_COLOR_FALLBACK_FLAG}u, fallback);
        // Write the converged color into a distinct ping-pong buffer. The next
        // greedy dispatch reads this snapshot directly, eliminating the old
        // full-body snapshot dispatch between every pair of coloring rounds.
        bodyColorOutput[gid] = packedColor;
      }
      
      fn constraintOtherBodyForContact(
        body: u32,
        contactIndex: u32,
        pairContacts: ptr<storage, array<vec4f>, read_write>,
      ) -> u32 {
        if (contactIndex >= ${maxPairContacts}u) { return ${maxBodies}u; }
        let contactMeta = loadContactMeta(pairContacts, contactIndex);
        let i = u32(contactMeta.x + 0.5);
        let j = u32(contactMeta.y + 0.5);
        if (i == body) { return j; }
        if (j == body) { return i; }
        return ${maxBodies}u;
      }

      fn constraintOtherBodyForJoint(
        body: u32,
        jointIndex: u32,
        jointRecords: ptr<storage, array<vec4f>, read_write>,
      ) -> u32 {
        if (jointIndex >= ${maxJoints}u) { return ${maxBodies}u; }
        let jointMetaWords = loadJointMetaWords(jointRecords, jointIndex);
        if (jointMetaWords.w == 0u) { return ${maxBodies}u; }
        if (jointMetaWords.x == body) { return jointMetaWords.y; }
        if (jointMetaWords.y == body) { return jointMetaWords.x; }
        return ${maxBodies}u;
      }

      fn constraintOtherBodyForSpring(
        body: u32,
        springIndex: u32,
        springRecords: ptr<storage, array<vec4f>, read_write>,
      ) -> u32 {
        if (springIndex >= ${maxSprings}u) { return ${maxBodies}u; }
        let springMetaWords = loadSpringMetaWords(springRecords, springIndex);
        if (springMetaWords.z == 0u) { return ${maxBodies}u; }
        if (springMetaWords.x == body) { return springMetaWords.y; }
        if (springMetaWords.y == body) { return springMetaWords.x; }
        return ${maxBodies}u;
      }
    `, [contactRecordHelpers, jointRecordHelpers, springRecordHelpers, bodyConstraintRefHelpers]);

    this.greedyBodyColorsKernel = greedyBodyColorsShader({
      positions: storage(positions, 'vec4f', maxBodies).toReadOnly(),
      bodyConstraintCounts: storage(bodyConstraintCounts, 'uint', maxBodies),
      bodyConstraintRefs: storage(bodyConstraintRefs, 'uint', bodyConstraintRefs.count).toReadOnly(),
      pairContacts: storage(pairContacts, 'vec4f', maxPairContacts * CONTACT_RECORD_VEC4S),
      jointRecords: storage(jointRecords, 'vec4f', maxJoints * JOINT_RECORD_VEC4S),
      springRecords: storage(springRecords, 'vec4f', maxSprings * SPRING_RECORD_VEC4S),
      bodyColorScratch: storage(bodyColorScratch, 'uint', maxBodies).toReadOnly(),
      bodyColorOutput: storage(this.bodyColorScratchNextAttr, 'uint', maxBodies),
      constrainedBodies: storage(
        this.constrainedBodyCompactAttr,
        'uint',
        maxBodies + 1,
      ).toReadOnly(),
      bodyCount: uniform(0),
      colorCount: uniform(1),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Greedy Body Colors A to B');
    assertStorageBufferBudget('AVBD Greedy Body Colors A to B', 9);

    this.greedyBodyColorsReverseKernel = greedyBodyColorsShader({
      positions: storage(positions, 'vec4f', maxBodies).toReadOnly(),
      bodyConstraintCounts: storage(bodyConstraintCounts, 'uint', maxBodies),
      bodyConstraintRefs: storage(bodyConstraintRefs, 'uint', bodyConstraintRefs.count).toReadOnly(),
      pairContacts: storage(pairContacts, 'vec4f', maxPairContacts * CONTACT_RECORD_VEC4S),
      jointRecords: storage(jointRecords, 'vec4f', maxJoints * JOINT_RECORD_VEC4S),
      springRecords: storage(springRecords, 'vec4f', maxSprings * SPRING_RECORD_VEC4S),
      bodyColorScratch: storage(this.bodyColorScratchNextAttr, 'uint', maxBodies).toReadOnly(),
      bodyColorOutput: storage(bodyColorScratch, 'uint', maxBodies),
      constrainedBodies: storage(
        this.constrainedBodyCompactAttr,
        'uint',
        maxBodies + 1,
      ).toReadOnly(),
      bodyCount: uniform(0),
      colorCount: uniform(1),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Greedy Body Colors B to A');
    assertStorageBufferBudget('AVBD Greedy Body Colors B to A', 9);

    // A bounded Jacobi greedy sweep can still end with a small number of
    // equal-color edges when both endpoints change color in its final round.
    // Such an edge would make the in-place GS body solve race.  Validate the
    // final snapshot once and move the higher-index endpoint of every
    // remaining conflict into the separate Jacobi fallback bucket.  This is a
    // deterministic safety closure: every normal/normal graph edge is then
    // guaranteed to have distinct colors, independent of graph diameter.
    const markBodyColorFallbackConflictsShader = wgslFn(/* wgsl */`
      fn compute(
        positions: ptr<storage, array<vec4f>, read>,
        bodyConstraintCounts: ptr<storage, array<atomic<u32>>, read_write>,
        pairActivity: ptr<storage, array<u32>, read>,
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        jointRecords: ptr<storage, array<vec4f>, read_write>,
        springRecords: ptr<storage, array<vec4f>, read_write>,
        constrainedBodies: ptr<storage, array<u32>, read>,
        bodyCount: u32,
        pairDispatchCount: u32,
        jointCount: u32,
        springCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let lane = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        let laneCount = min(constrainedBodies[0], ${maxBodies}u);
        if (lane >= laneCount) { return; }

        // Scan the compact edge sets once rather than pointer-chasing every
        // body's adjacency (which visited each dynamic edge twice). Sparse
        // graphs now launch only one lane per constrained body, while striding
        // still covers edge sets that are denser than the body stream.
        let solverHeader = pairActivity[${pairActiveContactsOffset}u];
        let activeContactCount = min(
          min(solverHeader & ${SOLVER_CONTACT_COUNT_MASK}u, pairDispatchCount),
          ${maxActivePairContacts}u,
        );
        let activeEntriesOffset = select(
          ${pairActiveContactsOffset + 1}u,
          ${pairCanonicalActiveContactsOffset + 1}u,
          (solverHeader & ${SOLVER_CONTACT_CANONICAL_ALIAS_BIT}u) != 0u,
        );
        for (var k = lane; k < activeContactCount; k += laneCount) {
          let contactIndex = pairActivity[activeEntriesOffset + k];
          if (contactIndex >= ${maxPairContacts}u) { continue; }
          let contactMeta = loadContactMeta(pairContacts, contactIndex);
          markFallbackConflictEdge(
            positions,
            bodyConstraintCounts,
            bodyCount,
            u32(contactMeta.x + 0.5),
            u32(contactMeta.y + 0.5),
          );
        }

        for (var k = lane; k < jointCount; k += laneCount) {
          let jointMeta = loadJointMetaWords(jointRecords, k);
          if (jointMeta.w != 0u) {
            markFallbackConflictEdge(
              positions, bodyConstraintCounts, bodyCount, jointMeta.x, jointMeta.y,
            );
          }
        }

        for (var k = lane; k < springCount; k += laneCount) {
          let springMeta = loadSpringMetaWords(springRecords, k);
          if (springMeta.z != 0u) {
            markFallbackConflictEdge(
              positions, bodyConstraintCounts, bodyCount, springMeta.x, springMeta.y,
            );
          }
        }
      }

      fn markFallbackConflictEdge(
        positions: ptr<storage, array<vec4f>, read>,
        bodyConstraintCounts: ptr<storage, array<atomic<u32>>, read_write>,
        bodyCount: u32,
        bodyA: u32,
        bodyB: u32,
      ) {
        if (
          bodyA >= bodyCount || bodyB >= bodyCount || bodyA == bodyB
          || positions[bodyA].w <= 0.0 || positions[bodyB].w <= 0.0
        ) { return; }

        let wordA = atomicLoad(&bodyConstraintCounts[bodyA]);
        let wordB = atomicLoad(&bodyConstraintCounts[bodyB]);
        if (
          (wordA & 0xFFFFu) == 0u || (wordB & 0xFFFFu) == 0u
          || (wordA & ${BODY_COLOR_FALLBACK_FLAG}u) != 0u
          || (wordB & ${BODY_COLOR_FALLBACK_FLAG}u) != 0u
        ) { return; }
        let colorA = (wordA >> 16u) & 0xFFu;
        let colorB = (wordB >> 16u) & 0xFFu;
        if (colorA == colorB) {
          // Deterministically move the higher-index endpoint into the Jacobi
          // lane, matching the dependency direction of incremental coloring.
          atomicOr(
            &bodyConstraintCounts[max(bodyA, bodyB)],
            ${BODY_COLOR_FALLBACK_FLAG}u,
          );
        }
      }
    `, [contactRecordHelpers, jointRecordHelpers, springRecordHelpers]);

    this.markBodyColorFallbackConflictsKernel = markBodyColorFallbackConflictsShader({
      positions: storage(positions, 'vec4f', maxBodies).toReadOnly(),
      bodyConstraintCounts: storage(bodyConstraintCounts, 'uint', maxBodies).toAtomic(),
      pairActivity: storage(pairActivity, 'uint', pairActivityWordCount).toReadOnly(),
      pairContacts: storage(pairContacts, 'vec4f', maxPairContacts * CONTACT_RECORD_VEC4S),
      jointRecords: storage(jointRecords, 'vec4f', maxJoints * JOINT_RECORD_VEC4S),
      springRecords: storage(springRecords, 'vec4f', maxSprings * SPRING_RECORD_VEC4S),
      constrainedBodies: storage(
        this.constrainedBodyCompactAttr,
        'uint',
        maxBodies + 1,
      ).toReadOnly(),
      bodyCount: uniform(0),
      pairDispatchCount: uniform(0),
      jointCount: uniform(0),
      springCount: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Close Body Color Conflicts Into Fallback');
    assertStorageBufferBudget('AVBD Close Body Color Conflicts Into Fallback', 7);

    const markHardColorConflictsShader = wgslFn(/* wgsl */`
      fn compute(
        bodyConstraintCounts: ptr<storage, array<atomic<u32>>, read_write>,
        jointRecords: ptr<storage, array<vec4f>, read_write>,
        springRecords: ptr<storage, array<vec4f>, read_write>,
        bodyCount: u32,
        jointCount: u32,
        springCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;

        if (gid < jointCount) {
          let jointMetaWords = loadJointMetaWords(jointRecords, gid);
          if (jointMetaWords.w != 0u) {
            markHardColorConflictEdge(bodyConstraintCounts, bodyCount, jointMetaWords.x, jointMetaWords.y);
          }
        }

        if (gid < springCount) {
          let springMetaWords = loadSpringMetaWords(springRecords, gid);
          if (springMetaWords.z != 0u) {
            markHardColorConflictEdge(bodyConstraintCounts, bodyCount, springMetaWords.x, springMetaWords.y);
          }
        }
      }

      fn markHardColorConflictEdge(
        bodyConstraintCounts: ptr<storage, array<atomic<u32>>, read_write>,
        bodyCount: u32,
        bodyA: u32,
        bodyB: u32,
      ) {
        if (bodyA == 0xffffffffu || bodyB == 0xffffffffu) { return; }
        if (bodyA >= bodyCount || bodyB >= bodyCount || bodyA == bodyB) { return; }

        let wordA = atomicLoad(&bodyConstraintCounts[bodyA]);
        let wordB = atomicLoad(&bodyConstraintCounts[bodyB]);
        let countA = wordA & 0xFFFFu;
        let countB = wordB & 0xFFFFu;
        if (countA == 0u || countB == 0u) { return; }
        if (
          (wordA & ${BODY_COLOR_FALLBACK_FLAG}u) != 0u
          || (wordB & ${BODY_COLOR_FALLBACK_FLAG}u) != 0u
        ) { return; }

        let colorA = (wordA >> 16u) & 0xFFu;
        let colorB = (wordB >> 16u) & 0xFFu;
        if (colorA != colorB) { return; }

        // Keep the smaller-index color fixed, matching the paper's dependency
        // orientation; the higher-index endpoint adapts.
        let loser = max(bodyA, bodyB);
        atomicOr(&bodyConstraintCounts[loser], ${BODY_COLOR_REPAIR_FLAG}u);
      }
    `, [jointRecordHelpers, springRecordHelpers]);

    this.markHardColorConflictsKernel = markHardColorConflictsShader({
      bodyConstraintCounts: storage(bodyConstraintCounts, 'uint', maxBodies).toAtomic(),
      jointRecords: storage(jointRecords, 'vec4f', maxJoints * JOINT_RECORD_VEC4S),
      springRecords: storage(springRecords, 'vec4f', maxSprings * SPRING_RECORD_VEC4S),
      bodyCount: uniform(0),
      jointCount: uniform(0),
      springCount: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Mark Hard Body Color Conflicts');
    assertStorageBufferBudget('AVBD Mark Hard Body Color Conflicts', 3);

    const repairHardBodyColorsShader = wgslFn(/* wgsl */`
      fn compute(
        bodyConstraintCounts: ptr<storage, array<u32>, read_write>,
        bodyConstraintRefs: ptr<storage, array<u32>, read>,
        jointRecords: ptr<storage, array<vec4f>, read_write>,
        springRecords: ptr<storage, array<vec4f>, read_write>,
        constrainedBodies: ptr<storage, array<u32>, read>,
        bodyCount: u32,
        colorCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let compactIndex = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        let constrainedCount = min(constrainedBodies[0], ${maxBodies}u);
        if (compactIndex >= constrainedCount) { return; }
        let gid = constrainedBodies[compactIndex + 1u];
        if (gid >= bodyCount) { return; }

        let word = bodyConstraintCounts[gid];
        if ((word & ${BODY_COLOR_REPAIR_FLAG}u) == 0u) { return; }
        if ((word & ${BODY_COLOR_FALLBACK_FLAG}u) != 0u) { return; }

        let constraintCount = word & 0xFFFFu;
        if (constraintCount == 0u) {
          bodyConstraintCounts[gid] = word & ${BODY_SLEEP_FLAG}u;
          return;
        }

        let colors = max(1u, min(colorCount, 32u));
        var usedMask = 0u;
        var constraintOverflowNode = bodyConstraintRefs[
          ${bodyConstraintOverflowHeadOffset}u + gid
        ];
        for (var k = 0u; k < constraintCount; k++) {
          let constraintRef = loadBodyConstraintRef(
            bodyConstraintRefs, gid, k, &constraintOverflowNode,
          );
          let constraintTag = constraintRef & ${CONSTRAINT_REF_TAG_MASK}u;
          let constraintIndex = constraintRef & ${CONSTRAINT_REF_INDEX_MASK}u;
          var other = ${maxBodies}u;
          if (constraintTag == ${CONSTRAINT_REF_TAG_JOINT}u) {
            other = repairConstraintOtherBodyForJoint(gid, constraintIndex, jointRecords);
          } else if (constraintTag == ${CONSTRAINT_REF_TAG_SPRING}u) {
            other = repairConstraintOtherBodyForSpring(gid, constraintIndex, springRecords);
          } else {
            continue;
          }
          if (other >= bodyCount || other == gid) { continue; }

          let otherWord = bodyConstraintCounts[other];
          let otherConstraintCount = otherWord & 0xFFFFu;
          if (otherConstraintCount == 0u) { continue; }

          let otherColor = (otherWord >> 16u) & 0xFFu;
          if (otherColor < colors) {
            usedMask |= 1u << otherColor;
          }
        }

        var chosenColor = (word >> 16u) & 0xFFu;
        var fallback = false;
        var needsNewColor = chosenColor >= colors;
        if (!needsNewColor) {
          needsNewColor = (usedMask & (1u << chosenColor)) != 0u;
        }
        if (needsNewColor) {
          var found = false;
          for (var color = 0u; color < 32u; color++) {
            if (color >= colors) { break; }
            if ((usedMask & (1u << color)) == 0u) {
              chosenColor = color;
              found = true;
              break;
            }
          }
          if (!found) {
            chosenColor = gid % colors;
            fallback = true;
          }
        }

        bodyConstraintCounts[gid] = (word & ${BODY_SLEEP_FLAG}u) | (chosenColor << 16u) | constraintCount | select(0u, ${BODY_COLOR_FALLBACK_FLAG}u, fallback);
      }

      fn repairConstraintOtherBodyForJoint(
        body: u32,
        jointIndex: u32,
        jointRecords: ptr<storage, array<vec4f>, read_write>,
      ) -> u32 {
        if (jointIndex >= ${maxJoints}u) { return ${maxBodies}u; }
        let jointMetaWords = loadJointMetaWords(jointRecords, jointIndex);
        if (jointMetaWords.w == 0u) { return ${maxBodies}u; }
        if (jointMetaWords.x == body) { return jointMetaWords.y; }
        if (jointMetaWords.y == body) { return jointMetaWords.x; }
        return ${maxBodies}u;
      }

      fn repairConstraintOtherBodyForSpring(
        body: u32,
        springIndex: u32,
        springRecords: ptr<storage, array<vec4f>, read_write>,
      ) -> u32 {
        if (springIndex >= ${maxSprings}u) { return ${maxBodies}u; }
        let springMetaWords = loadSpringMetaWords(springRecords, springIndex);
        if (springMetaWords.z == 0u) { return ${maxBodies}u; }
        if (springMetaWords.x == body) { return springMetaWords.y; }
        if (springMetaWords.y == body) { return springMetaWords.x; }
        return ${maxBodies}u;
      }
    `, [jointRecordHelpers, springRecordHelpers, bodyConstraintRefHelpers]);

    this.repairHardBodyColorsKernel = repairHardBodyColorsShader({
      bodyConstraintCounts: storage(bodyConstraintCounts, 'uint', maxBodies),
      bodyConstraintRefs: storage(bodyConstraintRefs, 'uint', bodyConstraintRefs.count).toReadOnly(),
      jointRecords: storage(jointRecords, 'vec4f', maxJoints * JOINT_RECORD_VEC4S),
      springRecords: storage(springRecords, 'vec4f', maxSprings * SPRING_RECORD_VEC4S),
      constrainedBodies: storage(
        this.constrainedBodyCompactAttr,
        'uint',
        maxBodies + 1,
      ).toReadOnly(),
      bodyCount: uniform(0),
      colorCount: uniform(1),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Repair Hard Body Colors');
    assertStorageBufferBudget('AVBD Repair Hard Body Colors', 5);

    // Build one compact body-id list partitioned by color. The three metadata
    // ranges live behind the maxBodies list payload:
    //   counts[color], offsets[color], scatterCursors[color].
    // All four kernels are dispatched in one compute pass from prepare(), so
    // dispatch boundaries provide the count -> prefix -> scatter ordering
    // without adding a queue submission per phase.
    const clearBodyColorBucketsShader = wgslFn(/* wgsl */`
      fn compute(
        bodyColorCompact: ptr<storage, array<atomic<u32>>, read_write>,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid != 0u) { return; }
        for (var color = 0u; color < ${bodyColorBucketVariants}u; color++) {
          atomicStore(&bodyColorCompact[${bodyColorCountOffset}u + color], 0u);
          atomicStore(&bodyColorCompact[${bodyColorOffsetOffset}u + color], 0u);
          atomicStore(&bodyColorCompact[${bodyColorCursorOffset}u + color], 0u);
        }
      }
    `);
    this.clearBodyColorBucketsKernel = clearBodyColorBucketsShader({
      bodyColorCompact: storage(this.bodyColorCompactAttr, 'uint', bodyColorCompactWordCount).toAtomic(),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Clear Compact Body Color Buckets');
    assertStorageBufferBudget('AVBD Clear Compact Body Color Buckets', 1);

    const countBodyColorBucketsShader = wgslFn(/* wgsl */`
      fn compute(
        positions: ptr<storage, array<vec4f>, read>,
        bodyConstraintCounts: ptr<storage, array<u32>, read>,
        bodyColorCompact: ptr<storage, array<atomic<u32>>, read_write>,
        constrainedBodies: ptr<storage, array<u32>, read>,
        bodyCount: u32,
        colorCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let compactIndex = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        let constrainedCount = min(constrainedBodies[0], ${maxBodies}u);
        if (compactIndex >= constrainedCount) { return; }
        let gid = constrainedBodies[compactIndex + 1u];
        if (gid >= bodyCount || positions[gid].w <= 0.0) { return; }
        let countWord = bodyConstraintCounts[gid];
        let constraintCount = countWord & 0xFFFFu;
        if (constraintCount == 0u) { return; }
        let colors = max(1u, min(colorCount, ${bodyColorKernelVariants}u));
        let bodyColor = (countWord >> 16u) & 0xFFu;
        let fallback = (countWord & ${BODY_COLOR_FALLBACK_FLAG}u) != 0u;
        if (!fallback && bodyColor >= colors) { return; }
        let bucket = select(bodyColor, colors, fallback);
        atomicAdd(&bodyColorCompact[${bodyColorCountOffset}u + bucket], 1u);
      }
    `);
    this.countBodyColorBucketsKernel = countBodyColorBucketsShader({
      positions: storage(positions, 'vec4f', maxBodies).toReadOnly(),
      bodyConstraintCounts: storage(bodyConstraintCounts, 'uint', maxBodies).toReadOnly(),
      bodyColorCompact: storage(this.bodyColorCompactAttr, 'uint', bodyColorCompactWordCount).toAtomic(),
      constrainedBodies: storage(
        this.constrainedBodyCompactAttr,
        'uint',
        maxBodies + 1,
      ).toReadOnly(),
      bodyCount: uniform(0),
      colorCount: uniform(1),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Count Compact Body Color Buckets');
    assertStorageBufferBudget('AVBD Count Compact Body Color Buckets', 4);

    const prefixBodyColorBucketsShader = wgslFn(/* wgsl */`
      fn compute(
        bodyColorCompact: ptr<storage, array<atomic<u32>>, read_write>,
        colorDispatchIndirect: ptr<storage, array<u32>, read_write>,
        fallbackDispatchIndirect: ptr<storage, array<u32>, read_write>,
        colorCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid != 0u) { return; }

        let colors = max(1u, min(colorCount, ${bodyColorKernelVariants}u));
        var runningOffset = 0u;
        var maxBucketCount = 0u;
        var fallbackBucketCount = 0u;
        for (var color = 0u; color < ${bodyColorBucketVariants}u; color++) {
          var bucketCount = 0u;
          if (color <= colors) {
            bucketCount = atomicLoad(&bodyColorCompact[${bodyColorCountOffset}u + color]);
          }
          atomicStore(&bodyColorCompact[${bodyColorOffsetOffset}u + color], runningOffset);
          atomicStore(&bodyColorCompact[${bodyColorCursorOffset}u + color], 0u);
          runningOffset += bucketCount;
          if (color < colors) {
            maxBucketCount = max(maxBucketCount, bucketCount);
          } else if (color == colors) {
            fallbackBucketCount = bucketCount;
          }
        }

        let colorWorkgroups = (maxBucketCount + ${WORKGROUP_SIZE}u - 1u) / ${WORKGROUP_SIZE}u;
        colorDispatchIndirect[0] = colorWorkgroups;
        colorDispatchIndirect[1] = 1u;
        colorDispatchIndirect[2] = 1u;
        let fallbackWorkgroups = (fallbackBucketCount + ${WORKGROUP_SIZE}u - 1u) / ${WORKGROUP_SIZE}u;
        fallbackDispatchIndirect[0] = fallbackWorkgroups;
        fallbackDispatchIndirect[1] = 1u;
        fallbackDispatchIndirect[2] = 1u;

      }
    `);
    this.prefixBodyColorBucketsKernel = prefixBodyColorBucketsShader({
      bodyColorCompact: storage(this.bodyColorCompactAttr, 'uint', bodyColorCompactWordCount).toAtomic(),
      colorDispatchIndirect: storage(this.bodyColorDispatchIndirectAttr, 'uint', 3),
      fallbackDispatchIndirect: storage(this.bodyColorFallbackDispatchIndirectAttr, 'uint', 3),
      colorCount: uniform(1),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Prefix Compact Body Color Buckets');
    assertStorageBufferBudget('AVBD Prefix Compact Body Color Buckets', 3);

    // WebGPU exposes ten storage bindings per compute stage.  Pack up to nine
    // exact color dispatch records beside the compact-count buffer so common
    // 8/16-color scenes need only one/two tiny builders instead of one command
    // per color.  This keeps exact workgroup counts without trading the saved
    // shader work for command-encoder overhead.
    this.buildBodyColorDispatchArgsKernels = [];
    for (
      let batchStart = 0;
      batchStart < bodyColorKernelVariants;
      batchStart += BODY_COLOR_DISPATCH_ARGS_BATCH_SIZE
    ) {
      const batchAttributes = this.bodyColorDispatchIndirectAttrs.slice(
        batchStart,
        batchStart + BODY_COLOR_DISPATCH_ARGS_BATCH_SIZE,
      );
      const dispatchParameters = batchAttributes.map(
        (_, localIndex) =>
          `        dispatchIndirect${localIndex}: ptr<storage, array<u32>, read_write>,`,
      ).join('\n');
      const dispatchWrites = batchAttributes.map((_, localIndex) => {
        const bucket = batchStart + localIndex;
        return `
        let bucketCount${localIndex} = bodyColorCompact[${bodyColorCountOffset + bucket}u];
        dispatchIndirect${localIndex}[0] = (bucketCount${localIndex} + ${WORKGROUP_SIZE}u - 1u) / ${WORKGROUP_SIZE}u;
        dispatchIndirect${localIndex}[1] = 1u;
        dispatchIndirect${localIndex}[2] = 1u;`;
      }).join('');
      const buildBodyColorDispatchArgsBatchShader = wgslFn(/* wgsl */`
        fn compute(
          bodyColorCompact: ptr<storage, array<u32>, read>,
${dispatchParameters}
        ) -> void {${dispatchWrites}
        }
      `);
      const bindings: Record<string, any> = {
        bodyColorCompact: storage(
          this.bodyColorCompactAttr,
          'uint',
          bodyColorCompactWordCount,
        ).toReadOnly(),
      };
      batchAttributes.forEach((dispatchIndirect, localIndex) => {
        bindings[`dispatchIndirect${localIndex}`] = storage(dispatchIndirect, 'uint', 3);
      });
      this.buildBodyColorDispatchArgsKernels.push(
        buildBodyColorDispatchArgsBatchShader(bindings)
          .computeKernel([1, 1, 1])
          .setName(`AVBD Build Exact Color Dispatch Args ${batchStart}-${batchStart + batchAttributes.length - 1}`),
      );
      assertStorageBufferBudget(
        'AVBD Build Exact Color Dispatch Args Batch',
        1 + batchAttributes.length,
      );
    }

    // One cooperative workgroup owns one body, so its indirect x count is the
    // exact bucket size (not ceil(bucket/64)). Keep these records separate
    // from scalar/generic dispatches; fallback and adaptive kernels continue
    // to consume the established 64-thread records.
    this.buildCooperativeBodyColorDispatchArgsKernels = [];
    for (
      let batchStart = 0;
      batchStart < this.cooperativeBodyColorDispatchIndirectAttrs.length;
      batchStart += BODY_COLOR_DISPATCH_ARGS_BATCH_SIZE
    ) {
      const batchAttributes = this.cooperativeBodyColorDispatchIndirectAttrs.slice(
        batchStart,
        batchStart + BODY_COLOR_DISPATCH_ARGS_BATCH_SIZE,
      );
      const dispatchParameters = batchAttributes.map(
        (_, localIndex) =>
          `        dispatchIndirect${localIndex}: ptr<storage, array<u32>, read_write>,`,
      ).join('\n');
      const dispatchWrites = batchAttributes.map((_, localIndex) => {
        const bucket = batchStart + localIndex;
        return `
        let bucketCount${localIndex} = bodyColorCompact[${bodyColorCountOffset + bucket}u];
        dispatchIndirect${localIndex}[0] = bucketCount${localIndex};
        dispatchIndirect${localIndex}[1] = 1u;
        dispatchIndirect${localIndex}[2] = 1u;`;
      }).join('');
      const buildCooperativeDispatchArgsShader = wgslFn(/* wgsl */`
        fn compute(
          bodyColorCompact: ptr<storage, array<u32>, read>,
${dispatchParameters}
        ) -> void {${dispatchWrites}
        }
      `);
      const bindings: Record<string, any> = {
        bodyColorCompact: storage(
          this.bodyColorCompactAttr,
          'uint',
          bodyColorCompactWordCount,
        ).toReadOnly(),
      };
      batchAttributes.forEach((dispatchIndirect, localIndex) => {
        bindings[`dispatchIndirect${localIndex}`] = storage(dispatchIndirect, 'uint', 3);
      });
      this.buildCooperativeBodyColorDispatchArgsKernels.push(
        buildCooperativeDispatchArgsShader(bindings)
          .computeKernel([1, 1, 1])
          .setName(
            `AVBD Build Cooperative Color Dispatch Args ${batchStart}-${batchStart + batchAttributes.length - 1}`,
          ),
      );
      assertStorageBufferBudget(
        'AVBD Build Cooperative Color Dispatch Args Batch',
        1 + batchAttributes.length,
      );
    }

    const scatterBodyColorBucketsShader = wgslFn(/* wgsl */`
      fn compute(
        positions: ptr<storage, array<vec4f>, read>,
        bodyConstraintCounts: ptr<storage, array<u32>, read>,
        bodyColorCompact: ptr<storage, array<atomic<u32>>, read_write>,
        constrainedBodies: ptr<storage, array<u32>, read>,
        bodyCount: u32,
        colorCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let compactIndex = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        let constrainedCount = min(constrainedBodies[0], ${maxBodies}u);
        if (compactIndex >= constrainedCount) { return; }
        let gid = constrainedBodies[compactIndex + 1u];
        if (gid >= bodyCount || positions[gid].w <= 0.0) { return; }
        let countWord = bodyConstraintCounts[gid];
        let constraintCount = countWord & 0xFFFFu;
        if (constraintCount == 0u) { return; }
        let colors = max(1u, min(colorCount, ${bodyColorKernelVariants}u));
        let bodyColor = (countWord >> 16u) & 0xFFu;
        let fallback = (countWord & ${BODY_COLOR_FALLBACK_FLAG}u) != 0u;
        if (!fallback && bodyColor >= colors) { return; }
        let bucket = select(bodyColor, colors, fallback);

        let bucketSlot = atomicAdd(
          &bodyColorCompact[${bodyColorCursorOffset}u + bucket],
          1u,
        );
        let bucketCount = atomicLoad(&bodyColorCompact[${bodyColorCountOffset}u + bucket]);
        if (bucketSlot >= bucketCount) { return; }
        let bucketOffset = atomicLoad(&bodyColorCompact[${bodyColorOffsetOffset}u + bucket]);
        let listIndex = bucketOffset + bucketSlot;
        if (listIndex < ${maxBodies}u) {
          atomicStore(&bodyColorCompact[listIndex], gid);
        }
      }
    `);
    this.scatterBodyColorBucketsKernel = scatterBodyColorBucketsShader({
      positions: storage(positions, 'vec4f', maxBodies).toReadOnly(),
      bodyConstraintCounts: storage(bodyConstraintCounts, 'uint', maxBodies).toReadOnly(),
      bodyColorCompact: storage(this.bodyColorCompactAttr, 'uint', bodyColorCompactWordCount).toAtomic(),
      constrainedBodies: storage(
        this.constrainedBodyCompactAttr,
        'uint',
        maxBodies + 1,
      ).toReadOnly(),
      bodyCount: uniform(0),
      colorCount: uniform(1),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Scatter Compact Body Color Buckets');
    assertStorageBufferBudget('AVBD Scatter Compact Body Color Buckets', 4);

    // Body-colored AVBD primal pass: each invocation stamps all incident
    // contacts for one body into a single 6x6 local system.
    const makePrimalBodySolveShader = (
      mode: 'generic' | 'localDiag',
      outputMode: 'inPlace' | 'scratch',
      constraintMode: 'all' | 'contacts',
      referenceContactStrict = false,
      adaptiveResidualTracking = false,
      scratchAdaptiveResidualOutput = false,
      cooperativeBodySolve = false,
    ) => {
      if (
        referenceContactStrict
        && (mode !== 'generic' || constraintMode !== 'contacts')
      ) {
        throw new Error('Reference primal specialization is contact-only strict');
      }
      if (adaptiveResidualTracking && !referenceContactStrict) {
        throw new Error('Adaptive residual tracking requires the reference contact specialization');
      }
      if (
        scratchAdaptiveResidualOutput
        && (referenceContactStrict || outputMode !== 'scratch' || mode !== 'generic')
      ) {
        throw new Error('Adaptive fallback residual output requires the generic scratch solver');
      }
      if (
        cooperativeBodySolve
        && (
          !referenceContactStrict
          || adaptiveResidualTracking
          || scratchAdaptiveResidualOutput
          || outputMode !== 'inPlace'
          || constraintMode !== 'contacts'
        )
      ) {
        throw new Error(
          'Cooperative body solve requires the fixed contact-only reference specialization',
        );
      }
      const inertiaParam = '        inverseInertia: ptr<storage, array<vec4f>, read>,\n';
      const hardConstraintStorageParams = constraintMode === 'all'
        ? /* wgsl */`        jointRecords: ptr<storage, array<vec4f>, read_write>,
        springRecords: ptr<storage, array<vec4f>, read_write>,
`
        : '';
      const inertialPoseParam = outputMode === 'inPlace'
        ? '        inertialPose: ptr<storage, array<vec4f>, read_write>,\n'
        : '        inertialPose: ptr<storage, array<vec4f>, read>,\n';
      const bodySolveOutputParam = outputMode === 'scratch'
        ? '        bodySolveOutputPose: ptr<storage, array<vec4f>, read_write>,\n'
        : '';
      const adaptiveResidualParams = adaptiveResidualTracking
        ? /* wgsl */`        adaptiveState: ptr<storage, array<atomic<u32>>, read_write>,
        adaptiveResiduals: ptr<storage, array<atomic<u32>>, read_write>,
`
        : '';
      // The fixed reference path consumes the dense sidecar. Adaptive
      // diagnostics retain the authoritative record so their existing
      // ten-binding shader remains within the requested WebGPU limit.
      const useSolverHotState = this.denseContactSolverState
        && !adaptiveResidualTracking
        && (
          referenceContactStrict
          || (outputMode === 'scratch' && constraintMode === 'contacts')
        );
      const usePackedContactSolverGeometry = this.packedContactSolverGeometry
        && referenceContactStrict
        && !adaptiveResidualTracking;
      const maintainCaptureBodyDelta = this.captureBodyDeltaCache
        && referenceContactStrict
        && !adaptiveResidualTracking;
      const captureBodyDeltaOutputParam = maintainCaptureBodyDelta
        ? '        captureBodyDelta: ptr<storage, array<vec4f>, read_write>,\n'
        : '';
      const contactSolverHotParam = useSolverHotState
        ? '        contactSolverHot: ptr<storage, array<vec4f>, read_write>,\n'
        : '';
      const solverContactRecordParam = usePackedContactSolverGeometry
        ? '        contactSolverGeometry: ptr<storage, array<vec4f>, read>,\n'
        : '        pairContacts: ptr<storage, array<vec4f>, read_write>,\n';
      const tangentInvLengthStorageParam = referenceContactStrict
        && this.cacheContactTangentInvLength
        && !usePackedContactSolverGeometry
        ? '        contactTangentInvLength: ptr<storage, array<f32>, read>,\n'
        : '';
      const contactTangentBasisSource = usePackedContactSolverGeometry
        ? this.cacheContactTangentInvLength
          ? '          let tangentBasis = tangentBasisFromCachedT1AndInvLength(n, cachedTangentT1, armA.w, cachedTangentInvLength);'
          : '          let tangentBasis = tangentBasisFromCachedT1(n, cachedTangentT1, armA.w);'
        : referenceContactStrict && this.cacheContactTangentInvLength
        ? '          let tangentBasis = tangentBasisFromCachedT1AndInvLength(n, loadContactTangentT1(pairContacts, p), armA.w, contactTangentInvLength[p]);'
        : '          let tangentBasis = tangentBasisFromCachedT1(n, loadContactTangentT1(pairContacts, p), armA.w);';
      const cachedConstraintC0Source = usePackedContactSolverGeometry
        ? ''
        : '          let cachedConstraintC0 = loadContactConstraintC0(pairContacts, p);\n';
      const solverContactGeometrySource = usePackedContactSolverGeometry
        ? /* wgsl */`
          let solverGeometryBase = p * ${CONTACT_SOLVER_GEOMETRY_VEC4S}u;
          let solverGeometry0 = contactSolverGeometry[solverGeometryBase];
          let solverGeometry1 = contactSolverGeometry[solverGeometryBase + 1u];
          let solverGeometry2 = contactSolverGeometry[solverGeometryBase + 2u];
          let solverGeometry3 = contactSolverGeometry[solverGeometryBase + 3u];
          let solverGeometry4 = contactSolverGeometry[solverGeometryBase + 4u];
          let i = u32(solverGeometry0.x + 0.5);
          let j = u32(solverGeometry0.y + 0.5);
          let encodedContactInfo = bitcast<u32>(solverGeometry0.z);
          let n = vec3f(solverGeometry0.w, solverGeometry1.xy);
          let armA = vec4f(solverGeometry1.zw, solverGeometry2.xy);
          let armB = vec4f(solverGeometry2.zw, solverGeometry3.x, 0.0);
          let cachedConstraintC0 = vec4f(solverGeometry3.yzw, 0.0);
          let cachedTangentT1 = solverGeometry4.xyz;
          let cachedTangentInvLength = solverGeometry4.w;
          let speculativeContact = contactInfoIsSpeculative(encodedContactInfo);
`
        : /* wgsl */`
          let n = loadContactNormalPen(pairContacts, p).xyz;
          let armA = loadContactArmA(pairContacts, p);
          let armB = loadContactArmB(pairContacts, p);
          let contactMeta = loadContactMeta(pairContacts, p);
          let encodedContactInfo = bitcast<u32>(contactMeta.w);
          let speculativeContact = contactInfoIsSpeculative(encodedContactInfo);
          let i = u32(contactMeta.x + 0.5);
          let j = u32(contactMeta.y + 0.5);
`;
      const otherConstraintPosition = /* wgsl */`
          let otherPos = inertialPose[otherPoseBase + 2u].xyz;
          let otherInitialBase = other * 2u;
          let initialOtherPos = initialPose[otherInitialBase].xyz;
          let initialOtherQ = initialPose[otherInitialBase + 1u];
          let dPosOtherConstraint = otherPos - initialOtherPos;
`;
      // A box manifold contributes several consecutive physical rows for the
      // same body pair.  The scalar reference solver used to reload the other
      // body's pose/delta for every row even though no endpoint can change
      // during one colored body invocation.  Keep one body-local cache: this
      // removes four random vec4 reads from repeated manifold rows without
      // changing contact order, arithmetic, or the stored f32 values used by
      // the solve.
      const referenceOtherBodyCacheDeclaration = referenceContactStrict
        && !cooperativeBodySolve
        ? /* wgsl */`
        var cachedOtherBody = 0xffffffffu;
        var cachedOtherDPosConstraint = vec3f(0.0);
        var cachedOtherDThetaConstraint = vec3f(0.0);
`
        : '';
      const referenceOtherConstraintState = referenceContactStrict
        ? /* wgsl */`
${cooperativeBodySolve ? /* wgsl */`
          let dPosOtherConstraint = inertialPose[otherPoseBase + 2u].xyz
            - initialPose[other * 2u].xyz;
          let dThetaOtherConstraint = inertialPose[otherPoseBase + 4u].xyz;
` : /* wgsl */`
          if (other != cachedOtherBody) {
            let cachedOtherPoseBase = other * ${INERTIAL_POSE_VEC4S_PER_BODY}u;
            let cachedOtherInitialBase = other * 2u;
            cachedOtherDPosConstraint = inertialPose[cachedOtherPoseBase + 2u].xyz
              - initialPose[cachedOtherInitialBase].xyz;
            // Strict colored contact solves publish this delta together with
            // pose slots 2/3, so later colors observe the identical GS state.
            cachedOtherDThetaConstraint = inertialPose[cachedOtherPoseBase + 4u].xyz;
            cachedOtherBody = other;
          }
          let dPosOtherConstraint = cachedOtherDPosConstraint;
          let dThetaOtherConstraint = cachedOtherDThetaConstraint;
`}
`
        : '';
      const scratchStaticConstraintDelta = scratchAdaptiveResidualOutput
        ? 'vec4f(inertialPose[poseBase + 4u].xyz, 0.0)'
        : 'inertialPose[poseBase + 4u]';
      const staticPoseOutput = outputMode === 'inPlace'
        ? /* wgsl */`
          let currentConstraintDeltaTheta = inertialPose[poseBase + 4u];
          inertialPose[poseBase + 2u] = currentPose;
          inertialPose[poseBase + 3u] = q;
          inertialPose[poseBase + 4u] = currentConstraintDeltaTheta;
`
        : /* wgsl */`
          bodySolveOutputPose[bodySolveOutputBase] = currentPose;
          bodySolveOutputPose[bodySolveOutputBase + 1u] = q;
          bodySolveOutputPose[bodySolveOutputBase + 2u] = ${scratchStaticConstraintDelta};
`;
      const scratchAdaptiveResidualWrite = scratchAdaptiveResidualOutput
        ? /* wgsl */`
        var adaptiveFallbackBodyResidual = 0.0;
        if (adaptiveFallbackResidualEnabled != 0u) {
          let fallbackLinearResidual = max(
            max(abs(appliedDPos.x), abs(appliedDPos.y)),
            abs(appliedDPos.z),
          ) / max(adaptiveFallbackLinearTolerance, 1e-30);
          let fallbackAngularResidual = max(
            max(abs(appliedDTheta.x), abs(appliedDTheta.y)),
            abs(appliedDTheta.z),
          ) / max(adaptiveFallbackAngularTolerance, 1e-30);
          adaptiveFallbackBodyResidual = max(
            fallbackLinearResidual,
            fallbackAngularResidual,
          );
          if (!solveOutputFinite || !isFiniteF32(adaptiveFallbackBodyResidual)) {
            // A negative normalized residual is the explicit transport-only
            // INVALID sentinel consumed by markAdaptiveResidualViolation.
            adaptiveFallbackBodyResidual = -1.0;
          }
        }
`
        : '';
      const finalPoseOutput = outputMode === 'inPlace'
        ? /* wgsl */`
        let finalDQConstraintRaw = qmul(q, qconj(initialQ));
        let finalDQConstraint = select(
          finalDQConstraintRaw,
          -finalDQConstraintRaw,
          finalDQConstraintRaw.w < 0.0,
        );
        let publishedPose = vec4f(pos, currentPose.w);
        let publishedDTheta = vec4f(2.0 * finalDQConstraint.xyz, 0.0);
        inertialPose[poseBase + 2u] = publishedPose;
        inertialPose[poseBase + 3u] = q;
        inertialPose[poseBase + 4u] = publishedDTheta;
${maintainCaptureBodyDelta ? /* wgsl */`        captureBodyDelta[initialBase] = vec4f(
          publishedPose.xyz - initialPos,
          0.0,
        );
        captureBodyDelta[initialBase + 1u] = publishedDTheta;
` : ''}
`
        : /* wgsl */`
        let finalDQConstraintRaw = qmul(q, qconj(initialQ));
        let finalDQConstraint = select(
          finalDQConstraintRaw,
          -finalDQConstraintRaw,
          finalDQConstraintRaw.w < 0.0,
        );
        bodySolveOutputPose[bodySolveOutputBase] = vec4f(pos, currentPose.w);
        bodySolveOutputPose[bodySolveOutputBase + 1u] = q;
        bodySolveOutputPose[bodySolveOutputBase + 2u] = vec4f(
          2.0 * finalDQConstraint.xyz,
          ${scratchAdaptiveResidualOutput ? 'adaptiveFallbackBodyResidual' : '0.0'},
        );
`;
      const otherConstraintRotation = constraintMode === 'contacts'
        && outputMode === 'inPlace'
        ? /* wgsl */`
          // Strict colored contact-only solves publish this value together
          // with pose slots 2/3, so later colors observe the same GS iterate
          // without two quaternion multiplies per incident endpoint.
          let dThetaOtherConstraint = inertialPose[otherPoseBase + 4u].xyz;
`
        : /* wgsl */`
          let otherQ = inertialPose[otherPoseBase + 3u];
          let dqOtherConstraintRaw = qmul(otherQ, qconj(initialOtherQ));
          let dqOtherConstraint = select(
            dqOtherConstraintRaw,
            -dqOtherConstraintRaw,
            dqOtherConstraintRaw.w < 0.0,
          );
          let dThetaOtherConstraint = 2.0 * dqOtherConstraint.xyz;
`;
      const localDiagonalUniformParam = mode === 'generic'
        ? '        useLocalDiagonalInertia: u32,\n'
        : '';
      const scratchAdaptiveResidualUniformParams = scratchAdaptiveResidualOutput
        ? /* wgsl */`        adaptiveFallbackResidualEnabled: u32,
        adaptiveFallbackLinearTolerance: f32,
        adaptiveFallbackAngularTolerance: f32,
`
        : '';
      const inertiaSetup = mode === 'localDiag'
        ? /* wgsl */`
        let inv = inverseInertia[gid];
        let invMass = inv.w;
        let initialBase = gid * 2u;
        let poseBase = gid * ${INERTIAL_POSE_VEC4S_PER_BODY}u;
        let currentPose = inertialPose[poseBase + 2u];
        var pos = currentPose.xyz;
        var q = normalize(inertialPose[poseBase + 3u]);
        if (!(invMass > 0.0)) {
${staticPoseOutput}
          return;
        }
        let localInvI = vec3f(
          max(inv.x, 1e-8),
          max(inv.y, 1e-8),
          max(inv.z, 1e-8),
        );

        let initialPos = initialPose[initialBase].xyz;
        let initialQ = normalize(initialPose[initialBase + 1u]);
        let inertialPos = inertialPose[poseBase].xyz;
        let inertialQ = normalize(inertialPose[poseBase + 1u]);

        let dqConstraintRaw = qmul(q, qconj(initialQ));
        let dqConstraint = select(dqConstraintRaw, -dqConstraintRaw, dqConstraintRaw.w < 0.0);
        let dThetaConstraint = 2.0 * dqConstraint.xyz;
        let dqInertialRaw = qmul(q, qconj(inertialQ));
        let dqInertial = select(dqInertialRaw, -dqInertialRaw, dqInertialRaw.w < 0.0);
        let dThetaInertial = 2.0 * dqInertial.xyz;
        let dPosInertial = pos - inertialPos;
        let dPosConstraint = pos - initialPos;

        var A: array<array<f32, 6>, 6>;
        var b: array<f32, 6>;
        for (var r = 0u; r < 6u; r++) {
          b[r] = 0.0;
          for (var c = 0u; c < 6u; c++) {
            A[r][c] = 0.0;
          }
        }

        let invDt2 = 1.0 / max(dt * dt, 1e-8);
        let mass = 1.0 / max(invMass, 1e-8);
        let mOverDt2 = inertialDiagWeight * invDt2;
        let localIx = 1.0 / localInvI.x;
        let localIy = 1.0 / localInvI.y;
        let localIz = 1.0 / localInvI.z;
        let i00 = localIx;
        let i11 = localIy;
        let i22 = localIz;
        let i01 = 0.0;
        let i02 = 0.0;
        let i12 = 0.0;

        let linearInertialDiag = mOverDt2 * mass;
        let angularInertialDiag = mOverDt2 * vec3f(i00, i11, i22);
        let linearInertialShift = abs(linearInertialDiag) * ${AVBD_RELATIVE_INERTIAL_SHIFT};
        let angularInertialScale = max(
          abs(angularInertialDiag.x),
          max(abs(angularInertialDiag.y), abs(angularInertialDiag.z)),
        );
        let angularInertialShift = angularInertialScale * ${AVBD_RELATIVE_INERTIAL_SHIFT};
        A[0][0] = linearInertialDiag + linearInertialShift;
        A[1][1] = linearInertialDiag + linearInertialShift;
        A[2][2] = linearInertialDiag + linearInertialShift;
        A[3][3] = angularInertialDiag.x + angularInertialShift;
        A[4][4] = angularInertialDiag.y + angularInertialShift;
        A[5][5] = angularInertialDiag.z + angularInertialShift;
        b[0] = mOverDt2 * mass * dPosInertial.x;
        b[1] = mOverDt2 * mass * dPosInertial.y;
        b[2] = mOverDt2 * mass * dPosInertial.z;
        b[3] = mOverDt2 * (i00 * dThetaInertial.x);
        b[4] = mOverDt2 * (i11 * dThetaInertial.y);
        b[5] = mOverDt2 * (i22 * dThetaInertial.z);
`
        : /* wgsl */`
        let inv = inverseInertia[gid];
        let invMass = inv.w;
        let initialBase = gid * 2u;
        let poseBase = gid * ${INERTIAL_POSE_VEC4S_PER_BODY}u;
        let currentPose = inertialPose[poseBase + 2u];
        var pos = currentPose.xyz;
        var q = normalize(inertialPose[poseBase + 3u]);
        if (!(invMass > 0.0)) {
${staticPoseOutput}
          return;
        }
        let localInvI = vec3f(
          max(inv.x, 1e-8),
          max(inv.y, 1e-8),
          max(inv.z, 1e-8),
        );

        let initialPos = initialPose[initialBase].xyz;
        let initialQ = normalize(initialPose[initialBase + 1u]);
        let inertialPos = inertialPose[poseBase].xyz;
        let inertialQ = normalize(inertialPose[poseBase + 1u]);

        let dqConstraintRaw = qmul(q, qconj(initialQ));
        let dqConstraint = select(dqConstraintRaw, -dqConstraintRaw, dqConstraintRaw.w < 0.0);
        let dThetaConstraint = 2.0 * dqConstraint.xyz;
        let dqInertialRaw = qmul(q, qconj(inertialQ));
        let dqInertial = select(dqInertialRaw, -dqInertialRaw, dqInertialRaw.w < 0.0);
        let dThetaInertial = 2.0 * dqInertial.xyz;
        let dPosInertial = pos - inertialPos;
        let dPosConstraint = pos - initialPos;

        var A: array<array<f32, 6>, 6>;
        var b: array<f32, 6>;
        for (var r = 0u; r < 6u; r++) {
          b[r] = 0.0;
          for (var c = 0u; c < 6u; c++) {
            A[r][c] = 0.0;
          }
        }

        let invDt2 = 1.0 / max(dt * dt, 1e-8);
        let mass = 1.0 / max(invMass, 1e-8);
        let mOverDt2 = inertialDiagWeight * invDt2;

        // Equation 20: rotate the rest-frame moment tensor with the current
        // primal quaternion. Building I_world directly here is both more exact
        // and cheaper than submitting a separate derived-inertia pass that can
        // only observe an earlier iterate.
        let localIx = 1.0 / localInvI.x;
        let localIy = 1.0 / localInvI.y;
        let localIz = 1.0 / localInvI.z;
        let axis0 = qrot(q, vec3f(1.0, 0.0, 0.0));
        let axis1 = qrot(q, vec3f(0.0, 1.0, 0.0));
        let axis2 = qrot(q, vec3f(0.0, 0.0, 1.0));
        let i00 = max(localIx * axis0.x * axis0.x + localIy * axis1.x * axis1.x + localIz * axis2.x * axis2.x, 1e-8);
        let i11 = max(localIx * axis0.y * axis0.y + localIy * axis1.y * axis1.y + localIz * axis2.y * axis2.y, 1e-8);
        let i22 = max(localIx * axis0.z * axis0.z + localIy * axis1.z * axis1.z + localIz * axis2.z * axis2.z, 1e-8);
        let i01 = localIx * axis0.x * axis0.y + localIy * axis1.x * axis1.y + localIz * axis2.x * axis2.y;
        let i02 = localIx * axis0.x * axis0.z + localIy * axis1.x * axis1.z + localIz * axis2.x * axis2.z;
        let i12 = localIx * axis0.y * axis0.z + localIy * axis1.y * axis1.z + localIz * axis2.y * axis2.z;

        let linearInertialDiag = mOverDt2 * mass;
        let angularInertial00 = mOverDt2 * i00;
        let angularInertial11 = mOverDt2 * i11;
        let angularInertial22 = mOverDt2 * i22;
        let angularInertial01 = mOverDt2 * i01;
        let angularInertial02 = mOverDt2 * i02;
        let angularInertial12 = mOverDt2 * i12;
        let linearInertialShift = abs(linearInertialDiag) * ${AVBD_RELATIVE_INERTIAL_SHIFT};
        let angularInertialScale = max(
          max(abs(angularInertial00), max(abs(angularInertial11), abs(angularInertial22))),
          max(abs(angularInertial01), max(abs(angularInertial02), abs(angularInertial12))),
        );
        let angularInertialShift = angularInertialScale * ${AVBD_RELATIVE_INERTIAL_SHIFT};
        A[0][0] = linearInertialDiag + linearInertialShift;
        A[1][1] = linearInertialDiag + linearInertialShift;
        A[2][2] = linearInertialDiag + linearInertialShift;
        A[3][3] = angularInertial00 + angularInertialShift;
        A[4][4] = angularInertial11 + angularInertialShift;
        A[5][5] = angularInertial22 + angularInertialShift;
        A[3][4] = angularInertial01;
        A[4][3] = A[3][4];
        A[3][5] = angularInertial02;
        A[5][3] = A[3][5];
        A[4][5] = angularInertial12;
        A[5][4] = A[4][5];
        b[0] = mOverDt2 * mass * dPosInertial.x;
        b[1] = mOverDt2 * mass * dPosInertial.y;
        b[2] = mOverDt2 * mass * dPosInertial.z;
        b[3] = mOverDt2 * (i00 * dThetaInertial.x + i01 * dThetaInertial.y + i02 * dThetaInertial.z);
        b[4] = mOverDt2 * (i01 * dThetaInertial.x + i11 * dThetaInertial.y + i12 * dThetaInertial.z);
        b[5] = mOverDt2 * (i02 * dThetaInertial.x + i12 * dThetaInertial.y + i22 * dThetaInertial.z);
`;

      // The public-reference contact hot path carries only the 21 independent
      // lower-triangle entries.  Keep every degree of freedom as a named f32
      // scalar so Dawn cannot lower dynamic matrix indexing to private-memory
      // loads/stores in the innermost body solve.
      const referencePackedInertiaSetup = /* wgsl */`
        let inv = inverseInertia[gid];
        let invMass = inv.w;
        let initialBase = gid * 2u;
        let poseBase = gid * ${INERTIAL_POSE_VEC4S_PER_BODY}u;
        let currentPose = inertialPose[poseBase + 2u];
        var pos = currentPose.xyz;
        var q = normalize(inertialPose[poseBase + 3u]);
${cooperativeBodySolve ? '' : /* wgsl */`
        if (!(invMass > 0.0)) {
${staticPoseOutput}
          return;
        }
`}
        let localInvI = vec3f(
          max(inv.x, 1e-8),
          max(inv.y, 1e-8),
          max(inv.z, 1e-8),
        );

        let initialPos = initialPose[initialBase].xyz;
        let initialQ = normalize(initialPose[initialBase + 1u]);
        let inertialPos = inertialPose[poseBase].xyz;
        let inertialQ = normalize(inertialPose[poseBase + 1u]);

        let dqConstraintRaw = qmul(q, qconj(initialQ));
        let dqConstraint = select(dqConstraintRaw, -dqConstraintRaw, dqConstraintRaw.w < 0.0);
        let dThetaConstraint = 2.0 * dqConstraint.xyz;
        let dqInertialRaw = qmul(q, qconj(inertialQ));
        let dqInertial = select(dqInertialRaw, -dqInertialRaw, dqInertialRaw.w < 0.0);
        let dThetaInertial = 2.0 * dqInertial.xyz;
        let dPosInertial = pos - inertialPos;
        let dPosConstraint = pos - initialPos;

        let invDt2 = 1.0 / max(dt * dt, 1e-8);
        let mass = 1.0 / max(invMass, 1e-8);
        let mOverDt2 = inertialDiagWeight * invDt2;

        let localIx = 1.0 / localInvI.x;
        let localIy = 1.0 / localInvI.y;
        let localIz = 1.0 / localInvI.z;
        let axis0 = qrot(q, vec3f(1.0, 0.0, 0.0));
        let axis1 = qrot(q, vec3f(0.0, 1.0, 0.0));
        let axis2 = qrot(q, vec3f(0.0, 0.0, 1.0));
        let i00 = max(localIx * axis0.x * axis0.x + localIy * axis1.x * axis1.x + localIz * axis2.x * axis2.x, 1e-8);
        let i11 = max(localIx * axis0.y * axis0.y + localIy * axis1.y * axis1.y + localIz * axis2.y * axis2.y, 1e-8);
        let i22 = max(localIx * axis0.z * axis0.z + localIy * axis1.z * axis1.z + localIz * axis2.z * axis2.z, 1e-8);
        let i01 = localIx * axis0.x * axis0.y + localIy * axis1.x * axis1.y + localIz * axis2.x * axis2.y;
        let i02 = localIx * axis0.x * axis0.z + localIy * axis1.x * axis1.z + localIz * axis2.x * axis2.z;
        let i12 = localIx * axis0.y * axis0.z + localIy * axis1.y * axis1.z + localIz * axis2.y * axis2.z;

        let linearInertialDiag = mOverDt2 * mass;
        let angularInertial00 = mOverDt2 * i00;
        let angularInertial11 = mOverDt2 * i11;
        let angularInertial22 = mOverDt2 * i22;
        let angularInertial01 = mOverDt2 * i01;
        let angularInertial02 = mOverDt2 * i02;
        let angularInertial12 = mOverDt2 * i12;
        let linearInertialShift = abs(linearInertialDiag) * ${AVBD_RELATIVE_INERTIAL_SHIFT};
        let angularInertialScale = max(
          max(abs(angularInertial00), max(abs(angularInertial11), abs(angularInertial22))),
          max(abs(angularInertial01), max(abs(angularInertial02), abs(angularInertial12))),
        );
        let angularInertialShift = angularInertialScale * ${AVBD_RELATIVE_INERTIAL_SHIFT};

        var a00 = linearInertialDiag + linearInertialShift;
        var a10 = 0.0;
        var a11 = linearInertialDiag + linearInertialShift;
        var a20 = 0.0;
        var a21 = 0.0;
        var a22 = linearInertialDiag + linearInertialShift;
        var a30 = 0.0;
        var a31 = 0.0;
        var a32 = 0.0;
        var a33 = angularInertial00 + angularInertialShift;
        var a40 = 0.0;
        var a41 = 0.0;
        var a42 = 0.0;
        var a43 = angularInertial01;
        var a44 = angularInertial11 + angularInertialShift;
        var a50 = 0.0;
        var a51 = 0.0;
        var a52 = 0.0;
        var a53 = angularInertial02;
        var a54 = angularInertial12;
        var a55 = angularInertial22 + angularInertialShift;
        var b0 = mOverDt2 * mass * dPosInertial.x;
        var b1 = mOverDt2 * mass * dPosInertial.y;
        var b2 = mOverDt2 * mass * dPosInertial.z;
        var b3 = mOverDt2 * (i00 * dThetaInertial.x + i01 * dThetaInertial.y + i02 * dThetaInertial.z);
        var b4 = mOverDt2 * (i01 * dThetaInertial.x + i11 * dThetaInertial.y + i12 * dThetaInertial.z);
        var b5 = mOverDt2 * (i02 * dThetaInertial.x + i12 * dThetaInertial.y + i22 * dThetaInertial.z);
`;

      // This is deliberately a source-code flavor, not a uniform-driven mode.
      // The reference pipeline therefore has no serial/fallback selectors and
      // exposes only values that genuinely vary between dispatches.
      const solverUniformParams = referenceContactStrict
        ? /* wgsl */`        bodyCount: u32,
        currentColor: u32,
        sweepOffset: u32,
        dt: f32,
${adaptiveResidualTracking ? /* wgsl */`        adaptiveLinearTolerance: f32,
        adaptiveAngularTolerance: f32,
` : ''}
`
        : /* wgsl */`        bodyCount: u32,
        bodyIndexBase: u32,
        dispatchBodyCount: u32,
        bodySolveMode: u32,
        currentColor: u32,
        fallbackColorIndex: u32,
        sweepOffset: u32,
        regularizationAlpha: f32,
        tangentialRegularizationAlpha: f32,
        relaxation: f32,
        frictionRelaxation: f32,
        frictionStatic: f32,
        frictionDynamic: f32,
        useReferenceTangentialUpdate: u32,
        frictionSolveScale: f32,
        kStart: f32,
        dualForceMax: f32,
        enableNormalReleaseHeuristic: u32,
        enableHessianRescaling: u32,
        dt: f32,
        inertialDiagWeight: f32,
        maxLinearCorrection: f32,
        maxAngularCorrection: f32,
${scratchAdaptiveResidualUniformParams}${localDiagonalUniformParam}        useNormalContactMargin: u32,
        alwaysStampNormalHessian: u32,
        alwaysStampTangentialHessian: u32,
`;
      const bodySelection = cooperativeBodySolve
        ? /* wgsl */`
        // Exact cooperative dispatch: one workgroup owns one compact body.
        // The GPU-built indirect x count equals this color's bucket count, so
        // every lane can reach the shared-memory barriers without a bounds
        // return or a partially active workgroup.
        let bucketOffset = bodyColorCompact[${bodyColorOffsetOffset}u + currentColor];
        let gid = bodyColorCompact[bucketOffset + workgroupId.x];
        let countWord = bodyConstraintCounts[gid];
        var contactCount = countWord & 0xFFFFu;
        let bodySolveOutputBase = gid * 3u;
`
        : referenceContactStrict
        ? /* wgsl */`
        let localIndex = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        let bucketCount = bodyColorCompact[${bodyColorCountOffset}u + currentColor];
        if (localIndex >= bucketCount) { return; }
        let bucketOffset = bodyColorCompact[${bodyColorOffsetOffset}u + currentColor];
        let gid = bodyColorCompact[bucketOffset + localIndex];
        if (gid >= bodyCount) { return; }
        let countWord = bodyConstraintCounts[gid];
        let contactCount = countWord & 0xFFFFu;
        let bodySolveOutputBase = gid * 3u;
`
        : /* wgsl */`
        let localIndex = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        var gid = 0u;
        if (bodySolveMode == 0u) {
          let bucketCount = bodyColorCompact[${bodyColorCountOffset}u + currentColor];
          if (localIndex >= bucketCount) { return; }
          let bucketOffset = bodyColorCompact[${bodyColorOffsetOffset}u + currentColor];
          gid = bodyColorCompact[bucketOffset + localIndex];
        } else {
          if (localIndex >= dispatchBodyCount) { return; }
          gid = bodyIndexBase + localIndex;
        }
        if (gid >= bodyCount) { return; }

        let countWord = bodyConstraintCounts[gid];
        let bodyColor = (countWord >> 16u) & 0xFFu;
        if (bodySolveMode == 0u) {
          let fallback = (countWord & ${BODY_COLOR_FALLBACK_FLAG}u) != 0u;
          let fallbackPass = currentColor == fallbackColorIndex;
          if (fallbackPass != fallback) { return; }
          if (!fallbackPass && bodyColor != currentColor) { return; }
        }
        let contactCount = countWord & 0xFFFFu;
        let bodySolveOutputBase = gid * 3u;
`;
      const referenceSolverConstants = referenceContactStrict
        ? /* wgsl */`
        // Exact public reference profile constants. Keeping alpha as f32 and
        // evaluating (1-alpha) below preserves the generic shader's rounding.
        let regularizationAlpha: f32 = ${AVBD_REFERENCE_REGULARIZATION_ALPHA};
        let tangentialRegularizationAlpha: f32 = ${AVBD_REFERENCE_REGULARIZATION_ALPHA};
        let relaxation: f32 = 1.0;
        let inertialDiagWeight: f32 = 1.0;
        let maxLinearCorrection: f32 = 1000000000.0;
        let maxAngularCorrection: f32 = 1000000000.0;
        let kStart: f32 = ${AVBD_K_START};
        let dualForceMax: f32 = 10000000000.0;
`
        : '';
      const contactStateSource = referenceContactStrict
        ? /* wgsl */`
${cachedConstraintC0Source}
          let c0 = cachedConstraintC0.x;
          let cRegN = contactConstraintC0Scale(encodedContactInfo, regularizationAlpha) * c0
            + dot(jAL, dPosA)
            + dot(jAA, dThetaA)
            + dot(jBL, dPosB)
            + dot(jBA, dThetaB);
          let c0T1 = cachedConstraintC0.y;
          let c0T2 = cachedConstraintC0.z;

          // Reference capture stores the two tangent multipliers directly in
          // dual.yz and mirrors the material coefficient in penalty.w.
          let dualState = ${useSolverHotState
    ? 'loadSolverContactDual(contactSolverHot, p)'
    : 'loadContactDual(pairContacts, p)'};
          let contactPenalty = ${useSolverHotState
    ? 'loadSolverContactPenalty(contactSolverHot, p)'
    : 'loadContactPenalty(pairContacts, p)'};
          let dualN = clamp(dualState.x, -dualForceMax, 0.0);
          let penaltyN = max(contactPenalty.x, kStart);
          let penaltyTB = max(contactPenalty.yz, vec2f(kStart));
          let contactFrictionScale = max(contactPenalty.w, 0.0);
          let dualTB = dualState.yz;
`
        : /* wgsl */`
          let penetration0 = max(loadContactNormalPen(pairContacts, p).w, 0.0);
${cachedConstraintC0Source}
          let c0 = cachedConstraintC0.x;
          let cRegN = contactConstraintC0Scale(encodedContactInfo, regularizationAlpha) * c0
            + dot(jAL, dPosA)
            + dot(jAA, dThetaA)
            + dot(jBL, dPosB)
            + dot(jBA, dThetaB);
          let c0T1 = cachedConstraintC0.y;
          let c0T2 = cachedConstraintC0.z;

${useSolverHotState ? /* wgsl */`
          let solverContactPenalty = loadSolverContactPenalty(contactSolverHot, p);
          let contactState = makeContactState(
            loadSolverContactDual(contactSolverHot, p),
            solverContactPenalty,
            max(solverContactPenalty.w, 0.0),
            kStart,
          );
` : /* wgsl */`
          let contactState = loadContactStateHot(pairContacts, p, kStart);
`}
          let dualState = contactState.dual;
          let dualN = clamp(dualState.x, -dualForceMax, 0.0);
          let penaltyN = max(contactState.penaltyN, kStart);
          let penaltyTBRaw = max(contactState.penalty.yz, vec2f(kStart));
          let contactFrictionScale = contactState.frictionScale;
          let frictionStaticLocal = frictionStatic * contactFrictionScale * frictionSolveScale;
          let frictionDynamicLocal = frictionDynamic * contactFrictionScale * frictionSolveScale;
          let useReferenceTangential = useReferenceTangentialUpdate > 0u;
          let penaltyTB = penaltyTBRaw;
          var dualTB = contactStateDualTB(
            contactState,
            n,
            t1,
            t2,
            select(0u, 1u, useReferenceTangential),
          );
          let prevTMag = length(dualTB);
          let staticBoundPrev = frictionStaticLocal * abs(dualN);
          let useStatic = prevTMag <= staticBoundPrev + 1e-6;
`;
      const referenceNormalRankOneStamp = makePackedLdl6RankOneStampWGSL({
        row: [
          'jNLinear.x', 'jNLinear.y', 'jNLinear.z',
          'jNAngular.x', 'jNAngular.y', 'jNAngular.z',
        ],
        stiffness: 'penaltyN',
        force: 'lambdaAppliedN',
        indent: '          ',
      });
      const referenceTangent1RankOneStamp = makePackedLdl6RankOneStampWGSL({
        row: [
          'jT1Linear.x', 'jT1Linear.y', 'jT1Linear.z',
          'jT1Angular.x', 'jT1Angular.y', 'jT1Angular.z',
        ],
        stiffness: 'penaltyTB.x',
        force: 'lambdaAppliedTB.x',
        indent: '          ',
      });
      const referenceTangent2RankOneStamp = makePackedLdl6RankOneStampWGSL({
        row: [
          'jT2Linear.x', 'jT2Linear.y', 'jT2Linear.z',
          'jT2Angular.x', 'jT2Angular.y', 'jT2Angular.z',
        ],
        stiffness: 'penaltyTB.y',
        force: 'lambdaAppliedTB.y',
        indent: '          ',
      });
      const referencePackedSolveSource = makePackedModifiedLdl6SolveWGSL({
        relativePivot: `${AVBD_RELATIVE_LDL_PIVOT}`,
        indent: '        ',
      });
      const normalSolveSource = referenceContactStrict
        ? /* wgsl */`
          let lambdaPlusN = dualN + penaltyN * cRegN;
          let lambdaAppliedN = clamp(lambdaPlusN, -dualForceMax, 0.0);
${referenceNormalRankOneStamp}
`
        : /* wgsl */`
          let lambdaLower = -dualForceMax;
          let lambdaPlusN = dualN + penaltyN * cRegN;
          var lambdaAppliedN = clamp(lambdaPlusN, lambdaLower, 0.0);
          let normalSupportThreshold = 1e-6;
          let normalReleaseTolerance = max(2e-5, 1.25 * penetration0);
          let normalReleaseDecayNear = 0.85;
          let normalReleaseDecayFar = 0.6;
          let normalReleaseMinSupport = -5e-4;
          if (
            enableNormalReleaseHeuristic > 0u &&
            penetration0 > 1e-6
            && dualN < -normalSupportThreshold
            && cRegN > 0.0
            && -lambdaAppliedN <= normalSupportThreshold
          ) {
            let releaseDecay = select(normalReleaseDecayFar, normalReleaseDecayNear, cRegN <= normalReleaseTolerance);
            lambdaAppliedN = min(dualN * releaseDecay, normalReleaseMinSupport);
          }
          let frictionSeparationTolerance = normalReleaseTolerance;
          let frictionOffSeparation = -lambdaAppliedN <= normalSupportThreshold
            || (enableNormalReleaseHeuristic > 0u && cRegN > frictionSeparationTolerance);
          if (lambdaAppliedN < -1e-9 || alwaysStampNormalHessian > 0u) {
            var kHessN = penaltyN;
            if (
              lambdaAppliedN < -1e-9 &&
              enableHessianRescaling > 0u
              && abs(lambdaAppliedN - lambdaPlusN) > 1e-8
              && abs(cRegN) > 1e-8
            ) {
              // Eq.14-style stiffness rescaling for clamped multipliers.
              kHessN = max(abs((lambdaAppliedN - dualN) / cRegN), 1e-6);
            }
            var jNRhs: array<f32, 6>;
            jNRhs[0] = jNLinear.x; jNRhs[1] = jNLinear.y; jNRhs[2] = jNLinear.z;
            jNRhs[3] = jNAngular.x; jNRhs[4] = jNAngular.y; jNRhs[5] = jNAngular.z;
            var jNHess: array<f32, 6>;
            jNHess[0] = jNLinear.x; jNHess[1] = jNLinear.y; jNHess[2] = jNLinear.z;
            jNHess[3] = jNAngular.x; jNHess[4] = jNAngular.y; jNHess[5] = jNAngular.z;
            for (var r = 0u; r < 6u; r++) {
              b[r] += jNRhs[r] * lambdaAppliedN;
              for (var c = 0u; c < 6u; c++) {
                // Use the packed lower-triangle operand order for both
                // symmetric entries. k*Jr*Jc and k*Jc*Jr are algebraically
                // equal but can round differently in f32; averaging those two
                // histories made the generic hard-capable path drift from the
                // reference packed solve even with zero live hard rows.
                let canonicalRow = max(r, c);
                let canonicalColumn = min(r, c);
                A[r][c] += kHessN
                  * jNHess[canonicalRow]
                  * jNHess[canonicalColumn];
              }
            }
          }
`;
      const tangentialSolveSource = referenceContactStrict
        ? /* wgsl */`
          if (!speculativeContact) {
          // Public reference: always stamp both tangential K rows, while the
          // conical projection clamps only their augmented-force RHS.
          let jt1AL = -t1;
          let jt1BL = t1;
          let jt1AA = -cross(ra, t1);
          let jt1BA = cross(rb, t1);
          let cRegT1 = contactConstraintC0Scale(encodedContactInfo, tangentialRegularizationAlpha) * c0T1
            + dot(jt1AL, dPosA)
            + dot(jt1AA, dThetaA)
            + dot(jt1BL, dPosB)
            + dot(jt1BA, dThetaB);
          let jT1Linear = sign * t1;
          let jT1Angular = select(jt1BA, jt1AA, isA);

          let jt2AL = -t2;
          let jt2BL = t2;
          let jt2AA = -cross(ra, t2);
          let jt2BA = cross(rb, t2);
          let cRegT2 = contactConstraintC0Scale(encodedContactInfo, tangentialRegularizationAlpha) * c0T2
            + dot(jt2AL, dPosA)
            + dot(jt2AA, dThetaA)
            + dot(jt2BL, dPosB)
            + dot(jt2BA, dThetaB);
          let lambdaPlusTB = vec2f(
            penaltyTB.x * cRegT1 + dualTB.x,
            penaltyTB.y * cRegT2 + dualTB.y,
          );
          var lambdaAppliedTB = lambdaPlusTB;
          let referenceBound = contactFrictionScale * max(-lambdaAppliedN, 0.0);
          let lambdaAppliedTBLen2 = dot(lambdaAppliedTB, lambdaAppliedTB);
          if (lambdaAppliedTBLen2 > referenceBound * referenceBound && lambdaAppliedTBLen2 > 1e-12) {
            lambdaAppliedTB *= referenceBound * inverseSqrt(lambdaAppliedTBLen2);
          }
          if (referenceBound <= 1e-6) {
            lambdaAppliedTB = vec2f(0.0);
          }

          let jT2Linear = sign * t2;
          let jT2Angular = select(jt2BA, jt2AA, isA);
${referenceTangent1RankOneStamp}
${referenceTangent2RankOneStamp}
          }
`
        : /* wgsl */`
          // The public 3D reference stamps the unclamped K Hessian for all
          // three contact axes. Keep the tangential block even when the force
          // projects to zero; only its RHS is cone-clamped.
          if (
            !speculativeContact
            && (alwaysStampTangentialHessian > 0u
              || frictionStaticLocal > 1e-8
              || frictionDynamicLocal > 1e-8)
          ) {
            let stampTangentialOnSeparating = frictionOffSeparation && alwaysStampTangentialHessian > 0u;
            // Default AVBD friction policy skips tangential rows when the
            // contact is separating. The full-hessian experiment keeps the
            // tangential stiffness in A while still clamping the RHS to zero.
            if (!useReferenceTangential && frictionOffSeparation && !stampTangentialOnSeparating) {
              continue;
            }
            if (useReferenceTangential && lambdaAppliedN >= -1e-9 && alwaysStampTangentialHessian == 0u) {
              continue;
            }
            let jt1AL = -t1;
            let jt1BL = t1;
            let jt1AA = -cross(ra, t1);
            let jt1BA = cross(rb, t1);
            let cRegT1 = contactConstraintC0Scale(encodedContactInfo, tangentialRegularizationAlpha) * c0T1
              + dot(jt1AL, dPosA)
              + dot(jt1AA, dThetaA)
              + dot(jt1BL, dPosB)
              + dot(jt1BA, dThetaB);
            let jT1Linear = sign * t1;
            let jT1Angular = select(jt1BA, jt1AA, isA);
            var jT1: array<f32, 6>;
            jT1[0] = jT1Linear.x; jT1[1] = jT1Linear.y; jT1[2] = jT1Linear.z;
            jT1[3] = jT1Angular.x; jT1[4] = jT1Angular.y; jT1[5] = jT1Angular.z;

            let jt2AL = -t2;
            let jt2BL = t2;
            let jt2AA = -cross(ra, t2);
            let jt2BA = cross(rb, t2);
            let cRegT2 = contactConstraintC0Scale(encodedContactInfo, tangentialRegularizationAlpha) * c0T2
              + dot(jt2AL, dPosA)
              + dot(jt2AA, dThetaA)
              + dot(jt2BL, dPosB)
              + dot(jt2BA, dThetaB);
            // Couple tangential rows with a conical clamp in (t1,t2) space.
            // Independent axis clamps allow sqrt(2) overshoot and can cause
            // lateral breakout in stacked contacts.
            let lambdaPlusTB = vec2f(
              penaltyTB.x * cRegT1 + dualTB.x,
              penaltyTB.y * cRegT2 + dualTB.y,
            );
            var lambdaAppliedTB = lambdaPlusTB;
            var wT1 = penaltyTB.x * frictionRelaxation;
            var wT2 = penaltyTB.y * frictionRelaxation;
            if (useReferenceTangential) {
              let referenceBound = frictionStaticLocal * max(-lambdaAppliedN, 0.0);
              let lambdaAppliedTBLen2 = dot(lambdaAppliedTB, lambdaAppliedTB);
              if (lambdaAppliedTBLen2 > referenceBound * referenceBound && lambdaAppliedTBLen2 > 1e-12) {
                lambdaAppliedTB *= referenceBound * inverseSqrt(lambdaAppliedTBLen2);
              }
              if (referenceBound <= 1e-6) {
                lambdaAppliedTB = vec2f(0.0);
              }
            } else {
              // AVBD friction policy:
              // start in static mode only when previous dual was inside static cone.
              // if this iteration violates static cone, switch to dynamic cone.
              // In the primal stage, keep tangential bounds on the previous normal
              // dual state for this contact iteration. This matches the 2D reference
              // structure where tangential bounds are derived from persisted lambda_n
              // (updated in dual/capture), not from the row-local normal projection.
              let frictionSupportN = select(max(dualN, lambdaAppliedN), 0.0, stampTangentialOnSeparating);
              let staticBound = frictionStaticLocal * max(-frictionSupportN, 0.0);
              let dynamicBound = frictionDynamicLocal * max(-frictionSupportN, 0.0);
              let lambdaPlusTBlen2 = dot(lambdaAppliedTB, lambdaAppliedTB);
              var frictionBound = select(dynamicBound, staticBound, useStatic);
              if (useStatic && lambdaPlusTBlen2 > staticBound * staticBound + 1e-12) {
                frictionBound = dynamicBound;
              }
              frictionBound = max(frictionBound, 0.0);

              let lambdaAppliedTBLen2 = dot(lambdaAppliedTB, lambdaAppliedTB);
              if (lambdaAppliedTBLen2 > frictionBound * frictionBound && lambdaAppliedTBLen2 > 1e-12) {
                lambdaAppliedTB *= frictionBound * inverseSqrt(lambdaAppliedTBLen2);
              }
              // Eq.14-style tangential stiffness rescaling when cone projection
              // clamps the unconstrained lambda update.
              if (
                !stampTangentialOnSeparating &&
                enableHessianRescaling > 0u
                && abs(lambdaAppliedTB.x - lambdaPlusTB.x) > 1e-8
                && abs(cRegT1) > 1e-8
              ) {
                wT1 = max(abs((lambdaAppliedTB.x - dualTB.x) / cRegT1), 1e-6) * frictionRelaxation;
              }
              if (
                !stampTangentialOnSeparating &&
                enableHessianRescaling > 0u
                && abs(lambdaAppliedTB.y - lambdaPlusTB.y) > 1e-8
                && abs(cRegT2) > 1e-8
              ) {
                wT2 = max(abs((lambdaAppliedTB.y - dualTB.y) / cRegT2), 1e-6) * frictionRelaxation;
              }
            }
            let lambdaAppliedT1 = lambdaAppliedTB.x;
            let lambdaAppliedT2 = lambdaAppliedTB.y;

            let jT2Linear = sign * t2;
            let jT2Angular = select(jt2BA, jt2AA, isA);
            var jT2: array<f32, 6>;
            jT2[0] = jT2Linear.x; jT2[1] = jT2Linear.y; jT2[2] = jT2Linear.z;
            jT2[3] = jT2Angular.x; jT2[4] = jT2Angular.y; jT2[5] = jT2Angular.z;
            for (var r = 0u; r < 6u; r++) {
              b[r] += jT1[r] * lambdaAppliedT1;
              for (var c = 0u; c < 6u; c++) {
                let canonicalRow = max(r, c);
                let canonicalColumn = min(r, c);
                A[r][c] += wT1
                  * jT1[canonicalRow]
                  * jT1[canonicalColumn];
              }
            }
            for (var r = 0u; r < 6u; r++) {
              b[r] += jT2[r] * lambdaAppliedT2;
              for (var c = 0u; c < 6u; c++) {
                let canonicalRow = max(r, c);
                let canonicalColumn = min(r, c);
                A[r][c] += wT2
                  * jT2[canonicalRow]
                  * jT2[canonicalColumn];
              }
            }
          }
`;

      const cooperativePackedVec4s = [
        ['a00', 'a10', 'a11', 'a20'],
        ['a21', 'a22', 'a30', 'a31'],
        ['a32', 'a33', 'a40', 'a41'],
        ['a42', 'a43', 'a44', 'a50'],
        ['a51', 'a52', 'a53', 'a54'],
        ['a55', 'b0', 'b1', 'b2'],
        ['b3', 'b4', 'b5', '0.0'],
      ] as const;
      const cooperativePackedNames = cooperativePackedVec4s
        .flat()
        .filter((name) => name !== '0.0');
      const cooperativeSharedSystemParam = cooperativeBodySolve
        ? `        cooperativePartials: ptr<workgroup, array<vec4f, ${COOPERATIVE_PACKED_SYSTEM_SHARED_VEC4S}>>,\n`
        : '';
      const cooperativePostInertiaSource = cooperativeBodySolve
        ? /* wgsl */`
        if (!(invMass > 0.0)) {
          contactCount = 0u;
        }
        if (localId.x != 0u) {
${cooperativePackedNames.map((name) => `          ${name} = 0.0;`).join('\n')}
        }
`
        : '';
      const cooperativeContactLoopHeader = cooperativeBodySolve
        ? /* wgsl */`
        var contactLoopStart = localId.x;
        var contactLoopStride = ${COOPERATIVE_BODY_SOLVE_LANES}u;
        if (contactCount > ${maxConstraintsPerBody}u) {
          // Overflow refs form a forward-only linked stream. Preserve the
          // exact scalar traversal for this rare high-degree body until the
          // manifold CSR path replaces the spill chain.
          contactLoopStart = select(contactCount, 0u, localId.x == 0u);
          contactLoopStride = 1u;
        }
        for (var k = contactLoopStart; k < contactCount; k += contactLoopStride) {
`
        : /* wgsl */`
        for (var k = 0u; k < contactCount; k++) {
`;
      const cooperativeSharedStores = cooperativePackedVec4s
        .map((components, index) =>
          `        cooperativePartials[cooperativePartialBase + ${index}u] = vec4f(${components.join(', ')});`)
        .join('\n');
      const cooperativeSharedUnpack = cooperativePackedVec4s
        .map((components, index) => {
          const lanes = ['x', 'y', 'z', 'w'];
          return components
            .map((name, lane) => name === '0.0'
              ? ''
              : `        ${name} = cooperativePartials[${index}u].${lanes[lane]};`)
            .filter(Boolean)
            .join('\n');
        })
        .join('\n');
      const cooperativePackedReductionSource = cooperativeBodySolve
        ? /* wgsl */`
        let cooperativePartialBase = localId.x * ${COOPERATIVE_PACKED_SYSTEM_VEC4S}u;
${cooperativeSharedStores}
        workgroupBarrier();
        if (localId.x < 8u) {
          let otherBase = (localId.x + 8u) * ${COOPERATIVE_PACKED_SYSTEM_VEC4S}u;
          for (var component = 0u; component < ${COOPERATIVE_PACKED_SYSTEM_VEC4S}u; component++) {
            cooperativePartials[cooperativePartialBase + component] +=
              cooperativePartials[otherBase + component];
          }
        }
        workgroupBarrier();
        if (localId.x < 4u) {
          let otherBase = (localId.x + 4u) * ${COOPERATIVE_PACKED_SYSTEM_VEC4S}u;
          for (var component = 0u; component < ${COOPERATIVE_PACKED_SYSTEM_VEC4S}u; component++) {
            cooperativePartials[cooperativePartialBase + component] +=
              cooperativePartials[otherBase + component];
          }
        }
        workgroupBarrier();
        if (localId.x < 2u) {
          let otherBase = (localId.x + 2u) * ${COOPERATIVE_PACKED_SYSTEM_VEC4S}u;
          for (var component = 0u; component < ${COOPERATIVE_PACKED_SYSTEM_VEC4S}u; component++) {
            cooperativePartials[cooperativePartialBase + component] +=
              cooperativePartials[otherBase + component];
          }
        }
        workgroupBarrier();
        if (localId.x != 0u) { return; }
        for (var component = 0u; component < ${COOPERATIVE_PACKED_SYSTEM_VEC4S}u; component++) {
          cooperativePartials[component] +=
            cooperativePartials[${COOPERATIVE_PACKED_SYSTEM_VEC4S}u + component];
        }
${cooperativeSharedUnpack}
        if (!(invMass > 0.0)) {
${staticPoseOutput}
          return;
        }
`
        : '';

      return wgslFn(/* wgsl */`
      fn compute(
        initialPose: ptr<storage, array<vec4f>, read>,
${inertialPoseParam}${bodySolveOutputParam}
${captureBodyDeltaOutputParam}
${inertiaParam}        bodyConstraintCounts: ptr<storage, array<u32>, read>,
        bodyConstraintRefs: ptr<storage, array<u32>, read>,
        bodyColorCompact: ptr<storage, array<u32>, read>,
${solverContactRecordParam}
${contactSolverHotParam}${tangentInvLengthStorageParam}${hardConstraintStorageParams}${adaptiveResidualParams}${cooperativeSharedSystemParam}
${solverUniformParams}
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
${bodySelection}
${referenceSolverConstants}

${referenceContactStrict ? referencePackedInertiaSetup : inertiaSetup}

${cooperativePostInertiaSource}
${referenceOtherBodyCacheDeclaration}
        var constraintOverflowNode = bodyConstraintRefs[
          ${bodyConstraintOverflowHeadOffset}u + gid
        ];
        // Normalize the rotating direct-list start once per body solve.  Doing
        // the dynamic u32 remainder inside the constraint loop made dense
        // piles pay for tens of millions of GPU integer divisions per step.
        // The branch/subtract below preserves the identical cyclic order.
        var directSweepOffset = 0u;
        if (contactCount > 0u && contactCount <= ${maxConstraintsPerBody}u) {
          directSweepOffset = sweepOffset % contactCount;
        }
${cooperativeContactLoopHeader}
          // Direct lists retain the rotating sweep start. Sparse spill lists
          // are streamed once from their head so high-degree bodies remain
          // O(degree) instead of restarting the chain for every logical ref.
          var contactIndex = k;
          if (contactCount <= ${maxConstraintsPerBody}u) {
            let shiftedIndex = k + directSweepOffset;
            contactIndex = select(
              shiftedIndex,
              shiftedIndex - contactCount,
              shiftedIndex >= contactCount,
            );
          }
          let constraintRef = loadBodyConstraintRef(
            bodyConstraintRefs, gid, contactIndex, &constraintOverflowNode,
          );
${constraintMode === 'contacts' ? /* wgsl */`
          // Contact-only scenes never append tagged joint/spring references.
          // Keep the unified direct/spill list, but decode its entry directly
          // as a contact index so the strict hot path carries no hard-constraint
          // branches or storage bindings. The spill helper's invalid sentinel
          // is still rejected by the maxPairContacts guard below.
          let p = constraintRef;
` : /* wgsl */`
          let constraintTag = constraintRef & ${CONSTRAINT_REF_TAG_MASK}u;
          let constraintIndex = constraintRef & ${CONSTRAINT_REF_INDEX_MASK}u;
          if (constraintTag == ${CONSTRAINT_REF_TAG_JOINT}u) {
            let jointIndex = constraintIndex;
            if (jointIndex >= ${maxJoints}u) { continue; }
            let jointMeta = loadJointMetaWords(jointRecords, jointIndex);
            if (jointMeta.w == 0u) { continue; }
            let jointBodyA = jointMeta.x;
            let jointBodyB = jointMeta.y;
            let jointType = jointMeta.z;
            let isJointA = jointBodyA == gid;
            let isJointB = jointBodyB == gid;
            if (!isJointA && !isJointB) { continue; }

            let anchorA = loadJointAnchorA(jointRecords, jointIndex);
            let anchorB = loadJointAnchorB(jointRecords, jointIndex);
            let stiffness = loadJointStiffness(jointRecords, jointIndex);
            let restRelative = loadJointRestRelativeRotation(jointRecords, jointIndex);
            let penaltyLin = loadJointPenaltyLin(jointRecords, jointIndex).xyz;
            let penaltyAng = loadJointPenaltyAng(jointRecords, jointIndex).xyz;
            let lambdaLin = loadJointLambdaLin(jointRecords, jointIndex).xyz;
            let lambdaAng = loadJointLambdaAng(jointRecords, jointIndex).xyz;
            let c0Lin = loadJointC0Lin(jointRecords, jointIndex).xyz;
            let c0Ang = loadJointC0Ang(jointRecords, jointIndex).xyz;
            let torqueArm = max(anchorA.w, 1.0);

            var currentQA = vec4f(0.0, 0.0, 0.0, 1.0);
            var currentPosA = anchorA.xyz;
            if (jointBodyA != WORLD_BODY_INDEX) {
              currentQA = normalize(select(
                inertialPose[jointBodyA * ${INERTIAL_POSE_VEC4S_PER_BODY}u + 3u],
                q,
                jointBodyA == gid,
              ));
              currentPosA = select(
                inertialPose[jointBodyA * ${INERTIAL_POSE_VEC4S_PER_BODY}u + 2u].xyz,
                pos,
                jointBodyA == gid,
              );
            }
            let currentQB = normalize(select(
              inertialPose[jointBodyB * ${INERTIAL_POSE_VEC4S_PER_BODY}u + 3u],
              q,
              jointBodyB == gid,
            ));
            let currentPosB = select(
              inertialPose[jointBodyB * ${INERTIAL_POSE_VEC4S_PER_BODY}u + 2u].xyz,
              pos,
              jointBodyB == gid,
            );
            let rA = qrot(currentQA, anchorA.xyz);
            let rB = qrot(currentQB, anchorB.xyz);
            let worldAnchorA = jointWorldAnchor(jointBodyA, anchorA.xyz, currentPosA, currentQA);
            let worldAnchorB = currentPosB + rB;
            let rigidLinear = !isFiniteF32(stiffness.x);
            let linearConstraint = (worldAnchorA - worldAnchorB) - select(vec3f(0.0), c0Lin * regularizationAlpha, rigidLinear);
            let linearForce = penaltyLin * linearConstraint + lambdaLin;

            var linearRow0 = array<f32, 6>(0.0, 0.0, 0.0, 0.0, 0.0, 0.0);
            var linearRow1 = array<f32, 6>(0.0, 0.0, 0.0, 0.0, 0.0, 0.0);
            var linearRow2 = array<f32, 6>(0.0, 0.0, 0.0, 0.0, 0.0, 0.0);
            if (isJointA) {
              linearRow0 = array<f32, 6>(1.0, 0.0, 0.0, 0.0, rA.z, -rA.y);
              linearRow1 = array<f32, 6>(0.0, 1.0, 0.0, -rA.z, 0.0, rA.x);
              linearRow2 = array<f32, 6>(0.0, 0.0, 1.0, rA.y, -rA.x, 0.0);
            } else {
              linearRow0 = array<f32, 6>(-1.0, 0.0, 0.0, 0.0, -rB.z, rB.y);
              linearRow1 = array<f32, 6>(0.0, -1.0, 0.0, rB.z, 0.0, -rB.x);
              linearRow2 = array<f32, 6>(0.0, 0.0, -1.0, -rB.y, rB.x, 0.0);
            }
            let linearSign = select(-1.0, 1.0, isJointA);
            addDiagonalVectorConstraint(&A, &b, linearRow0, linearRow1, linearRow2, penaltyLin, linearForce);
            let rGeom = select(-rB, rA, isJointA);
            let jointGeomDiag = jointBallSocketGeometricDiagonal(rGeom, linearForce);
            A[3][3] += jointGeomDiag.x;
            A[4][4] += jointGeomDiag.y;
            A[5][5] += jointGeomDiag.z;

            if (jointType == JOINT_TYPE_FIXED && stiffness.y > 0.0) {
              let rigidAngular = !isFiniteF32(stiffness.y);
              let angularConstraint = jointFixedAngularConstraint(jointBodyA, currentQA, currentQB, restRelative, torqueArm) - select(vec3f(0.0), c0Ang * regularizationAlpha, rigidAngular);
              let angularForce = penaltyAng * angularConstraint + lambdaAng;
              let angularRow0 = array<f32, 6>(0.0, 0.0, 0.0, linearSign * torqueArm, 0.0, 0.0);
              let angularRow1 = array<f32, 6>(0.0, 0.0, 0.0, 0.0, linearSign * torqueArm, 0.0);
              let angularRow2 = array<f32, 6>(0.0, 0.0, 0.0, 0.0, 0.0, linearSign * torqueArm);
              addDiagonalVectorConstraint(&A, &b, angularRow0, angularRow1, angularRow2, penaltyAng, angularForce);
            }
            continue;
          }

          if (constraintTag == ${CONSTRAINT_REF_TAG_SPRING}u) {
            let springIndex = constraintIndex;
            if (springIndex >= ${maxSprings}u) { continue; }
            let springMeta = loadSpringMetaWords(springRecords, springIndex);
            if (springMeta.z == 0u) { continue; }
            let springBodyA = springMeta.x;
            let springBodyB = springMeta.y;
            let isSpringA = springBodyA == gid;
            let isSpringB = springBodyB == gid;
            if (!isSpringA && !isSpringB) { continue; }

            let anchorARest = loadSpringAnchorARest(springRecords, springIndex);
            let anchorBStiffness = loadSpringAnchorBStiffness(springRecords, springIndex);
            let restLength = max(anchorARest.w, 0.0);
            let materialStiffness = max(anchorBStiffness.w, 0.0);
            let springState = loadSpringState(springRecords, springIndex);
            let rigidDistance = springMeta.w != 0u;
            let springPenalty = select(
              min(max(springState.x, 0.0), materialStiffness),
              max(springState.x, kStart),
              rigidDistance,
            );
            if (springPenalty <= 0.0) { continue; }

            var currentSpringQA = vec4f(0.0, 0.0, 0.0, 1.0);
            var currentSpringPosA = anchorARest.xyz;
            if (springBodyA != WORLD_BODY_INDEX) {
              currentSpringQA = normalize(select(
                inertialPose[springBodyA * ${INERTIAL_POSE_VEC4S_PER_BODY}u + 3u],
                q,
                springBodyA == gid,
              ));
              currentSpringPosA = select(
                inertialPose[springBodyA * ${INERTIAL_POSE_VEC4S_PER_BODY}u + 2u].xyz,
                pos,
                springBodyA == gid,
              );
            }
            let currentSpringQB = normalize(select(
              inertialPose[springBodyB * ${INERTIAL_POSE_VEC4S_PER_BODY}u + 3u],
              q,
              springBodyB == gid,
            ));
            let currentSpringPosB = select(
              inertialPose[springBodyB * ${INERTIAL_POSE_VEC4S_PER_BODY}u + 2u].xyz,
              pos,
              springBodyB == gid,
            );
            let rSpringA = qrot(currentSpringQA, anchorARest.xyz);
            let rSpringB = qrot(currentSpringQB, anchorBStiffness.xyz);
            let worldSpringA = jointWorldAnchor(springBodyA, anchorARest.xyz, currentSpringPosA, currentSpringQA);
            let worldSpringB = currentSpringPosB + rSpringB;
            let springDelta = worldSpringA - worldSpringB;
            let springLength = length(springDelta);
            if (springLength <= 1e-6) { continue; }
            let springNormal = springDelta / springLength;
            let rawSpringConstraint = springLength - restLength;
            // Paper Eq. 18 regularizes hard constraints against the constraint
            // value C0 captured at the beginning of this timestep. Finite
            // springs remain the unregularized Eq. 16 energy.
            let springConstraint = rawSpringConstraint - select(
              0.0,
              regularizationAlpha * springState.y,
              rigidDistance,
            );
            let springLambda = select(0.0, springState.w, rigidDistance);
            let springForce = springPenalty * springConstraint + springLambda;
            let springJLin = select(-springNormal, springNormal, isSpringA);
            let springArm = select(rSpringB, rSpringA, isSpringA);
            let springJAng = select(-cross(springArm, springNormal), cross(springArm, springNormal), isSpringA);
            let springRow = array<f32, 6>(
              springJLin.x,
              springJLin.y,
              springJLin.z,
              springJAng.x,
              springJAng.y,
              springJAng.z,
            );
            addScalarConstraint(&A, &b, springRow, springPenalty, springForce);
            // Paper Eq. 17 replaces the generally indefinite geometric term
            // force * Hessian(C) with a positive diagonal whose entries are the
            // norms of the corresponding full Hessian columns. Include the
            // translation/rotation cross block and the exact second derivative
            // of the rotated local anchor; a centre anchor therefore has zero
            // angular geometric curvature without a special case.
            let springBodySign = select(-1.0, 1.0, isSpringA);
            let geometricDiag = distanceConstraintGeometricDiagonal(
              springNormal,
              springArm,
              springLength,
              springBodySign,
              springForce,
            );
            A[0][0] += geometricDiag.linear.x;
            A[1][1] += geometricDiag.linear.y;
            A[2][2] += geometricDiag.linear.z;
            A[3][3] += geometricDiag.angular.x;
            A[4][4] += geometricDiag.angular.y;
            A[5][5] += geometricDiag.angular.z;
            continue;
          }

          let p = constraintIndex;
`}
          if (p >= ${maxPairContacts}u) { continue; }

${solverContactGeometrySource}
          if (dot(n, n) <= 1e-10) { continue; }
          let isA = i == gid;
          let isB = j == gid;
          if (!isA && !isB) { continue; }
          let other = select(i, j, isA);
          if (other >= bodyCount || other == gid) { continue; }
          let otherPoseBase = other * ${INERTIAL_POSE_VEC4S_PER_BODY}u;
${referenceContactStrict
    ? referenceOtherConstraintState
    : `${otherConstraintPosition}${otherConstraintRotation}`}
          // prepare transitioned ARM_A/B.xyz to their world-space values at
          // x^t. Equation 18 keeps this Jacobian fixed for the complete AVBD
          // linearization cycle, so every body/color touch can consume it
          // directly without repeating two quaternion rotations.
          let ra = armA.xyz;
          let rb = armB.xyz;

          let dPosA = select(dPosOtherConstraint, dPosConstraint, isA);
          let dPosB = select(dPosConstraint, dPosOtherConstraint, isA);
          let dThetaA = select(dThetaOtherConstraint, dThetaConstraint, isA);
          let dThetaB = select(dThetaConstraint, dThetaOtherConstraint, isA);

          let jAL = -n;
          let jBL = n;
          let jAA = -cross(ra, n);
          let jBA = cross(rb, n);

${contactTangentBasisSource}
          let t1 = tangentBasis.t1;
          let t2 = tangentBasis.t2;

${contactStateSource}

          let sign = select(1.0, -1.0, isA);
          let jNLinear = sign * n;
          // jAA/jBA were already formed for the shared constraint value.
          // Reuse the endpoint row instead of recomputing the same cross
          // product three times for every body-contact touch.
          let jNAngular = select(jBA, jAA, isA);

${normalSolveSource}

${tangentialSolveSource}
        }

${cooperativePackedReductionSource}
${referenceContactStrict ? referencePackedSolveSource : /* wgsl */`
        // Re-symmetrize and diagonally equilibrate before modified LDL. The
        // six coordinates mix metres and radians, while augmented penalties
        // may span many orders of magnitude. Factoring S*A*S with unit-scale
        // diagonals makes the relative pivot decision invariant to body mass,
        // object size, and the chosen world units.
        var pivotFloorByDof: array<f32, 6>;
        var equilibrationScale: array<f32, 6>;
        var solveInputFinite = true;
        for (var r = 0u; r < 6u; r++) {
          for (var c = r + 1u; c < 6u; c++) {
            let sRaw = 0.5 * (A[r][c] + A[c][r]);
            if (!isFiniteF32(sRaw)) { solveInputFinite = false; }
            let s = select(0.0, sRaw, isFiniteF32(sRaw));
            A[r][c] = s;
            A[c][r] = s;
          }
          let diagonalRaw = A[r][r];
          if (!isFiniteF32(diagonalRaw) || !isFiniteF32(b[r])) {
            solveInputFinite = false;
          }
          let diagonal = select(0.0, diagonalRaw, isFiniteF32(diagonalRaw));
          b[r] = select(0.0, b[r], isFiniteF32(b[r]));
          A[r][r] = diagonal;
          equilibrationScale[r] = inverseSqrt(max(abs(diagonal), 1e-30));
        }
        for (var r = 0u; r < 6u; r++) {
          let scaledB = b[r] * equilibrationScale[r];
          if (!isFiniteF32(scaledB)) { solveInputFinite = false; }
          b[r] = select(0.0, scaledB, isFiniteF32(scaledB));
          for (var c = 0u; c < 6u; c++) {
            let scaledA = A[r][c] * equilibrationScale[r] * equilibrationScale[c];
            if (!isFiniteF32(scaledA)) { solveInputFinite = false; }
            A[r][c] = select(0.0, scaledA, isFiniteF32(scaledA));
          }
        }
        for (var r = 0u; r < 6u; r++) {
          pivotFloorByDof[r] = max(
            abs(A[r][r]) * ${AVBD_RELATIVE_LDL_PIVOT},
            ${AVBD_RELATIVE_LDL_PIVOT},
          );
        }

        // The AVBD body Hessian is SPD (inertia plus J^T K J). Modify only a
        // non-positive/non-finite/f32-unresolvable Schur pivot in the
        // equilibrated system; this is a relative modified-Cholesky safeguard,
        // not a fixed unit-dependent diagonal spring.
        var L: array<array<f32, 6>, 6>;
        var D: array<f32, 6>;
        for (var r = 0u; r < 6u; r++) {
          D[r] = 0.0;
          for (var c = 0u; c < 6u; c++) {
            L[r][c] = 0.0;
          }
        }

        for (var i = 0u; i < 6u; i++) {
          for (var j = 0u; j < i; j++) {
            var sumL = A[i][j];
            for (var k = 0u; k < j; k++) {
              sumL -= L[i][k] * D[k] * L[j][k];
            }
            if (!isFiniteF32(sumL)) { solveInputFinite = false; }
            let finiteSumL = select(0.0, sumL, isFiniteF32(sumL));
            let lij = finiteSumL / D[j];
            if (!isFiniteF32(lij)) { solveInputFinite = false; }
            L[i][j] = select(0.0, lij, isFiniteF32(lij));
          }

          var diag = A[i][i];
          for (var k = 0u; k < i; k++) {
            let diagContribution = L[i][k] * L[i][k] * D[k];
            if (!isFiniteF32(diagContribution)) { solveInputFinite = false; }
            diag -= select(0.0, diagContribution, isFiniteF32(diagContribution));
          }
          let pivotFloor = pivotFloorByDof[i];
          let healthyPivot = isFiniteF32(diag) && diag >= pivotFloor;
          D[i] = select(pivotFloor, diag, healthyPivot);
          L[i][i] = 1.0;
        }

        var y: array<f32, 6>;
        for (var i = 0u; i < 6u; i++) {
          var sumF = b[i];
          for (var k = 0u; k < i; k++) {
            sumF -= L[i][k] * y[k];
          }
          y[i] = sumF;
        }

        var z: array<f32, 6>;
        for (var i = 0u; i < 6u; i++) {
          z[i] = y[i] / D[i];
        }

        var x: array<f32, 6>;
        for (var i = 0u; i < 6u; i++) { x[i] = 0.0; }
        for (var rev = 0u; rev < 6u; rev++) {
          let row = 5u - rev;
          var sumB = z[row];
          for (var c = row + 1u; c < 6u; c++) {
            sumB -= L[c][row] * x[c];
          }
          x[row] = sumB;
        }
        for (var i = 0u; i < 6u; i++) {
          x[i] *= equilibrationScale[i];
        }
        var solveOutputFinite = solveInputFinite;
        for (var i = 0u; i < 6u; i++) {
          solveOutputFinite = solveOutputFinite && isFiniteF32(x[i]);
        }
        if (!solveOutputFinite) {
          // A corrupt local Newton step must never poison persistent body state
          // or the next BVH build. Reject the whole six-DoF update atomically.
          for (var i = 0u; i < 6u; i++) { x[i] = 0.0; }
        }
`}
${referenceContactStrict ? /* wgsl */`
        var dPosSolve = vec3f(x0, x1, x2);
        var dThetaSolve = vec3f(x3, x4, x5);
` : /* wgsl */`
        var dPosSolve = vec3f(x[0], x[1], x[2]);
        var dThetaSolve = vec3f(x[3], x[4], x[5]);
`}
        let linLen2 = dot(dPosSolve, dPosSolve);
        if (linLen2 > maxLinearCorrection * maxLinearCorrection && linLen2 > 1e-12) {
          dPosSolve *= maxLinearCorrection * inverseSqrt(linLen2);
        }
        let angLen2 = dot(dThetaSolve, dThetaSolve);
        if (angLen2 > maxAngularCorrection * maxAngularCorrection && angLen2 > 1e-12) {
          dThetaSolve *= maxAngularCorrection * inverseSqrt(angLen2);
        }

        let appliedDPos = -dPosSolve * relaxation;
        let appliedDTheta = -dThetaSolve * relaxation;
${adaptiveResidualTracking ? /* wgsl */`
        if (atomicLoad(&adaptiveState[${ADAPTIVE_ENABLED}u]) != 0u) {
          if (!solveOutputFinite) {
            markAdaptiveInvalid(adaptiveState);
          }
          if (atomicLoad(&adaptiveState[${ADAPTIVE_TRACK_RESIDUALS}u]) != 0u) {
            let residualShard = gid & ${ADAPTIVE_RESIDUAL_SHARDS - 1}u;
            let normalizedBodyResidual = max(
              adaptiveMaxAbs3(appliedDPos) / max(adaptiveLinearTolerance, 1e-30),
              adaptiveMaxAbs3(appliedDTheta) / max(adaptiveAngularTolerance, 1e-30),
            );
            markAdaptiveResidualViolation(
              adaptiveState,
              adaptiveResiduals,
              residualShard,
              0u,
              normalizedBodyResidual,
            );
          }
        }
` : ''}
${scratchAdaptiveResidualWrite}
        pos += appliedDPos;
        q = normalize(q + 0.5 * qmul(vec4f(appliedDTheta, 0.0), q));

${finalPoseOutput}
      }
    `, referenceContactStrict
      // The specialized hot path reads dual/penalty records directly, so the
      // uniform-policy contact state helpers are absent from its WGSL source.
      ? [
        qrot,
        qmul,
        qconj,
        tangentBasisHelpers,
        contactRecordHelpers,
        ...(useSolverHotState ? [contactSolverHotHelpers] : []),
        jointConstraintHelpers,
        bodyConstraintRefHelpers,
        ...(adaptiveResidualTracking ? [adaptiveResidualHelpers] : []),
      ]
      : constraintMode === 'contacts'
      // jointConstraintHelpers also owns the shared f32 finite predicate used
      // by the LDL safeguard; unused joint functions are compiler-DCE'd and do
      // not reintroduce their record buffers or hot-loop branches.
      ? [
        qrot,
        qmul,
        qconj,
        tangentBasisHelpers,
        contactRecordHelpers,
        contactStateHelpers,
        ...(useSolverHotState ? [contactSolverHotHelpers] : []),
        jointConstraintHelpers,
        bodyConstraintRefHelpers,
      ]
      : [
        qrot,
        qmul,
        qconj,
        tangentBasisHelpers,
        contactRecordHelpers,
        contactStateHelpers,
        ...(useSolverHotState ? [contactSolverHotHelpers] : []),
        jointRecordHelpers,
        springRecordHelpers,
        jointConstraintHelpers,
        bodyConstraintRefHelpers,
      ]);
    };

    assertStorageBufferBudget(
      'AVBD Contact-Only Strict In-Place Body Primal Solve',
      (this.cacheContactTangentInvLength ? 9 : 8)
        - (this.packedContactSolverGeometry && this.cacheContactTangentInvLength ? 1 : 0)
        + (this.captureBodyDeltaCache ? 1 : 0),
    );
    assertStorageBufferBudget(
      'AVBD Adaptive Contact-Only Strict Body Primal Solve',
      this.cacheContactTangentInvLength ? 10 : 9,
    );
    assertStorageBufferBudget('AVBD Strict In-Place Body Primal Solve', 9);
    assertStorageBufferBudget('AVBD Scratch Body Primal Solve', 10);
    assertStorageBufferBudget(
      'AVBD Contact-Only Strict Fallback Scratch Body Primal Solve',
      this.cacheContactTangentInvLength ? 10 : 9,
    );

    const strictConstraintMode = hasHardConstraints ? 'all' : 'contacts';
    const primalBodySolveShaderInPlace = makePrimalBodySolveShader(
      'generic',
      'inPlace',
      strictConstraintMode,
    );
    const referencePrimalBodySolveShaderInPlace = hasHardConstraints
      ? null
      : makePrimalBodySolveShader(
        'generic',
        'inPlace',
        'contacts',
        true,
        false,
        false,
        this.cooperativeReferenceBodySolve,
      );
    const adaptiveReferencePrimalBodySolveShaderInPlace = hasHardConstraints
      ? null
      : makePrimalBodySolveShader('generic', 'inPlace', 'contacts', true, true);
    // Fallback and serial diagnostics intentionally remain on the generic
    // scratch solver, even for contact-only configured worlds.
    const primalBodySolveShaderScratch = makePrimalBodySolveShader(
      'generic',
      'scratch',
      hasHardConstraints || !this.denseContactSolverState ? 'all' : 'contacts',
    );
    // The adaptive fallback flavor keeps the same ten storage bindings. It
    // exports one normalized correction through the otherwise-unused w lane
    // of bodySolveOutputPose[body * 3 + 2]; a separate commit consumes it.
    const adaptiveFallbackPrimalBodySolveShaderScratch = makePrimalBodySolveShader(
      'generic',
      'scratch',
      'all',
      false,
      false,
      true,
    );
    const createPrimalBodySolveKernel = (
      color: number,
      adaptiveFallbackResidualOutput = false,
    ): any => {
      const inPlace = color < bodyColorKernelVariants;
      const contactOnlyStrict = inPlace && !hasHardConstraints;
      const contactOnlyFixedFallback = !inPlace
        && !hasHardConstraints
        && this.denseContactSolverState
        && !adaptiveFallbackResidualOutput;
      const inertialPoseBinding = storage(
        inertialPose,
        'vec4f',
        maxBodies * INERTIAL_POSE_VEC4S_PER_BODY,
      );
      const bindings: Record<string, any> = {
        initialPose: storage(initialPose, 'vec4f', maxBodies * 2).toReadOnly(),
        inertialPose: inPlace ? inertialPoseBinding : inertialPoseBinding.toReadOnly(),
        inverseInertia: storage(inverseInertia, 'vec4f', maxBodies).toReadOnly(),
        bodyConstraintCounts: storage(bodyConstraintCounts, 'uint', maxBodies).toReadOnly(),
        bodyConstraintRefs: storage(bodyConstraintRefs, 'uint', bodyConstraintRefs.count).toReadOnly(),
        bodyColorCompact: storage(this.bodyColorCompactAttr, 'uint', bodyColorCompactWordCount).toReadOnly(),
        pairContacts: storage(pairContacts, 'vec4f', maxPairContacts * CONTACT_RECORD_VEC4S),
        bodyCount: uniform(0),
        bodyIndexBase: uniform(0),
        dispatchBodyCount: uniform(0),
        bodySolveMode: uniform(0),
        currentColor: uniform(color),
        fallbackColorIndex: uniform(bodyColorKernelVariants),
        sweepOffset: uniform(0),
        regularizationAlpha: uniform(AVBD_REGULARIZATION_ALPHA_DEFAULT),
        tangentialRegularizationAlpha: uniform(AVBD_REGULARIZATION_ALPHA_DEFAULT),
        relaxation: uniform(1.0),
        frictionRelaxation: uniform(1.0),
        frictionStatic: uniform(AVBD_FRICTION_STATIC),
        frictionDynamic: uniform(AVBD_FRICTION_DYNAMIC),
        useReferenceTangentialUpdate: uniform(1),
        frictionSolveScale: uniform(1.0),
        kStart: uniform(AVBD_K_START),
        dualForceMax: uniform(10000000000.0),
        enableNormalReleaseHeuristic: uniform(0),
        enableHessianRescaling: uniform(0),
        dt: uniform(1 / 60 / 4),
        inertialDiagWeight: uniform(1.0),
        maxLinearCorrection: uniform(1000000000.0),
        maxAngularCorrection: uniform(1000000000.0),
        useLocalDiagonalInertia: uniform(0),
        useNormalContactMargin: uniform(1),
        alwaysStampNormalHessian: uniform(1),
        alwaysStampTangentialHessian: uniform(1),
        workgroupId,
        localId,
      };
      if (adaptiveFallbackResidualOutput) {
        bindings.adaptiveFallbackResidualEnabled = uniform(1);
        bindings.adaptiveFallbackLinearTolerance = uniform(2.5e-5);
        bindings.adaptiveFallbackAngularTolerance = uniform(2.5e-5);
      }
      if (contactOnlyFixedFallback) {
        bindings.contactSolverHot = storage(
          this.contactSolverHotAttr,
          'vec4f',
          maxPairContacts * CONTACT_SOLVER_HOT_VEC4S,
        );
      }
      if (!contactOnlyStrict && !contactOnlyFixedFallback) {
        bindings.jointRecords = storage(jointRecords, 'vec4f', maxJoints * JOINT_RECORD_VEC4S);
        bindings.springRecords = storage(springRecords, 'vec4f', maxSprings * SPRING_RECORD_VEC4S);
      }
      if (!inPlace) {
        bindings.bodySolveOutputPose = storage(
          this.bodySolveOutputPoseAttr,
          'vec4f',
          maxBodies * 3,
        );
      }
      const shader = inPlace
        ? primalBodySolveShaderInPlace
        : adaptiveFallbackResidualOutput
        ? adaptiveFallbackPrimalBodySolveShaderScratch
        : primalBodySolveShaderScratch;
      return shader(bindings)
        .computeKernel([WORKGROUP_SIZE, 1, 1])
        .setName(inPlace
          ? `${contactOnlyStrict ? 'AVBD Contact-Only' : 'AVBD'} Strict In-Place Body Primal Solve Color ${color}`
          : `${adaptiveFallbackResidualOutput ? 'AVBD Adaptive' : 'AVBD'} Fallback Scratch Body Primal Solve`);
    };

    // Distinct currentColor uniform buffers let all color dispatches live in
    // one WebGPU compute pass. This preserves global dispatch boundaries and
    // Gauss-Seidel color order while avoiding one queue.submit per color.
    this.primalBodySolveKernels = Array.from(
      { length: bodyColorBucketVariants },
      (_, color) => createPrimalBodySolveKernel(color),
    );
    this.adaptiveFallbackPrimalBodySolveKernel = createPrimalBodySolveKernel(
      this.maxBodyColorCount,
      true,
    );
    const createReferencePrimalKernels = (
      shader: any | null,
      adaptiveResidualTracking: boolean,
      cooperativeBodySolve = false,
    ): any[] => shader
      ? Array.from({ length: bodyColorKernelVariants }, (_, color) => {
        const usePackedContactSolverGeometry = this.packedContactSolverGeometry
          && !adaptiveResidualTracking;
        const bindings: Record<string, any> = {
          initialPose: storage(initialPose, 'vec4f', maxBodies * 2).toReadOnly(),
          inertialPose: storage(
            inertialPose,
            'vec4f',
            maxBodies * INERTIAL_POSE_VEC4S_PER_BODY,
          ),
          inverseInertia: storage(inverseInertia, 'vec4f', maxBodies).toReadOnly(),
          bodyConstraintCounts: storage(bodyConstraintCounts, 'uint', maxBodies).toReadOnly(),
          bodyConstraintRefs: storage(bodyConstraintRefs, 'uint', bodyConstraintRefs.count).toReadOnly(),
          bodyColorCompact: storage(
            this.bodyColorCompactAttr,
            'uint',
            bodyColorCompactWordCount,
          ).toReadOnly(),
          bodyCount: uniform(0),
          currentColor: uniform(color),
          sweepOffset: uniform(0),
          dt: uniform(1 / 60 / 4),
          workgroupId,
          localId,
        };
        if (usePackedContactSolverGeometry) {
          bindings.contactSolverGeometry = storage(
            this.contactSolverGeometryAttr,
            'vec4f',
            maxPairContacts * CONTACT_SOLVER_GEOMETRY_VEC4S,
          ).toReadOnly();
        } else {
          bindings.pairContacts = storage(
            pairContacts,
            'vec4f',
            maxPairContacts * CONTACT_RECORD_VEC4S,
          );
        }
        if (this.denseContactSolverState && !adaptiveResidualTracking) {
          bindings.contactSolverHot = storage(
            this.contactSolverHotAttr,
            'vec4f',
            maxPairContacts * CONTACT_SOLVER_HOT_VEC4S,
          );
        }
        if (this.captureBodyDeltaCache && !adaptiveResidualTracking) {
          bindings.captureBodyDelta = storage(
            this.captureBodyDeltaAttr,
            'vec4f',
            maxBodies * 2,
          );
        }
        if (this.cacheContactTangentInvLength && !usePackedContactSolverGeometry) {
          bindings.contactTangentInvLength = storage(
            this.contactTangentInvLengthAttr,
            'float',
            maxPairContacts,
          ).toReadOnly();
        }
        if (adaptiveResidualTracking) {
          bindings.adaptiveState = storage(
            this.adaptiveStateAttr,
            'uint',
            ADAPTIVE_STATE_WORDS,
          ).toAtomic();
          bindings.adaptiveResiduals = storage(
            this.adaptiveResidualsAttr,
            'uint',
            ADAPTIVE_RESIDUAL_WORDS,
          ).toAtomic();
          bindings.adaptiveLinearTolerance = uniform(2.5e-5);
          bindings.adaptiveAngularTolerance = uniform(2.5e-5);
        }
        if (cooperativeBodySolve) {
          bindings.cooperativePartials = workgroupArray(
            'vec4',
            COOPERATIVE_PACKED_SYSTEM_SHARED_VEC4S,
          );
        }
        return shader(bindings)
          .computeKernel([
            cooperativeBodySolve ? COOPERATIVE_BODY_SOLVE_LANES : WORKGROUP_SIZE,
            1,
            1,
          ])
          .setName(
            `AVBD ${adaptiveResidualTracking ? 'Adaptive ' : ''}${cooperativeBodySolve ? 'Cooperative ' : ''}Reference Contact-Only Strict Body Primal Solve Color ${color}`,
          );
      })
      : [];
    this.referencePrimalBodySolveKernels = createReferencePrimalKernels(
      referencePrimalBodySolveShaderInPlace,
      false,
      this.cooperativeReferenceBodySolve,
    );
    this.adaptiveReferencePrimalBodySolveKernels = createReferencePrimalKernels(
      adaptiveReferencePrimalBodySolveShaderInPlace,
      true,
    );
    const commitBodySolveShader = wgslFn(/* wgsl */`
      fn compute(
${this.captureBodyDeltaCache
  ? /* wgsl */`        initialPose: ptr<storage, array<vec4f>, read>,
        captureBodyDelta: ptr<storage, array<vec4f>, read_write>,
`
  : ''}
        bodyConstraintCounts: ptr<storage, array<u32>, read>,
        bodyColorCompact: ptr<storage, array<u32>, read>,
        bodySolveOutputPose: ptr<storage, array<vec4f>, read>,
        inertialPose: ptr<storage, array<vec4f>, read_write>,
        bodyCount: u32,
        bodyIndexBase: u32,
        dispatchBodyCount: u32,
        bodySolveMode: u32,
        currentColor: u32,
        fallbackColorIndex: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let localIndex = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        var gid = 0u;
        if (bodySolveMode == 0u) {
          let bucketCount = bodyColorCompact[${bodyColorCountOffset}u + currentColor];
          if (localIndex >= bucketCount) { return; }
          let bucketOffset = bodyColorCompact[${bodyColorOffsetOffset}u + currentColor];
          gid = bodyColorCompact[bucketOffset + localIndex];
        } else {
          if (localIndex >= dispatchBodyCount) { return; }
          gid = bodyIndexBase + localIndex;
        }
        if (gid >= bodyCount) { return; }

        let countWord = bodyConstraintCounts[gid];
        let bodyColor = (countWord >> 16u) & 0xFFu;
        if (bodySolveMode == 0u) {
          let fallback = (countWord & ${BODY_COLOR_FALLBACK_FLAG}u) != 0u;
          let fallbackPass = currentColor == fallbackColorIndex;
          if (fallbackPass != fallback) { return; }
          if (!fallbackPass && bodyColor != currentColor) { return; }
        }

        let outputBase = gid * 3u;
        let poseBase = gid * ${INERTIAL_POSE_VEC4S_PER_BODY}u;
        let committedPose = bodySolveOutputPose[outputBase];
        let committedDTheta = bodySolveOutputPose[outputBase + 2u];
        inertialPose[poseBase + 2u] = committedPose;
        inertialPose[poseBase + 3u] = normalize(bodySolveOutputPose[outputBase + 1u]);
        inertialPose[poseBase + 4u] = committedDTheta;
${this.captureBodyDeltaCache ? /* wgsl */`        let deltaBase = gid * 2u;
        captureBodyDelta[deltaBase] = vec4f(
          committedPose.xyz - initialPose[deltaBase].xyz,
          0.0,
        );
        captureBodyDelta[deltaBase + 1u] = vec4f(committedDTheta.xyz, 0.0);
` : ''}
      }
    `);

    const createCommitBodySolveKernel = (color: number): any => {
      const bindings: Record<string, any> = {
        bodyConstraintCounts: storage(bodyConstraintCounts, 'uint', maxBodies).toReadOnly(),
        bodyColorCompact: storage(this.bodyColorCompactAttr, 'uint', bodyColorCompactWordCount).toReadOnly(),
        bodySolveOutputPose: storage(this.bodySolveOutputPoseAttr, 'vec4f', maxBodies * 3).toReadOnly(),
        inertialPose: storage(inertialPose, 'vec4f', maxBodies * INERTIAL_POSE_VEC4S_PER_BODY),
        bodyCount: uniform(0),
        bodyIndexBase: uniform(0),
        dispatchBodyCount: uniform(0),
        bodySolveMode: uniform(0),
        currentColor: uniform(color),
        fallbackColorIndex: uniform(bodyColorKernelVariants),
        workgroupId,
        localId,
      };
      if (this.captureBodyDeltaCache) {
        bindings.initialPose = storage(initialPose, 'vec4f', maxBodies * 2).toReadOnly();
        bindings.captureBodyDelta = storage(
          this.captureBodyDeltaAttr,
          'vec4f',
          maxBodies * 2,
        );
      }
      return commitBodySolveShader(bindings)
        .computeKernel([WORKGROUP_SIZE, 1, 1])
        .setName(`AVBD Commit Body Solve Color ${color}`);
    };
    this.commitBodySolveKernels = Array.from(
      { length: bodyColorBucketVariants },
      (_, color) => createCommitBodySolveKernel(color),
    );
    assertStorageBufferBudget(
      'AVBD Commit Body Solve',
      4 + (this.captureBodyDeltaCache ? 2 : 0),
    );

    const adaptiveFallbackCommitBodySolveShader = wgslFn(/* wgsl */`
      fn compute(
        bodyConstraintCounts: ptr<storage, array<u32>, read>,
        bodyColorCompact: ptr<storage, array<u32>, read>,
        bodySolveOutputPose: ptr<storage, array<vec4f>, read>,
        inertialPose: ptr<storage, array<vec4f>, read_write>,
        adaptiveState: ptr<storage, array<atomic<u32>>, read_write>,
        adaptiveResiduals: ptr<storage, array<atomic<u32>>, read_write>,
        bodyCount: u32,
        bodyIndexBase: u32,
        dispatchBodyCount: u32,
        bodySolveMode: u32,
        currentColor: u32,
        fallbackColorIndex: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let localIndex = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        var gid = 0u;
        if (bodySolveMode == 0u) {
          let bucketCount = bodyColorCompact[${bodyColorCountOffset}u + currentColor];
          if (localIndex >= bucketCount) { return; }
          let bucketOffset = bodyColorCompact[${bodyColorOffsetOffset}u + currentColor];
          gid = bodyColorCompact[bucketOffset + localIndex];
        } else {
          if (localIndex >= dispatchBodyCount) { return; }
          gid = bodyIndexBase + localIndex;
        }
        if (gid >= bodyCount) { return; }

        let countWord = bodyConstraintCounts[gid];
        let bodyColor = (countWord >> 16u) & 0xFFu;
        if (bodySolveMode == 0u) {
          let fallback = (countWord & ${BODY_COLOR_FALLBACK_FLAG}u) != 0u;
          let fallbackPass = currentColor == fallbackColorIndex;
          if (fallbackPass != fallback) { return; }
          if (!fallbackPass && bodyColor != currentColor) { return; }
        }

        let outputBase = gid * 3u;
        let poseBase = gid * ${INERTIAL_POSE_VEC4S_PER_BODY}u;
        let constraintDelta = bodySolveOutputPose[outputBase + 2u];
        if (atomicLoad(&adaptiveState[${ADAPTIVE_ENABLED}u]) != 0u) {
          if (!adaptiveFiniteF32(constraintDelta.w) || constraintDelta.w < 0.0) {
            markAdaptiveInvalid(adaptiveState);
          }
          if (atomicLoad(&adaptiveState[${ADAPTIVE_TRACK_RESIDUALS}u]) != 0u) {
            if (adaptiveFiniteF32(constraintDelta.w) && constraintDelta.w > 1.0) {
              if (atomicLoad(
                &adaptiveState[${ADAPTIVE_FALLBACK_BODY_VIOLATION}u]
              ) == 0u) {
                atomicStore(
                  &adaptiveState[${ADAPTIVE_FALLBACK_BODY_VIOLATION}u],
                  1u,
                );
              }
            }
            markAdaptiveResidualViolation(
              adaptiveState,
              adaptiveResiduals,
              gid & ${ADAPTIVE_RESIDUAL_SHARDS - 1}u,
              0u,
              constraintDelta.w,
            );
          }
        }
        inertialPose[poseBase + 2u] = bodySolveOutputPose[outputBase];
        inertialPose[poseBase + 3u] = normalize(bodySolveOutputPose[outputBase + 1u]);
        // output.w is transport-only adaptive telemetry; persistent pose state
        // retains the invariant zero w lane used by every downstream reader.
        inertialPose[poseBase + 4u] = vec4f(constraintDelta.xyz, 0.0);
      }
    `, [adaptiveResidualHelpers]);
    this.adaptiveFallbackCommitBodySolveKernel = adaptiveFallbackCommitBodySolveShader({
      bodyConstraintCounts: storage(bodyConstraintCounts, 'uint', maxBodies).toReadOnly(),
      bodyColorCompact: storage(this.bodyColorCompactAttr, 'uint', bodyColorCompactWordCount).toReadOnly(),
      bodySolveOutputPose: storage(this.bodySolveOutputPoseAttr, 'vec4f', maxBodies * 3).toReadOnly(),
      inertialPose: storage(inertialPose, 'vec4f', maxBodies * INERTIAL_POSE_VEC4S_PER_BODY),
      adaptiveState: storage(this.adaptiveStateAttr, 'uint', ADAPTIVE_STATE_WORDS).toAtomic(),
      adaptiveResiduals: storage(
        this.adaptiveResidualsAttr,
        'uint',
        ADAPTIVE_RESIDUAL_WORDS,
      ).toAtomic(),
      bodyCount: uniform(0),
      bodyIndexBase: uniform(0),
      dispatchBodyCount: uniform(0),
      bodySolveMode: uniform(0),
      currentColor: uniform(this.maxBodyColorCount),
      fallbackColorIndex: uniform(this.maxBodyColorCount),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Adaptive Fallback Commit + Residual');
    assertStorageBufferBudget('AVBD Adaptive Fallback Commit + Residual', 6);

    const capturePairDualStateShader = wgslFn(/* wgsl */`
      fn compute(
        inertialPose: ptr<storage, array<vec4f>, read>,
        initialPose: ptr<storage, array<vec4f>, read>,
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        pairActivity: ptr<storage, array<u32>, read_write>,
        pairDispatchCount: u32,
        regularizationAlpha: f32,
        beta: f32,
        frictionStatic: f32,
        frictionDynamic: f32,
        kStart: f32,
        kMax: f32,
        lambdaMax: f32,
        preventPenetratingNormalDropout: u32,
        enableNormalReleaseHeuristic: u32,
        freezeTangentialPenaltyOnStick: u32,
        freezeTangentialPenaltyUpdates: u32,
        useIsotropicTangentialPenaltyOnStick: u32,
        rampTangentialPenaltyOnlyWhenNotSticking: u32,
        useReferenceTangentialUpdate: u32,
        stickExitThreshold: f32,
        tangentialPenaltyRampDeadzone: f32,
        tangentialPenaltySlipRampMaxDelta: f32,
        tangentialPenaltyCapScale: f32,
        useNormalContactMargin: u32,
        dt: f32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = (
          workgroupId.y * ${CONTACT_DISPATCH_WORKGROUP_WIDTH}u + workgroupId.x
        ) * ${WORKGROUP_SIZE}u + localId.x;
        let solverHeader = pairActivity[${pairActiveContactsOffset}u];
        let activeCount = min(min(
          solverHeader & ${SOLVER_CONTACT_COUNT_MASK}u,
          pairDispatchCount,
        ), ${maxActivePairContacts}u);
        if (gid >= activeCount) { return; }

        let activeEntriesOffset = select(
          ${pairActiveContactsOffset + 1}u,
          ${pairCanonicalActiveContactsOffset + 1}u,
          (solverHeader & ${SOLVER_CONTACT_CANONICAL_ALIAS_BIT}u) != 0u,
        );
        let p = pairActivity[activeEntriesOffset + gid];
        if (p >= ${maxPairContacts}u) { return; }
        let prevContactStateSeed = loadContactStateHot(pairContacts, p, kStart);
        let contactMeta = loadContactMeta(pairContacts, p);
        let encodedMeta = bitcast<u32>(contactMeta.w);
        let speculativeContact = contactInfoIsSpeculative(encodedMeta);
        let preserveSolverScratch = select(
          0u,
          1u,
          (encodedMeta & CONTACT_SOLVER_SCRATCH_ACTIVE_BIT) != 0u,
        );

        let n = loadContactNormalPen(pairContacts, p).xyz;
        if (dot(n, n) <= 1e-10) {
          storeContactRecord(
            pairContacts,
            p,
            vec4f(0.0),
            kStart,
            vec2f(kStart),
            0.0,
            vec2f(0.0),
            prevContactStateSeed.frictionScale,
            kStart,
            preserveSolverScratch,
          );
          return;
        }

        let i = u32(contactMeta.x + 0.5);
        let j = u32(contactMeta.y + 0.5);
        if (i >= ${maxBodies}u || j >= ${maxBodies}u || i == j) {
          storeContactRecord(
            pairContacts,
            p,
            vec4f(0.0),
            kStart,
            vec2f(kStart),
            0.0,
            vec2f(0.0),
            prevContactStateSeed.frictionScale,
            kStart,
            preserveSolverScratch,
          );
          return;
        }

        let penetration0 = max(loadContactNormalPen(pairContacts, p).w, 0.0);
        let armA = loadContactArmA(pairContacts, p);
        let armB = loadContactArmB(pairContacts, p);
        let ra = armA.xyz;
        let rb = armB.xyz;

        let solvePoseBaseA = i * ${INERTIAL_POSE_VEC4S_PER_BODY}u;
        let solvePoseBaseB = j * ${INERTIAL_POSE_VEC4S_PER_BODY}u;
        let posA = inertialPose[solvePoseBaseA + 2u].xyz;
        let posB = inertialPose[solvePoseBaseB + 2u].xyz;
        let poseBaseA = i * 2u;
        let poseBaseB = j * 2u;
        let prevPosA = initialPose[poseBaseA].xyz;
        let prevPosB = initialPose[poseBaseB].xyz;
        let dThetaA = inertialPose[solvePoseBaseA + 4u].xyz;
        let dThetaB = inertialPose[solvePoseBaseB + 4u].xyz;

        let dPosA = posA - prevPosA;
        let dPosB = posB - prevPosB;

        let jAL = -n;
        let jBL = n;
        let jAA = -cross(ra, n);
        let jBA = cross(rb, n);

        let tangentBasis = tangentBasisFromCachedT1(n, loadContactTangentT1(pairContacts, p), armA.w);
        let t1 = tangentBasis.t1;
        let t2 = tangentBasis.t2;

        let jt1AL = -t1;
        let jt1BL = t1;
        let jt1AA = -cross(ra, t1);
        let jt1BA = cross(rb, t1);

        let jt2AL = -t2;
        let jt2BL = t2;
        let jt2AA = -cross(ra, t2);
        let jt2BA = cross(rb, t2);

        let cachedConstraintC0 = loadContactConstraintC0(pairContacts, p);
        let c0 = cachedConstraintC0.x;
        let cRegN = contactConstraintC0Scale(encodedMeta, regularizationAlpha) * c0
          + dot(jAL, dPosA)
          + dot(jAA, dThetaA)
          + dot(jBL, dPosB)
          + dot(jBA, dThetaB);
        let c0T1 = cachedConstraintC0.y;
        let c0T2 = cachedConstraintC0.z;
        let cRegT1 = contactConstraintC0Scale(encodedMeta, regularizationAlpha) * c0T1
          + dot(jt1AL, dPosA)
          + dot(jt1AA, dThetaA)
          + dot(jt1BL, dPosB)
          + dot(jt1BA, dThetaB);
        let cRegT2 = contactConstraintC0Scale(encodedMeta, regularizationAlpha) * c0T2
          + dot(jt2AL, dPosA)
          + dot(jt2AA, dThetaA)
          + dot(jt2BL, dPosB)
          + dot(jt2BA, dThetaB);
        // Eq. 18 keeps J cached at x^t. Two extra current-pose arm rotations
        // are needed only by the optional dropout diagnostic, which is off in
        // the paper/production path.
        var rawPenetration = 0.0;
        if (preventPenetratingNormalDropout > 0u) {
          let qA = inertialPose[solvePoseBaseA + 3u];
          let qB = inertialPose[solvePoseBaseB + 3u];
          let scratchActive = (encodedMeta & CONTACT_SOLVER_SCRATCH_ACTIVE_BIT) != 0u;
          let sphereArmFlags = select(
            u32(armB.w + 0.5) & 0x3u,
            (encodedMeta >> CONTACT_SOLVER_SCRATCH_ARM_FLAGS_SHIFT) & 0x3u,
            scratchActive,
          );
          let scratchArms = loadContactShadow(pairContacts, p);
          let constraintC0 = loadContactConstraintC0(pairContacts, p);
          let raStored = select(armA.xyz, scratchArms.xyz, scratchActive);
          let rbStored = select(
            armB.xyz,
            vec3f(scratchArms.w, constraintC0.w, armB.w),
            scratchActive,
          );
          let raCurrent = select(
            qrot(qA, raStored),
            raStored,
            (sphereArmFlags & 1u) != 0u,
          );
          let rbCurrent = select(
            qrot(qB, rbStored),
            rbStored,
            (sphereArmFlags & 2u) != 0u,
          );
          let currentPointA = posA + raCurrent;
          let currentPointB = posB + rbCurrent;
          rawPenetration = dot(currentPointA - currentPointB, n);
        }
        let normalReleaseTolerance = max(2e-5, 1.25 * penetration0);
        let normalReleaseDecayNear = 0.85;
        let normalReleaseDecayFar = 0.6;
        let normalReleaseMinSupport = -5e-4;
        let normalSupportThreshold = 1e-6;
        let frictionSeparationTolerance = normalReleaseTolerance;
        let featureKey = encodedMeta & 0x1FFu;
        let cooldown = (encodedMeta >> 9u) & 0x7Fu;
        let preserveWarmstart = (encodedMeta >> 16u) & 0x1u;
        let warmstartDebugReason = (encodedMeta >> 21u) & 0x7u;
        let prevStick = ((encodedMeta >> 17u) & 0x1u) != 0u;
        let stickAnchorReuse = (encodedMeta >> 18u) & 0x1u;

        let prevContactState = prevContactStateSeed;
        let prevDual = prevContactState.dual;
        let prevDualN = min(prevDual.x, 0.0);
        let prevDualTWorldRaw = prevDual.yzw;
        let prevDualTWorld = prevDualTWorldRaw - n * dot(prevDualTWorldRaw, n);
        let prevDualTB = select(
          vec2f(dot(prevDualTWorld, t1), dot(prevDualTWorld, t2)),
          prevDual.yz,
          useReferenceTangentialUpdate > 0u,
        );
        let prevPenalty = prevContactState.penalty;
        let prevPenaltyN = clamp(prevContactState.penaltyN, kStart, kMax);
        let prevPenaltyTBRaw = clamp(max(prevPenalty.yz, vec2f(kStart)), vec2f(kStart), vec2f(kMax));
        let prevPenaltyTB = prevPenaltyTBRaw;
        let frictionScale = prevContactState.frictionScale;
        let frictionStaticLocal = frictionStatic * frictionScale;
        let frictionDynamicLocal = frictionDynamic * frictionScale;
        var dualN = clamp(prevDualN + prevPenaltyN * cRegN, -lambdaMax, 0.0);
        // Diagnostic A/B: do not let the raw dual clamp drop all normal support
        // while the row is still geometrically penetrating.
        if (
          preventPenetratingNormalDropout > 0u
          && rawPenetration > 0.0
          && prevDualN < -normalSupportThreshold
          && dualN > -normalSupportThreshold
        ) {
          dualN = prevDualN;
        }
        if (
          enableNormalReleaseHeuristic > 0u &&
          penetration0 > 1e-6
          && prevDualN < -normalSupportThreshold
          && cRegN > 0.0
          && dualN > -normalSupportThreshold
        ) {
          let releaseDecay = select(normalReleaseDecayFar, normalReleaseDecayNear, cRegN <= normalReleaseTolerance);
          dualN = min(prevDualN * releaseDecay, normalReleaseMinSupport);
        }

        let prevTMag = length(prevDualTB);
        let staticBoundPrev = frictionStaticLocal * abs(prevDualN);
        let useStatic = prevTMag <= staticBoundPrev + 1e-6;
        let lambdaPlusTB = vec2f(
          prevDualTB.x + prevPenaltyTB.x * cRegT1,
          prevDualTB.y + prevPenaltyTB.y * cRegT2,
        );
        let frictionSupportN = max(prevDualN, dualN);
        let staticBound = frictionStaticLocal * max(-frictionSupportN, 0.0);
        let dynamicBound = frictionDynamicLocal * max(-frictionSupportN, 0.0);
        let lambdaPlusTBlen2 = dot(lambdaPlusTB, lambdaPlusTB);
        let noNormalSupportNow = dualN > -normalSupportThreshold;
        var penaltyN = prevPenaltyN;
        if (dualN < 0.0 && dualN > -lambdaMax) {
          penaltyN = clamp(prevPenaltyN + beta * abs(cRegN), kStart, kMax);
        }
        let tangentialError = length(vec2f(cRegT1, cRegT2));
        var penaltyTB = prevPenaltyTB;
        var dualTB = lambdaPlusTB;
        var stick = false;
        var tangentialInsideCone = false;
        var tangentialRampHappened = false;
        let freezeAllTangentialPenaltyUpdates = freezeTangentialPenaltyUpdates > 0u;
        if (useReferenceTangentialUpdate > 0u) {
          let referenceBound = frictionStaticLocal * max(-dualN, 0.0);
          let unconstrainedTLen2 = dot(lambdaPlusTB, lambdaPlusTB);
          let referenceBound2 = referenceBound * referenceBound;
          let wholeVectorInsideCone = unconstrainedTLen2 <= referenceBound2 + 1e-12;
          if (!wholeVectorInsideCone && unconstrainedTLen2 > 1e-12) {
            dualTB *= referenceBound * inverseSqrt(unconstrainedTLen2);
          }
          if (dualN >= 0.0 || referenceBound <= 1e-6) {
            dualTB = vec2f(0.0);
          }
          tangentialInsideCone = wholeVectorInsideCone;
          if (dualN < 0.0) {
            // Coulomb projection is one vector constraint: if either tangent
            // pushes the vector outside the cone, neither penalty advances.
            // Updating axes independently creates an artificial anisotropy.
            if (!freezeAllTangentialPenaltyUpdates && wholeVectorInsideCone) {
              penaltyTB = min(
                prevPenaltyTB + beta * abs(vec2f(cRegT1, cRegT2)),
                vec2f(kMax),
              );
              tangentialRampHappened = abs(penaltyTB.x - prevPenaltyTB.x) > 1e-6
                || abs(penaltyTB.y - prevPenaltyTB.y) > 1e-6;
            }
            stick = tangentialInsideCone && tangentialError < ${AVBD_STICK_THRESHOLD};
          } else {
            stick = false;
          }
        } else {
          var frictionBound = select(dynamicBound, staticBound, useStatic);
          if (useStatic && lambdaPlusTBlen2 > staticBound * staticBound + 1e-12) {
            frictionBound = dynamicBound;
          }
          frictionBound = max(frictionBound, 0.0);

          let dualTBLen2 = dot(dualTB, dualTB);
          if (dualTBLen2 > frictionBound * frictionBound && dualTBLen2 > 1e-12) {
            if (dualTBLen2 > frictionBound * frictionBound) {
              dualTB *= frictionBound * inverseSqrt(dualTBLen2);
            }
          }
          // If the normal row has no support (or contact is separating), drop
          // tangential memory so friction does not persist without contact.
          if (noNormalSupportNow || (enableNormalReleaseHeuristic > 0u && cRegN > frictionSeparationTolerance)) {
            dualTB = vec2f(0.0);
          }
          let stickThreshold = select(
            ${AVBD_STICK_THRESHOLD},
            max(stickExitThreshold, ${AVBD_STICK_THRESHOLD}),
            prevStick,
          );
          let insideStaticCone = staticBound > 1e-6
            && lambdaPlusTBlen2 <= staticBound * staticBound + 1e-12
            && !noNormalSupportNow;
          stick = insideStaticCone && tangentialError < stickThreshold;
          let freezeTangentialPenalty = freezeTangentialPenaltyOnStick > 0u && stick;
          let isInteriorCone = frictionBound > 1e-6
            && lambdaPlusTBlen2 < frictionBound * frictionBound - 1e-8;
          tangentialInsideCone = isInteriorCone;
          let tangentialRampActive = tangentialError > tangentialPenaltyRampDeadzone;
          let allowTangentialRamp = !freezeAllTangentialPenaltyUpdates
            && !freezeTangentialPenalty
            && (rampTangentialPenaltyOnlyWhenNotSticking == 0u || !stick);
          if (isInteriorCone && tangentialRampActive && allowTangentialRamp) {
            var tangentialRampT1 = beta * abs(cRegT1);
            var tangentialRampT2 = beta * abs(cRegT2);
            if (tangentialPenaltySlipRampMaxDelta > 0.0) {
              tangentialRampT1 = min(tangentialRampT1, tangentialPenaltySlipRampMaxDelta);
              tangentialRampT2 = min(tangentialRampT2, tangentialPenaltySlipRampMaxDelta);
            }
            penaltyTB.x = clamp(prevPenaltyTB.x + tangentialRampT1, kStart, kMax);
            penaltyTB.y = clamp(prevPenaltyTB.y + tangentialRampT2, kStart, kMax);
            tangentialRampHappened = true;
          }
          if (tangentialPenaltyCapScale > 0.0) {
            let tangentialPenaltyCap = max(penaltyN * tangentialPenaltyCapScale, kStart);
            penaltyTB = min(penaltyTB, vec2f(tangentialPenaltyCap));
          }
          if (useIsotropicTangentialPenaltyOnStick > 0u && stick) {
            let sharedPenaltyT = max(0.5 * (penaltyTB.x + penaltyTB.y), kStart);
            penaltyTB = vec2f(sharedPenaltyT);
          }
        }
        if (speculativeContact) {
          // A swept guard has no material tangent manifold.  Its sole job is
          // to stop normal time-of-impact crossing during this substep.
          dualTB = vec2f(0.0);
          penaltyTB = vec2f(kStart);
          stick = false;
          tangentialInsideCone = false;
          tangentialRampHappened = false;
        }

        let storedDual = select(
          vec4f(dualN, t1 * dualTB.x + t2 * dualTB.y),
          vec4f(dualN, dualTB, 0.0),
          useReferenceTangentialUpdate > 0u,
        );
        let storedPenaltyTB = penaltyTB;
        let storedPenaltyN = penaltyN;
        // pairLambdas mirrors the live dual state in positive magnitudes for
        // debug/readback. It is derived state, not the
        // authoritative contact record.
        let pairLambdaTB = dualTB;
        let pairLambdaN = -dualN;
        storeContactRecord(
          pairContacts,
          p,
          storedDual,
          storedPenaltyN,
          storedPenaltyTB,
          pairLambdaN,
          pairLambdaTB,
          frictionScale,
          kStart,
          preserveSolverScratch,
        );

        let currentMeta = loadContactMeta(pairContacts, p);
        storeContactMeta(pairContacts, p, vec4f(
          currentMeta.x,
          currentMeta.y,
          currentMeta.z,
          bitcast<f32>(
          featureKey
          | (cooldown << 9u)
          | (preserveWarmstart << 16u)
          | (select(0u, 1u, stick) << 17u)
          | (stickAnchorReuse << 18u)
          | (select(0u, 1u, tangentialInsideCone) << 19u)
          | (select(0u, 1u, tangentialRampHappened) << 20u)
          | (warmstartDebugReason << 21u)
          | contactInfoPersistentBits(encodedMeta)
          | (encodedMeta & CONTACT_SOLVER_SCRATCH_MASK),
          ),
        ));
      }
    `, [qrot, qmul, qconj, tangentBasisHelpers, contactRecordHelpers, contactStateHelpers]);

    // The fixed reference capture consumes the same body displacement for every
    // incident contact row. Seed it once per body at x^t; fixed strict body
    // publishes and fallback commits maintain the exact f32 values thereafter,
    // replacing random pose-buffer endpoint reads without a per-round rebuild.
    if (this.captureBodyDeltaCache) {
      const captureBodyDeltaShader = wgslFn(/* wgsl */`
        fn compute(
          inertialPose: ptr<storage, array<vec4f>, read>,
          initialPose: ptr<storage, array<vec4f>, read>,
          captureBodyDelta: ptr<storage, array<vec4f>, read_write>,
          bodyCount: u32,
          workgroupId: vec3u,
          localId: vec3u,
        ) -> void {
          let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
          if (gid >= bodyCount) { return; }
          let solveBase = gid * ${INERTIAL_POSE_VEC4S_PER_BODY}u;
          let deltaBase = gid * 2u;
          captureBodyDelta[deltaBase] = vec4f(
            inertialPose[solveBase + 2u].xyz - initialPose[deltaBase].xyz,
            0.0,
          );
          captureBodyDelta[deltaBase + 1u] = vec4f(
            inertialPose[solveBase + 4u].xyz,
            0.0,
          );
        }
      `);
      this.captureBodyDeltaKernel = captureBodyDeltaShader({
        inertialPose: storage(
          inertialPose,
          'vec4f',
          maxBodies * INERTIAL_POSE_VEC4S_PER_BODY,
        ).toReadOnly(),
        initialPose: storage(initialPose, 'vec4f', maxBodies * 2).toReadOnly(),
        captureBodyDelta: storage(this.captureBodyDeltaAttr, 'vec4f', maxBodies * 2),
        bodyCount: uniform(0),
        workgroupId,
        localId,
      }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Build Capture Body Delta Cache');
      assertStorageBufferBudget('AVBD Build Capture Body Delta Cache', 3);
    } else {
      this.captureBodyDeltaKernel = null;
    }

    // Compile-time reference capture flavor. The production contact-only path
    // has no dropout/release/static-dynamic/freeze/ramp/cap policy modes, so
    // those branches and their uniforms must not enter its generated WGSL.
    // beta remains dynamic because the public controls can tune it at runtime.
    const makeReferenceCapturePairDualStateShader = (
      adaptiveResidualTracking: boolean,
      manifoldCapture = false,
      persistentFinalCapture = false,
    ) => {
      if (adaptiveResidualTracking && manifoldCapture) {
        throw new Error('Adaptive residual capture retains the physical-row stream.');
      }
      if (
        persistentFinalCapture
        && (adaptiveResidualTracking || manifoldCapture)
      ) {
        throw new Error('Persistent final capture is only valid for fixed physical-row solves.');
      }
      const useSolverHotState = this.denseContactSolverState
        && !adaptiveResidualTracking;
      const useCaptureBodyDeltaCache = this.captureBodyDeltaCache
        && !adaptiveResidualTracking;
      const usePackedCaptureGeometry = this.packedContactSolverGeometry
        && useSolverHotState
        && this.referenceFinalizerSpecialization;
      const invalidContactExit = manifoldCapture ? 'continue;' : 'return;';
      const capturePoseStorageParams = useCaptureBodyDeltaCache
        ? '        captureBodyDelta: ptr<storage, array<vec4f>, read>,\n'
        : /* wgsl */`        inertialPose: ptr<storage, array<vec4f>, read>,
        initialPose: ptr<storage, array<vec4f>, read>,
`;
      const captureContactStorageParams = usePackedCaptureGeometry
        ? /* wgsl */`        contactSolverGeometry: ptr<storage, array<vec4f>, read>,
${persistentFinalCapture
  ? '        pairContacts: ptr<storage, array<vec4f>, read_write>,\n'
  : ''}`
        : /* wgsl */`        pairContacts: ptr<storage, array<vec4f>, read_write>,
${this.cacheContactTangentInvLength
  ? '        contactTangentInvLength: ptr<storage, array<f32>, read>,\n'
  : ''}`;
      const captureContactGeometrySource = usePackedCaptureGeometry
        ? /* wgsl */`
        let solverGeometryBase = p * ${CONTACT_SOLVER_GEOMETRY_VEC4S}u;
        let solverGeometry0 = contactSolverGeometry[solverGeometryBase];
        let solverGeometry1 = contactSolverGeometry[solverGeometryBase + 1u];
        let solverGeometry2 = contactSolverGeometry[solverGeometryBase + 2u];
        let solverGeometry3 = contactSolverGeometry[solverGeometryBase + 3u];
        let solverGeometry4 = contactSolverGeometry[solverGeometryBase + 4u];
        let i = u32(solverGeometry0.x + 0.5);
        let j = u32(solverGeometry0.y + 0.5);
        let encodedMeta = bitcast<u32>(solverGeometry0.z);
        let speculativeContact = contactInfoIsSpeculative(encodedMeta);
        let preserveSolverScratch = select(
          0u,
          1u,
          (encodedMeta & CONTACT_SOLVER_SCRATCH_ACTIVE_BIT) != 0u,
        );
        let n = vec3f(solverGeometry0.w, solverGeometry1.xy);
        let armA = vec4f(solverGeometry1.zw, solverGeometry2.xy);
        let rb = vec3f(solverGeometry2.zw, solverGeometry3.x);
        let cachedConstraintC0 = vec4f(solverGeometry3.yzw, 0.0);
        let cachedTangentT1 = solverGeometry4.xyz;
        let cachedTangentInvLength = solverGeometry4.w;
        let solverEndpointsValid = i < ${maxBodies}u
          && j < ${maxBodies}u
          && i != j;
`
        : /* wgsl */`
        let contactMeta = loadContactMeta(pairContacts, p);
        let encodedMeta = bitcast<u32>(contactMeta.w);
        let speculativeContact = contactInfoIsSpeculative(encodedMeta);
        let preserveSolverScratch = select(
          0u,
          1u,
          (encodedMeta & CONTACT_SOLVER_SCRATCH_ACTIVE_BIT) != 0u,
        );
        let n = loadContactNormalPen(pairContacts, p).xyz;
        let i = u32(contactMeta.x + 0.5);
        let j = u32(contactMeta.y + 0.5);
        let solverEndpointsValid = true;
`;
      const captureContactArmSource = usePackedCaptureGeometry
        ? ''
        : /* wgsl */`
        let armA = loadContactArmA(pairContacts, p);
        let rb = loadContactArmB(pairContacts, p).xyz;
`;
      const captureContactTangentSource = usePackedCaptureGeometry
        ? this.cacheContactTangentInvLength
          ? '        let tangentBasis = tangentBasisFromCachedT1AndInvLength(n, cachedTangentT1, armA.w, cachedTangentInvLength);'
          : '        let tangentBasis = tangentBasisFromCachedT1(n, cachedTangentT1, armA.w);'
        : this.cacheContactTangentInvLength
        ? '        let tangentBasis = tangentBasisFromCachedT1AndInvLength(n, loadContactTangentT1(pairContacts, p), armA.w, contactTangentInvLength[p]);'
        : '        let tangentBasis = tangentBasisFromCachedT1(n, loadContactTangentT1(pairContacts, p), armA.w);';
      const captureConstraintC0Source = usePackedCaptureGeometry
        ? ''
        : '        let cachedConstraintC0 = loadContactConstraintC0(pairContacts, p);\n';
      const packedSolverStateCodeSource = usePackedCaptureGeometry
        ? /* wgsl */`
        let capturedSolverStateCode = 1u
          | (select(0u, 1u, stick) << 1u)
          | (select(0u, 1u, wholeVectorInsideCone) << 2u)
          | (select(0u, 1u, tangentialRampHappened) << 3u);
`
        : '';
      const captureMetaWriteSource = usePackedCaptureGeometry
        ? ''
        : /* wgsl */`
        // Preserve all persistent identity/debug fields and, critically, the
        // scratch lifecycle bits consumed by the unconditional finalize pass.
        let featureKey = encodedMeta & 0x1FFu;
        let cooldown = (encodedMeta >> 9u) & 0x7Fu;
        let preserveWarmstart = (encodedMeta >> 16u) & 0x1u;
        let stickAnchorReuse = (encodedMeta >> 18u) & 0x1u;
        let warmstartDebugReason = (encodedMeta >> 21u) & 0x7u;
        let currentMeta = loadContactMeta(pairContacts, p);
        storeContactMeta(pairContacts, p, vec4f(
          currentMeta.x,
          currentMeta.y,
          currentMeta.z,
          bitcast<f32>(
            featureKey
            | (cooldown << 9u)
            | (preserveWarmstart << 16u)
            | (select(0u, 1u, stick) << 17u)
            | (stickAnchorReuse << 18u)
            | (select(0u, 1u, wholeVectorInsideCone) << 19u)
            | (select(0u, 1u, tangentialRampHappened) << 20u)
            | (warmstartDebugReason << 21u)
            | contactInfoPersistentBits(encodedMeta)
            | (encodedMeta & CONTACT_SOLVER_SCRATCH_MASK),
          ),
        ));
`;
      const captureListStorageParams = manifoldCapture
        ? /* wgsl */`        pairActivity: ptr<storage, array<u32>, read>,
        activeManifolds: ptr<storage, array<u32>, read>,
        persistentManifoldMap: ptr<storage, array<u32>, read>,
`
        : '        pairActivity: ptr<storage, array<u32>, read>,\n';
      const captureDispatchUniform = '        pairDispatchCount: u32,\n';
      const captureWorkSelection = manifoldCapture
        ? /* wgsl */`
        let gid = (
          workgroupId.y * ${CONTACT_DISPATCH_WORKGROUP_WIDTH}u + workgroupId.x
        ) * ${WORKGROUP_SIZE}u + localId.x;
        let solverHeader = pairActivity[${pairActiveContactsOffset}u];
        let canonicalAlias =
          (solverHeader & ${SOLVER_CONTACT_CANONICAL_ALIAS_BIT}u) != 0u;
        let activeCount = min(min(
          solverHeader & ${SOLVER_CONTACT_COUNT_MASK}u,
          pairDispatchCount,
        ), ${maxActivePairContacts}u);
        var outputMask = 1u;
        var logicalBase = 0u;
        var fallbackPhysicalRow = ${maxPairContacts}u;
        var captureSlotCount = 1u;
        if (canonicalAlias) {
          let activeManifoldCount = min(
            activeManifolds[0],
            ${maxPersistentManifolds}u,
          );
          if (gid >= activeManifoldCount) { return; }
          let manifold = activeManifolds[gid + 1u];
          if (manifold >= ${maxPersistentManifolds}u) { return; }
          outputMask = persistentManifoldMap[
            ${persistentOutputMaskOffset}u + manifold
          ];
          if (outputMask == 0u) { return; }
          logicalBase = manifold * ${pairManifoldSlots}u;
          captureSlotCount = ${pairManifoldSlots}u;
        } else {
          if (gid >= activeCount) { return; }
          fallbackPhysicalRow = pairActivity[
            ${pairActiveContactsOffset + 1}u + gid
          ];
          if (fallbackPhysicalRow >= ${maxPairContacts}u) { return; }
        }
        var cachedBodyA = 0xffffffffu;
        var cachedBodyB = 0xffffffffu;
        var cachedDPosA = vec3f(0.0);
        var cachedDPosB = vec3f(0.0);
        var cachedDThetaA = vec3f(0.0);
        var cachedDThetaB = vec3f(0.0);
        for (var slot = 0u; slot < captureSlotCount; slot++) {
          if (canonicalAlias && (outputMask & (1u << slot)) == 0u) { continue; }
          var p = fallbackPhysicalRow;
          if (canonicalAlias) {
            p = persistentManifoldMap[
              ${persistentSlotRowOffset}u + logicalBase + slot
            ];
          }
          if (p >= ${maxPairContacts}u) { continue; }
`
        : /* wgsl */`
        let gid = (
          workgroupId.y * ${CONTACT_DISPATCH_WORKGROUP_WIDTH}u + workgroupId.x
        ) * ${WORKGROUP_SIZE}u + localId.x;
        let solverHeader = pairActivity[${pairActiveContactsOffset}u];
        let activeCount = min(min(
          solverHeader & ${SOLVER_CONTACT_COUNT_MASK}u,
          pairDispatchCount,
        ), ${maxActivePairContacts}u);
        if (gid >= activeCount) { return; }

        let activeEntriesOffset = select(
          ${pairActiveContactsOffset + 1}u,
          ${pairCanonicalActiveContactsOffset + 1}u,
          (solverHeader & ${SOLVER_CONTACT_CANONICAL_ALIAS_BIT}u) != 0u,
        );
        let p = pairActivity[activeEntriesOffset + gid];
        if (p >= ${maxPairContacts}u) { return; }
`;
      const contactEndpointDeltaSource = manifoldCapture
        ? useCaptureBodyDeltaCache
          ? /* wgsl */`
        if (i != cachedBodyA || j != cachedBodyB) {
          let deltaBaseA = i * 2u;
          let deltaBaseB = j * 2u;
          cachedDPosA = captureBodyDelta[deltaBaseA].xyz;
          cachedDPosB = captureBodyDelta[deltaBaseB].xyz;
          cachedDThetaA = captureBodyDelta[deltaBaseA + 1u].xyz;
          cachedDThetaB = captureBodyDelta[deltaBaseB + 1u].xyz;
          cachedBodyA = i;
          cachedBodyB = j;
        }
        let dPosA = cachedDPosA;
        let dPosB = cachedDPosB;
        let dThetaA = cachedDThetaA;
        let dThetaB = cachedDThetaB;
`
          : /* wgsl */`
        if (i != cachedBodyA || j != cachedBodyB) {
          let solvePoseBaseA = i * ${INERTIAL_POSE_VEC4S_PER_BODY}u;
          let solvePoseBaseB = j * ${INERTIAL_POSE_VEC4S_PER_BODY}u;
          cachedDThetaA = inertialPose[solvePoseBaseA + 4u].xyz;
          cachedDThetaB = inertialPose[solvePoseBaseB + 4u].xyz;
          cachedDPosA = inertialPose[solvePoseBaseA + 2u].xyz
            - initialPose[i * 2u].xyz;
          cachedDPosB = inertialPose[solvePoseBaseB + 2u].xyz
            - initialPose[j * 2u].xyz;
          cachedBodyA = i;
          cachedBodyB = j;
        }
        let dPosA = cachedDPosA;
        let dPosB = cachedDPosB;
        let dThetaA = cachedDThetaA;
        let dThetaB = cachedDThetaB;
`
        : useCaptureBodyDeltaCache
          ? /* wgsl */`
        let deltaBaseA = i * 2u;
        let deltaBaseB = j * 2u;
        let dPosA = captureBodyDelta[deltaBaseA].xyz;
        let dPosB = captureBodyDelta[deltaBaseB].xyz;
        let dThetaA = captureBodyDelta[deltaBaseA + 1u].xyz;
        let dThetaB = captureBodyDelta[deltaBaseB + 1u].xyz;
`
          : /* wgsl */`
        let solvePoseBaseA = i * ${INERTIAL_POSE_VEC4S_PER_BODY}u;
        let solvePoseBaseB = j * ${INERTIAL_POSE_VEC4S_PER_BODY}u;
        let dThetaA = inertialPose[solvePoseBaseA + 4u].xyz;
        let dThetaB = inertialPose[solvePoseBaseB + 4u].xyz;
        let posA = inertialPose[solvePoseBaseA + 2u].xyz;
        let posB = inertialPose[solvePoseBaseB + 2u].xyz;
        let prevPosA = initialPose[i * 2u].xyz;
        let prevPosB = initialPose[j * 2u].xyz;
        let dPosA = posA - prevPosA;
        let dPosB = posB - prevPosB;
`;
      const captureLoopClose = manifoldCapture ? '        }\n' : '';
      return wgslFn(/* wgsl */`
      fn compute(
${capturePoseStorageParams}
${captureContactStorageParams}
${useSolverHotState
  ? '        contactSolverHot: ptr<storage, array<vec4f>, read_write>,\n'
  : ''}
${captureListStorageParams}
${adaptiveResidualTracking ? /* wgsl */`
        adaptiveState: ptr<storage, array<atomic<u32>>, read_write>,
        adaptiveResiduals: ptr<storage, array<atomic<u32>>, read_write>,
` : ''}
${captureDispatchUniform}
        beta: f32,
${adaptiveResidualTracking ? /* wgsl */`
        adaptiveDualTolerance: f32,
        adaptivePenaltyTolerance: f32,
` : ''}
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let regularizationAlpha: f32 = ${AVBD_REFERENCE_REGULARIZATION_ALPHA};
        let kStart: f32 = ${AVBD_K_START};
        let kMax: f32 = 10000000000.0;
        let lambdaMax: f32 = 10000000000.0;

${captureWorkSelection}
        let prevDual = ${useSolverHotState
  ? 'loadSolverContactDual(contactSolverHot, p)'
  : 'loadContactDual(pairContacts, p)'};
        let prevPenalty = ${useSolverHotState
  ? 'loadSolverContactPenalty(contactSolverHot, p)'
  : 'loadContactPenalty(pairContacts, p)'};
        let frictionScale = max(prevPenalty.w, 0.0);
${captureContactGeometrySource}
        if (dot(n, n) <= 1e-10) {
${adaptiveResidualTracking ? /* wgsl */`
          if (atomicLoad(&adaptiveState[${ADAPTIVE_ENABLED}u]) != 0u) {
            let previousContactFinite = adaptiveFinite4(prevDual)
              && adaptiveFinite4(prevPenalty)
              && adaptiveFiniteF32(kStart);
            if (!previousContactFinite) {
              markAdaptiveInvalid(adaptiveState);
            } else if (
              atomicLoad(&adaptiveState[${ADAPTIVE_TRACK_RESIDUALS}u]) != 0u
            ) {
              let residualShard = (gid * 747796405u) & ${ADAPTIVE_RESIDUAL_SHARDS - 1}u;
              let dualMagnitude = max(
                abs(prevDual.x),
                max(abs(prevDual.y), max(abs(prevDual.z), abs(prevDual.w))),
              );
              let penaltyDelta = max(
                abs(prevPenalty.x - kStart),
                max(abs(prevPenalty.y - kStart), abs(prevPenalty.z - kStart)),
              );
              let normalizedContactResidual = max(
                dualMagnitude / max(1.0, dualMagnitude)
                  / max(adaptiveDualTolerance, 1e-30),
                penaltyDelta
                  / max(1.0, max(abs(prevPenalty.x), max(abs(prevPenalty.y), abs(prevPenalty.z))))
                  / max(adaptivePenaltyTolerance, 1e-30),
              );
              markAdaptiveResidualViolation(
                adaptiveState,
                adaptiveResiduals,
                residualShard,
                1u,
                normalizedContactResidual,
              );
            }
          }
` : ''}
${useSolverHotState ? /* wgsl */`${persistentFinalCapture ? '' : /* wgsl */`          storeSolverContactState(
            contactSolverHot,
            p,
            vec4f(0.0),
            packContactPenalty(kStart, vec2f(kStart), frictionScale, kStart),
          );
`}${persistentFinalCapture ? /* wgsl */`          commitReferenceContactPersistent(
            pairContacts,
            p,
            vec4f(0.0),
            packContactPenalty(kStart, vec2f(kStart), frictionScale, kStart),
            0u,
            kStart,
          );
` : ''}
` : /* wgsl */`          storeContactRecord(
            pairContacts,
            p,
            vec4f(0.0),
            kStart,
            vec2f(kStart),
            0.0,
            vec2f(0.0),
            frictionScale,
            kStart,
            preserveSolverScratch,
          );
`}
          ${invalidContactExit}
        }

        if (!solverEndpointsValid || i >= ${maxBodies}u || j >= ${maxBodies}u || i == j) {
${adaptiveResidualTracking ? /* wgsl */`
          if (atomicLoad(&adaptiveState[${ADAPTIVE_ENABLED}u]) != 0u) {
            markAdaptiveInvalid(adaptiveState);
          }
` : ''}
${useSolverHotState ? /* wgsl */`${persistentFinalCapture ? '' : /* wgsl */`          storeSolverContactState(
            contactSolverHot,
            p,
            vec4f(0.0),
            packContactPenalty(kStart, vec2f(kStart), frictionScale, kStart),
          );
`}${persistentFinalCapture ? /* wgsl */`          commitReferenceContactPersistent(
            pairContacts,
            p,
            vec4f(0.0),
            packContactPenalty(kStart, vec2f(kStart), frictionScale, kStart),
            0u,
            kStart,
          );
` : ''}
` : /* wgsl */`          storeContactRecord(
            pairContacts,
            p,
            vec4f(0.0),
            kStart,
            vec2f(kStart),
            0.0,
            vec2f(0.0),
            frictionScale,
            kStart,
            preserveSolverScratch,
          );
`}
          ${invalidContactExit}
        }

        // Equation 18 uses the world-space arms cached by prepare at x^t.
${captureContactArmSource}
        let ra = armA.xyz;
${contactEndpointDeltaSource}

        let jAL = -n;
        let jBL = n;
        let jAA = -cross(ra, n);
        let jBA = cross(rb, n);
${captureContactTangentSource}
        let t1 = tangentBasis.t1;
        let t2 = tangentBasis.t2;
        let jt1AL = -t1;
        let jt1BL = t1;
        let jt1AA = -cross(ra, t1);
        let jt1BA = cross(rb, t1);
        let jt2AL = -t2;
        let jt2BL = t2;
        let jt2AA = -cross(ra, t2);
        let jt2BA = cross(rb, t2);

${captureConstraintC0Source}
        let cRegN = contactConstraintC0Scale(encodedMeta, regularizationAlpha) * cachedConstraintC0.x
          + dot(jAL, dPosA)
          + dot(jAA, dThetaA)
          + dot(jBL, dPosB)
          + dot(jBA, dThetaB);
        let cRegT1 = contactConstraintC0Scale(encodedMeta, regularizationAlpha) * cachedConstraintC0.y
          + dot(jt1AL, dPosA)
          + dot(jt1AA, dThetaA)
          + dot(jt1BL, dPosB)
          + dot(jt1BA, dThetaB);
        let cRegT2 = contactConstraintC0Scale(encodedMeta, regularizationAlpha) * cachedConstraintC0.z
          + dot(jt2AL, dPosA)
          + dot(jt2AA, dThetaA)
          + dot(jt2BL, dPosB)
          + dot(jt2BA, dThetaB);

        let prevDualN = min(prevDual.x, 0.0);
        let prevDualTB = prevDual.yz;
        let prevPenaltyN = clamp(max(prevPenalty.x, kStart), kStart, kMax);
        let prevPenaltyTB = clamp(
          max(prevPenalty.yz, vec2f(kStart)),
          vec2f(kStart),
          vec2f(kMax),
        );
        let dualN = clamp(prevDualN + prevPenaltyN * cRegN, -lambdaMax, 0.0);
        var penaltyN = prevPenaltyN;
        if (dualN < 0.0 && dualN > -lambdaMax) {
          penaltyN = clamp(prevPenaltyN + beta * abs(cRegN), kStart, kMax);
        }

        let lambdaPlusTB = vec2f(
          prevDualTB.x + prevPenaltyTB.x * cRegT1,
          prevDualTB.y + prevPenaltyTB.y * cRegT2,
        );
        var dualTB = lambdaPlusTB;
        let referenceBound = frictionScale * max(-dualN, 0.0);
        let unconstrainedTLen2 = dot(lambdaPlusTB, lambdaPlusTB);
        let wholeVectorInsideCone = !speculativeContact
          && unconstrainedTLen2 <= referenceBound * referenceBound + 1e-12;
        if (!wholeVectorInsideCone && unconstrainedTLen2 > 1e-12) {
          dualTB *= referenceBound * inverseSqrt(unconstrainedTLen2);
        }
        if (dualN >= 0.0 || referenceBound <= 1e-6) {
          dualTB = vec2f(0.0);
        }

        let tangentialError = length(vec2f(cRegT1, cRegT2));
        var penaltyTB = prevPenaltyTB;
        var stick = false;
        var tangentialRampHappened = false;
        if (dualN < 0.0) {
          // Reference Coulomb projection is a single vector constraint. Both
          // tangent penalties advance only while the whole vector is inside.
          if (wholeVectorInsideCone) {
            penaltyTB = min(
              prevPenaltyTB + beta * abs(vec2f(cRegT1, cRegT2)),
              vec2f(kMax),
            );
            tangentialRampHappened = abs(penaltyTB.x - prevPenaltyTB.x) > 1e-6
              || abs(penaltyTB.y - prevPenaltyTB.y) > 1e-6;
          }
          stick = wholeVectorInsideCone && tangentialError < ${AVBD_STICK_THRESHOLD};
        }

${adaptiveResidualTracking ? /* wgsl */`
        if (atomicLoad(&adaptiveState[${ADAPTIVE_ENABLED}u]) != 0u) {
          // Check raw storage before min/max/clamp can mask a NaN operand.
          let contactStateFinite = adaptiveFinite4(prevDual)
            && adaptiveFinite4(prevPenalty)
            && adaptiveFinite4(vec4f(prevDualN, prevDualTB, prevPenaltyN))
            && adaptiveFinite4(vec4f(prevPenaltyTB, dualN, dualTB.x))
            && adaptiveFinite4(vec4f(dualTB.y, penaltyN, penaltyTB))
            && adaptiveFinite4(vec4f(cRegN, cRegT1, cRegT2, 0.0));
          if (!contactStateFinite) {
            markAdaptiveInvalid(adaptiveState);
          } else if (
            atomicLoad(&adaptiveState[${ADAPTIVE_TRACK_RESIDUALS}u]) != 0u
          ) {
            let dualScale = max(
              1.0,
              max(
                max(abs(prevDualN), abs(dualN)),
                max(
                  max(abs(prevDualTB.x), abs(dualTB.x)),
                  max(abs(prevDualTB.y), abs(dualTB.y)),
                ),
              ),
            );
            let dualResidual = max(
              abs(dualN - prevDualN),
              max(abs(dualTB.x - prevDualTB.x), abs(dualTB.y - prevDualTB.y)),
            ) / dualScale;
            let penaltyScale = max(
              1.0,
              max(
                max(abs(prevPenaltyN), abs(penaltyN)),
                max(
                  max(abs(prevPenaltyTB.x), abs(penaltyTB.x)),
                  max(abs(prevPenaltyTB.y), abs(penaltyTB.y)),
                ),
              ),
            );
            let penaltyResidual = max(
              abs(penaltyN - prevPenaltyN),
              max(
                abs(penaltyTB.x - prevPenaltyTB.x),
                abs(penaltyTB.y - prevPenaltyTB.y),
              ),
            ) / penaltyScale;
            let residualShard = (gid * 747796405u) & ${ADAPTIVE_RESIDUAL_SHARDS - 1}u;
            let normalizedContactResidual = max(
              dualResidual / max(adaptiveDualTolerance, 1e-30),
              penaltyResidual / max(adaptivePenaltyTolerance, 1e-30),
            );
            markAdaptiveResidualViolation(
              adaptiveState,
              adaptiveResiduals,
              residualShard,
              1u,
              normalizedContactResidual,
            );
          }
        }
` : ''}

${packedSolverStateCodeSource}
${useSolverHotState ? /* wgsl */`${persistentFinalCapture ? '' : /* wgsl */`        storeSolverContactState(
          contactSolverHot,
          p,
          vec4f(dualN, dualTB, ${usePackedCaptureGeometry ? 'f32(capturedSolverStateCode)' : '0.0'}),
          packContactPenalty(penaltyN, penaltyTB, frictionScale, kStart),
        );
`}${persistentFinalCapture ? /* wgsl */`        // The persistent row is the final public state. prepare overwrites the
        // sidecar before the next solve, so avoid a dead 32-byte final write.
        commitReferenceContactPersistent(
          pairContacts,
          p,
          vec4f(dualN, dualTB, f32(capturedSolverStateCode)),
          packContactPenalty(penaltyN, penaltyTB, frictionScale, kStart),
          capturedSolverStateCode,
          kStart,
        );
` : ''}
` : /* wgsl */`        storeContactRecord(
          pairContacts,
          p,
          vec4f(dualN, dualTB, 0.0),
          penaltyN,
          penaltyTB,
          -dualN,
          dualTB,
          frictionScale,
          kStart,
          preserveSolverScratch,
        );
`}

${captureMetaWriteSource}
${captureLoopClose}
      }
    `, [
      tangentBasisHelpers,
      contactRecordHelpers,
      contactStateHelpers,
      ...(persistentFinalCapture ? [referenceContactCommitHelpers] : []),
      ...(useSolverHotState ? [contactSolverHotHelpers] : []),
      ...(adaptiveResidualTracking ? [adaptiveResidualHelpers] : []),
    ]);
    };
    const referenceCapturePairDualStateShader = makeReferenceCapturePairDualStateShader(false);
    const referenceFinalCapturePairDualStateShader =
      finalCaptureCommitFusion
      && this.packedContactSolverGeometry
      && this.referenceFinalizerSpecialization
        ? makeReferenceCapturePairDualStateShader(false, false, true)
        : null;
    const referenceManifoldCapturePairDualStateShader = manifoldReferenceCapture
      ? makeReferenceCapturePairDualStateShader(false, true)
      : null;
    const adaptiveReferenceCapturePairDualStateShader = makeReferenceCapturePairDualStateShader(true);

    this.capturePairDualStateKernel = capturePairDualStateShader({
      inertialPose: storage(inertialPose, 'vec4f', maxBodies * INERTIAL_POSE_VEC4S_PER_BODY).toReadOnly(),
      initialPose: storage(initialPose, 'vec4f', maxBodies * 2).toReadOnly(),
      pairContacts: storage(pairContacts, 'vec4f', maxPairContacts * CONTACT_RECORD_VEC4S),
      pairActivity: storage(pairActivity, 'uint', pairActivityWordCount),
      pairDispatchCount: uniform(0),
      regularizationAlpha: uniform(AVBD_REGULARIZATION_ALPHA_DEFAULT),
      beta: uniform(AVBD_BETA),
      frictionStatic: uniform(AVBD_FRICTION_STATIC),
      frictionDynamic: uniform(AVBD_FRICTION_DYNAMIC),
      kStart: uniform(AVBD_K_START),
      kMax: uniform(10000000000.0),
      lambdaMax: uniform(10000000000.0),
      preventPenetratingNormalDropout: uniform(0),
      enableNormalReleaseHeuristic: uniform(0),
      freezeTangentialPenaltyOnStick: uniform(0),
      freezeTangentialPenaltyUpdates: uniform(0),
      useIsotropicTangentialPenaltyOnStick: uniform(0),
      rampTangentialPenaltyOnlyWhenNotSticking: uniform(0),
      useReferenceTangentialUpdate: uniform(1),
      stickExitThreshold: uniform(AVBD_STICK_THRESHOLD),
      tangentialPenaltyRampDeadzone: uniform(0.0),
      tangentialPenaltySlipRampMaxDelta: uniform(0.0),
      tangentialPenaltyCapScale: uniform(0.0),
      useNormalContactMargin: uniform(1),
      dt: uniform(1 / 60 / 4),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Capture Pair Dual');
    const createReferenceCapturePairDualStateKernel = (
      shader: any,
      adaptiveResidualTracking: boolean,
      manifoldCapture = false,
      persistentFinalCapture = false,
    ): any => {
      const useCaptureBodyDeltaCache = this.captureBodyDeltaCache
        && !adaptiveResidualTracking;
      const usePackedCaptureGeometry = this.packedContactSolverGeometry
        && !adaptiveResidualTracking
        && this.referenceFinalizerSpecialization;
      const bindings: Record<string, any> = {
        pairActivity: storage(pairActivity, 'uint', pairActivityWordCount).toReadOnly(),
        pairDispatchCount: uniform(0),
        beta: uniform(AVBD_BETA),
        workgroupId,
        localId,
      };
      if (usePackedCaptureGeometry) {
        bindings.contactSolverGeometry = storage(
          this.contactSolverGeometryAttr,
          'vec4f',
          maxPairContacts * CONTACT_SOLVER_GEOMETRY_VEC4S,
        ).toReadOnly();
        if (persistentFinalCapture) {
          bindings.pairContacts = storage(
            pairContacts,
            'vec4f',
            maxPairContacts * CONTACT_RECORD_VEC4S,
          );
        }
      } else {
        bindings.pairContacts = storage(
          pairContacts,
          'vec4f',
          maxPairContacts * CONTACT_RECORD_VEC4S,
        );
      }
      if (useCaptureBodyDeltaCache) {
        bindings.captureBodyDelta = storage(
          this.captureBodyDeltaAttr,
          'vec4f',
          maxBodies * 2,
        ).toReadOnly();
      } else {
        bindings.inertialPose = storage(
          inertialPose,
          'vec4f',
          maxBodies * INERTIAL_POSE_VEC4S_PER_BODY,
        ).toReadOnly();
        bindings.initialPose = storage(initialPose, 'vec4f', maxBodies * 2).toReadOnly();
      }
      if (manifoldCapture) {
        bindings.activeManifolds = storage(
          activeManifolds,
          'uint',
          activeManifolds.count,
        ).toReadOnly();
        bindings.persistentManifoldMap = storage(
          persistentManifoldMap,
          'uint',
          persistentManifoldMap.count,
        ).toReadOnly();
      }
      if (this.denseContactSolverState && !adaptiveResidualTracking) {
        bindings.contactSolverHot = storage(
          this.contactSolverHotAttr,
          'vec4f',
          maxPairContacts * CONTACT_SOLVER_HOT_VEC4S,
        );
      }
      if (this.cacheContactTangentInvLength && !usePackedCaptureGeometry) {
        bindings.contactTangentInvLength = storage(
          this.contactTangentInvLengthAttr,
          'float',
          maxPairContacts,
        ).toReadOnly();
      }
      if (adaptiveResidualTracking) {
        bindings.adaptiveState = storage(
          this.adaptiveStateAttr,
          'uint',
          ADAPTIVE_STATE_WORDS,
        ).toAtomic();
        bindings.adaptiveResiduals = storage(
          this.adaptiveResidualsAttr,
          'uint',
          ADAPTIVE_RESIDUAL_WORDS,
        ).toAtomic();
        bindings.adaptiveDualTolerance = uniform(2.5e-4);
        bindings.adaptivePenaltyTolerance = uniform(2.5e-4);
      }
      return shader(bindings)
        .computeKernel([WORKGROUP_SIZE, 1, 1])
        .setName(`AVBD ${adaptiveResidualTracking ? 'Adaptive ' : ''}${manifoldCapture ? 'Manifold ' : ''}${persistentFinalCapture ? 'Final Commit ' : ''}Reference Contact-Only Capture Pair Dual`);
    };
    this.referenceCapturePairDualStateKernel = hasHardConstraints
      ? null
      : createReferenceCapturePairDualStateKernel(
        referenceCapturePairDualStateShader,
        false,
      );
    this.referenceFinalCapturePairDualStateKernel = hasHardConstraints
      || !referenceFinalCapturePairDualStateShader
      ? null
      : createReferenceCapturePairDualStateKernel(
        referenceFinalCapturePairDualStateShader,
        false,
        false,
        true,
      );
    this.referenceManifoldCapturePairDualStateKernel = hasHardConstraints
      || !referenceManifoldCapturePairDualStateShader
      ? null
      : createReferenceCapturePairDualStateKernel(
        referenceManifoldCapturePairDualStateShader,
        false,
        true,
      );
    this.adaptiveReferenceCapturePairDualStateKernel = hasHardConstraints
      ? null
      : createReferenceCapturePairDualStateKernel(
        adaptiveReferenceCapturePairDualStateShader,
        true,
      );
    assertStorageBufferBudget(
      'AVBD Reference Contact-Only Capture Pair Dual',
      (this.cacheContactTangentInvLength ? 6 : 5)
        - (this.captureBodyDeltaCache ? 1 : 0)
        - (this.packedContactSolverGeometry && this.cacheContactTangentInvLength ? 1 : 0),
    );
    assertStorageBufferBudget(
      'AVBD Final Commit Reference Contact-Only Capture Pair Dual',
      (this.cacheContactTangentInvLength ? 7 : 6)
        - (this.captureBodyDeltaCache ? 1 : 0)
        - (this.packedContactSolverGeometry && this.cacheContactTangentInvLength ? 1 : 0),
    );
    assertStorageBufferBudget(
      'AVBD Manifold Reference Contact-Only Capture Pair Dual',
      (this.cacheContactTangentInvLength ? 8 : 7)
        - (this.captureBodyDeltaCache ? 1 : 0)
        - (this.packedContactSolverGeometry && this.cacheContactTangentInvLength ? 1 : 0),
    );
    assertStorageBufferBudget(
      'AVBD Adaptive Reference Contact-Only Capture Pair Dual',
      this.cacheContactTangentInvLength ? 7 : 6,
    );

    const finalizeContactSolverScratchShader = wgslFn(/* wgsl */`
      fn compute(
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        contactSolverHot: ptr<storage, array<vec4f>, read_write>,
        pairActivity: ptr<storage, array<u32>, read_write>,
        pairDispatchCount: u32,
        useReferenceTangentialUpdate: u32,
        useSolverHotState: u32,
        kStart: f32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = (
          workgroupId.y * ${CONTACT_DISPATCH_WORKGROUP_WIDTH}u + workgroupId.x
        ) * ${WORKGROUP_SIZE}u + localId.x;
        let solverHeader = pairActivity[${pairActiveContactsOffset}u];
        let activeCount = min(min(
          solverHeader & ${SOLVER_CONTACT_COUNT_MASK}u,
          pairDispatchCount,
        ), ${maxActivePairContacts}u);
        if (gid >= activeCount) { return; }

        let activeEntriesOffset = select(
          ${pairActiveContactsOffset + 1}u,
          ${pairCanonicalActiveContactsOffset + 1}u,
          (solverHeader & ${SOLVER_CONTACT_CANONICAL_ALIAS_BIT}u) != 0u,
        );
        let p = pairActivity[activeEntriesOffset + gid];
        if (p >= ${maxPairContacts}u) { return; }
        let contactMeta = loadContactMeta(pairContacts, p);
        let encodedMeta = bitcast<u32>(contactMeta.w);

        var state = loadContactStateHot(pairContacts, p, kStart);
        if (useSolverHotState != 0u) {
          let solverPenalty = loadSolverContactPenalty(contactSolverHot, p);
          state = makeContactState(
            loadSolverContactDual(contactSolverHot, p),
            solverPenalty,
            max(solverPenalty.w, 0.0),
            kStart,
          );
          // The persistent row remains authoritative outside the solve. Commit
          // the exact sidecar values once, after all fixed iterations.
          storeContactDual(pairContacts, p, state.dual);
          storeContactPenalty(pairContacts, p, state.penalty);
        }
        let n = loadContactNormalPen(pairContacts, p).xyz;
        var lambdaTB = vec2f(0.0);
        if (dot(n, n) > 1e-10) {
          let armAWorld = loadContactArmA(pairContacts, p);
          let tangentBasis = tangentBasisFromCachedT1(
            n,
            loadContactTangentT1(pairContacts, p),
            armAWorld.w,
          );
          lambdaTB = contactStateDualTB(
            state,
            n,
            tangentBasis.t1,
            tangentBasis.t2,
            useReferenceTangentialUpdate,
          );
        }

        if ((encodedMeta & CONTACT_SOLVER_SCRATCH_ACTIVE_BIT) != 0u) {
          let scratchArms = loadContactShadow(pairContacts, p);
          let armAWorld = loadContactArmA(pairContacts, p);
          let armBWorld = loadContactArmB(pairContacts, p);
          let constraintC0 = loadContactConstraintC0(pairContacts, p);
          let sphereArmFlags = (encodedMeta >> CONTACT_SOLVER_SCRATCH_ARM_FLAGS_SHIFT) & 0x3u;
          storeContactArmA(pairContacts, p, vec4f(scratchArms.xyz, armAWorld.w));
          storeContactArmB(pairContacts, p, vec4f(
            scratchArms.w,
            constraintC0.w,
            armBWorld.w,
            f32(sphereArmFlags),
          ));
          storeContactConstraintC0(pairContacts, p, vec4f(constraintC0.xyz, 0.0));
          storeContactMeta(pairContacts, p, vec4f(
            contactMeta.xyz,
            bitcast<f32>(encodedMeta & ~(
              CONTACT_SOLVER_SCRATCH_MASK | CONTACT_INFO_RESERVED_BIT_30
            )),
          ));
        }
        if (
          (encodedMeta & CONTACT_SOLVER_SCRATCH_ACTIVE_BIT) != 0u
          || useSolverHotState != 0u
        ) {
          storeContactShadow(pairContacts, p, packContactShadow(
            state.lambdaN,
            lambdaTB,
            state.frictionScale,
          ));
        }
      }
    `, [
      tangentBasisHelpers,
      contactRecordHelpers,
      contactStateHelpers,
      contactSolverHotHelpers,
    ]);

    this.finalizeContactSolverScratchKernel = finalizeContactSolverScratchShader({
      pairContacts: storage(pairContacts, 'vec4f', maxPairContacts * CONTACT_RECORD_VEC4S),
      contactSolverHot: storage(
        this.contactSolverHotAttr,
        'vec4f',
        maxPairContacts * CONTACT_SOLVER_HOT_VEC4S,
      ),
      pairActivity: storage(pairActivity, 'uint', pairActivityWordCount),
      pairDispatchCount: uniform(0),
      useReferenceTangentialUpdate: uniform(1),
      useSolverHotState: uniform(0),
      kStart: uniform(AVBD_K_START),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Commit Contact Solver State + Restore Persistent Arms');
    assertStorageBufferBudget('AVBD Commit Contact Solver State + Restore Persistent Arms', 3);

    // Fixed reference solves always consume tangent-basis dual coordinates
    // from the dense sidecar. Specializing that final pass keeps the exact
    // persistent writes while removing the generic world-space tangent
    // projection and both uniform mode branches from every active contact.
    const referenceFinalizeContactSolverScratchShader = wgslFn(/* wgsl */`
      fn compute(
        pairContacts: ptr<storage, array<vec4f>, read_write>,
        contactSolverHot: ptr<storage, array<vec4f>, read_write>,
        pairActivity: ptr<storage, array<u32>, read>,
        pairDispatchCount: u32,
        kStart: f32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = (
          workgroupId.y * ${CONTACT_DISPATCH_WORKGROUP_WIDTH}u + workgroupId.x
        ) * ${WORKGROUP_SIZE}u + localId.x;
        let solverHeader = pairActivity[${pairActiveContactsOffset}u];
        let activeCount = min(min(
          solverHeader & ${SOLVER_CONTACT_COUNT_MASK}u,
          pairDispatchCount,
        ), ${maxActivePairContacts}u);
        if (gid >= activeCount) { return; }

        let activeEntriesOffset = select(
          ${pairActiveContactsOffset + 1}u,
          ${pairCanonicalActiveContactsOffset + 1}u,
          (solverHeader & ${SOLVER_CONTACT_CANONICAL_ALIAS_BIT}u) != 0u,
        );
        let p = pairActivity[activeEntriesOffset + gid];
        if (p >= ${maxPairContacts}u) { return; }

        let solverPenalty = loadSolverContactPenalty(contactSolverHot, p);
        let solverDual = loadSolverContactDual(contactSolverHot, p);
        let solverStateCode = u32(solverDual.w + 0.5);
        commitReferenceContactPersistent(
          pairContacts,
          p,
          solverDual,
          solverPenalty,
          solverStateCode,
          kStart,
        );
      }
    `, [
      contactRecordHelpers,
      contactStateHelpers,
      referenceContactCommitHelpers,
      contactSolverHotHelpers,
    ]);
    const needsStandaloneReferenceFinalizer =
      this.referenceFinalizerSpecialization
      && (
        !finalCaptureCommitFusion
        || !this.packedContactSolverGeometry
        || manifoldReferenceCapture
      );
    this.referenceFinalizeContactSolverScratchKernel =
      needsStandaloneReferenceFinalizer
        ? referenceFinalizeContactSolverScratchShader({
          pairContacts: storage(
            pairContacts,
            'vec4f',
            maxPairContacts * CONTACT_RECORD_VEC4S,
          ),
          contactSolverHot: storage(
            this.contactSolverHotAttr,
            'vec4f',
            maxPairContacts * CONTACT_SOLVER_HOT_VEC4S,
          ),
          pairActivity: storage(
            pairActivity,
            'uint',
            pairActivityWordCount,
          ).toReadOnly(),
          pairDispatchCount: uniform(0),
          kStart: uniform(AVBD_K_START),
          workgroupId,
          localId,
        }).computeKernel([WORKGROUP_SIZE, 1, 1])
          .setName('AVBD Reference Commit Contact Solver State + Restore Persistent Arms')
        : null;
    if (needsStandaloneReferenceFinalizer) {
      assertStorageBufferBudget(
        'AVBD Reference Commit Contact Solver State + Restore Persistent Arms',
        3,
      );
    }

    const captureJointDualStateShader = wgslFn(/* wgsl */`
      fn compute(
        inertialPose: ptr<storage, array<vec4f>, read>,
        jointRecords: ptr<storage, array<vec4f>, read_write>,
        springRecords: ptr<storage, array<vec4f>, read_write>,
        jointCount: u32,
        springCount: u32,
        regularizationAlpha: f32,
        beta: f32,
        betaAngular: f32,
        kStart: f32,
        lambdaMax: f32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;

        if (gid < springCount) {
          let springMetaWords = loadSpringMetaWords(springRecords, gid);
          let springBodyA = springMetaWords.x;
          let springBodyB = springMetaWords.y;
          let worldBodyIndex = 0xffffffffu;
          let validSpringBodies = springBodyB < ${maxBodies}u
            && (springBodyA == worldBodyIndex || springBodyA < ${maxBodies}u);
          if (springMetaWords.z != 0u && validSpringBodies) {
            let anchorARest = loadSpringAnchorARest(springRecords, gid);
            let anchorBStiffness = loadSpringAnchorBStiffness(springRecords, gid);
            let restLength = max(anchorARest.w, 0.0);
            let materialStiffness = max(anchorBStiffness.w, 0.0);

            var worldAnchorA = anchorARest.xyz;
            if (springBodyA != worldBodyIndex) {
              let poseBaseA = springBodyA * ${INERTIAL_POSE_VEC4S_PER_BODY}u;
              let posA = inertialPose[poseBaseA + 2u].xyz;
              let qA = normalize(inertialPose[poseBaseA + 3u]);
              worldAnchorA = posA + qrot(qA, anchorARest.xyz);
            }
            let poseBaseB = springBodyB * ${INERTIAL_POSE_VEC4S_PER_BODY}u;
            let posB = inertialPose[poseBaseB + 2u].xyz;
            let qB = normalize(inertialPose[poseBaseB + 3u]);
            let worldAnchorB = posB + qrot(qB, anchorBStiffness.xyz);
            let springLength = length(worldAnchorA - worldAnchorB);
            let springConstraint = springLength - restLength;

            let previousState = loadSpringState(springRecords, gid);
            let rigidDistance = springMetaWords.w != 0u;
            if (rigidDistance) {
              // Paper Eq. 18: use the same regularized constraint in both the
              // primal force/Hessian and the augmented-Lagrangian dual update.
              let regularizedConstraint = springConstraint
                - regularizationAlpha * previousState.y;
              let previousPenalty = max(previousState.x, kStart);
              let currentPenalty = min(
                ${AVBD_JOINT_PENALTY_MAX},
                previousPenalty + beta * abs(regularizedConstraint),
              );
              let currentLambda = clamp(
                previousState.w + previousPenalty * regularizedConstraint,
                -lambdaMax,
                lambdaMax,
              );
              storeSpringState(
                springRecords,
                gid,
                vec4f(currentPenalty, previousState.y, springLength, currentLambda),
              );
            } else {
              let previousPenalty = min(max(previousState.x, 0.0), materialStiffness);
              // Paper Equation 16: ramp finite stiffness after every complete primal sweep.
              let currentPenalty = min(
                materialStiffness,
                previousPenalty + beta * abs(springConstraint),
              );
              storeSpringState(
                springRecords,
                gid,
                vec4f(
                  currentPenalty,
                  springConstraint,
                  springLength,
                  currentPenalty - previousPenalty,
                ),
              );
            }
          }
        }

        if (gid >= jointCount) { return; }

        let jointMetaWords = loadJointMetaWords(jointRecords, gid);
        if (jointMetaWords.w == 0u) { return; }

        let bodyA = jointMetaWords.x;
        let bodyB = jointMetaWords.y;
        let jointType = jointMetaWords.z;
        let anchorA = loadJointAnchorA(jointRecords, gid);
        let anchorB = loadJointAnchorB(jointRecords, gid);
        let torqueArm = max(anchorA.w, 1.0);
        var currentQA = vec4f(0.0, 0.0, 0.0, 1.0);
        var currentPosA = anchorA.xyz;
        if (bodyA != WORLD_BODY_INDEX) {
          let solvePoseBaseA = bodyA * ${INERTIAL_POSE_VEC4S_PER_BODY}u;
          currentQA = normalize(inertialPose[solvePoseBaseA + 3u]);
          currentPosA = inertialPose[solvePoseBaseA + 2u].xyz;
        }
        let solvePoseBaseB = bodyB * ${INERTIAL_POSE_VEC4S_PER_BODY}u;
        let currentQB = normalize(inertialPose[solvePoseBaseB + 3u]);
        let currentPosB = inertialPose[solvePoseBaseB + 2u].xyz;
        let worldAnchorA = jointWorldAnchor(bodyA, anchorA.xyz, currentPosA, currentQA);
        let worldAnchorB = currentPosB + qrot(currentQB, anchorB.xyz);

        let stiffness = loadJointStiffness(jointRecords, gid);
        let restRelative = loadJointRestRelativeRotation(jointRecords, gid);
        let maxPenaltyLin = vec3f(min(max(stiffness.x, kStart), ${AVBD_JOINT_PENALTY_MAX}));
        let maxPenaltyAng = vec3f(min(max(stiffness.y, 0.0), ${AVBD_JOINT_PENALTY_MAX}));

        let prevLambdaLin = loadJointLambdaLin(jointRecords, gid).xyz;
        let prevLambdaAng = loadJointLambdaAng(jointRecords, gid).xyz;
        let prevPenaltyLin = max(loadJointPenaltyLin(jointRecords, gid).xyz, vec3f(kStart));
        let c0Lin = loadJointC0Lin(jointRecords, gid).xyz;
        let c0Ang = loadJointC0Ang(jointRecords, gid).xyz;

        let rigidLinear = !isFiniteF32(stiffness.x);
        let angularEnabled = jointType == JOINT_TYPE_FIXED && stiffness.y > 0.0;
        let rigidAngular = angularEnabled && !isFiniteF32(stiffness.y);
        let minPenaltyAng = select(vec3f(0.0), vec3f(kStart), angularEnabled);
        let prevPenaltyAng = max(loadJointPenaltyAng(jointRecords, gid).xyz, minPenaltyAng);
        let linearConstraint = (worldAnchorA - worldAnchorB) - select(vec3f(0.0), c0Lin * regularizationAlpha, rigidLinear);
        let penaltyLin = min(prevPenaltyLin + beta * abs(linearConstraint), maxPenaltyLin);
        let lambdaLin = clamp(prevLambdaLin + prevPenaltyLin * linearConstraint, vec3f(-lambdaMax), vec3f(lambdaMax));
        storeJointLambdaLin(
          jointRecords,
          gid,
          vec4f(select(prevLambdaLin, lambdaLin, rigidLinear), 0.0),
        );
        storeJointPenaltyLin(jointRecords, gid, vec4f(penaltyLin, 0.0));

        if (angularEnabled) {
          let angularConstraint = jointFixedAngularConstraint(bodyA, currentQA, currentQB, restRelative, torqueArm) - select(vec3f(0.0), c0Ang * regularizationAlpha, rigidAngular);
          let lambdaAng = clamp(prevLambdaAng + prevPenaltyAng * angularConstraint, vec3f(-lambdaMax), vec3f(lambdaMax));
          let penaltyAng = min(prevPenaltyAng + betaAngular * abs(angularConstraint), maxPenaltyAng);
          storeJointLambdaAng(
            jointRecords,
            gid,
            vec4f(select(prevLambdaAng, lambdaAng, rigidAngular), 0.0),
          );
          storeJointPenaltyAng(jointRecords, gid, vec4f(penaltyAng, 0.0));
        } else {
          storeJointLambdaAng(jointRecords, gid, vec4f(0.0));
          storeJointPenaltyAng(jointRecords, gid, vec4f(0.0));
        }
      }
    `, [qrot, qmul, qconj, jointRecordHelpers, jointConstraintHelpers, springRecordHelpers]);

    this.captureJointDualStateKernel = captureJointDualStateShader({
      inertialPose: storage(inertialPose, 'vec4f', maxBodies * INERTIAL_POSE_VEC4S_PER_BODY).toReadOnly(),
      jointRecords: storage(jointRecords, 'vec4f', maxJoints * JOINT_RECORD_VEC4S),
      springRecords: storage(springRecords, 'vec4f', maxSprings * SPRING_RECORD_VEC4S),
      jointCount: uniform(0),
      springCount: uniform(0),
      regularizationAlpha: uniform(AVBD_REGULARIZATION_ALPHA_DEFAULT),
      beta: uniform(AVBD_BETA),
      betaAngular: uniform(AVBD_REFERENCE_ANGULAR_DUAL_UPDATE_BETA),
      kStart: uniform(AVBD_K_START),
      lambdaMax: uniform(10000000000.0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Capture Joint and Spring Dual');
    assertStorageBufferBudget('AVBD Capture Joint and Spring Dual', 4);

    const finalizeVelocitiesShader = wgslFn(/* wgsl */`
      fn compute(
        inertialPose: ptr<storage, array<vec4f>, read>,
        initialPose: ptr<storage, array<vec4f>, read>,
        bodyConstraintCounts: ptr<storage, array<u32>, read>,
        positions: ptr<storage, array<vec4f>, read_write>,
        quaternions: ptr<storage, array<vec4f>, read_write>,
        velocities: ptr<storage, array<vec4f>, read_write>,
        prevLinearVelocities: ptr<storage, array<vec4f>, read_write>,
        angularVelocities: ptr<storage, array<vec4f>, read_write>,
        dt: f32,
        bodyCount: u32,
        workgroupId: vec3u,
        localId: vec3u,
      ) -> void {
        let gid = workgroupId.x * ${WORKGROUP_SIZE}u + localId.x;
        if (gid >= bodyCount) { return; }

        let solvePoseBase = gid * ${INERTIAL_POSE_VEC4S_PER_BODY}u;
        let constrained = (bodyConstraintCounts[gid] & 0xFFFFu) != 0u;
        // The exact minimizer for a body with no incident constraints is its
        // inertial target. The previous color-bucket builder committed this by
        // scanning every body. Folding it into the already-required finalize
        // pass keeps free-body integration exact without another O(bodyCount)
        // coloring dispatch.
        let currentPose = select(
          inertialPose[solvePoseBase],
          inertialPose[solvePoseBase + 2u],
          constrained,
        );
        let currentQ = normalize(select(
          inertialPose[solvePoseBase + 1u],
          inertialPose[solvePoseBase + 3u],
          constrained,
        ));
        positions[gid] = currentPose;
        quaternions[gid] = currentQ;

        if (currentPose.w == 0.0) {
          prevLinearVelocities[gid] = vec4f(0.0);
          velocities[gid] = vec4f(0.0);
          angularVelocities[gid] = vec4f(0.0);
          return;
        }

        // Preserve the pre-finalize linear velocity for adaptive warmstarting.
        prevLinearVelocities[gid] = velocities[gid];

        let dtSafe = max(dt, 1e-6);
        let poseBase = gid * 2u;
        let prevPos = initialPose[poseBase].xyz;
        var v = (currentPose.xyz - prevPos) / dtSafe;

        let prevQ = normalize(initialPose[poseBase + 1u]);
        let dqRaw = qmul(currentQ, qconj(prevQ));
        let dq = select(dqRaw, -dqRaw, dqRaw.w < 0.0);
        let w = 2.0 * dq.xyz / dtSafe;

        velocities[gid] = vec4f(v, 0.0);
        angularVelocities[gid] = vec4f(w, 0.0);
      }
    `, [qmul, qconj]);

    this.finalizeVelocitiesKernel = finalizeVelocitiesShader({
      inertialPose: storage(inertialPose, 'vec4f', maxBodies * INERTIAL_POSE_VEC4S_PER_BODY).toReadOnly(),
      initialPose: storage(initialPose, 'vec4f', maxBodies * 2).toReadOnly(),
      bodyConstraintCounts: storage(bodyConstraintCounts, 'uint', maxBodies).toReadOnly(),
      positions: storage(positions, 'vec4f', maxBodies),
      quaternions: storage(quaternions, 'vec4f', maxBodies),
      velocities: storage(velocities, 'vec4f', maxBodies),
      prevLinearVelocities: storage(prevLinearVelocities, 'vec4f', maxBodies),
      angularVelocities: storage(angularVelocities, 'vec4f', maxBodies),
      dt: uniform(1 / 60 / 4),
      bodyCount: uniform(0),
      workgroupId,
      localId,
    }).computeKernel([WORKGROUP_SIZE, 1, 1]).setName('AVBD Finalize Velocities');
  }

  prepare(
    renderer: any,
    bodyCount: number,
    pairDispatchCount: number,
    jointCount: number,
    springCount: number,
    bodyColorCount: number,
    lambdaWarmstartScale: number,
    dt: number,
    bodySolveMode: AvbdBodySolveMode = 'colored',
    coloringRounds = 1,
    cacheWorldArms = true,
  ): void {
    const boundedDispatch = Math.min(pairDispatchCount, this.maxActivePairContacts);
    const warmstartScale = Math.max(0.0, Math.min(1.0, lambdaWarmstartScale));
    const preparePass: any[] = [];
    const enqueue = (kernel: any, dispatch: any): void => {
      kernel.count = dispatch;
      preparePass.push(kernel);
    };
    const flush = (): void => {
      if (preparePass.length > 0) renderer.compute(preparePass);
    };

    if (boundedDispatch > 0) {
      this.buildContactDispatchArgsKernel.computeNode.parameters.pairDispatchCount.value = boundedDispatch;
      this.prepareStateKernel.computeNode.parameters.pairDispatchCount.value = boundedDispatch;
      this.prepareStateKernel.computeNode.parameters.lambdaWarmstartScale.value = warmstartScale;
      this.prepareStateKernel.computeNode.parameters.cacheWorldArms.value = cacheWorldArms ? 1 : 0;
      enqueue(this.buildContactDispatchArgsKernel, [1, 1, 1]);
      enqueue(this.prepareStateKernel, this.contactDispatchIndirectAttr);
    }

    if (jointCount > 0) {
      this.prepareJointStateKernel.computeNode.parameters.jointCount.value = jointCount;
      this.prepareJointStateKernel.computeNode.parameters.lambdaWarmstartScale.value = warmstartScale;
      this.prepareJointStateKernel.computeNode.parameters.dt.value = dt;
      enqueue(this.prepareJointStateKernel, [Math.ceil(jointCount / WORKGROUP_SIZE), 1, 1]);
    }

    if (bodyCount <= 0) {
      flush();
      return;
    }

    const bodyWorkgroups = Math.ceil(bodyCount / WORKGROUP_SIZE);
    // Reset only the live count/indirect record. Discovery below fills the
    // compact stream in the same pass that must already inspect adjacency.
    enqueue(this.clearConstrainedBodyCompactKernel, [1, 1, 1]);
    if (this.contactOnlyAdjacencyAlias) {
      this.prepareContactOnlyAdjacencyKernel.computeNode.parameters.bodyCount.value = bodyCount;
      enqueue(this.prepareContactOnlyAdjacencyKernel, [bodyWorkgroups, 1, 1]);
    } else {
      this.buildSolverConstraintListsKernel.computeNode.parameters.bodyCount.value = bodyCount;
      enqueue(this.clearBodyConstraintOverflowKernel, [1, 1, 1]);
      enqueue(this.buildSolverConstraintListsKernel, [bodyWorkgroups, 1, 1]);
    }
    if (jointCount > 0) {
      this.appendJointConstraintRefsKernel.computeNode.parameters.jointCount.value = jointCount;
      enqueue(this.appendJointConstraintRefsKernel, [Math.ceil(jointCount / WORKGROUP_SIZE), 1, 1]);
    }
    if (springCount > 0) {
      this.appendSpringConstraintRefsKernel.computeNode.parameters.springCount.value = springCount;
      this.appendSpringConstraintRefsKernel.computeNode.parameters.lambdaWarmstartScale.value = warmstartScale;
      enqueue(this.appendSpringConstraintRefsKernel, [Math.ceil(springCount / WORKGROUP_SIZE), 1, 1]);
    }
    enqueue(this.buildConstrainedBodyDispatchArgsKernel, [1, 1, 1]);

    if (bodySolveMode === 'serial') {
      flush();
      return;
    }

    const clampedColors = Math.max(
      1,
      Math.min(this.maxBodyColorCount, Math.floor(bodyColorCount)),
    );
    this.greedyBodyColorsKernel.computeNode.parameters.bodyCount.value = bodyCount;
    this.greedyBodyColorsKernel.computeNode.parameters.colorCount.value = clampedColors;
    this.greedyBodyColorsReverseKernel.computeNode.parameters.bodyCount.value = bodyCount;
    this.greedyBodyColorsReverseKernel.computeNode.parameters.colorCount.value = clampedColors;
    // The paper performs one incremental Jacobi greedy-coloring sweep per
    // timestep. A newly built graph has no prior converged coloring, so the
    // engine may request a bounded pre-solve convergence warm-up without
    // advancing positions or dual variables.
    const boundedColoringRounds = Math.max(1, Math.min(16, Math.floor(coloringRounds)));
    for (let round = 0; round < boundedColoringRounds; round++) {
      enqueue(
        (round & 1) === 0 ? this.greedyBodyColorsKernel : this.greedyBodyColorsReverseKernel,
        this.constrainedBodyDispatchIndirectAttr,
      );
    }

    const hardConstraintCount = Math.max(jointCount, springCount);
    if (hardConstraintCount > 0) {
      this.markHardColorConflictsKernel.computeNode.parameters.bodyCount.value = bodyCount;
      this.markHardColorConflictsKernel.computeNode.parameters.jointCount.value = jointCount;
      this.markHardColorConflictsKernel.computeNode.parameters.springCount.value = springCount;
      this.repairHardBodyColorsKernel.computeNode.parameters.bodyCount.value = bodyCount;
      this.repairHardBodyColorsKernel.computeNode.parameters.colorCount.value = clampedColors;
      const hardConstraintWorkgroups = Math.ceil(hardConstraintCount / WORKGROUP_SIZE);
      for (let round = 0; round < BODY_COLOR_HARD_REPAIR_ROUNDS; round++) {
        enqueue(this.markHardColorConflictsKernel, [hardConstraintWorkgroups, 1, 1]);
        enqueue(this.repairHardBodyColorsKernel, this.constrainedBodyDispatchIndirectAttr);
      }
    }

    this.markBodyColorFallbackConflictsKernel.computeNode.parameters.bodyCount.value = bodyCount;
    this.markBodyColorFallbackConflictsKernel.computeNode.parameters.pairDispatchCount.value = boundedDispatch;
    this.markBodyColorFallbackConflictsKernel.computeNode.parameters.jointCount.value = jointCount;
    this.markBodyColorFallbackConflictsKernel.computeNode.parameters.springCount.value = springCount;
    enqueue(
      this.markBodyColorFallbackConflictsKernel,
      this.constrainedBodyDispatchIndirectAttr,
    );

    this.countBodyColorBucketsKernel.computeNode.parameters.bodyCount.value = bodyCount;
    this.countBodyColorBucketsKernel.computeNode.parameters.colorCount.value = clampedColors;
    this.prefixBodyColorBucketsKernel.computeNode.parameters.colorCount.value = clampedColors;
    this.scatterBodyColorBucketsKernel.computeNode.parameters.bodyCount.value = bodyCount;
    this.scatterBodyColorBucketsKernel.computeNode.parameters.colorCount.value = clampedColors;
    enqueue(this.clearBodyColorBucketsKernel, [1, 1, 1]);
    enqueue(this.countBodyColorBucketsKernel, this.constrainedBodyDispatchIndirectAttr);
    enqueue(this.prefixBodyColorBucketsKernel, [1, 1, 1]);
    const dispatchArgsBatchCount = Math.ceil(
      clampedColors / BODY_COLOR_DISPATCH_ARGS_BATCH_SIZE,
    );
    for (let batch = 0; batch < dispatchArgsBatchCount; batch++) {
      enqueue(this.buildBodyColorDispatchArgsKernels[batch]!, [1, 1, 1]);
      if (this.cooperativeReferenceBodySolve) {
        enqueue(
          this.buildCooperativeBodyColorDispatchArgsKernels[batch]!,
          [1, 1, 1],
        );
      }
    }
    enqueue(this.scatterBodyColorBucketsKernel, this.constrainedBodyDispatchIndirectAttr);
    flush();
  }

  private canUseReferenceContactStrictPrimal(
    regularizationAlpha: number,
    tangentialRegularizationAlpha: number,
    frictionSolveScale: number,
    solveTuning: PrimalSolveTuning | undefined,
    bodySolveMode: AvbdBodySolveMode,
  ): boolean {
    const same = (a: number, b: number): boolean => Math.abs(a - b) <= 1e-9;
    return !this.hasHardConstraints
      && this.referencePrimalBodySolveKernels.length === this.maxBodyColorCount
      && bodySolveMode === 'colored'
      && same(regularizationAlpha, AVBD_REFERENCE_REGULARIZATION_ALPHA)
      && same(tangentialRegularizationAlpha, AVBD_REFERENCE_REGULARIZATION_ALPHA)
      && same(frictionSolveScale, 1.0)
      && same(this.currentFrictionStatic, 1.0)
      && same(this.currentFrictionDynamic, 1.0)
      && same(this.currentPenaltyFloor, AVBD_K_START)
      && !this.currentPreventPenetratingNormalDropout
      && !this.currentFreezeTangentialPenaltyUpdates
      && same(Math.max(0.0, solveTuning?.relaxation ?? 1.0), 1.0)
      && same(Math.max(0.0, solveTuning?.frictionRelaxation ?? 1.0), 1.0)
      && same(Math.max(0.0, solveTuning?.inertialDiagWeight ?? 1.0), 1.0)
      && same(Math.max(0.0, solveTuning?.maxLinearCorrection ?? 0.25), 1.0e9)
      && same(Math.max(0.0, solveTuning?.maxAngularCorrection ?? 0.35), 1.0e9);
  }

  private hasCompleteAdaptiveColorSpecialization(activeColorCount: number): boolean {
    const expectedDispatchTargets = this.maxBodyColorCount + 2;
    const expectedClearBatches = Math.ceil(expectedDispatchTargets / 9);
    const certifiedColorCapacity = this.maxBodyColorCount === 4
      || this.maxBodyColorCount === 8;
    return certifiedColorCapacity
      && !this.hasHardConstraints
      && activeColorCount === this.maxBodyColorCount
      && this.adaptiveReferencePrimalBodySolveKernels.length === this.maxBodyColorCount
      && this.bodyColorDispatchIndirectAttrs.length === this.maxBodyColorCount
      && this.adaptiveDispatchTargetCount === expectedDispatchTargets
      && this.clearAdaptiveDispatchArgsKernels.length === expectedClearBatches
      && Boolean(
        this.clearAdaptiveRoundResidualKernel
        && this.beginAdaptiveResidualTrackingKernel
        && this.reduceAdaptiveRoundResidualKernel
        && this.decideAdaptiveEarlyOutKernel
        && this.adaptiveFallbackPrimalBodySolveKernel
        && this.adaptiveFallbackCommitBodySolveKernel
        && this.bodyColorFallbackDispatchIndirectAttr
        && this.adaptiveReferenceCapturePairDualStateKernel
        && this.contactDispatchIndirectAttr
        && this.finalizeContactDispatchIndirectAttr,
      );
  }

  private getAdaptiveColorSpecializationTelemetry(): AvbdAdaptiveColorSpecializationTelemetry {
    const residualReductionClosure = Boolean(
      this.beginAdaptiveResidualTrackingKernel
      && this.clearAdaptiveRoundResidualKernel
      && this.reduceAdaptiveRoundResidualKernel
      && this.decideAdaptiveEarlyOutKernel,
    );
    const fallbackResidualClosure = Boolean(
      this.adaptiveFallbackPrimalBodySolveKernel
      && this.adaptiveFallbackCommitBodySolveKernel
      && this.bodyColorFallbackDispatchIndirectAttr,
    );
    const contactResidualClosure = Boolean(
      this.adaptiveReferenceCapturePairDualStateKernel
      && this.contactDispatchIndirectAttr
      && this.finalizeContactDispatchIndirectAttr,
    );
    return Object.freeze({
      colorCapacity: this.maxBodyColorCount,
      strictPrimalKernelCount: this.adaptiveReferencePrimalBodySolveKernels.length,
      strictDispatchCount: this.bodyColorDispatchIndirectAttrs.length,
      earlyOutDispatchTargetCount: this.adaptiveDispatchTargetCount,
      earlyOutDispatchClearBatchCount: this.clearAdaptiveDispatchArgsKernels.length,
      residualReductionClosure,
      fallbackResidualClosure,
      contactResidualClosure,
      residualBooleanStoresDeduplicated:
        ADAPTIVE_RESIDUAL_BOOLEAN_STORES_DEDUPLICATED,
      completeForConfiguredColorCapacity: this.hasCompleteAdaptiveColorSpecialization(
        this.maxBodyColorCount,
      ),
    });
  }

  setAdaptiveIterationPolicy(policy: AvbdAdaptiveIterationPolicy): void {
    this.adaptiveIterationPolicy = {
      enabled: Boolean(policy.enabled),
      minIterations: Math.max(1, Math.min(64, Math.floor(policy.minIterations))),
      linearTolerance: Math.max(0.0, policy.linearTolerance ?? 2.5e-5),
      angularTolerance: Math.max(0.0, policy.angularTolerance ?? 2.5e-5),
      dualTolerance: Math.max(0.0, policy.dualTolerance ?? 2.5e-4),
      penaltyTolerance: Math.max(0.0, policy.penaltyTolerance ?? 2.5e-4),
      forceInvalidForTesting: Boolean(policy.forceInvalidForTesting),
      deferPreMinResiduals: policy.deferPreMinResiduals ?? true,
    };
  }

  primalSolveBodies(
    renderer: any,
    bodyCount: number,
    sweepCount: number,
    bodyColorCount: number,
    regularizationAlpha: number,
    dt: number,
    frictionSolveScale = 1.0,
    tangentialRegularizationAlpha = regularizationAlpha,
    solveTuning?: PrimalSolveTuning,
    sweepStartOffset = 0,
    colorStart = 0,
    colorCountOverride?: number,
    bodySolveMode: AvbdBodySolveMode = 'colored',
  ): void {
    if (bodyCount <= 0 || sweepCount <= 0 || bodyColorCount <= 0) return;

    const bodySolveModeValue = bodySolveMode === 'serial' ? 1 : 0;
    const clampedColors = Math.max(
      1,
      Math.min(this.maxBodyColorCount, Math.floor(bodyColorCount)),
    );
    const clampedColorStart = Math.max(0, Math.min(clampedColors - 1, Math.floor(colorStart)));
    const requestedColorCount = colorCountOverride === undefined
      ? clampedColors - clampedColorStart
      : Math.max(0, Math.floor(colorCountOverride));
    const colorEnd = Math.min(clampedColors, clampedColorStart + requestedColorCount);
    if (colorEnd <= clampedColorStart) return;
    // Cooperative fixed-reference kernels require their one-workgroup-per-body
    // indirect stream and are encoded only by the combined one-sweep path.
    // Serial or multi-sweep diagnostics retain the scalar generic mapping.
    const useReferenceContactStrict = !this.cooperativeReferenceBodySolve
      && this.canUseReferenceContactStrictPrimal(
        regularizationAlpha,
        tangentialRegularizationAlpha,
        frictionSolveScale,
        solveTuning,
        bodySolveMode,
      );

    const configureColorKernel = (color: number, kernelIndex = color): {
      solveKernel: any;
      commitKernel: any;
      solveParams: any;
      commitParams: any;
    } => {
      const referenceStrict = useReferenceContactStrict
        && kernelIndex === color
        && color < this.maxBodyColorCount;
      const solveKernel = referenceStrict
        ? this.referencePrimalBodySolveKernels[color]!
        : this.primalBodySolveKernels[kernelIndex]!;
      const commitKernel = this.commitBodySolveKernels[kernelIndex]!;
      const solveParams = solveKernel.computeNode.parameters;
      const commitParams = commitKernel.computeNode.parameters;
      solveParams.bodyCount.value = bodyCount;
      solveParams.currentColor.value = color;
      solveParams.dt.value = dt;
      if (!referenceStrict) {
        solveParams.bodyIndexBase.value = 0;
        solveParams.dispatchBodyCount.value = bodyCount;
        solveParams.bodySolveMode.value = bodySolveModeValue;
        solveParams.fallbackColorIndex.value = clampedColors;
        solveParams.regularizationAlpha.value = regularizationAlpha;
        solveParams.tangentialRegularizationAlpha.value = tangentialRegularizationAlpha;
        solveParams.frictionSolveScale.value = frictionSolveScale;
        solveParams.relaxation.value = Math.max(0.0, solveTuning?.relaxation ?? 1.0);
        solveParams.frictionRelaxation.value = Math.max(0.0, solveTuning?.frictionRelaxation ?? 1.0);
        solveParams.inertialDiagWeight.value = Math.max(0.0, solveTuning?.inertialDiagWeight ?? 1.0);
        solveParams.maxLinearCorrection.value = Math.max(0.0, solveTuning?.maxLinearCorrection ?? 0.25);
        solveParams.maxAngularCorrection.value = Math.max(0.0, solveTuning?.maxAngularCorrection ?? 0.35);
      }
      commitParams.bodyCount.value = bodyCount;
      commitParams.bodyIndexBase.value = 0;
      commitParams.dispatchBodyCount.value = bodyCount;
      commitParams.bodySolveMode.value = bodySolveModeValue;
      commitParams.currentColor.value = color;
      commitParams.fallbackColorIndex.value = clampedColors;
      return { solveKernel, commitKernel, solveParams, commitParams };
    };

    const sweepPasses = Math.max(1, Math.floor(sweepCount));
    if (bodySolveMode === 'serial') {
      // Serial diagnostics require the scratch shader because every one-body
      // solve is followed by the generic commit kernel. The strict color-0
      // specialization publishes in-place and intentionally has no scratch
      // output for that commit to consume. PhysicsEngine must pass an
      // immediate renderer here: bodyIndexBase changes for every call and a
      // deferred ComputeNode collector would alias all calls to the last body.
      const { solveKernel, commitKernel, solveParams, commitParams } =
        configureColorKernel(0, this.maxBodyColorCount);
      for (let sweep = 0; sweep < sweepPasses; sweep++) {
        solveParams.sweepOffset.value = sweepStartOffset + sweep;
        for (let bodyIndex = 0; bodyIndex < bodyCount; bodyIndex++) {
          solveParams.bodyIndexBase.value = bodyIndex;
          solveParams.dispatchBodyCount.value = 1;
          commitParams.bodyIndexBase.value = bodyIndex;
          commitParams.dispatchBodyCount.value = 1;
          // Keep the solve/commit dependency in one WebGPU compute pass. Three
          // submits once for an array of compute nodes, which avoids one queue
          // submission per body in the serial diagnostic path.
          renderer.compute(
            [solveKernel, commitKernel],
            [1, 1, 1],
          );
        }
      }
      return;
    }

    for (let sweep = 0; sweep < sweepPasses; sweep++) {
      const colorPass: any[] = [];
      for (let color = clampedColorStart; color < colorEnd; color++) {
        const { solveKernel, solveParams } = configureColorKernel(color);
        solveParams.sweepOffset.value = sweepStartOffset + sweep;
        colorPass.push(solveKernel);
      }
      // Each color has an exact GPU-built dispatch. The previous shared
      // maximum launched every color at the size of the largest bucket, which
      // turned skewed colorings into millions of empty shader invocations.
      for (let color = clampedColorStart; color < colorEnd; color++) {
        renderer.compute(
          colorPass[color - clampedColorStart],
          this.bodyColorDispatchIndirectAttrs[color]!,
        );
      }
      if (colorEnd === clampedColors) {
        const fallback = configureColorKernel(clampedColors, this.maxBodyColorCount);
        fallback.solveParams.sweepOffset.value = sweepStartOffset + sweep;
        renderer.compute(
          [fallback.solveKernel, fallback.commitKernel],
          this.bodyColorFallbackDispatchIndirectAttr,
        );
      }
      // The command-renderer batches both calls into one final submission.
      // A zero fallback count now encodes zero workgroups instead of repeating
      // the largest strict-color bucket for an empty ninth lane.
    }
  }

  /**
   * Encodes invariant colored primal/dual rounds into one Three compute group.
   * Strict colors publish directly because they are independent sets; only the
   * final Jacobi fallback retains a scratch/commit pair. Every lane uses its
   * exact GPU-built bucket dispatch, so empty colors and a zero-body fallback
   * remain GPU no-ops without returning to full-body launches.
   *
   * The serial diagnostic mode and multi-sweep offset rotation retain the old
   * immediate submission path because their uniforms intentionally vary.
   * They must never enter the native deferred-command collector.
   */
  primalSolveAndCaptureIterations(
    renderer: any,
    iterationCount: number,
    bodyCount: number,
    pairDispatchCount: number,
    jointCount: number,
    springCount: number,
    sweepCount: number,
    bodyColorCount: number,
    regularizationAlpha: number,
    dt: number,
    frictionSolveScale = 1.0,
    tangentialRegularizationAlpha = regularizationAlpha,
    solveTuning?: PrimalSolveTuning,
    sweepStartOffset = 0,
    bodySolveMode: AvbdBodySolveMode = 'colored',
  ): void {
    this.currentRegularizationAlpha = Math.max(
      0.0,
      Math.min(1.0, regularizationAlpha),
    );
    const iterations = Math.max(0, Math.floor(iterationCount));
    if (iterations <= 0 || bodyCount <= 0 || bodyColorCount <= 0) return;

    const sweepPasses = Math.max(1, Math.floor(sweepCount));
    if (bodySolveMode === 'serial' || sweepPasses !== 1) {
      const clampedColors = Math.max(
        1,
        Math.min(this.maxBodyColorCount, Math.floor(bodyColorCount)),
      );
      const referencePrimalSelected = this.canUseReferenceContactStrictPrimal(
        regularizationAlpha,
        tangentialRegularizationAlpha,
        frictionSolveScale,
        solveTuning,
        bodySolveMode,
      );
      // Multi-sweep and serial diagnostics deliberately use captureFromSolve,
      // whose concrete capture kernel is the generic flavor.
      this.recordSolvePathSelection({
        bodySolveMode,
        iterations,
        sweeps: sweepPasses,
        activeColorCount: clampedColors,
        pairDispatchCount: Math.min(pairDispatchCount, this.maxActivePairContacts),
        jointCount,
        springCount,
        referencePrimalSelected,
        cooperativePrimalSelected: false,
        manifoldCaptureSelected: false,
        captureBodyDeltaCacheSelected: false,
        packedContactSolverGeometrySelected: false,
        finalCaptureCommitFusionSelected: false,
        referenceCaptureSelected: false,
        referenceCaptureEncoded: false,
        adaptiveSelected: false,
        fallbackLaneEncoded: bodySolveMode === 'colored',
      });
      for (let iteration = 0; iteration < iterations; iteration++) {
        this.primalSolveBodies(
          renderer,
          bodyCount,
          sweepPasses,
          bodyColorCount,
          regularizationAlpha,
          dt,
          frictionSolveScale,
          tangentialRegularizationAlpha,
          solveTuning,
          sweepStartOffset,
          0,
          undefined,
          bodySolveMode,
        );
        this.captureFromSolve(
          renderer,
          pairDispatchCount,
          jointCount,
          springCount,
          dt,
          regularizationAlpha,
          iteration,
        );
      }
      this.finalizeContactSolverScratch(renderer, pairDispatchCount);
      return;
    }

    const clampedColors = Math.max(
      1,
      Math.min(this.maxBodyColorCount, Math.floor(bodyColorCount)),
    );
    const useReferenceContactStrict = this.referenceCapturePairDualStateKernel !== null
      && this.canUseReferenceContactStrictPrimal(
        regularizationAlpha,
        tangentialRegularizationAlpha,
        frictionSolveScale,
        solveTuning,
        bodySolveMode,
      );
    const adaptiveMinIterations = Math.min(
      iterations,
      this.adaptiveIterationPolicy.minIterations,
    );
    const useAdaptiveIterations = this.adaptiveIterationPolicy.enabled
      && useReferenceContactStrict
      && jointCount === 0
      && springCount === 0
      // Every configured strict color must have a specialized primal kernel,
      // an exact indirect dispatch, and coverage in the early-out clear set.
      // The same closure includes the Jacobi fallback residual/commit pair and
      // contact dual residual lane, so four- and eight-color worlds use the
      // identical fail-open convergence protocol rather than a color-specific
      // shortcut.
      && this.hasCompleteAdaptiveColorSpecialization(clampedColors)
      && iterations > adaptiveMinIterations;
    const useCooperativeReferencePrimal = useReferenceContactStrict
      && this.cooperativeReferenceBodySolve
      && !useAdaptiveIterations;
    const useManifoldReferenceCapture = useReferenceContactStrict
      && !useAdaptiveIterations
      && this.referenceManifoldCapturePairDualStateKernel !== null;
    const adaptiveWarmupIterations = useAdaptiveIterations
      && this.adaptiveIterationPolicy.deferPreMinResiduals
      ? Math.max(0, adaptiveMinIterations - 1)
      : 0;
    const boundedDispatch = Math.min(pairDispatchCount, this.maxActivePairContacts);
    const useCaptureBodyDeltaCache = useReferenceContactStrict
      && !useAdaptiveIterations
      && boundedDispatch > 0
      && this.captureBodyDeltaKernel !== null;
    const usePackedContactSolverGeometry = useReferenceContactStrict
      && !useAdaptiveIterations
      && boundedDispatch > 0
      && this.packedContactSolverGeometry;
    const useFinalCaptureCommitFusion = useReferenceContactStrict
      && !useAdaptiveIterations
      && !useManifoldReferenceCapture
      && boundedDispatch > 0
      && iterations > 0
      && this.referenceFinalCapturePairDualStateKernel !== null;
    this.recordSolvePathSelection({
      bodySolveMode,
      iterations,
      sweeps: sweepPasses,
      activeColorCount: clampedColors,
      pairDispatchCount: boundedDispatch,
      jointCount,
      springCount,
      referencePrimalSelected: useReferenceContactStrict,
      cooperativePrimalSelected: useCooperativeReferencePrimal,
      manifoldCaptureSelected: useManifoldReferenceCapture,
      captureBodyDeltaCacheSelected: useCaptureBodyDeltaCache,
      packedContactSolverGeometrySelected: usePackedContactSolverGeometry,
      finalCaptureCommitFusionSelected: useFinalCaptureCommitFusion,
      referenceCaptureSelected: useReferenceContactStrict,
      referenceCaptureEncoded: useReferenceContactStrict && boundedDispatch > 0,
      adaptiveSelected: useAdaptiveIterations,
      fallbackLaneEncoded: true,
    });
    const capturePairDualStateKernel = useAdaptiveIterations
      ? this.adaptiveReferenceCapturePairDualStateKernel!
      : useManifoldReferenceCapture
      ? this.referenceManifoldCapturePairDualStateKernel!
      : useReferenceContactStrict
      ? this.referenceCapturePairDualStateKernel!
      : this.capturePairDualStateKernel;
    const finalCapturePairDualStateKernel = useFinalCaptureCommitFusion
      ? this.referenceFinalCapturePairDualStateKernel!
      : capturePairDualStateKernel;
    const finalizeContactSolverScratchKernel =
      this.referenceFinalizerSpecialization
      && useReferenceContactStrict
      && !useAdaptiveIterations
      && this.referenceFinalizeContactSolverScratchKernel !== null
        ? this.referenceFinalizeContactSolverScratchKernel
        : this.finalizeContactSolverScratchKernel;
    const primalNodes: any[] = [];
    for (let color = 0; color < clampedColors; color++) {
      const solveKernel = useAdaptiveIterations
        ? this.adaptiveReferencePrimalBodySolveKernels[color]!
        : useReferenceContactStrict
        ? this.referencePrimalBodySolveKernels[color]!
        : this.primalBodySolveKernels[color]!;
      const solveParams = solveKernel.computeNode.parameters;
      solveParams.bodyCount.value = bodyCount;
      solveParams.currentColor.value = color;
      solveParams.sweepOffset.value = sweepStartOffset;
      solveParams.dt.value = dt;
      if (useAdaptiveIterations) {
        solveParams.adaptiveLinearTolerance.value = this.adaptiveIterationPolicy.linearTolerance;
        solveParams.adaptiveAngularTolerance.value = this.adaptiveIterationPolicy.angularTolerance;
      } else if (!useReferenceContactStrict) {
        solveParams.bodyIndexBase.value = 0;
        solveParams.dispatchBodyCount.value = bodyCount;
        solveParams.bodySolveMode.value = 0;
        solveParams.fallbackColorIndex.value = clampedColors;
        solveParams.regularizationAlpha.value = regularizationAlpha;
        solveParams.tangentialRegularizationAlpha.value = tangentialRegularizationAlpha;
        solveParams.frictionSolveScale.value = frictionSolveScale;
        solveParams.relaxation.value = Math.max(0.0, solveTuning?.relaxation ?? 1.0);
        solveParams.frictionRelaxation.value = Math.max(0.0, solveTuning?.frictionRelaxation ?? 1.0);
        solveParams.inertialDiagWeight.value = Math.max(0.0, solveTuning?.inertialDiagWeight ?? 1.0);
        solveParams.maxLinearCorrection.value = Math.max(0.0, solveTuning?.maxLinearCorrection ?? 0.25);
        solveParams.maxAngularCorrection.value = Math.max(0.0, solveTuning?.maxAngularCorrection ?? 0.35);
      }
      solveKernel.count = useCooperativeReferencePrimal
        ? this.cooperativeBodyColorDispatchIndirectAttrs[color]!
        : this.bodyColorDispatchIndirectAttrs[color]!;
      primalNodes.push(solveKernel);
    }
    {
      const solveKernel = useAdaptiveIterations
        ? this.adaptiveFallbackPrimalBodySolveKernel
        : this.primalBodySolveKernels[this.maxBodyColorCount]!;
      const commitKernel = useAdaptiveIterations
        ? this.adaptiveFallbackCommitBodySolveKernel
        : this.commitBodySolveKernels[this.maxBodyColorCount]!;
      const solveParams = solveKernel.computeNode.parameters;
      const commitParams = commitKernel.computeNode.parameters;
      solveParams.bodyCount.value = bodyCount;
      solveParams.currentColor.value = clampedColors;
      solveParams.sweepOffset.value = sweepStartOffset;
      solveParams.dt.value = dt;
      solveParams.bodyIndexBase.value = 0;
      solveParams.dispatchBodyCount.value = bodyCount;
      solveParams.bodySolveMode.value = 0;
      solveParams.fallbackColorIndex.value = clampedColors;
      solveParams.regularizationAlpha.value = regularizationAlpha;
      solveParams.tangentialRegularizationAlpha.value = tangentialRegularizationAlpha;
      solveParams.frictionSolveScale.value = frictionSolveScale;
      solveParams.relaxation.value = Math.max(0.0, solveTuning?.relaxation ?? 1.0);
      solveParams.frictionRelaxation.value = Math.max(0.0, solveTuning?.frictionRelaxation ?? 1.0);
      solveParams.inertialDiagWeight.value = Math.max(0.0, solveTuning?.inertialDiagWeight ?? 1.0);
      solveParams.maxLinearCorrection.value = Math.max(0.0, solveTuning?.maxLinearCorrection ?? 0.25);
      solveParams.maxAngularCorrection.value = Math.max(0.0, solveTuning?.maxAngularCorrection ?? 0.35);
      if (useAdaptiveIterations) {
        solveParams.adaptiveFallbackResidualEnabled.value = 1;
        solveParams.adaptiveFallbackLinearTolerance.value =
          this.adaptiveIterationPolicy.linearTolerance;
        solveParams.adaptiveFallbackAngularTolerance.value =
          this.adaptiveIterationPolicy.angularTolerance;
      }
      commitParams.bodyCount.value = bodyCount;
      commitParams.bodyIndexBase.value = 0;
      commitParams.dispatchBodyCount.value = bodyCount;
      commitParams.bodySolveMode.value = 0;
      commitParams.currentColor.value = clampedColors;
      commitParams.fallbackColorIndex.value = clampedColors;
      solveKernel.count = this.bodyColorFallbackDispatchIndirectAttr;
      commitKernel.count = this.bodyColorFallbackDispatchIndirectAttr;
      primalNodes.push(solveKernel, commitKernel);
    }

    if (boundedDispatch > 0) {
      if (useCaptureBodyDeltaCache) {
        this.captureBodyDeltaKernel!.computeNode.parameters.bodyCount.value = bodyCount;
        this.captureBodyDeltaKernel!.count = [
          Math.ceil(bodyCount / WORKGROUP_SIZE),
          1,
          1,
        ];
      }
      const captureParams = capturePairDualStateKernel.computeNode.parameters;
      captureParams.pairDispatchCount.value = boundedDispatch;
      if (useAdaptiveIterations) {
        captureParams.adaptiveDualTolerance.value = this.adaptiveIterationPolicy.dualTolerance;
        captureParams.adaptivePenaltyTolerance.value = this.adaptiveIterationPolicy.penaltyTolerance;
      } else if (!useReferenceContactStrict) {
        captureParams.regularizationAlpha.value = regularizationAlpha;
        captureParams.dt.value = dt;
      }
      capturePairDualStateKernel.count = useManifoldReferenceCapture
        ? this.captureContactDispatchIndirectAttr
        : this.contactDispatchIndirectAttr;
      if (useFinalCaptureCommitFusion) {
        const finalCaptureParams = finalCapturePairDualStateKernel.computeNode.parameters;
        finalCaptureParams.pairDispatchCount.value = boundedDispatch;
        finalCapturePairDualStateKernel.count = this.contactDispatchIndirectAttr;
      }
      finalizeContactSolverScratchKernel.computeNode.parameters.pairDispatchCount.value =
        boundedDispatch;
      if (finalizeContactSolverScratchKernel === this.finalizeContactSolverScratchKernel) {
        finalizeContactSolverScratchKernel.computeNode.parameters.useSolverHotState.value =
          this.denseContactSolverState
            && useReferenceContactStrict
            && !useAdaptiveIterations ? 1 : 0;
      }
      finalizeContactSolverScratchKernel.count = this.finalizeContactDispatchIndirectAttr;
    }
    const hasJointCapture = Math.max(jointCount, springCount) > 0;
    if (hasJointCapture) {
      this.captureJointDualStateKernel.computeNode.parameters.jointCount.value = jointCount;
      this.captureJointDualStateKernel.computeNode.parameters.springCount.value = springCount;
      this.captureJointDualStateKernel.computeNode.parameters.regularizationAlpha.value = regularizationAlpha;
      this.captureJointDualStateKernel.count = [
        Math.ceil(Math.max(jointCount, springCount) / WORKGROUP_SIZE),
        1,
        1,
      ];
    }

    const iterationNodes: any[] = [];
    if (useAdaptiveIterations || this.adaptiveGpuStateMayBeEnabled) {
      const initializeParams = this.initializeAdaptiveStateKernel.computeNode.parameters;
      initializeParams.requested.value = useAdaptiveIterations ? 1 : 0;
      initializeParams.minIterations.value = adaptiveMinIterations;
      initializeParams.maxIterations.value = iterations;
      initializeParams.trackResidualsImmediately.value = useAdaptiveIterations
        && !this.adaptiveIterationPolicy.deferPreMinResiduals ? 1 : 0;
      this.initializeAdaptiveStateKernel.count = [1, 1, 1];
      iterationNodes.push(this.initializeAdaptiveStateKernel);
      this.adaptiveGpuStateMayBeEnabled = useAdaptiveIterations;
    }
    if (useAdaptiveIterations) {
      const decideParams = this.decideAdaptiveEarlyOutKernel.computeNode.parameters;
      decideParams.minIterations.value = adaptiveMinIterations;
      decideParams.maxIterations.value = iterations;
      this.clearAdaptiveRoundResidualKernel.count = [
        Math.ceil(ADAPTIVE_RESIDUAL_SHARDS / WORKGROUP_SIZE),
        1,
        1,
      ];
      this.clearAdaptiveRoundResidualKernel.computeNode.parameters.forceInvalidForTesting.value =
        this.adaptiveIterationPolicy.forceInvalidForTesting
          && adaptiveWarmupIterations === 0 ? 1 : 0;
      this.reduceAdaptiveRoundResidualKernel.count = [1, 1, 1];
      this.decideAdaptiveEarlyOutKernel.count = [1, 1, 1];
      for (const clearKernel of this.clearAdaptiveDispatchArgsKernels) {
        clearKernel.count = [1, 1, 1];
      }
      const beginTrackingParams = this.beginAdaptiveResidualTrackingKernel
        .computeNode.parameters;
      beginTrackingParams.completedWarmupIterations.value = adaptiveWarmupIterations;
      beginTrackingParams.forcePreminInvalidForTesting.value =
        this.adaptiveIterationPolicy.forceInvalidForTesting
          && adaptiveWarmupIterations > 0 ? 1 : 0;
      this.beginAdaptiveResidualTrackingKernel.count = [1, 1, 1];
    }
    // Seed every body once at x^t. Fixed strict color solves and the Jacobi
    // fallback commit then publish their own exact body deltas in place, so
    // capture sees the same f32 values without rebuilding the full stream on
    // every AVBD iteration.
    if (useCaptureBodyDeltaCache) {
      iterationNodes.push(this.captureBodyDeltaKernel!);
    }
    for (let iteration = 0; iteration < iterations; iteration++) {
      const residualRound = useAdaptiveIterations
        && iteration >= adaptiveWarmupIterations;
      if (
        useAdaptiveIterations
        && adaptiveWarmupIterations > 0
        && iteration === adaptiveWarmupIterations
      ) {
        iterationNodes.push(this.beginAdaptiveResidualTrackingKernel);
      }
      if (residualRound) {
        iterationNodes.push(this.clearAdaptiveRoundResidualKernel);
      }
      iterationNodes.push(...primalNodes);
      if (boundedDispatch > 0) {
        iterationNodes.push(
          useFinalCaptureCommitFusion && iteration === iterations - 1
            ? finalCapturePairDualStateKernel
            : capturePairDualStateKernel,
        );
      }
      if (hasJointCapture) iterationNodes.push(this.captureJointDualStateKernel);
      if (residualRound) {
        iterationNodes.push(
          this.reduceAdaptiveRoundResidualKernel,
          this.decideAdaptiveEarlyOutKernel,
          ...this.clearAdaptiveDispatchArgsKernels,
        );
      }
    }
    // This separately latched indirect pass is deliberately outside the
    // iteration/capture lane. Future residual early-out may zero subsequent
    // solve work, but persistent local arms and shadow must always be restored.
    if (boundedDispatch > 0 && !useFinalCaptureCommitFusion) {
      iterationNodes.push(finalizeContactSolverScratchKernel);
    }
    if (iterationNodes.length > 0) {
      renderer.compute(iterationNodes);
    }
  }

  private recordSolvePathSelection(selection: {
    bodySolveMode: AvbdBodySolveMode;
    iterations: number;
    sweeps: number;
    activeColorCount: number;
    pairDispatchCount: number;
    jointCount: number;
    springCount: number;
    referencePrimalSelected: boolean;
    cooperativePrimalSelected: boolean;
    manifoldCaptureSelected: boolean;
    captureBodyDeltaCacheSelected: boolean;
    packedContactSolverGeometrySelected: boolean;
    finalCaptureCommitFusionSelected: boolean;
    referenceCaptureSelected: boolean;
    referenceCaptureEncoded: boolean;
    adaptiveSelected: boolean;
    fallbackLaneEncoded: boolean;
  }): void {
    this.solvePathInvocations++;
    if (selection.referencePrimalSelected) this.referencePrimalInvocations++;
    else this.genericPrimalInvocations++;
    if (selection.cooperativePrimalSelected) this.cooperativePrimalInvocations++;
    if (selection.manifoldCaptureSelected) this.manifoldCaptureInvocations++;
    if (selection.captureBodyDeltaCacheSelected) this.captureBodyDeltaCacheInvocations++;
    if (selection.packedContactSolverGeometrySelected) {
      this.packedContactSolverGeometryInvocations++;
    }
    if (selection.finalCaptureCommitFusionSelected) {
      this.finalCaptureCommitFusionInvocations++;
    }
    if (selection.referenceCaptureSelected && selection.referenceCaptureEncoded) {
      this.referenceCaptureInvocations++;
    } else if (selection.pairDispatchCount > 0) {
      this.genericCaptureInvocations++;
    }
    if (selection.adaptiveSelected) this.adaptiveInvocations++;
    this.solvePathBodySolveModeLast = selection.bodySolveMode;
    this.solvePathIterationsLast = selection.iterations;
    this.solvePathSweepsLast = selection.sweeps;
    this.solvePathActiveColorCountLast = selection.activeColorCount;
    this.solvePathPairDispatchCountLast = selection.pairDispatchCount;
    this.solvePathJointCountLast = selection.jointCount;
    this.solvePathSpringCountLast = selection.springCount;
    this.solvePathReferencePrimalSelectedLast = selection.referencePrimalSelected;
    this.solvePathCooperativePrimalSelectedLast = selection.cooperativePrimalSelected;
    this.solvePathManifoldCaptureSelectedLast = selection.manifoldCaptureSelected;
    this.solvePathCaptureBodyDeltaCacheSelectedLast =
      selection.captureBodyDeltaCacheSelected;
    this.solvePathPackedContactSolverGeometrySelectedLast =
      selection.packedContactSolverGeometrySelected;
    this.solvePathFinalCaptureCommitFusionSelectedLast =
      selection.finalCaptureCommitFusionSelected;
    this.solvePathReferenceCaptureSelectedLast = selection.referenceCaptureSelected;
    this.solvePathReferenceCaptureEncodedLast = selection.referenceCaptureEncoded;
    this.solvePathAdaptiveSelectedLast = selection.adaptiveSelected;
    this.solvePathFallbackLaneEncodedLast = selection.fallbackLaneEncoded;
  }

  getSolvePathTelemetry(): AvbdSolvePathTelemetry {
    return {
      hasHardConstraints: this.hasHardConstraints,
      contactOnly: !this.hasHardConstraints,
      contactTangentInvLengthCache: this.cacheContactTangentInvLength,
      denseContactSolverState: this.denseContactSolverState,
      referenceFinalizerSpecialization: this.referenceFinalizerSpecialization,
      cooperativeBodySolveConfigured: this.cooperativeReferenceBodySolve,
      cooperativeBodySolveLanes: COOPERATIVE_BODY_SOLVE_LANES,
      manifoldCaptureConfigured:
        this.referenceManifoldCapturePairDualStateKernel !== null,
      captureBodyDeltaCacheConfigured: this.captureBodyDeltaCache,
      packedContactSolverGeometryConfigured: this.packedContactSolverGeometry,
      finalCaptureCommitFusionConfigured:
        this.referenceFinalCapturePairDualStateKernel !== null,
      configuredColorCapacity: this.maxBodyColorCount,
      solveInvocations: this.solvePathInvocations,
      referencePrimalInvocations: this.referencePrimalInvocations,
      cooperativePrimalInvocations: this.cooperativePrimalInvocations,
      manifoldCaptureInvocations: this.manifoldCaptureInvocations,
      captureBodyDeltaCacheInvocations: this.captureBodyDeltaCacheInvocations,
      packedContactSolverGeometryInvocations:
        this.packedContactSolverGeometryInvocations,
      finalCaptureCommitFusionInvocations:
        this.finalCaptureCommitFusionInvocations,
      referenceCaptureInvocations: this.referenceCaptureInvocations,
      adaptiveInvocations: this.adaptiveInvocations,
      genericPrimalInvocations: this.genericPrimalInvocations,
      genericCaptureInvocations: this.genericCaptureInvocations,
      bodySolveModeLast: this.solvePathBodySolveModeLast,
      iterationsLast: this.solvePathIterationsLast,
      sweepsLast: this.solvePathSweepsLast,
      activeColorCountLast: this.solvePathActiveColorCountLast,
      pairDispatchCountLast: this.solvePathPairDispatchCountLast,
      jointCountLast: this.solvePathJointCountLast,
      springCountLast: this.solvePathSpringCountLast,
      referencePrimalSelectedLast: this.solvePathReferencePrimalSelectedLast,
      cooperativePrimalSelectedLast: this.solvePathCooperativePrimalSelectedLast,
      manifoldCaptureSelectedLast: this.solvePathManifoldCaptureSelectedLast,
      captureBodyDeltaCacheSelectedLast:
        this.solvePathCaptureBodyDeltaCacheSelectedLast,
      packedContactSolverGeometrySelectedLast:
        this.solvePathPackedContactSolverGeometrySelectedLast,
      finalCaptureCommitFusionSelectedLast:
        this.solvePathFinalCaptureCommitFusionSelectedLast,
      referenceCaptureSelectedLast: this.solvePathReferenceCaptureSelectedLast,
      referenceCaptureEncodedLast: this.solvePathReferenceCaptureEncodedLast,
      adaptiveSelectedLast: this.solvePathAdaptiveSelectedLast,
      fallbackLaneEncodedLast: this.solvePathFallbackLaneEncodedLast,
      adaptiveColorSpecialization: this.getAdaptiveColorSpecializationTelemetry(),
    };
  }

  captureFromSolve(
    renderer: any,
    pairDispatchCount: number,
    jointCount: number,
    springCount: number,
    dt: number,
    regularizationAlpha: number,
    captureIterationIndex = -1,
  ): void {
    const boundedDispatch = Math.min(pairDispatchCount, this.maxActivePairContacts);

    if (boundedDispatch > 0) {
      this.capturePairDualStateKernel.computeNode.parameters.pairDispatchCount.value = boundedDispatch;
      this.capturePairDualStateKernel.computeNode.parameters.regularizationAlpha.value = regularizationAlpha;
      this.capturePairDualStateKernel.computeNode.parameters.dt.value = dt;
      renderer.compute(this.capturePairDualStateKernel, this.contactDispatchIndirectAttr);
    }

    const constraintCount = Math.max(jointCount, springCount);
    if (constraintCount > 0) {
      this.captureJointDualStateKernel.computeNode.parameters.jointCount.value = jointCount;
      this.captureJointDualStateKernel.computeNode.parameters.springCount.value = springCount;
      this.captureJointDualStateKernel.computeNode.parameters.regularizationAlpha.value = regularizationAlpha;
      renderer.compute(this.captureJointDualStateKernel, [Math.ceil(constraintCount / WORKGROUP_SIZE), 1, 1]);
    }
  }

  finalizeContactSolverScratch(renderer: any, pairDispatchCount: number): void {
    const boundedDispatch = Math.min(pairDispatchCount, this.maxActivePairContacts);
    if (boundedDispatch <= 0) return;
    this.finalizeContactSolverScratchKernel.computeNode.parameters.pairDispatchCount.value = boundedDispatch;
    renderer.compute(
      this.finalizeContactSolverScratchKernel,
      this.finalizeContactDispatchIndirectAttr,
    );
  }

  finalizeVelocities(renderer: any, bodyCount: number, dt: number): void {
    if (bodyCount <= 0) return;
    this.finalizeVelocitiesKernel.computeNode.parameters.bodyCount.value = bodyCount;
    this.finalizeVelocitiesKernel.computeNode.parameters.dt.value = dt;
    const workgroups = Math.ceil(bodyCount / WORKGROUP_SIZE);
    renderer.compute(this.finalizeVelocitiesKernel, [workgroups, 1, 1]);
  }

  clearPhaseDebugCounters(renderer: any): void {
    if (!this.debugEnabled) return;
    renderer.compute(this.clearPhaseDebugCountersKernel, [1, 1, 1]);
  }

  async readBodyColorDiagnosticsAsync(
    renderer: any,
    bodyCount: number,
  ): Promise<{
    constrainedBodies: number;
    fallbackBodies: number;
    totalBodyConstraintRefs: number;
    saturatedBodyConstraintLists: number;
    maxBodyConstraintRefs: number;
    coloredConstraintEdges: number;
    sameColorConstraintEdges: number;
  } | null> {
    if (
      bodyCount <= 0
      || !renderer
      || typeof renderer.getArrayBufferAsync !== 'function'
    ) {
      return null;
    }

    this.accumulateBodyColorDebugCountersKernel.computeNode.parameters.bodyCount.value = bodyCount;
    renderer.compute(this.clearDebugCountersKernel, [1, 1, 1]);
    renderer.compute(
      this.accumulateBodyColorDebugCountersKernel,
      this.constrainedBodyDispatchIndirectAttr,
    );
    const raw = await renderer.getArrayBufferAsync(this.debugCountersAttr);
    const values = new Uint32Array(raw);
    return {
      constrainedBodies: values[11] ?? 0,
      fallbackBodies: values[12] ?? 0,
      totalBodyConstraintRefs: values[13] ?? 0,
      saturatedBodyConstraintLists: values[14] ?? 0,
      maxBodyConstraintRefs: values[15] ?? 0,
      coloredConstraintEdges: values[16] ?? 0,
      sameColorConstraintEdges: values[17] ?? 0,
    };
  }

  getAdaptiveIterationStats(): AvbdAdaptiveIterationStats {
    return { ...this.lastAdaptiveIterationStats };
  }

  /** GPU-owned convergence state consumed by sleeping after the final dual update. */
  getAdaptiveSleepStateAttribute(): StorageBufferAttribute {
    return this.adaptiveStateAttr;
  }

  resetAdaptiveIterationStats(): void {
    this.adaptiveStatsGeneration++;
    const stateArray = this.adaptiveStateAttr.array as Uint32Array;
    stateArray.fill(0);
    stateArray[AVBD_ADAPTIVE_SLEEP_CONVERGED] = 1;
    this.adaptiveStateAttr.needsUpdate = true;
    const residualArray = this.adaptiveResidualsAttr.array as Uint32Array;
    residualArray.fill(0);
    this.adaptiveResidualsAttr.needsUpdate = true;
    this.adaptiveGpuStateMayBeEnabled = false;
    this.adaptiveStatsReadbackInFlight = false;
    this.adaptiveStatsReadbackPromise = null;
    this.lastAdaptiveIterationStats = {
      enabledLast: false,
      sleepConvergedLast: true,
      sleepPolishConfiguredLast: false,
      sleepPolishRequestedNext: false,
      sleepPolishActiveLast: false,
      sleepPolishCompletedLast: 0,
      sleepPolishRequiredSteps: AVBD_ADAPTIVE_SLEEP_POLISH_STEPS,
      executedIterationsLast: 0,
      minIterationsLast: 0,
      maxIterationsLast: 0,
      earlyExitLast: false,
      fallbackBodiesLast: 0,
      invalidLast: false,
      bodyResidualViolationLast: false,
      fallbackBodyResidualViolationLast: false,
      contactResidualViolationLast: false,
      adaptiveSteps: 0,
      totalIterations: 0,
      averageIterations: 0,
      earlyExits: 0,
      failOpenSteps: 0,
      fallbackFailOpenSteps: 0,
      invalidFailOpenSteps: 0,
    };
  }

  readAdaptiveIterationStatsAsync(
    renderer: any,
  ): Promise<AvbdAdaptiveIterationStats | null> {
    if (!renderer || typeof renderer.getArrayBufferAsync !== 'function') {
      return Promise.resolve(null);
    }
    // Three's WebGPU backend owns one staging-map lifecycle per storage
    // attribute. An automatic 120-frame poll and an explicit certification
    // read can arrive together; coalesce them instead of starting two maps of
    // the same attribute, which can delete the backend record under the other
    // read and leave a large-scene regression stuck or rejected.
    if (this.adaptiveStatsReadbackPromise) return this.adaptiveStatsReadbackPromise;
    const generation = this.adaptiveStatsGeneration;
    const readbackPromise = (async (): Promise<AvbdAdaptiveIterationStats | null> => {
      const raw = await renderer.getArrayBufferAsync(this.adaptiveStateAttr);
      if (generation !== this.adaptiveStatsGeneration) return null;
      const values = new Uint32Array(raw);
      const adaptiveSteps = values[ADAPTIVE_TOTAL_STEPS] ?? 0;
      const totalIterations = values[ADAPTIVE_TOTAL_ITERATIONS] ?? 0;
      const stats: AvbdAdaptiveIterationStats = {
        enabledLast: (values[ADAPTIVE_ENABLED] ?? 0) !== 0,
        sleepConvergedLast: (values[AVBD_ADAPTIVE_SLEEP_CONVERGED] ?? 0) !== 0,
        sleepPolishConfiguredLast:
          (values[AVBD_ADAPTIVE_SLEEP_POLISH_CONFIGURED] ?? 0) !== 0,
        sleepPolishRequestedNext:
          (values[AVBD_ADAPTIVE_SLEEP_POLISH_REQUESTED] ?? 0) !== 0,
        sleepPolishActiveLast:
          (values[ADAPTIVE_SLEEP_POLISH_ACTIVE] ?? 0) !== 0,
        sleepPolishCompletedLast:
          values[ADAPTIVE_SLEEP_POLISH_COMPLETED] ?? 0,
        sleepPolishRequiredSteps: AVBD_ADAPTIVE_SLEEP_POLISH_STEPS,
        executedIterationsLast: values[ADAPTIVE_EXECUTED_LAST] ?? 0,
        minIterationsLast: values[ADAPTIVE_MIN_LAST] ?? 0,
        maxIterationsLast: values[ADAPTIVE_MAX_LAST] ?? 0,
        earlyExitLast: (values[ADAPTIVE_EARLY_LAST] ?? 0) !== 0,
        fallbackBodiesLast: values[ADAPTIVE_FALLBACK_LAST] ?? 0,
        invalidLast: (values[ADAPTIVE_INVALID_LAST] ?? 0) !== 0,
        bodyResidualViolationLast: (values[ADAPTIVE_BODY_VIOLATION] ?? 0) !== 0,
        fallbackBodyResidualViolationLast:
          (values[ADAPTIVE_FALLBACK_BODY_VIOLATION] ?? 0) !== 0,
        contactResidualViolationLast: (values[ADAPTIVE_CONTACT_VIOLATION] ?? 0) !== 0,
        adaptiveSteps,
        totalIterations,
        averageIterations: adaptiveSteps > 0 ? totalIterations / adaptiveSteps : 0,
        earlyExits: values[ADAPTIVE_TOTAL_EARLY_EXITS] ?? 0,
        failOpenSteps: values[ADAPTIVE_TOTAL_FAIL_OPEN] ?? 0,
        fallbackFailOpenSteps: values[ADAPTIVE_TOTAL_FALLBACK_FAIL_OPEN] ?? 0,
        invalidFailOpenSteps: values[ADAPTIVE_TOTAL_INVALID_FAIL_OPEN] ?? 0,
      };
      this.lastAdaptiveIterationStats = stats;
      return { ...stats };
    })();
    this.adaptiveStatsReadbackPromise = readbackPromise;
    this.adaptiveStatsReadbackInFlight = true;
    const clearReadback = (): void => {
      if (this.adaptiveStatsReadbackPromise === readbackPromise) {
        this.adaptiveStatsReadbackPromise = null;
        this.adaptiveStatsReadbackInFlight = false;
      }
    };
    void readbackPromise.then(clearReadback, clearReadback);
    return readbackPromise;
  }

  maybePollAdaptiveIterationStats(renderer: any, frameId: number, force = false): boolean {
    if (
      !this.adaptiveIterationPolicy.enabled
      || this.adaptiveStatsReadbackInFlight
      || frameId <= 0
      || (!force && frameId % 120 !== 0)
      || !renderer
      || typeof renderer.getArrayBufferAsync !== 'function'
    ) {
      return false;
    }
    void this.readAdaptiveIterationStatsAsync(renderer)
      .catch((error: unknown) => {
        console.warn('[AVBD] adaptive iteration stats readback failed', error);
      });
    return true;
  }

  capturePhaseDebug(
    renderer: any,
    pairDispatchCount: number,
    regularizationAlpha: number,
    phaseOffset: 0 | 9,
    frictionSolveScale = 1.0,
    tangentialRegularizationAlpha = regularizationAlpha,
  ): void {
    if (!this.debugEnabled) return;
    const boundedDispatch = Math.min(pairDispatchCount, this.maxActivePairContacts);
    if (boundedDispatch <= 0) return;
    this.accumulatePhaseDebugCountersKernel.computeNode.parameters.pairDispatchCount.value = boundedDispatch;
    this.accumulatePhaseDebugCountersKernel.computeNode.parameters.regularizationAlpha.value = regularizationAlpha;
    this.accumulatePhaseDebugCountersKernel.computeNode.parameters.tangentialRegularizationAlpha.value = tangentialRegularizationAlpha;
    this.accumulatePhaseDebugCountersKernel.computeNode.parameters.phaseOffset.value = phaseOffset;
    this.accumulatePhaseDebugCountersKernel.computeNode.parameters.frictionSolveScale.value = frictionSolveScale;
    renderer.compute(
      this.accumulatePhaseDebugCountersKernel,
      this.finalizeContactDispatchIndirectAttr,
    );
  }

  setDebugEnabled(enabled: boolean): void {
    this.debugEnabled = enabled;
    this.debugReadbackInFlight = false;
    this.separatingTraceReadbackInFlight = false;
    this.separatingTraceArmed = true;
    this.lastDebugLogFrame = -1;
  }

  setDebugLogInterval(intervalFrames: number): void {
    this.debugEveryNFrames = Math.max(1, Math.floor(intervalFrames));
    this.lastDebugLogFrame = -1;
  }

  setFriction(staticFriction: number, dynamicFriction = staticFriction): void {
    const staticClamped = Math.max(0.0, staticFriction);
    const dynamicClamped = Math.max(0.0, dynamicFriction);
    this.currentFrictionStatic = staticClamped;
    this.currentFrictionDynamic = dynamicClamped;

    for (const kernel of this.primalBodySolveKernels) {
      kernel.computeNode.parameters.frictionStatic.value = staticClamped;
      kernel.computeNode.parameters.frictionDynamic.value = dynamicClamped;
    }
    this.adaptiveFallbackPrimalBodySolveKernel.computeNode.parameters.frictionStatic.value =
      staticClamped;
    this.adaptiveFallbackPrimalBodySolveKernel.computeNode.parameters.frictionDynamic.value =
      dynamicClamped;

    this.prepareStateKernel.computeNode.parameters.frictionStatic.value = staticClamped;

    this.capturePairDualStateKernel.computeNode.parameters.frictionStatic.value = staticClamped;
    this.capturePairDualStateKernel.computeNode.parameters.frictionDynamic.value = dynamicClamped;

    this.accumulatePhaseDebugCountersKernel.computeNode.parameters.frictionStatic.value = staticClamped;
    this.accumulatePhaseDebugCountersKernel.computeNode.parameters.frictionDynamic.value = dynamicClamped;

    this.accumulateDebugCountersKernel.computeNode.parameters.frictionStatic.value = staticClamped;
    this.accumulateDebugCountersKernel.computeNode.parameters.frictionDynamic.value = dynamicClamped;
  }

  setDualUpdateBeta(beta: number): void {
    this.setDualUpdateBetas(beta, Math.min(Math.max(0.0, beta), 100.0));
  }

  setDualUpdateBetas(linearBeta: number, angularBeta: number): void {
    const linearClamped = Math.max(0.0, linearBeta);
    const angularClamped = Math.max(0.0, angularBeta);
    this.capturePairDualStateKernel.computeNode.parameters.beta.value = linearClamped;
    if (this.referenceCapturePairDualStateKernel) {
      this.referenceCapturePairDualStateKernel.computeNode.parameters.beta.value = linearClamped;
    }
    if (this.referenceFinalCapturePairDualStateKernel) {
      this.referenceFinalCapturePairDualStateKernel.computeNode.parameters.beta.value = linearClamped;
    }
    if (this.adaptiveReferenceCapturePairDualStateKernel) {
      this.adaptiveReferenceCapturePairDualStateKernel.computeNode.parameters.beta.value = linearClamped;
    }
    this.captureJointDualStateKernel.computeNode.parameters.beta.value = linearClamped;
    this.captureJointDualStateKernel.computeNode.parameters.betaAngular.value = angularClamped;
  }

  setPreventPenetratingNormalDropout(enabled: boolean): void {
    this.currentPreventPenetratingNormalDropout = enabled;
    this.capturePairDualStateKernel.computeNode.parameters.preventPenetratingNormalDropout.value = enabled ? 1 : 0;
  }

  setFreezeTangentialPenaltyUpdates(enabled: boolean): void {
    this.currentFreezeTangentialPenaltyUpdates = enabled;
    this.capturePairDualStateKernel.computeNode.parameters.freezeTangentialPenaltyUpdates.value = enabled ? 1 : 0;
  }

  setPenaltyDecayGamma(gamma: number): void {
    const clamped = Math.max(0.0, Math.min(1.0, gamma));
    this.prepareStateKernel.computeNode.parameters.gamma.value = clamped;
    this.prepareJointStateKernel.computeNode.parameters.gamma.value = clamped;
    this.appendSpringConstraintRefsKernel.computeNode.parameters.gamma.value = clamped;
  }

  setPenaltyFloor(kStart: number): void {
    const clamped = Math.max(1e-6, kStart);
    this.currentPenaltyFloor = clamped;
    this.accumulatePhaseDebugCountersKernel.computeNode.parameters.kStart.value = clamped;
    this.accumulateDebugCountersKernel.computeNode.parameters.kStart.value = clamped;
    this.prepareStateKernel.computeNode.parameters.kStart.value = clamped;
    this.prepareJointStateKernel.computeNode.parameters.kStart.value = clamped;
    this.appendSpringConstraintRefsKernel.computeNode.parameters.kStart.value = clamped;
    for (const kernel of this.primalBodySolveKernels) {
      kernel.computeNode.parameters.kStart.value = clamped;
    }
    this.adaptiveFallbackPrimalBodySolveKernel.computeNode.parameters.kStart.value = clamped;
    this.capturePairDualStateKernel.computeNode.parameters.kStart.value = clamped;
    this.finalizeContactSolverScratchKernel.computeNode.parameters.kStart.value = clamped;
    this.captureJointDualStateKernel.computeNode.parameters.kStart.value = clamped;
  }

  setCollisionMargin(margin: number): void {
    this.currentCollisionMargin = Math.max(0.0, margin);
  }

  private traceSeparatingContacts(
    renderer: any,
    frameId: number,
    mainSeparating: number,
    mainBounded: number,
  ): void {
    if (this.separatingTraceReadbackInFlight) return;
    if (!renderer || typeof renderer.getArrayBufferAsync !== 'function') return;

    this.separatingTraceReadbackInFlight = true;
    const regularizationAlpha = this.currentRegularizationAlpha;
    const normalContactMargin = this.currentCollisionMargin;
    const suspiciousGapThreshold = 2.0 * normalContactMargin + 1e-6;
    const keySlotMask = this.contactKeySlotBitCount > 0 ? ((1 << this.contactKeySlotBitCount) - 1) : 0;

    Promise.all([
      renderer.getArrayBufferAsync(this.pairActivityAttr),
      renderer.getArrayBufferAsync(this.pairContactsAttr),
      renderer.getArrayBufferAsync(this.initialPoseAttr),
      renderer.getArrayBufferAsync(this.quaternionsAttr),
      renderer.getArrayBufferAsync(this.positionsAttr),
    ]).then(([
      activityRaw,
      pairContactsRaw,
      initialPoseRaw,
      quaternionsRaw,
      positionsRaw,
    ]: ArrayBuffer[]) => {
      const activity = new Uint32Array(activityRaw);
      const pairContacts = new Float32Array(pairContactsRaw);
      const pairContactWords = new Uint32Array(pairContactsRaw);
      const initialPose = new Float32Array(initialPoseRaw);
      const quaternions = new Float32Array(quaternionsRaw);
      const positions = new Float32Array(positionsRaw);
      const contactMetaBase = (p: number): number => contactRecordVec4FloatIndex(p, CONTACT_RECORD_META_OFFSET);
      const contactNormalPenBase = (p: number): number =>
        contactRecordVec4FloatIndex(p, CONTACT_RECORD_NORMAL_PEN_OFFSET);
      const contactArmABase = (p: number): number => contactRecordVec4FloatIndex(p, CONTACT_RECORD_ARM_A_OFFSET);
      const contactArmBBase = (p: number): number => contactRecordVec4FloatIndex(p, CONTACT_RECORD_ARM_B_OFFSET);
      const contactConstraintC0Base = (p: number): number =>
        contactRecordVec4FloatIndex(p, CONTACT_RECORD_CONSTRAINT_C0_OFFSET);
      const contactDualBase = (p: number): number => contactRecordVec4FloatIndex(p, CONTACT_RECORD_DUAL_OFFSET);
      const contactPenaltyBase = (p: number): number => contactRecordVec4FloatIndex(p, CONTACT_RECORD_PENALTY_OFFSET);

      const dot3 = (ax: number, ay: number, az: number, bx: number, by: number, bz: number): number =>
        ax * bx + ay * by + az * bz;
      const cross3 = (
        ax: number, ay: number, az: number,
        bx: number, by: number, bz: number,
      ): [number, number, number] => [
        ay * bz - az * by,
        az * bx - ax * bz,
        ax * by - ay * bx,
      ];
      const normalizeQuat = (x: number, y: number, z: number, w: number): [number, number, number, number] => {
        const len = Math.hypot(x, y, z, w);
        if (len <= 1e-12) return [0.0, 0.0, 0.0, 1.0];
        const inv = 1.0 / len;
        return [x * inv, y * inv, z * inv, w * inv];
      };
      const rotateVecByQuat = (
        qx: number, qy: number, qz: number, qw: number,
        vx: number, vy: number, vz: number,
      ): [number, number, number] => {
        const tx = 2.0 * (qy * vz - qz * vy);
        const ty = 2.0 * (qz * vx - qx * vz);
        const tz = 2.0 * (qx * vy - qy * vx);
        return [
          vx + qw * tx + (qy * tz - qz * ty),
          vy + qw * ty + (qz * tx - qx * tz),
          vz + qw * tz + (qx * ty - qy * tx),
        ];
      };
      const buildCanonicalTangentBasis = (
        nx: number, ny: number, nz: number,
      ): { t1: [number, number, number]; t2: [number, number, number] } => {
        const refAxis = Math.abs(ny) > 0.999
          ? ([1.0, 0.0, 0.0] as const)
          : ([0.0, 1.0, 0.0] as const);
        const [t1RawX, t1RawY, t1RawZ] = cross3(refAxis[0], refAxis[1], refAxis[2], nx, ny, nz);
        const t1Len2 = dot3(t1RawX, t1RawY, t1RawZ, t1RawX, t1RawY, t1RawZ);
        const t1: [number, number, number] = t1Len2 > 1e-12
          ? [t1RawX / Math.sqrt(t1Len2), t1RawY / Math.sqrt(t1Len2), t1RawZ / Math.sqrt(t1Len2)]
          : [0.0, 0.0, 1.0];
        const [t2RawX, t2RawY, t2RawZ] = cross3(nx, ny, nz, t1[0], t1[1], t1[2]);
        const t2Len2 = dot3(t2RawX, t2RawY, t2RawZ, t2RawX, t2RawY, t2RawZ);
        const t2: [number, number, number] = t2Len2 > 1e-12
          ? [t2RawX / Math.sqrt(t2Len2), t2RawY / Math.sqrt(t2Len2), t2RawZ / Math.sqrt(t2Len2)]
          : [1.0, 0.0, 0.0];
        return { t1, t2 };
      };
      const buildTangentBasisFromPreferredT1 = (
        nx: number, ny: number, nz: number,
        preferredT1X: number, preferredT1Y: number, preferredT1Z: number,
      ): { t1: [number, number, number]; t2: [number, number, number] } => {
        const dotPreferred = preferredT1X * nx + preferredT1Y * ny + preferredT1Z * nz;
        const projectedX = preferredT1X - nx * dotPreferred;
        const projectedY = preferredT1Y - ny * dotPreferred;
        const projectedZ = preferredT1Z - nz * dotPreferred;
        const projectedLen2 = dot3(projectedX, projectedY, projectedZ, projectedX, projectedY, projectedZ);
        if (projectedLen2 <= 1e-12) {
          return buildCanonicalTangentBasis(nx, ny, nz);
        }
        const invProjectedLen = 1.0 / Math.sqrt(projectedLen2);
        const t1: [number, number, number] = [
          projectedX * invProjectedLen,
          projectedY * invProjectedLen,
          projectedZ * invProjectedLen,
        ];
        const [t2RawX, t2RawY, t2RawZ] = cross3(nx, ny, nz, t1[0], t1[1], t1[2]);
        const t2Len2 = dot3(t2RawX, t2RawY, t2RawZ, t2RawX, t2RawY, t2RawZ);
        if (t2Len2 <= 1e-12) {
          return buildCanonicalTangentBasis(nx, ny, nz);
        }
        const invT2Len = 1.0 / Math.sqrt(t2Len2);
        return {
          t1,
          t2: [t2RawX * invT2Len, t2RawY * invT2Len, t2RawZ * invT2Len],
        };
      };
      const buildTangentBasisFromAngle = (
        nx: number, ny: number, nz: number,
        theta: number,
      ): { t1: [number, number, number]; t2: [number, number, number] } => {
        const canonical = buildCanonicalTangentBasis(nx, ny, nz);
        const c = Math.cos(theta);
        const s = Math.sin(theta);
        const preferredT1X = canonical.t1[0] * c + canonical.t2[0] * s;
        const preferredT1Y = canonical.t1[1] * c + canonical.t2[1] * s;
        const preferredT1Z = canonical.t1[2] * c + canonical.t2[2] * s;
        return buildTangentBasisFromPreferredT1(nx, ny, nz, preferredT1X, preferredT1Y, preferredT1Z);
      };
      const formatWarmstartReason = (reason: number): string => {
        switch (reason) {
          case 1: return 'ex';
          case 2: return 'pr';
          case 3: return 'ng';
          case 4: return 'in';
          case 5: return 'rp';
          case 6: return 'mx';
          default: return '--';
        }
      };

      const solverContactView = decodeSolverContactListHeader(
        activity[this.pairSolverActiveContactsOffset] ?? 0,
        this.pairCanonicalActiveContactsOffset,
        this.pairSolverActiveContactsOffset,
        this.maxActivePairContacts,
      );
      const activeContacts = activity.subarray(
        solverContactView.entriesOffset,
        solverContactView.entriesOffset + solverContactView.count,
      );
      const activeListCount = solverContactView.count;
      const offenders: Array<{
        p: number;
        i: number;
        j: number;
        keyBase: number;
        keySlot: number;
        featureKey: number;
        warmstartReason: number;
        preserveWarmstart: boolean;
        stick: boolean;
        reuse: boolean;
        penetration: number;
        rawPenetration: number;
        currentGap: number;
        c0: number;
        cRegN: number;
        dualN: number;
        penaltyN: number;
        clampedSeparated: boolean;
        reusedSeparated: boolean;
      }> = [];

      for (let k = 0; k < activeListCount; k++) {
        const p = activeContacts[k] ?? this.maxPairContacts;
        if (p >= this.maxPairContacts) continue;

        const metaBase = contactMetaBase(p);
        if ((pairContacts[metaBase + 2] ?? 0.0) < 0.5) continue;

        const i = Math.round(pairContacts[metaBase] ?? -1);
        const j = Math.round(pairContacts[metaBase + 1] ?? -1);
        if (i < 0 || j < 0 || i === j) continue;

        const nBase = contactNormalPenBase(p);
        const nx = pairContacts[nBase] ?? 0.0;
        const ny = pairContacts[nBase + 1] ?? 0.0;
        const nz = pairContacts[nBase + 2] ?? 0.0;
        const penetration = pairContacts[nBase + 3] ?? 0.0;
        if (dot3(nx, ny, nz, nx, ny, nz) <= 1e-12) continue;

        const iBase = i * 4;
        const jBase = j * 4;
        const iPosX = positions[iBase] ?? 0.0;
        const iPosY = positions[iBase + 1] ?? 0.0;
        const iPosZ = positions[iBase + 2] ?? 0.0;
        const jPosX = positions[jBase] ?? 0.0;
        const jPosY = positions[jBase + 1] ?? 0.0;
        const jPosZ = positions[jBase + 2] ?? 0.0;
        const iInitBase = i * 8;
        const jInitBase = j * 8;
        const dPosIX = iPosX - (initialPose[iInitBase] ?? iPosX);
        const dPosIY = iPosY - (initialPose[iInitBase + 1] ?? iPosY);
        const dPosIZ = iPosZ - (initialPose[iInitBase + 2] ?? iPosZ);
        const dPosJX = jPosX - (initialPose[jInitBase] ?? jPosX);
        const dPosJY = jPosY - (initialPose[jInitBase + 1] ?? jPosY);
        const dPosJZ = jPosZ - (initialPose[jInitBase + 2] ?? jPosZ);

        const iQuatBase = i * 4;
        const jQuatBase = j * 4;
        const [currQIX, currQIY, currQIZ, currQIW] = normalizeQuat(
          quaternions[iQuatBase] ?? 0.0,
          quaternions[iQuatBase + 1] ?? 0.0,
          quaternions[iQuatBase + 2] ?? 0.0,
          quaternions[iQuatBase + 3] ?? 1.0,
        );
        const [currQJX, currQJY, currQJZ, currQJW] = normalizeQuat(
          quaternions[jQuatBase] ?? 0.0,
          quaternions[jQuatBase + 1] ?? 0.0,
          quaternions[jQuatBase + 2] ?? 0.0,
          quaternions[jQuatBase + 3] ?? 1.0,
        );
        const [initQIX, initQIY, initQIZ, initQIW] = normalizeQuat(
          initialPose[iInitBase + 4] ?? 0.0,
          initialPose[iInitBase + 5] ?? 0.0,
          initialPose[iInitBase + 6] ?? 0.0,
          initialPose[iInitBase + 7] ?? 1.0,
        );
        const [initQJX, initQJY, initQJZ, initQJW] = normalizeQuat(
          initialPose[jInitBase + 4] ?? 0.0,
          initialPose[jInitBase + 5] ?? 0.0,
          initialPose[jInitBase + 6] ?? 0.0,
          initialPose[jInitBase + 7] ?? 1.0,
        );

        const armABase = contactArmABase(p);
        const armBBase = contactArmBBase(p);
        const raStoredX = pairContacts[armABase] ?? 0.0;
        const raStoredY = pairContacts[armABase + 1] ?? 0.0;
        const raStoredZ = pairContacts[armABase + 2] ?? 0.0;
        const tangentAngle = pairContacts[armABase + 3] ?? 0.0;
        const rbStoredX = pairContacts[armBBase] ?? 0.0;
        const rbStoredY = pairContacts[armBBase + 1] ?? 0.0;
        const rbStoredZ = pairContacts[armBBase + 2] ?? 0.0;
        const sphereArmFlags = Math.round(pairContacts[armBBase + 3] ?? 0.0);
        const [raX, raY, raZ] = (sphereArmFlags & 1) !== 0
          ? [raStoredX, raStoredY, raStoredZ]
          : rotateVecByQuat(currQIX, currQIY, currQIZ, currQIW, raStoredX, raStoredY, raStoredZ);
        const [rbX, rbY, rbZ] = (sphereArmFlags & 2) !== 0
          ? [rbStoredX, rbStoredY, rbStoredZ]
          : rotateVecByQuat(currQJX, currQJY, currQJZ, currQJW, rbStoredX, rbStoredY, rbStoredZ);
        const [prevRaX, prevRaY, prevRaZ] = (sphereArmFlags & 1) !== 0
          ? [raStoredX, raStoredY, raStoredZ]
          : rotateVecByQuat(initQIX, initQIY, initQIZ, initQIW, raStoredX, raStoredY, raStoredZ);
        const [prevRbX, prevRbY, prevRbZ] = (sphereArmFlags & 2) !== 0
          ? [rbStoredX, rbStoredY, rbStoredZ]
          : rotateVecByQuat(initQJX, initQJY, initQJZ, initQJW, rbStoredX, rbStoredY, rbStoredZ);

        const [crossRaN_X, crossRaN_Y, crossRaN_Z] = cross3(raX, raY, raZ, nx, ny, nz);
        const [crossRbN_X, crossRbN_Y, crossRbN_Z] = cross3(rbX, rbY, rbZ, nx, ny, nz);
        const tangentBasis = buildTangentBasisFromAngle(nx, ny, nz, tangentAngle);
        const [t1X, t1Y, t1Z] = tangentBasis.t1;
        const [t2X, t2Y, t2Z] = tangentBasis.t2;
        const dqArawX = currQIW * -initQIX + currQIX * initQIW + currQIY * -initQIZ - currQIZ * -initQIY;
        const dqArawY = currQIW * -initQIY - currQIX * -initQIZ + currQIY * initQIW + currQIZ * -initQIX;
        const dqArawZ = currQIW * -initQIZ + currQIX * -initQIY - currQIY * -initQIX + currQIZ * initQIW;
        const dqArawW = currQIW * initQIW - currQIX * -initQIX - currQIY * -initQIY - currQIZ * -initQIZ;
        const dqAX = dqArawW < 0.0 ? -dqArawX : dqArawX;
        const dqAY = dqArawW < 0.0 ? -dqArawY : dqArawY;
        const dqAZ = dqArawW < 0.0 ? -dqArawZ : dqArawZ;
        const dqBrawX = currQJW * -initQJX + currQJX * initQJW + currQJY * -initQJZ - currQJZ * -initQJY;
        const dqBrawY = currQJW * -initQJY - currQJX * -initQJZ + currQJY * initQJW + currQJZ * -initQJX;
        const dqBrawZ = currQJW * -initQJZ + currQJX * -initQJY - currQJY * -initQJX + currQJZ * initQJW;
        const dqBrawW = currQJW * initQJW - currQJX * -initQJX - currQJY * -initQJY - currQJZ * -initQJZ;
        const dqBX = dqBrawW < 0.0 ? -dqBrawX : dqBrawX;
        const dqBY = dqBrawW < 0.0 ? -dqBrawY : dqBrawY;
        const dqBZ = dqBrawW < 0.0 ? -dqBrawZ : dqBrawZ;
        const dThetaAX = 2.0 * dqAX;
        const dThetaAY = 2.0 * dqAY;
        const dThetaAZ = 2.0 * dqAZ;
        const dThetaBX = 2.0 * dqBX;
        const dThetaBY = 2.0 * dqBY;
        const dThetaBZ = 2.0 * dqBZ;

        const constraintC0Base = contactConstraintC0Base(p);
        const c0 = pairContacts[constraintC0Base] ?? 0.0;
        const anchorGap = c0 - normalContactMargin;
        const featureWord = pairContactWords[metaBase + 3] ?? 0;
        const c0Scale = (featureWord & CONTACT_INFO_SPECULATIVE_BIT) !== 0
          ? 1.0
          : 1.0 - regularizationAlpha;
        const cRegN = c0Scale * c0
          + dot3(-nx, -ny, -nz, dPosIX, dPosIY, dPosIZ)
          + dot3(-crossRaN_X, -crossRaN_Y, -crossRaN_Z, dThetaAX, dThetaAY, dThetaAZ)
          + dot3(nx, ny, nz, dPosJX, dPosJY, dPosJZ)
          + dot3(crossRbN_X, crossRbN_Y, crossRbN_Z, dThetaBX, dThetaBY, dThetaBZ);
        if (cRegN <= 0.0) continue;

        const currentPointAX = iPosX + raX;
        const currentPointAY = iPosY + raY;
        const currentPointAZ = iPosZ + raZ;
        const currentPointBX = jPosX + rbX;
        const currentPointBY = jPosY + rbY;
        const currentPointBZ = jPosZ + rbZ;
        const currentGap = -dot3(
          currentPointAX - currentPointBX,
          currentPointAY - currentPointBY,
          currentPointAZ - currentPointBZ,
          nx,
          ny,
          nz,
        );
        const rawPenetration = -currentGap;

        const dualBase = contactDualBase(p);
        const penaltyBase = contactPenaltyBase(p);
        const dualN = Math.min(pairContacts[dualBase] ?? 0.0, 0.0);
        const penaltyN = Math.max(pairContacts[penaltyBase] ?? 0.0, 1e-6);

        const preserveWarmstart = ((featureWord >>> 16) & 0x1) !== 0;
        const stick = ((featureWord >>> 17) & 0x1) !== 0;
        const reuse = ((featureWord >>> 18) & 0x1) !== 0;
        const warmstartReason = (featureWord >>> 21) & 0x7;
        const featureKey = featureWord & 0x1FF;
        const keyWord = pairContactWords[contactRecordVec4FloatIndex(p, CONTACT_RECORD_CACHE_OFFSET)] ?? 0;
        const key = keyWord & 0x7fffffff;
        const keyBase = this.contactKeySlotBitCount > 0 ? (key >>> this.contactKeySlotBitCount) : key;
        const keySlot = this.contactKeySlotBitCount > 0 ? (key & keySlotMask) : 0;
        const clampedSeparated = penetration <= 1e-6 && currentGap > suspiciousGapThreshold;
        const reusedSeparated = reuse && clampedSeparated;

        offenders.push({
          p,
          i,
          j,
          keyBase,
          keySlot,
          featureKey,
          warmstartReason,
          preserveWarmstart,
          stick,
          reuse,
          penetration,
          rawPenetration,
          currentGap,
          c0,
          cRegN,
          dualN,
          penaltyN,
          clampedSeparated,
          reusedSeparated,
        });
      }

      offenders.sort((a, b) =>
        (Number(b.reusedSeparated) - Number(a.reusedSeparated))
        || (Number(b.clampedSeparated) - Number(a.clampedSeparated))
        || (b.cRegN - a.cRegN)
        || (b.currentGap - a.currentGap)
        || (b.penaltyN - a.penaltyN)
      );

      const top = offenders.slice(0, 6).map((entry) =>
        `p=${entry.p} ij=${entry.i}/${entry.j} key=${entry.keyBase.toString(16)}:${entry.keySlot} `
        + `feat=0x${entry.featureKey.toString(16)} warm=${entry.preserveWarmstart ? 1 : 0} `
        + `wsrc=${formatWarmstartReason(entry.warmstartReason)} stick=${entry.stick ? 1 : 0} reuse=${entry.reuse ? 1 : 0} `
        + `pen=${entry.penetration.toFixed(4)} pRaw=${entry.rawPenetration.toFixed(4)} gNow=${entry.currentGap.toFixed(4)} `
        + `c0N=${entry.c0.toFixed(4)} cRegN=${entry.cRegN.toFixed(4)} dualN=${entry.dualN.toFixed(3)} `
        + `kN=${entry.penaltyN.toFixed(3)} clampSep=${entry.clampedSeparated ? 1 : 0} reuseSep=${entry.reusedSeparated ? 1 : 0}`,
      );

      console.log(
        `[AVBD Separating Trace] frame=${frameId} mainSep=${mainSeparating}/${mainBounded} `
        + `activeList=${activeListCount} offenders=${offenders.length} top=${top[0] ?? 'none'}`,
      );
      if (top.length > 0) {
        console.log(`[AVBD Separating Trace Dump] ${top.join(' ; ')}`);
      }
      if (offenders.length === 0) {
        console.log(
          `[AVBD Separating Trace] frame=${frameId} mismatch=1 `
          + `reason=reconstruction_found_no_positive_cRegN_rows`,
        );
      }
    }).catch((error: unknown) => {
      console.warn('AVBD separating trace readback failed:', error);
    }).finally(() => {
      this.separatingTraceReadbackInFlight = false;
    });
  }

  maybeLogDebug(renderer: any, frameId: number, pairDispatchCount: number, bodyCount: number): void {
    if (!this.debugEnabled) return;
    if (this.debugReadbackInFlight) return;
    if (this.lastDebugLogFrame === frameId) return;
    if (this.lastDebugLogFrame >= 0 && frameId - this.lastDebugLogFrame < this.debugEveryNFrames) return;
    if (!renderer || typeof renderer.getArrayBufferAsync !== 'function') return;

    const boundedDispatch = Math.min(pairDispatchCount, this.maxActivePairContacts);
    if (boundedDispatch <= 0) return;

    this.debugReadbackInFlight = true;
    this.lastDebugLogFrame = frameId;

    this.accumulateDebugCountersKernel.computeNode.parameters.pairDispatchCount.value = boundedDispatch;
    this.accumulateBodyColorDebugCountersKernel.computeNode.parameters.bodyCount.value = bodyCount;

    renderer.compute(this.clearDebugCountersKernel, [1, 1, 1]);
    // Adaptive early-out clears only the iterative contact dispatch. Debug
    // inspection must use the independently latched active-contact snapshot,
    // just like the unconditional world-arm finalizer.
    renderer.compute(this.accumulateDebugCountersKernel, this.finalizeContactDispatchIndirectAttr);
    renderer.compute(
      this.accumulateBodyColorDebugCountersKernel,
      this.constrainedBodyDispatchIndirectAttr,
    );

    Promise.all([
      renderer.getArrayBufferAsync(this.debugCountersAttr),
      renderer.getArrayBufferAsync(this.phaseDebugCountersAttr),
    ]).then(([dualRaw, phaseRaw]: [ArrayBuffer, ArrayBuffer]) => {
      const values = new Uint32Array(dualRaw);
      const scanned = values[0] ?? 0;
      const valid = values[1] ?? 0;
      const bounded = values[2] ?? 0;
      const staticRegime = values[3] ?? 0;
      const nearCone = values[4] ?? 0;
      const tinyNormal = values[5] ?? 0;
      const nyPos = values[6] ?? 0;
      const nyNeg = values[7] ?? 0;
      const boundViol = values[8] ?? 0;
      const nyVertical = values[9] ?? 0;
      const nyHorizontal = values[10] ?? 0;
      const constrainedBodies = values[11] ?? 0;
      const fallbackBodies = values[12] ?? 0;
      const totalBodyConstraintRefs = values[13] ?? 0;
      const saturatedBodyConstraintLists = values[14] ?? 0;
      const maxBodyConstraintRefs = values[15] ?? 0;
      const coloredConstraintEdges = values[16] ?? 0;
      const sameColorConstraintEdges = values[17] ?? 0;
      const staticPct = bounded > 0 ? (100 * staticRegime / bounded).toFixed(1) : '0.0';
      const nearConePct = bounded > 0 ? (100 * nearCone / bounded).toFixed(1) : '0.0';
      const fallbackPct = constrainedBodies > 0 ? (100 * fallbackBodies / constrainedBodies).toFixed(1) : '0.0';
      const avgBodyConstraintRefs = constrainedBodies > 0 ? (totalBodyConstraintRefs / constrainedBodies).toFixed(2) : '0.00';
      const colorConflictPct = coloredConstraintEdges > 0 ? (100 * sameColorConstraintEdges / coloredConstraintEdges).toFixed(2) : '0.00';
      console.log(
        `[AVBD Debug] frame=${frameId} scanned=${scanned} valid=${valid} bounded=${bounded} ` +
        `static=${staticRegime}(${staticPct}%) nearCone=${nearCone}(${nearConePct}%) ` +
        `tinyNormal=${tinyNormal} nyPos=${nyPos} nyNeg=${nyNeg} ` +
        `nyVertical=${nyVertical} nyHorizontal=${nyHorizontal} boundViol=${boundViol} ` +
        `colorFallback=${fallbackBodies}/${constrainedBodies}(${fallbackPct}%) ` +
        `bodyRefs=${totalBodyConstraintRefs} avgBodyRefs=${avgBodyConstraintRefs} ` +
        `maxBodyRefs=${maxBodyConstraintRefs} saturatedBodies=${saturatedBodyConstraintLists} ` +
        `colorConflicts=${sameColorConstraintEdges}/${coloredConstraintEdges}(${colorConflictPct}%)`,
      );

      const phase = new Uint32Array(phaseRaw);
      const mainScanned = phase[0] ?? 0;
      const mainBounded = phase[1] ?? 0;
      const mainNearCone = phase[2] ?? 0;
      const mainTinyNormal = phase[3] ?? 0;
      const mainNyPos = phase[4] ?? 0;
      const mainNyNeg = phase[5] ?? 0;
      const mainSeparating = phase[6] ?? 0;
      const mainConeClamp = phase[7] ?? 0;
      const mainFrictionSuppressed = phase[8] ?? 0;
      const postScanned = phase[9] ?? 0;
      const postBounded = phase[10] ?? 0;
      const postNearCone = phase[11] ?? 0;
      const postTinyNormal = phase[12] ?? 0;
      const postNyPos = phase[13] ?? 0;
      const postNyNeg = phase[14] ?? 0;
      const postSeparating = phase[15] ?? 0;
      const postConeClamp = phase[16] ?? 0;
      const postFrictionSuppressed = phase[17] ?? 0;
      const mainNearConePct = mainBounded > 0 ? (100 * mainNearCone / mainBounded).toFixed(1) : '0.0';
      const postNearConePct = postBounded > 0 ? (100 * postNearCone / postBounded).toFixed(1) : '0.0';
      console.log(
        `[AVBD Solve Phase Debug] frame=${frameId} ` +
        `main(scanned=${mainScanned} bounded=${mainBounded} nearCone=${mainNearCone}(${mainNearConePct}%) ` +
        `tinyN=${mainTinyNormal} ny+/-=${mainNyPos}/${mainNyNeg} sep=${mainSeparating} coneClamp=${mainConeClamp} fricOffSep=${mainFrictionSuppressed}) ` +
        `post(scanned=${postScanned} bounded=${postBounded} nearCone=${postNearCone}(${postNearConePct}%) ` +
        `tinyN=${postTinyNormal} ny+/-=${postNyPos}/${postNyNeg} sep=${postSeparating} coneClamp=${postConeClamp} fricOffSep=${postFrictionSuppressed})`,
      );

      if (mainSeparating > 0) {
        if (this.separatingTraceArmed) {
          this.traceSeparatingContacts(renderer, frameId, mainSeparating, mainBounded);
          this.separatingTraceArmed = false;
        }
      } else {
        this.separatingTraceArmed = true;
      }
    }).catch((error: unknown) => {
      console.warn('AVBD debug readback failed:', error);
    }).finally(() => {
      this.debugReadbackInFlight = false;
    });
  }
}
